import { useState, useEffect, useRef } from "react";
import {
  House, Leaf, Plant, Coffee, Book, Sparkle,
  Drop, Moon, Sun, Lightning, Notebook, Heart, Star,
  Users, Barbell, Flower, Acorn,
  Check, Info, Plus, Minus,
  Butterfly, Feather, Wind,
  Person, Gear, Envelope, Link, Scales, Fire, Waves, Tree,
  ChatCircle, Trophy, Target,
} from "@phosphor-icons/react";
import { supabase } from './supabase';
import { 
  loginUser, signUpUser, getMembers, getCheckins, saveCheckin, getTodayCheckin,
  getWorkouts, saveWorkout, getPRs, savePR, deletePR, getWeightLogs, saveWeightLog,
  getVideoLibrary, saveVideo, deleteVideo, updateWeeklyWorkout,
  getNutritionProfile, saveNutritionProfile, getNutritionLogs, saveNutritionLog, uploadFoodPhoto,
  getWins, saveWin, toggleLike, addComment, deleteWin,
  getKudos, sendKudos,
  getPTClients, getPTCheckItems, savePTCheckItem, deletePTCheckItem,
  getPTPrograms, savePTProgram, updatePTProgram, deletePTProgram,
  getPTGoals, savePTGoal, updatePTGoal, deletePTGoal,
  getPTAppointments, savePTAppointment, deletePTAppointment,
  getPTReactions, savePTReaction, markPTReactionsSeen,
  getQA, saveQuestion, saveAnswer, deleteQA,
  getAnnouncements, saveAnnouncement, deleteAnnouncement,
  getWeeklyWorkouts, saveWeeklyWorkout,
  getSetting, saveSetting,
  getAttendance, saveAttendance,
  resetMemberPassword,
  getMessages, sendMessage,
  saveUserEmail, getBelmondoMembers,
  getUserPrefs, saveUserPrefs,
  getJournalEntry, saveJournalEntry,
  getCupFillers, saveCupFiller, updateCupFiller, deleteCupFiller,
  getCommunityPosts, saveCommunityPost, deleteCommunityPost,
  getCommunityHearts, toggleCommunityHeart, getCommunityComments, saveCommunityComment,
  getProgramResources, saveProgramResource, deleteProgramResource,
  uploadCommunityImage,
  getExpansionWorkouts, saveExpansionWorkout, deleteExpansionWorkout, getAllExpansionWorkouts,
  getWorkoutLog, saveWorkoutLog, getUserWorkoutLogs,
  getMovementLibrary, saveMovement, deleteMovement,
  getExpansionPRs, saveExpansionPR, deleteExpansionPR,
  getProgramStartDate, saveProgramStartDate,
  getAllJournalEntriesForUsers, getAllCupFillersForUsers,
  registerPushSubscription, sendPushNotification,
  scheduleNotification, getScheduledNotifications, deleteScheduledNotification,
} from './supabase';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Jost:wght@300;400;500;600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #F5F0EB;
    --surface: #FDFAF6;
    --card: #FFFFFF;
    --pink: #E8647A;
    --pink-soft: #F0A0AF;
    --pink-pale: #FDE8EC;
    --deep: #1E1416;
    --mid: #5C3D44;
    --soft: #9E7880;
    --gold: #D4933A;
    --forest: #3D7A5A;
    --forest-pale: #E0F0E8;
    --border: rgba(232,100,122,0.15);
    --shadow: 0 2px 16px rgba(30,20,22,0.08);
    --shadow-lg: 0 8px 40px rgba(30,20,22,0.14);
  }
  body { font-family: 'Jost', sans-serif; background: var(--bg); color: var(--deep); }
  .app-shell { min-height: 100vh; max-width: 480px; margin: 0 auto; background: var(--surface); }
  .coach-shell { min-height: 100vh; max-width: 960px; margin: 0 auto; background: var(--surface); }

  /* ── Desktop responsive layout ───────────────────────────────────────────── */
  @media (min-width: 768px) {
    body { background: #DDD8D0; }

    /* App shell becomes a two-column grid: sidebar | content */
    .app-shell {
      max-width: 1160px;
      min-height: 100vh;
      margin: 0 auto;
      display: grid;
      grid-template-columns: 220px 1fr;
      grid-template-rows: auto 1fr;
      box-shadow: 0 0 80px rgba(30,20,22,0.14);
    }

    /* Top bar spans full width */
    .top-bar {
      grid-column: 1 / -1;
      grid-row: 1;
      top: 0;
      z-index: 100;
    }

    /* Nav becomes a vertical sidebar */
    .bottom-nav {
      grid-column: 1;
      grid-row: 2;
      flex-direction: column;
      border-bottom: none !important;
      border-right: 1px solid var(--border);
      overflow-x: visible;
      overflow-y: auto;
      padding: 16px 0;
      align-items: stretch;
      position: sticky;
      top: 61px;
      height: calc(100vh - 61px);
      width: 220px;
    }

    /* Nav items: horizontal (icon + label side by side) */
    .nav-item {
      flex: none !important;
      flex-direction: row !important;
      justify-content: flex-start !important;
      align-items: center;
      padding: 12px 20px !important;
      gap: 10px !important;
      font-size: 12px !important;
      letter-spacing: 0.5px;
      border-radius: 8px;
      margin: 2px 8px;
      width: auto !important;
      text-align: left;
    }
    .nav-item.active { background: rgba(0,0,0,0.06); }
    .nav-icon { font-size: 18px !important; }

    /* Page content: right column, nicely padded */
    .page-content {
      grid-column: 2;
      grid-row: 2;
      padding: 32px 40px 48px !important;
      max-width: 800px;
    }

    /* Cards slightly more generous on desktop */
    .card { padding: 22px 24px; }

    /* Auth screen stays centered */
    .auth-screen { max-width: 100%; }

    /* Coach shell stays wide, just better centered */
    .coach-shell { max-width: 1160px; box-shadow: 0 0 80px rgba(30,20,22,0.14); }
  }
  /* ─────────────────────────────────────────────────────────────────────────── */
  .pt-shell { --pink:#5B9BD5; --pink-soft:#A8C8E8; --pink-pale:#E8F2FA; --deep:#1A2332; --mid:#4A5568; --soft:#7B8FA6; --gold:#5B9BD5; --bg:#F4F7FA; --surface:#FAFCFE; --border:rgba(91,155,213,0.18); }
  .pt-coach-shell { --pink:#5B9BD5; --pink-soft:#A8C8E8; --pink-pale:#E8F2FA; --deep:#1A2332; --mid:#4A5568; --soft:#7B8FA6; --gold:#5B9BD5; --bg:#F4F7FA; --surface:#FAFCFE; --border:rgba(91,155,213,0.18); }
  .bc-shell { --pink:#9CAF88; --pink-soft:#BDD0AF; --pink-pale:#EEF4EA; --gold:#5B9BD5; --bg:#F8FAF7; --surface:#FCFDFC; --border:rgba(155,138,166,0.18); }
  .auth-screen { min-height: 100vh; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:40px 24px; background: linear-gradient(145deg, #1a1f2e 0%, #1e2a24 40%, #221c2a 100%); position: relative; overflow: hidden; }
  .auth-screen::before { content:''; position:absolute; top:-30%; left:-20%; width:60%; height:80%; background: radial-gradient(ellipse, #9CAF8818 0%, transparent 70%); pointer-events:none; }
  .auth-screen::after { content:''; position:absolute; bottom:-20%; right:-10%; width:50%; height:60%; background: radial-gradient(ellipse, #5B9BD518 0%, transparent 70%); pointer-events:none; }
  .auth-logo { font-family:'Cormorant Garamond',serif; font-size:42px; font-weight:800; letter-spacing:-1px; margin-bottom:2px; text-align:center; background: linear-gradient(135deg, #9CAF88 0%, #5B9BD5 50%, #9B8AA6 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
  .auth-sub { font-size:12px; letter-spacing:4px; text-transform:uppercase; color:rgba(255,255,255,0.4); margin-bottom:40px; text-align:center; }
  .auth-card { background:rgba(255,255,255,0.04); backdrop-filter:blur(20px); border:1px solid rgba(255,255,255,0.08); border-radius:24px; padding:36px 28px; width:100%; max-width:360px; box-shadow: 0 24px 80px rgba(0,0,0,0.4); }
  .auth-card h2 { font-family:'Cormorant Garamond',serif; font-size:24px; font-weight:700; color:white; margin-bottom:22px; }
  .auth-card .form-group label { color: rgba(255,255,255,0.5); }
  .auth-card .form-input { background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.12); color: white; }
  .auth-card .form-input::placeholder { color: rgba(255,255,255,0.25); }
  .auth-card .form-input:focus { border-color: #9B8AA6; background: rgba(255,255,255,0.08); }
  .auth-card .btn-primary { background: linear-gradient(135deg, #9CAF88, #5B9BD5); border: none; }
  .auth-card-light { background: var(--card); border-radius:24px; padding:36px 28px; width:100%; max-width:400px; box-shadow:var(--shadow-lg); }
  .auth-card-light h2 { font-family:'Cormorant Garamond',serif; font-size:24px; font-weight:700; color:var(--deep); margin-bottom:22px; }
  .form-group { margin-bottom:14px; }
  .form-group label { display:block; font-size:10px; letter-spacing:2px; text-transform:uppercase; color:var(--soft); margin-bottom:6px; font-weight:700; }
  .form-input { width:100%; padding:12px 16px; border:1.5px solid var(--border); border-radius:10px; font-family:'Jost',sans-serif; font-size:15px; color:var(--deep); background:var(--bg); outline:none; transition:border-color 0.2s; }
  .form-input:focus { border-color:var(--pink); }
  .form-textarea { resize:vertical; min-height:80px; }
  .form-select { width:100%; padding:12px 16px; border:1.5px solid var(--border); border-radius:10px; font-family:'Jost',sans-serif; font-size:14px; color:var(--deep); background:var(--bg); outline:none; }
  .btn-primary { width:100%; padding:14px; background:var(--pink); color:white; border:none; border-radius:12px; font-family:'Jost',sans-serif; font-size:15px; font-weight:700; cursor:pointer; transition:all 0.2s; }
  .btn-primary:hover { background:#d4566a; transform:translateY(-1px); }
  .btn-secondary { padding:10px 20px; background:transparent; color:var(--pink); border:1.5px solid var(--pink); border-radius:10px; font-family:'Jost',sans-serif; font-size:13px; font-weight:600; cursor:pointer; transition:all 0.2s; }
  .btn-secondary:hover { background:var(--pink); color:white; }
  .btn-ghost { padding:8px 16px; background:transparent; color:var(--soft); border:none; font-family:'Jost',sans-serif; font-size:13px; cursor:pointer; }
  .btn-forest { background:var(--forest); color:white; border:none; border-radius:10px; padding:10px 18px; font-family:'Jost',sans-serif; font-size:13px; font-weight:600; cursor:pointer; }
  .error-msg { color:var(--pink); font-size:13px; margin-top:8px; }
  .top-bar { padding:16px 20px 12px; display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid var(--border); background:var(--surface); position:sticky; top:0; z-index:50; }
  .top-bar-title { font-family:'Cormorant Garamond',serif; font-size:18px; font-weight:700; color:var(--deep); }
  .top-bar-sub { font-size:10px; color:var(--soft); letter-spacing:1.5px; text-transform:uppercase; margin-top:1px; }
  .avatar { width:36px; height:36px; border-radius:50%; background:var(--pink); display:flex; align-items:center; justify-content:center; color:white; font-weight:700; font-size:13px; flex-shrink:0; }
  .bottom-nav { position:sticky; top:0; width:100%; background:var(--surface); border-bottom:1px solid var(--border); display:flex; overflow-x:auto; z-index:60; scrollbar-width:none; }
  .bottom-nav::-webkit-scrollbar { display:none; }
  .nav-item { flex:1; display:flex; flex-direction:column; align-items:center; padding:8px 4px 10px; cursor:pointer; color:var(--soft); font-size:8px; letter-spacing:0.5px; text-transform:uppercase; font-weight:600; gap:3px; }
  .nav-item.active { color:var(--pink); }
  .nav-icon { font-size:18px; line-height:1; }
  .page-content { padding:18px 18px 32px; }
  .card { background:var(--card); border-radius:16px; padding:18px; margin-bottom:14px; box-shadow:var(--shadow); border:1px solid var(--border); }
  .card-title { font-family:'Cormorant Garamond',serif; font-size:19px; font-weight:700; color:var(--deep); margin-bottom:3px; }
  .card-sub { font-size:12px; color:var(--soft); margin-bottom:14px; }
  .section-label { font-size:10px; letter-spacing:2px; text-transform:uppercase; color:var(--soft); font-weight:700; margin-bottom:10px; }
  .check-item { display:flex; align-items:center; gap:12px; padding:10px 0; border-bottom:1px solid var(--border); cursor:pointer; }
  .check-item:last-child { border-bottom:none; }
  .check-box { width:22px; height:22px; border-radius:6px; border:2px solid var(--pink-soft); display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:all 0.2s; font-size:13px; }
  .check-box.checked { background:var(--forest); border-color:var(--forest); color:white; }
  .check-label { font-size:14px; color:var(--deep); font-weight:500; }
  .mood-row { display:flex; gap:8px; flex-wrap:wrap; }
  .mood-btn { padding:7px 13px; border-radius:20px; border:1.5px solid var(--border); background:transparent; font-size:12px; cursor:pointer; transition:all 0.15s; color:var(--mid); font-family:'Jost',sans-serif; font-weight:600; }
  .mood-btn.selected { background:var(--pink); color:white; border-color:var(--pink); }
  .win-card { background:var(--card); border-radius:14px; padding:14px; margin-bottom:10px; border-left:3px solid var(--gold); box-shadow:var(--shadow); position:relative; user-select:none; -webkit-user-select:none; }
  .win-card.gratitude { border-left-color:var(--pink-soft); }
  .win-text { font-size:13px; color:var(--mid); line-height:1.5; user-select:text; -webkit-user-select:text; }
  .win-card button { position:relative; z-index:10; cursor:pointer !important; pointer-events:all !important; }
  .qa-item { background:var(--card); border-radius:14px; padding:14px; margin-bottom:10px; border:1px solid var(--border); box-shadow:var(--shadow); position:relative; }
  .qa-item button { position:relative; z-index:10; cursor:pointer !important; pointer-events:all !important; }
  .pr-item { display:flex; align-items:center; justify-content:space-between; padding:10px 0; border-bottom:1px solid var(--border); }
  .pr-item:last-child { border-bottom:none; }
  .pr-name { font-size:13px; font-weight:600; color:var(--deep); }
  .pr-value { font-family:'Cormorant Garamond',serif; font-size:20px; font-weight:700; color:var(--pink); }
  .pr-date { font-size:10px; color:var(--soft); }
  .workout-item { padding:10px 0; border-bottom:1px solid var(--border); }
  .workout-item:last-child { border-bottom:none; }
  .workout-type { display:inline-block; font-size:9px; letter-spacing:1px; text-transform:uppercase; font-weight:700; padding:3px 10px; border-radius:20px; margin-bottom:4px; }
  .workout-type.inperson { background:var(--pink-pale); color:var(--pink); }
  .workout-type.athome { background:var(--forest-pale); color:var(--forest); }
  .workout-type.alternative { background:#F5F0EB; color:var(--soft); }
  .workout-note { font-size:12px; color:var(--mid); margin-top:3px; }
  .week-card { background:var(--card); border-radius:14px; padding:18px; margin-bottom:14px; border:1px solid var(--border); box-shadow:var(--shadow); }
  .week-title { font-family:'Cormorant Garamond',serif; font-size:17px; font-weight:700; color:var(--deep); margin-bottom:3px; }
  .week-posted { font-size:10px; color:var(--soft); margin-bottom:14px; }
  .workout-slot { background:var(--bg); border-radius:10px; padding:12px; margin-bottom:10px; }
  .workout-slot:last-child { margin-bottom:0; }
  .slot-label { font-size:10px; letter-spacing:1.5px; text-transform:uppercase; font-weight:700; color:var(--pink); margin-bottom:6px; }
  .slot-content { font-size:13px; color:var(--mid); line-height:1.7; white-space:pre-wrap; }
  .qa-item { background:var(--card); border-radius:14px; padding:14px; margin-bottom:10px; border:1px solid var(--border); box-shadow:var(--shadow); }
  .qa-question { font-size:14px; font-weight:600; color:var(--deep); margin-bottom:6px; }
  .qa-asker { font-size:10px; color:var(--soft); margin-bottom:8px; }
  .qa-answer { background:var(--pink-pale); border-radius:8px; padding:10px 12px; font-size:13px; color:var(--mid); line-height:1.5; }
  .qa-answer-label { font-size:10px; letter-spacing:1.5px; text-transform:uppercase; font-weight:700; color:var(--pink); margin-bottom:4px; }
  .serving-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:14px; }
  .serving-card { background:var(--bg); border-radius:12px; padding:12px; text-align:center; }
  .serving-icon { font-size:22px; margin-bottom:4px; }
  .serving-label { font-size:10px; color:var(--soft); letter-spacing:1px; text-transform:uppercase; margin-top:2px; font-weight:700; }
  .serving-unit { font-size:10px; color:var(--mid); margin-top:3px; }
  .coach-top { background:linear-gradient(135deg, #1E1416 0%, #3D1F28 100%); padding:20px 28px; display:flex; align-items:center; justify-content:space-between; }
  .coach-logo { font-family:'Cormorant Garamond',serif; font-size:22px; font-weight:800; color:var(--pink); }
  .coach-tabs { display:flex; border-bottom:2px solid var(--border); padding:0 20px; background:var(--surface); overflow-x:auto; }
  .coach-tab { padding:14px 16px; font-size:11px; font-weight:700; color:var(--soft); cursor:pointer; white-space:nowrap; border-bottom:2px solid transparent; margin-bottom:-2px; transition:all 0.2s; letter-spacing:0.5px; text-transform:uppercase; }
  .coach-tab.active { color:var(--pink); border-bottom-color:var(--pink); }
  .win-header { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
  .win-name { font-weight:700; font-size:13px; color:var(--deep); }
  .win-date { font-size:10px; color:var(--soft); margin-left:auto; }
  .win-tag { font-size:9px; letter-spacing:1px; text-transform:uppercase; font-weight:700; padding:2px 8px; border-radius:20px; }
  .win-tag.win { background:rgba(212,147,58,0.15); color:var(--gold); }
  .win-tag.gratitude { background:var(--pink-pale); color:var(--pink); }
  .member-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(200px,1fr)); gap:14px; }
  .member-card { background:var(--card); border-radius:14px; padding:16px; border:1px solid var(--border); cursor:pointer; transition:all 0.2s; }
  .member-card:hover { transform:translateY(-2px); box-shadow:var(--shadow-lg); }
  .member-card-name { font-family:'Cormorant Garamond',serif; font-size:18px; font-weight:700; color:var(--deep); margin-bottom:4px; }
  .member-stat { font-size:11px; color:var(--soft); display:flex; gap:6px; align-items:center; margin-top:5px; }
  .stat-dot { width:6px; height:6px; border-radius:50%; background:var(--forest); flex-shrink:0; }
  .stat-dot.empty { background:#E0D8D0; }
  .today-row { display:flex; align-items:center; justify-content:space-between; padding:12px; background:var(--card); border-radius:12px; margin-bottom:8px; border:1px solid var(--border); cursor:pointer; }
  .today-row:hover { box-shadow:var(--shadow); }
  .today-name { font-weight:700; font-size:14px; color:var(--deep); }
  .today-chips { display:flex; gap:5px; flex-wrap:wrap; }
  .chip { font-size:9px; padding:3px 8px; border-radius:20px; font-weight:700; letter-spacing:0.5px; }
  .chip.green { background:var(--forest-pale); color:var(--forest); }
  .chip.pink { background:var(--pink-pale); color:var(--pink); }
  .chip.gray { background:#F0EBE5; color:var(--soft); }
  .chip.gold { background:rgba(212,147,58,0.12); color:var(--gold); }
  .stats-row { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:20px; }
  .stat-box { background:var(--card); border-radius:14px; padding:18px; text-align:center; border:1px solid var(--border); }
  .stat-number { font-family:'Cormorant Garamond',serif; font-size:34px; font-weight:800; color:var(--pink); }
  .stat-label-big { font-size:10px; color:var(--soft); text-transform:uppercase; letter-spacing:1px; margin-top:2px; }
  .back-btn { display:flex; align-items:center; gap:6px; color:var(--soft); font-size:13px; cursor:pointer; margin-bottom:18px; background:none; border:none; font-family:'Jost',sans-serif; font-weight:600; }
  .empty-state { text-align:center; padding:36px 20px; color:var(--soft); font-size:13px; }
  .empty-icon { font-size:34px; margin-bottom:8px; }
  .detail-section { margin-bottom:24px; }
  .detail-title { font-family:'Cormorant Garamond',serif; font-size:20px; font-weight:700; color:var(--deep); margin-bottom:10px; }
  .slot-form { background:var(--bg); border-radius:12px; padding:14px; margin-bottom:12px; }
  .slot-form-label { font-size:10px; letter-spacing:2px; text-transform:uppercase; font-weight:700; margin-bottom:8px; }
  .anon-badge { font-size:10px; background:#F0EBE5; color:var(--soft); padding:2px 8px; border-radius:20px; font-weight:600; }
  .flyer-img { width:100%; border-radius:12px; margin-top:10px; }
  .ifthen-block { background:var(--bg); border-radius:12px; padding:14px; font-size:13px; color:var(--mid); line-height:1.8; white-space:pre-wrap; margin-top:10px; }
  .mt-8 { margin-top:8px; } .mt-12 { margin-top:12px; } .mt-16 { margin-top:16px; }
`;

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const fmt = (d) => new Date(d + 'T12:00:00').toLocaleDateString("en-US", { month: "short", day: "numeric" });
const initials = (name) => name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

const daysBetween = (d1, d2) => Math.floor((new Date(d1) - new Date(d2)) / 86400000);

const getStreakFromCheckins = (checkinDates) => {
  const sorted = [...checkinDates].sort().reverse();
  if (!sorted.length) return 0;
  let streak = 0; let cur = today();
  for (const d of sorted) {
    if (d === cur) { streak++; const prev = new Date(new Date(cur) - 86400000); cur = `${prev.getFullYear()}-${String(prev.getMonth()+1).padStart(2,'0')}-${String(prev.getDate()).padStart(2,'0')}`; }
    else if (d < cur) break;
  }
  return streak;
};

const getDaysSince = (dates) => {
  if (!dates.length) return 999;
  const latest = [...dates].sort().reverse()[0];
  return daysBetween(new Date(today()), new Date(latest));
};


const PROGRAMS = [
  { role: "rooted",    key: "rooted",    emoji: "🌱", name: "The Rooted Foundation", desc: "Build your foundation. Free to join — no code needed.", free: true,  color: "#9CAF88" },
  { role: "expansion", key: "expansion", emoji: "✨", name: "The Expansion",          desc: "Full workouts, nutrition coaching & a 12-week mindset journey.", free: false, color: "#5B9BD5" },
  { role: "member",    key: "bc",        emoji: "💪", name: "Get Thicc Camp",          desc: "The original boot camp experience.", free: false, color: "#E8647A" },
  { role: "pt_client", key: "pt",        emoji: "🏋️", name: "Personal Coaching",       desc: "One-on-one coaching with Madison.", free: false, color: "#5B9BD5" },
];

function AuthScreen({ onLogin }) {
  const [mode, setMode] = useState("landing"); // landing | login | signup_choose | signup_form
  const [signupProgram, setSignupProgram] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setUsername(""); setPassword(""); setName(""); setEmail(""); setConfirmPassword(""); setInviteCode(""); setError(""); };

  const handleLogin = async () => {
    if (!username || !password) { setError("Please enter your username and password."); return; }
    setLoading(true); setError("");
    const user = await loginUser(username, password);
    setLoading(false);
    if (user) onLogin(user); else setError("Incorrect username or password.");
  };

  const handleSignUp = async () => {
    if (!name || !username || !password) { setError("Please fill in all fields."); return; }
    if (password !== confirmPassword) { setError("Passwords don't match."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (!signupProgram.free && !inviteCode) { setError("Please enter your invite code."); return; }
    setLoading(true); setError("");
    const result = await signUpUser(name, username, password, inviteCode, signupProgram.role, email);
    setLoading(false);
    if (result.error) { setError(result.error); return; }
    onLogin(result.user);
  };

  const prog = signupProgram;

  return (
    <div className="auth-screen">
      <div className="auth-logo">{prog ? prog.name : "Belmondo Lifestyle"}</div>
      <div className="auth-sub">{prog ? "Create your account" : "by Madison"}</div>

      {mode === "landing" && (
        <div style={{ width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", gap: 12 }}>
          <button onClick={() => { setMode("login"); reset(); }} style={{ width: "100%", padding: 18, fontSize: 16, borderRadius: 14, background: "linear-gradient(135deg, #9CAF88, #5B9BD5)", color: "white", border: "none", fontFamily: "Jost, sans-serif", fontWeight: 700, cursor: "pointer", letterSpacing: "0.02em" }}>
            Sign In
          </button>
          <button onClick={() => { setMode("signup_choose"); reset(); }} style={{ width: "100%", padding: 16, fontSize: 15, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.75)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 14, fontFamily: "Jost, sans-serif", fontWeight: 700, cursor: "pointer" }}>
            Create Account
          </button>
        </div>
      )}

      {mode === "login" && (
        <div className="auth-card">
          <button onClick={() => { setMode("landing"); reset(); }} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 13, marginBottom: 16, fontFamily: "Jost, sans-serif" }}>← Back</button>
          <h2 style={{ marginBottom: 20 }}>Welcome back</h2>
          <div className="form-group"><label>Username</label><input className="form-input" value={username} onChange={e => setUsername(e.target.value)} placeholder="yourname" autoCapitalize="none" /></div>
          <div className="form-group"><label>Password</label><input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === "Enter" && handleLogin()} /></div>
          {error && <div className="error-msg" style={{ color: "#9CAF88", background: "#9CAF8818", border: "1px solid #9CAF8840" }}>{error}</div>}
          <button className="btn-primary mt-16" onClick={handleLogin} disabled={loading}>{loading ? "Signing in..." : "Sign In"}</button>
        </div>
      )}

      {mode === "signup_choose" && (
        <div className="auth-card-light" style={{ maxWidth: 400 }}>
          <button onClick={() => { setMode("landing"); reset(); }} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 13, marginBottom: 16, fontFamily: "Jost, sans-serif" }}>← Back</button>
          <h2 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 700, color: "var(--deep)", marginBottom: 6 }}>Choose Your Program</h2>
          <p style={{ fontSize: 13, color: "var(--soft)", marginBottom: 20 }}>Select the program you're joining</p>
          {PROGRAMS.map(p => (
            <div key={p.key} onClick={() => { setSignupProgram(p); setMode("signup_form"); reset(); }}
              style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "var(--bg)", borderRadius: 14, marginBottom: 10, cursor: "pointer", border: "2px solid transparent", transition: "all 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = p.color}
              onMouseLeave={e => e.currentTarget.style.borderColor = "transparent"}>
              <span style={{ fontSize: 26 }}>{p.emoji}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "var(--deep)" }}>{p.name}</div>
                <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2 }}>{p.desc}</div>
              </div>
              {p.free
                ? <span style={{ fontSize: 11, fontWeight: 700, color: p.color, background: p.color + "20", padding: "3px 8px", borderRadius: 20 }}>Free</span>
                : <span style={{ fontSize: 11, fontWeight: 600, color: "var(--soft)" }}>Code required</span>}
            </div>
          ))}
        </div>
      )}

      {mode === "signup_form" && prog && (
        <div className="auth-card-light">
          <button onClick={() => { setMode("signup_choose"); setSignupProgram(null); reset(); }} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 13, marginBottom: 16, fontFamily: "Jost, sans-serif" }}>← Back</button>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <span style={{ fontSize: 28 }}>{prog.emoji}</span>
            <div>
              <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, color: prog.color }}>{prog.name}</div>
              <div style={{ fontSize: 12, color: "var(--soft)" }}>Create your account</div>
            </div>
          </div>
          <div className="form-group"><label>Your Name</label><input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="First Last" /></div>
          <div className="form-group"><label>Username</label><input className="form-input" value={username} onChange={e => setUsername(e.target.value)} placeholder="e.g. jessica" autoCapitalize="none" /></div>
          <div className="form-group"><label>Email</label><input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" autoCapitalize="none" /></div>
          <div className="form-group"><label>Password</label><input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" /></div>
          <div className="form-group"><label>Confirm Password</label><input className="form-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" /></div>
          {!prog.free && (
            <div className="form-group"><label>Invite Code</label><input className="form-input" value={inviteCode} onChange={e => setInviteCode(e.target.value)} placeholder="Enter the code from Madison" autoCapitalize="none" />
              <div style={{ fontSize: 11, color: "var(--soft)", marginTop: 4 }}>Contact Madison if you don't have a code</div>
            </div>
          )}
          {error && <div className="error-msg">{error}</div>}
          <button className="btn-primary mt-16" onClick={handleSignUp} disabled={loading} style={{ background: prog.color }}>
            {loading ? "Creating account..." : "Create Account"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Email Prompt (one-time for existing users) ───────────────────────────────
function EmailPrompt({ user, onDone }) {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!email || !email.includes("@")) { return; }
    setSaving(true);
    await saveUserEmail(user.id, email);
    setSaving(false);
    onDone(email);
  };
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--bg)" }}>
      <style>{styles}</style>
      <div style={{ maxWidth: 400, width: "100%", background: "white", borderRadius: 20, padding: 32, boxShadow: "0 8px 32px rgba(0,0,0,0.1)" }}>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 8 }}>One quick thing ✉️</div>
        <p style={{ fontSize: 14, color: "var(--mid)", marginBottom: 24, lineHeight: 1.6 }}>We'd love to add your email so Madison can stay in touch with you. You'll only be asked this once.</p>
        <div className="form-group">
          <label>Your Email</label>
          <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" autoCapitalize="none" onKeyDown={e => e.key === "Enter" && handleSave()} />
        </div>
        <button className="btn-primary mt-16" onClick={handleSave} disabled={saving} style={{ width: "100%" }}>{saving ? "Saving..." : "Save Email"}</button>
        <button onClick={() => onDone(null)} style={{ width: "100%", marginTop: 10, background: "none", border: "none", color: "var(--soft)", fontSize: 13, cursor: "pointer", fontFamily: "Jost, sans-serif" }}>Skip for now</button>
      </div>
    </div>
  );
}

function MemberApp({ user, onLogout }) {
  const [tab, setTab] = useState("home");
  const [winsCount, setWinsCount] = useState(0);
  const [qaCount, setQaCount] = useState(0);
  const [hasNewWins, setHasNewWins] = useState(false);
  const [hasNewQA, setHasNewQA] = useState(false);

  const lsKey = (type) => `gt_last_seen_${type}_${user.id}`;

  useEffect(() => {
    const checkNew = async () => {
      const [wins, qa] = await Promise.all([getWins('bc'), getQA()]);
      const lastWins = localStorage.getItem(lsKey('wins'));
      const lastQA = localStorage.getItem(lsKey('qa'));
      setHasNewWins(lastWins ? wins.some(w => w.created_at > lastWins) : wins.length > 0);
      setHasNewQA(lastQA ? qa.some(q => q.created_at > lastQA || (q.answered_date && q.answered_date > lastQA)) : qa.length > 0);
    };
    checkNew();
  }, [user.id]);

  const [hasNewMessages, setHasNewMessages] = useState(false);

  useEffect(() => {
    const dmConvId = `bc_dm_${[user.id, "coach"].sort().join("_")}`;
    const lastMsg = localStorage.getItem(lsKey('messages'));
    Promise.all([getMessages(dmConvId), getMessages("bc_group")]).then(([dm, grp]) => {
      const allMsgs = [...dm, ...grp].filter(m => m.sender_id !== user.id);
      setHasNewMessages(lastMsg ? allMsgs.some(m => m.created_at > lastMsg) : allMsgs.length > 0);
    });
  }, [user.id]);

  const handleTabChange = (newTab) => {
    if (newTab === "wins") {
      localStorage.setItem(lsKey('wins'), new Date().toISOString());
      setHasNewWins(false);
    }
    if (newTab === "qa") {
      localStorage.setItem(lsKey('qa'), new Date().toISOString());
      setHasNewQA(false);
    }
    if (newTab === "messages") {
      localStorage.setItem(lsKey('messages'), new Date().toISOString());
      setHasNewMessages(false);
    }
    setTab(newTab);
  };

  const BC_GREEN = "#9CAF88";
  const BC_BLUE = "#5B9BD5";
  const BC_PURPLE = "#9B8AA6";
  const tabs = [
    { id: "home",      label: "Home",      Icon: House },
    { id: "checkin",   label: "Check-in",  Icon: Check },
    { id: "workouts",  label: "Workouts",  Icon: Barbell },
    { id: "prs",       label: "PRs",       Icon: Trophy },
    { id: "nutrition", label: "Nutrition", Icon: Acorn },
    { id: "wins",      label: "Wins",      Icon: Sparkle, dot: hasNewWins },
    { id: "messages",  label: "Messages",  Icon: ChatCircle, dot: hasNewMessages },
    { id: "qa",        label: "Q&A",       Icon: Notebook, dot: hasNewQA },
  ];

  return (
    <div className="app-shell bc-shell">
      <style>{styles}</style>
      <div className="top-bar">
        <div>
          <div className="top-bar-title">Get Thicc</div>
          <div className="top-bar-sub">{user.name}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="avatar">{initials(user.name)}</div>
          <button className="btn-ghost" onClick={onLogout} style={{ fontSize: 11 }}>Out</button>
        </div>
      </div>
      <div className="bottom-nav" style={{ borderBottom: `2px solid ${BC_PURPLE}30` }}>
        {tabs.map(t => (
          <div key={t.id} className={`nav-item ${tab === t.id ? "active" : ""}`} onClick={() => handleTabChange(t.id)} style={{ position: "relative", color: tab === t.id ? BC_GREEN : undefined }}>
            <t.Icon size={20} weight={tab === t.id ? "fill" : "regular"} />
            {t.dot && <span style={{ position: "absolute", top: 6, right: "calc(50% - 14px)", width: 8, height: 8, background: BC_GREEN, borderRadius: "50%", border: "2px solid var(--surface)" }} />}
            {t.label}
          </div>
        ))}
      </div>
      <div className="page-content">
        {tab === "home" && <MemberHome user={user} setTab={handleTabChange} />}
        {tab === "checkin" && <MemberCheckin user={user} />}
        {tab === "workouts" && <MemberWorkouts user={user} />}
        {tab === "prs" && <MemberPRs user={user} />}
        {tab === "nutrition" && <MemberNutrition user={user} />}
        {tab === "wins" && <MemberWins user={user} />}
        {tab === "qa" && <MemberQA user={user} />}
        {tab === "messages" && <BCMessaging user={user} isCoach={false} />}
      </div>
    </div>
  );
}

const DEFAULT_KUDOS_PRESETS = ["🔥","💪","✨","🌟","🍑","You got this!","Crushing it!","Miss you, get back in here! 😄","So proud of you!"];

function MemberHome({ user, setTab }) {
  const [myWorkouts, setMyWorkouts] = useState([]);
  const [myPRs, setMyPRs] = useState([]);
  const [myCheckins, setMyCheckins] = useState([]);
  const [recentWins, setRecentWins] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campWeek, setCampWeek] = useState(null);

  // Kudos
  const [myKudos, setMyKudos] = useState([]);
  const [showSendKudos, setShowSendKudos] = useState(false);
  const [kudosTo, setKudosTo] = useState(null);
  const [kudosMsg, setKudosMsg] = useState("");
  const [kudosCustom, setKudosCustom] = useState("");
  const [kudosSent, setKudosSent] = useState(false);
  const [members, setMembers] = useState([]);
  const [kudosPresets, setKudosPresets] = useState(DEFAULT_KUDOS_PRESETS);

  // MIA
  const [miaMembers, setMiaMembers] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [w, p, c, wins, ann, campStart, campEnd, allMembers, allCheckins, myKudosData, presetsRaw] = await Promise.all([
        getWorkouts(user.id),
        getPRs(user.id),
        getCheckins(user.id),
        getWins('bc'),
        getAnnouncements('bc'),
        getSetting('camp_start'),
        getSetting('camp_end'),
        getMembers(),
        getCheckins(null, 'bc'),
        getKudos(user.id),
        getSetting('kudos_presets'),
      ]);
      setMyWorkouts(w);
      setMyPRs(p);
      setMyCheckins(c);
      setRecentWins(wins.slice(0, 3));
      setAnnouncements(ann);
      setMyKudos(myKudosData);
      if (presetsRaw) {
        try { setKudosPresets(JSON.parse(presetsRaw)); } catch {}
      }
      if (campStart && campEnd) {
        const start = new Date(campStart);
        const end = new Date(campEnd);
        const now = new Date();
        const totalWeeks = Math.ceil((end - start) / (7 * 86400000));
        const currentWeek = Math.max(1, Math.min(Math.ceil((now - start) / (7 * 86400000)), totalWeeks));
        setCampWeek({ current: currentWeek, total: totalWeeks });
      }
      // MIA: members with no check-in in last 3 days (excluding self)
      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 3);
      const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth()+1).padStart(2,'0')}-${String(cutoff.getDate()).padStart(2,'0')}`;
      const otherMembers = allMembers.filter(m => m.id !== user.id);
      setMembers(otherMembers);
      const mia = otherMembers.filter(m => {
        const lastCheckin = allCheckins.filter(c => c.user_id === m.id).sort((a,b) => b.date.localeCompare(a.date))[0];
        return !lastCheckin || lastCheckin.date < cutoffStr;
      });
      setMiaMembers(mia);
      setLoading(false);
    };
    load();
  }, [user.id]);

  const todayCheckin = myCheckins.find(c => c.date === today());
  const streak = getStreakFromCheckins(myCheckins.map(c => c.date));

  const handleSendKudos = async () => {
    if (!kudosTo || !(kudosMsg || kudosCustom.trim())) return;
    const message = kudosCustom.trim() || kudosMsg;
    const success = await sendKudos({
      id: `kd_${Date.now()}`,
      fromUserId: user.id,
      fromUserName: user.name,
      toUserId: kudosTo.id,
      toUserName: kudosTo.name,
      message,
    });
    if (success) {
      setKudosSent(true);
      setKudosTo(null); setKudosMsg(""); setKudosCustom("");
      setTimeout(() => { setKudosSent(false); setShowSendKudos(false); }, 2500);
    }
  };

  const recentKudos = myKudos.filter(k => {
    const sent = new Date(k.created_at);
    return (new Date() - sent) < 2 * 24 * 60 * 60 * 1000;
  });

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div>Loading...</div>;

  return (
    <div>
      <div className="card" style={{ background: "linear-gradient(135deg, #1E1416 0%, #3D1F28 100%)", border: "none" }}>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, fontWeight: 800, color: "var(--pink)", marginBottom: 4 }}>
          Hey, {user.name}!
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: campWeek ? 6 : 18 }}>
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
        </div>
        {campWeek && (
          <div style={{ fontSize: 12, color: "var(--pink-soft)", fontWeight: 700, marginBottom: 14, letterSpacing: 0.5 }}>
            🏕️ Week {campWeek.current} of {campWeek.total}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
          {[["Workouts", myWorkouts.length], ["PRs", myPRs.length], ["Check-ins", myCheckins.length], ["🔥 Streak", streak]].map(([l, v]) => (
            <div key={l} style={{ textAlign: "center", background: "rgba(255,255,255,0.06)", borderRadius: 10, padding: "10px 4px" }}>
              <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: l === "🔥 Streak" ? "#FFD700" : "var(--pink)" }}>{v}</div>
              <div style={{ fontSize: 8, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: 1 }}>{l}</div>
            </div>
          ))}
        </div>
        {streak >= 3 && (
          <div style={{ marginTop: 12, background: "rgba(255,215,0,0.12)", borderRadius: 8, padding: "7px 12px", fontSize: 12, color: "#FFD700", fontWeight: 600 }}>
            🔥 {streak}-day check-in streak — keep it going!
          </div>
        )}
      </div>

      {/* Received Kudos */}
      {recentKudos.length > 0 && (
        <div className="card" style={{ borderLeft: "3px solid var(--pink)" }}>
          <div className="section-label">💌 You got some love!</div>
          {recentKudos.map(k => (
            <div key={k.id} style={{ padding: "7px 0", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
              <div className="avatar" style={{ width: 28, height: 28, fontSize: 10, flexShrink: 0 }}>{initials(k.from_user_name)}</div>
              <div>
                <span style={{ fontWeight: 700, fontSize: 12 }}>{k.from_user_name}</span>
                <span style={{ fontSize: 12, color: "var(--mid)", marginLeft: 6 }}>sent you</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--pink)", marginLeft: 6 }}>{k.message}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {announcements.length > 0 && (
        <div className="card" style={{ borderLeft: "3px solid var(--gold)" }}>
          <div className="section-label">From Madison 📣</div>
          {announcements.slice(0, 2).map(a => (
            <div key={a.id} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 14, color: "var(--deep)", fontWeight: 600, marginBottom: 2 }}>{a.title}</div>
              <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.5 }}>{a.body}</div>
              <div style={{ fontSize: 10, color: "var(--soft)", marginTop: 3 }}>{fmt(a.date)}</div>
            </div>
          ))}
        </div>
      )}

      {!todayCheckin && (
        <div className="card" style={{ borderLeft: "3px solid var(--pink)", cursor: "pointer" }} onClick={() => setTab("checkin")}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div className="card-title" style={{ fontSize: 16 }}>Daily Check-in</div>
              <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2 }}>You haven't checked in today yet</div>
            </div>
            <div style={{ fontSize: 22, color: "var(--pink)" }}>→</div>
          </div>
        </div>
      )}

      {/* Send Kudos */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setShowSendKudos(v => !v)}>
          <div>
            <div className="card-title" style={{ fontSize: 15 }}>💌 Send Kudos</div>
            <div style={{ fontSize: 11, color: "var(--soft)", marginTop: 1 }}>Hype up your girls!</div>
          </div>
          <div style={{ fontSize: 18, color: "var(--pink)" }}>{showSendKudos ? "↑" : "↓"}</div>
        </div>
        {showSendKudos && (
          <div style={{ marginTop: 14 }}>
            {kudosSent ? (
              <div style={{ textAlign: "center", padding: "18px 0", fontSize: 15, color: "var(--forest)", fontWeight: 700 }}>
                💌 Sent! You're the best! ✨
              </div>
            ) : (
              <>
                <div className="section-label">Who are you hyping?</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
                  {members.map(m => (
                    <button key={m.id}
                      onClick={() => setKudosTo(kudosTo?.id === m.id ? null : m)}
                      style={{ padding: "6px 12px", borderRadius: 20, border: `1.5px solid ${kudosTo?.id === m.id ? "var(--pink)" : "var(--border)"}`, background: kudosTo?.id === m.id ? "var(--pink-pale)" : "white", fontSize: 12, fontWeight: kudosTo?.id === m.id ? 700 : 400, color: kudosTo?.id === m.id ? "var(--pink)" : "var(--mid)", cursor: "pointer", fontFamily: "Jost, sans-serif" }}>
                      {m.name}
                    </button>
                  ))}
                </div>
                <div className="section-label">Pick a message</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                  {kudosPresets.map((p, i) => (
                    <button key={i}
                      onClick={() => { setKudosMsg(kudosMsg === p ? "" : p); setKudosCustom(""); }}
                      style={{ padding: "6px 12px", borderRadius: 20, border: `1.5px solid ${kudosMsg === p ? "var(--pink)" : "var(--border)"}`, background: kudosMsg === p ? "var(--pink-pale)" : "white", fontSize: 12, fontWeight: kudosMsg === p ? 700 : 400, color: kudosMsg === p ? "var(--pink)" : "var(--mid)", cursor: "pointer", fontFamily: "Jost, sans-serif" }}>
                      {p}
                    </button>
                  ))}
                </div>
                <div className="section-label">Or write your own</div>
                <input className="form-input" placeholder="Write something sweet..." value={kudosCustom}
                  onChange={e => { setKudosCustom(e.target.value); setKudosMsg(""); }}
                  style={{ marginBottom: 12 }} />
                <button className="btn-primary" onClick={handleSendKudos}
                  disabled={!kudosTo || !(kudosMsg || kudosCustom.trim())}
                  style={{ opacity: (!kudosTo || !(kudosMsg || kudosCustom.trim())) ? 0.5 : 1 }}>
                  Send Kudos 💌
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Check on your girls */}
      {miaMembers.length > 0 && (
        <div className="card" style={{ borderLeft: "3px solid var(--gold)" }}>
          <div className="section-label">👀 Check on your girls</div>
          <div style={{ fontSize: 11, color: "var(--soft)", marginBottom: 10 }}>Haven't checked in for 3+ days</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {miaMembers.map(m => (
              <button key={m.id}
                onClick={() => { setKudosTo(m); setShowSendKudos(true); window.scrollTo({ top: 999, behavior: "smooth" }); }}
                style={{ padding: "6px 14px", borderRadius: 20, border: "1.5px solid var(--gold)", background: "#FFF8EE", fontSize: 12, fontWeight: 600, color: "var(--gold)", cursor: "pointer", fontFamily: "Jost, sans-serif" }}>
                {m.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="section-label">Community Wins</div>
        {recentWins.length === 0
          ? <div style={{ fontSize: 13, color: "var(--soft)" }}>Be the first to share a win!</div>
          : recentWins.map(w => (
            <div key={w.id} style={{ padding: "9px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", gap: 7, alignItems: "center", marginBottom: 3 }}>
                <div className="avatar" style={{ width: 26, height: 26, fontSize: 10 }}>{initials(w.user_name)}</div>
                <span style={{ fontWeight: 700, fontSize: 12 }}>{w.user_name}</span>
                <span className={`win-tag ${w.type}`}>{w.type === "win" ? "Win" : w.type === "gratitude" ? "Gratitude" : "Share"}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--mid)", paddingLeft: 33 }}>{w.text}</div>
            </div>
          ))}
      </div>
    </div>
  );
}
function MemberCheckin({ user }) {
  const [selectedDate, setSelectedDate] = useState(today());
  const [checks, setChecks] = useState({
    water: false, sleep: false, supplements: false, workout: false,
    journaled: false, meditation: false, mobilized: false, nutrition: false, sharedWin: false
  });
  const [mood, setMood] = useState("");
  const [energy, setEnergy] = useState("");
  const [notes, setNotes] = useState("");
  const [customItems, setCustomItems] = useState([]);
  const [newCustom, setNewCustom] = useState("");
  const [saved, setSaved] = useState(false);
  const [yesterdayHits, setYesterdayHits] = useState([]);
  const [allCheckins, setAllCheckins] = useState([]);

  const defaultItems = [
    { key: "water", label: "💧 Drank 80oz of water or more" },
    { key: "sleep", label: "😴 Got 7+ hours of sleep" },
    { key: "supplements", label: "💊 Took supplements / vitamins" },
    { key: "workout", label: "🏋️ Completed a workout (at-home or in-gym)" },
    { key: "journaled", label: "📓 Journaled" },
    { key: "meditation", label: "🧘 Meditated / breathwork for 5+ minutes" },
    { key: "mobilized", label: "🤸 Mobilized for 10+ minutes" },
    { key: "nutrition", label: "🥗 Followed my nutrition plan" },
    { key: "sharedWin", label: "✨ Shared a win or gratitude with the group" },
  ];
  const moods = ["😊 Great", "😌 Good", "😐 Okay", "😔 Low", "😤 Stressed"];
  const energies = ["🔥 High", "⚡ Medium", "🌙 Low", "😴 Drained"];

  useEffect(() => {
    const load = async () => {
      const all = await getCheckins(user.id);
      setAllCheckins(all);
      const yest = new Date(new Date() - 86400000);
      const yesterday = `${yest.getFullYear()}-${String(yest.getMonth()+1).padStart(2,'0')}-${String(yest.getDate()).padStart(2,'0')}`;
      const yci = all.find(c => c.date === yesterday);
      if (yci) {
        const hits = Object.entries(yci.checks || {}).filter(([,v]) => v).map(([k]) => defaultItems.find(d => d.key === k)?.label?.slice(3).trim()).filter(Boolean);
        setYesterdayHits(hits);
      }
    };
    load();
  }, [user.id]);

  // Load check-in data whenever selected date changes
  useEffect(() => {
    const existing = allCheckins.find(c => c.date === selectedDate);
    if (existing) {
      setChecks(existing.checks || {});
      setMood(existing.mood || "");
      setEnergy(existing.energy || "");
      setNotes(existing.notes || "");
      setCustomItems(existing.custom_items || []);
    } else {
      setChecks({ water: false, sleep: false, supplements: false, workout: false, journaled: false, meditation: false, mobilized: false, nutrition: false, sharedWin: false });
      setMood(""); setEnergy(""); setNotes(""); setCustomItems([]);
    }
  }, [selectedDate, allCheckins]);

  const save = async () => {
    setSaved("saving");
    const success = await saveCheckin({
      id: `ci_${user.id}_${selectedDate}`,
      userId: user.id,
      userName: user.name,
      date: selectedDate,
      checks, mood, energy, notes, customItems,
    });
    if (success) {
      setAllCheckins(prev => {
        const filtered = prev.filter(c => c.date !== selectedDate);
        return [...filtered, { id: `ci_${user.id}_${selectedDate}`, user_id: user.id, date: selectedDate, checks, mood, energy, notes, custom_items: customItems }];
      });
    }
    setSaved(success ? "saved" : "error");
    setTimeout(() => setSaved(false), 2500);
  };

  const addCustom = () => {
    if (newCustom.trim()) { setCustomItems([...customItems, { key: `c_${Date.now()}`, label: newCustom.trim(), checked: false }]); setNewCustom(""); }
  };

  const completedCount = Object.values(checks).filter(Boolean).length + customItems.filter(c => c.checked).length;
  const totalCount = defaultItems.length + customItems.length;

  return (
    <div>
      {yesterdayHits.length > 0 && (
        <div className="card" style={{ borderLeft: "3px solid var(--forest)" }}>
          <div className="section-label">Yesterday you completed ✓</div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {yesterdayHits.map(h => <span key={h} className="chip green" style={{ fontSize: 10 }}>{h}</span>)}
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-title">Daily Check-in</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "var(--pink)", textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap" }}>Log for:</label>
          <input type="date" className="form-input" value={selectedDate} max={today()}
            onChange={e => setSelectedDate(e.target.value)}
            style={{ flex: 1, fontSize: 13 }} />
          {selectedDate !== today() && (
            <button className="btn-ghost" style={{ fontSize: 11, whiteSpace: "nowrap" }} onClick={() => setSelectedDate(today())}>Today</button>
          )}
        </div>
        {selectedDate !== today() && (
          <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 700, marginBottom: 10, background: "#FFF8EE", borderRadius: 8, padding: "6px 10px" }}>
            📅 Logging check-in for {new Date(selectedDate + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>
        )}
        <div className="card-sub">{selectedDate === today() ? new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : ""}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, background: "var(--bg)", borderRadius: 10, padding: "10px 14px" }}>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 28, fontWeight: 800, color: "var(--pink)" }}>{completedCount}/{totalCount}</div>
          <div style={{ fontSize: 12, color: "var(--soft)" }}>habits completed today</div>
        </div>
        <div className="section-label">Today's Habits</div>
        {defaultItems.map(item => (
          <div key={item.key} className="check-item" onClick={() => setChecks({ ...checks, [item.key]: !checks[item.key] })}>
            <div className={`check-box ${checks[item.key] ? "checked" : ""}`}>{checks[item.key] ? "✓" : ""}</div>
            <span className="check-label">{item.label}</span>
          </div>
        ))}
        {customItems.map(item => (
          <div key={item.key} className="check-item" onClick={() => setCustomItems(customItems.map(c => c.key === item.key ? { ...c, checked: !c.checked } : c))}>
            <div className={`check-box ${item.checked ? "checked" : ""}`}>{item.checked ? "✓" : ""}</div>
            <span className="check-label">✏️ {item.label}</span>
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input className="form-input" placeholder="Add custom habit..." value={newCustom} onChange={e => setNewCustom(e.target.value)} onKeyDown={e => e.key === "Enter" && addCustom()} style={{ flex: 1 }} />
          <button className="btn-forest" onClick={addCustom}>+</button>
        </div>
      </div>
      <div className="card">
        <div className="section-label">How are you feeling?</div>
        <div className="mood-row" style={{ marginBottom: 14 }}>
          {moods.map(m => <button key={m} className={`mood-btn ${mood === m ? "selected" : ""}`} onClick={() => setMood(m)}>{m}</button>)}
        </div>
        <div className="section-label">Energy Level</div>
        <div className="mood-row">
          {energies.map(e => <button key={e} className={`mood-btn ${energy === e ? "selected" : ""}`} onClick={() => setEnergy(e)}>{e}</button>)}
        </div>
      </div>
      <div className="card">
        <div className="section-label">Notes / Anything on your mind?</div>
        <textarea className="form-input form-textarea" placeholder="Body feels, recovery, life stuff..." value={notes} onChange={e => setNotes(e.target.value)} />
      </div>
      <button className="btn-primary" onClick={save} style={{ marginBottom: 14 }}>
        {saved === "saving" ? "Saving..." : saved === "saved" ? "✓ Check-in Saved!" : saved === "error" ? "⚠️ Save failed — try again" : "Save Check-in"}
      </button>
    </div>
  );
}

function MemberWorkouts({ user }) {
  const [view, setView] = useState("weekly");
  const [type, setType] = useState("inperson");
  const [notes, setNotes] = useState("");
  const [duration, setDuration] = useState("");
  const [saved, setSaved] = useState(false);
  const [myWorkouts, setMyWorkouts] = useState([]);
  const [weeklyWorkouts, setWeeklyWorkouts] = useState([]);
  const [videoLibrary, setVideoLibrary] = useState([]);
  const [videoSearch, setVideoSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      const [w, ww, vids] = await Promise.all([getWorkouts(user.id), getWeeklyWorkouts(), getVideoLibrary()]);
      setMyWorkouts(w);
      setWeeklyWorkouts(ww);
      setVideoLibrary(vids);
    };
    load();
  }, [user.id]);

  const logWorkout = async () => {
    const id = `wk_${Date.now()}`;
    setSaved("saving");
    const success = await saveWorkout({ id, userId: user.id, userName: user.name, date: today(), type, notes, duration });
    if (success) {
      setMyWorkouts(prev => [{ id, user_id: user.id, date: today(), type, notes, duration }, ...prev]);
      setNotes(""); setDuration("");
    }
    setSaved(success ? "saved" : "error");
    setTimeout(() => setSaved(false), 2000);
  };

  const getYouTubeId = (url) => {
    const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const filteredVideos = videoLibrary.filter(v =>
    !videoSearch || v.exercise.toLowerCase().includes(videoSearch.toLowerCase()) || v.title.toLowerCase().includes(videoSearch.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {[["weekly", "Weekly"], ["log", "Log"], ["history", "History"], ["videos", "📹 Videos"]].map(([v, l]) => (
          <button key={v} className={v === view ? "btn-secondary" : "btn-ghost"} onClick={() => setView(v)} style={{ flex: 1, padding: "9px 6px", fontSize: 12 }}>{l}</button>
        ))}
      </div>
      {view === "log" && (
        <div className="card">
          <div className="card-title">Log a Workout</div>
          <div className="section-label" style={{ marginTop: 8 }}>Type</div>
          <div className="mood-row" style={{ marginBottom: 14 }}>
            {[["inperson", "🏃 In-Person"], ["athome", "🏠 At-Home"], ["alternative", "🚴 Alternative"]].map(([val, label]) => (
              <button key={val} className={`mood-btn ${type === val ? "selected" : ""}`} onClick={() => setType(val)}>{label}</button>
            ))}
          </div>
          <div className="form-group">
            <label>Duration (mins)</label>
            <input className="form-input" type="number" placeholder="45" value={duration} onChange={e => setDuration(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea className="form-input form-textarea" placeholder="What did you do? How'd it feel?" value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <button className="btn-primary" onClick={logWorkout} disabled={saved === "saving"}>
            {saved === "saving" ? "Saving..." : saved === "saved" ? "✓ Workout Logged!" : saved === "error" ? "⚠️ Save failed — try again" : "Log Workout"}
          </button>
        </div>
      )}
      {view === "history" && (
        <div className="card">
          <div className="card-title">Workout History</div>
          {myWorkouts.length === 0
            ? <div className="empty-state"><div className="empty-icon">💪</div>No workouts logged yet!</div>
            : myWorkouts.map(w => (
              <div key={w.id} className="workout-item">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className={`workout-type ${w.type}`}>{w.type === "inperson" ? "In-Person" : w.type === "athome" ? "At-Home" : "Alternative"}</span>
                  <span style={{ fontSize: 11, color: "var(--soft)", marginLeft: "auto" }}>{fmt(w.date)}{w.duration ? ` · ${w.duration} min` : ""}</span>
                </div>
                {w.notes && <div className="workout-note">{w.notes}</div>}
              </div>
            ))}
        </div>
      )}
      {view === "videos" && (
        <div>
          <input className="form-input" placeholder="🔍 Search by exercise name..." value={videoSearch} onChange={e => setVideoSearch(e.target.value)} style={{ marginBottom: 14 }} />
          {filteredVideos.length === 0
            ? <div className="empty-state"><div className="empty-icon">📹</div>{videoSearch ? "No videos match that search" : "No videos in the library yet"}</div>
            : filteredVideos.map(v => {
              const ytId = getYouTubeId(v.youtube_url);
              return (
                <div key={v.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--deep)", marginBottom: 2 }}>{v.title}</div>
                  <div style={{ fontSize: 11, color: "var(--soft)", marginBottom: 10 }}>{v.exercise}</div>
                  {ytId && (
                    <div style={{ borderRadius: 10, overflow: "hidden", marginBottom: 8 }}>
                      <iframe
                        width="100%" height="200"
                        src={`https://www.youtube.com/embed/${ytId}`}
                        title={v.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{ display: "block" }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
      {view === "weekly" && (
        <div>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, color: "var(--deep)", marginBottom: 14 }}>Weekly Workouts</div>
          {weeklyWorkouts.length === 0
            ? <div className="empty-state"><div className="empty-icon">📋</div>Madison hasn't posted this week's workouts yet.</div>
            : weeklyWorkouts.map(ww => (
              <div key={ww.id} className="week-card">
                <div className="week-title">{ww.week}</div>
                <div className="week-posted">Posted by {ww.posted_by || ww.postedBy} · {fmt(ww.posted_date || ww.postedDate)}</div>
                {(ww.slots || []).map((slot, i) => {
                  const attachedVideos = (slot.video_ids || []).map(vid => videoLibrary.find(v => v.id === vid)).filter(Boolean);
                  return (
                    <div key={i} className="workout-slot">
                      <div className="slot-label">{slot.label || `Workout ${i + 1}`}</div>
                      <div className="slot-content">{slot.content}</div>
                      {slot.notes && <div style={{ fontSize: 12, color: "var(--mid)", marginTop: 6, fontStyle: "italic" }}>📝 {slot.notes}</div>}
                      {attachedVideos.length > 0 && (
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {attachedVideos.map(v => (
                            <a key={v.id} href={v.youtube_url} target="_blank" rel="noreferrer"
                              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", background: "#FF000015", borderRadius: 20, fontSize: 11, fontWeight: 700, color: "#CC0000", textDecoration: "none", border: "1px solid #FF000030" }}>
                              ▶ {v.title}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// ─── Weight Tracking Data ─────────────────────────────────────────────────────
const WEIGHTED_MOVEMENTS = [
  { key: "back_squat", label: "Back Squat", unit: "lbs", category: "Saturday" },
  { key: "rdl", label: "Romanian Deadlift (RDL)", unit: "lbs", category: "Saturday" },
  { key: "barbell_hip_thrust", label: "Barbell Hip Thrust", unit: "lbs", category: "Saturday" },
  { key: "ffe_split_squat", label: "Front Foot Elevated Split Squat", unit: "lbs", category: "Saturday" },
  { key: "deadlift", label: "Deadlift", unit: "lbs", category: "Saturday" },
  { key: "bulgarian_split_squat", label: "Bulgarian Split Squat", unit: "lbs", category: "Saturday" },
  { key: "skater_squat", label: "Skater Squat", unit: "notes", category: "Saturday" },
  { key: "kb_sumo_squat", label: "KB/DB Sumo Squat", unit: "lbs", category: "In-Gym" },
  { key: "curtsy_lunge", label: "Curtsy Lunge", unit: "lbs", category: "In-Gym" },
  { key: "single_leg_rdl", label: "KB/DB Single Leg RDL", unit: "lbs", category: "In-Gym" },
  { key: "glute_bridge", label: "DB/KB Glute Bridge", unit: "lbs", category: "In-Gym" },
  { key: "farmer_carry", label: "Farmer Carry", unit: "lbs", category: "In-Gym" },
  { key: "split_squat", label: "Split Squat", unit: "lbs", category: "In-Gym" },
  { key: "kb_deadlift", label: "KB Deadlift", unit: "lbs", category: "In-Gym" },
  { key: "box_step_up", label: "Weighted Box Step Up", unit: "lbs", category: "In-Gym" },
  { key: "goblet_squat", label: "Goblet Squat", unit: "lbs", category: "In-Gym" },
  { key: "walking_lunge", label: "Weighted Walking Lunge", unit: "lbs", category: "In-Gym" },
  { key: "suitcase_carry", label: "Suitcase Carry", unit: "lbs", category: "In-Gym" },
];

const PR_LIFTS = [
  { key: "pr_back_squat", label: "Back Squat", unit: "lbs", note: "1 rep max" },
  { key: "pr_deadlift", label: "Deadlift", unit: "lbs", note: "1 rep max" },
  { key: "pr_hollow_hold", label: "Hollow Hold", unit: "sec", note: "max time" },
];

function MemberPRs({ user }) {
  const [view, setView] = useState("prs");
  const [selectedMovement, setSelectedMovement] = useState(null);

  const [prLift, setPrLift] = useState("pr_back_squat");
  const [prValue, setPrValue] = useState("");
  const [prSaved, setPrSaved] = useState(false);
  const [myPRs, setMyPRs] = useState([]);
  const [myWeightLogs, setMyWeightLogs] = useState({});
  const [weightValue, setWeightValue] = useState("");
  const [weightNotes, setWeightNotes] = useState("");
  const [weightSaved, setWeightSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [prs, wl] = await Promise.all([getPRs(user.id), getWeightLogs(user.id)]);
      setMyPRs(prs);
      setMyWeightLogs(wl);
    };
    load();
  }, [user.id]);

  const logPR = async () => {
    if (!prValue) return;
    const lift = PR_LIFTS.find(p => p.key === prLift);
    const pr = { id: `pr_${Date.now()}`, userId: user.id, userName: user.name, exercise: lift.label, value: prValue, unit: lift.unit, note: lift.note, date: today() };
    setPrSaved("saving");
    const success = await savePR(pr);
    if (success) {
      setMyPRs(prev => [{ ...pr, user_id: user.id }, ...prev]);
      setPrValue("");
    }
    setPrSaved(success ? "saved" : "error");
    setTimeout(() => setPrSaved(false), 2000);
  };

  const logWeight = async () => {
    if (!selectedMovement) return;
    const entry = { date: today(), weight: weightValue, notes: weightNotes };
    setWeightSaved("saving");
    const success = await saveWeightLog(user.id, selectedMovement.key, entry);
    if (success) {
      setMyWeightLogs(prev => {
        const updated = { ...prev };
        if (!updated[selectedMovement.key]) updated[selectedMovement.key] = [];
        updated[selectedMovement.key] = [...updated[selectedMovement.key], entry];
        return updated;
      });
      setWeightValue(""); setWeightNotes("");
    }
    setWeightSaved(success ? "saved" : "error");
    setTimeout(() => setWeightSaved(false), 2000);
  };

  const getLatestWeight = (key) => {
    const entries = myWeightLogs[key] || [];
    return entries.length ? entries[entries.length - 1] : null;
  };

  const saturdayMovements = WEIGHTED_MOVEMENTS.filter(m => m.category === "Saturday");
  const gymMovements = WEIGHTED_MOVEMENTS.filter(m => m.category === "In-Gym");

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {[["prs", "My PRs"], ["weights", "Weight Tracker"], ["log_pr", "Log PR"]].map(([v, l]) => (
          <button key={v} className={v === view ? "btn-secondary" : "btn-ghost"} onClick={() => { setView(v); setSelectedMovement(null); }} style={{ flex: 1, padding: "9px 4px", fontSize: 11 }}>{l}</button>
        ))}
      </div>

      {/* ── PR VIEW ── */}
      {view === "prs" && (
        <div>
          {PR_LIFTS.map(lift => {
            const entries = myPRs.filter(p => p.exercise === lift.label).sort((a,b) => b.date.localeCompare(a.date));
            const best = lift.unit === "lbs" ? entries.reduce((mx, e) => parseFloat(e.value) > parseFloat(mx?.value || 0) ? e : mx, null) : entries[0];
            return (
              <div key={lift.key} className="card" style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div className="card-title" style={{ fontSize: 17 }}>{lift.label}</div>
                    <div style={{ fontSize: 10, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1 }}>{lift.note}</div>
                  </div>
                  {best && (
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 28, fontWeight: 800, color: "var(--pink)" }}>{best.value} {lift.unit}</div>
                      <div style={{ fontSize: 10, color: "var(--soft)" }}>Best · {fmt(best.date)}</div>
                    </div>
                  )}
                </div>
                {entries.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div className="section-label">History</div>
                    {entries.map(e => (
                      <div key={e.id} className="pr-item">
                        <div>
                          <div className="pr-date">{fmt(e.date)}</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div className="pr-value" style={{ fontSize: 16 }}>{e.value} {lift.unit}</div>
                          <div onClick={async () => {
                            await deletePR(e.id);
                            setMyPRs(prev => prev.filter(p => p.id !== e.id));
                          }} style={{ color: "var(--soft)", cursor: "pointer", fontSize: 16, userSelect: "none" }}>×</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {entries.length === 0 && <div style={{ fontSize: 13, color: "var(--soft)", marginTop: 8 }}>Not logged yet</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* ── LOG PR ── */}
      {view === "log_pr" && (
        <div className="card">
          <div className="card-title">Log a PR</div>
          <div className="card-sub">Only the three camp PRs — go get it!</div>
          <div className="section-label" style={{ marginTop: 8 }}>Which lift?</div>
          <div className="mood-row" style={{ marginBottom: 16 }}>
            {PR_LIFTS.map(l => (
              <button key={l.key} className={`mood-btn ${prLift === l.key ? "selected" : ""}`} onClick={() => setPrLift(l.key)}>
                {l.label}
              </button>
            ))}
          </div>
          <div className="form-group">
            <label>{PR_LIFTS.find(l => l.key === prLift)?.note === "max time" ? "Time (seconds)" : "Weight (lbs)"}</label>
            <input className="form-input" type="number" placeholder={PR_LIFTS.find(l => l.key === prLift)?.note === "max time" ? "e.g. 45" : "e.g. 185"} value={prValue} onChange={e => setPrValue(e.target.value)} />
          </div>
          <button className="btn-primary" onClick={logPR} disabled={prSaved === "saving"}>
            {prSaved === "saving" ? "Saving..." : prSaved === "saved" ? "✓ PR Logged! 🎉" : prSaved === "error" ? "⚠️ Save failed — try again" : "Save PR"}
          </button>
        </div>
      )}

      {/* ── WEIGHT TRACKER ── */}
      {view === "weights" && !selectedMovement && (
        <div>
          <div className="card-title" style={{ marginBottom: 4 }}>Weight Tracker</div>
          <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 16 }}>Tap a movement to log or view your weight history</div>

          <div className="section-label">Saturday Movements</div>
          {saturdayMovements.map(m => {
            const latest = getLatestWeight(m.key);
            return (
              <div key={m.key} onClick={() => setSelectedMovement(m)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"var(--card)", borderRadius:12, padding:"12px 14px", marginBottom:8, border:"1px solid var(--border)", cursor:"pointer", boxShadow:"var(--shadow)" }}>
                <div style={{ fontWeight:600, fontSize:14, color:"var(--deep)" }}>{m.label}</div>
                <div style={{ textAlign:"right" }}>
                  {latest
                    ? <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:18, fontWeight:700, color:"var(--pink)" }}>{m.unit === "notes" ? "📝" : `${latest.weight} lbs`}</div>
                    : <div style={{ fontSize:12, color:"var(--soft)" }}>Not logged</div>}
                  {latest && <div style={{ fontSize:10, color:"var(--soft)" }}>{fmt(latest.date)}</div>}
                </div>
              </div>
            );
          })}

          <div className="section-label" style={{ marginTop: 16 }}>In-Gym Movements</div>
          {gymMovements.map(m => {
            const latest = getLatestWeight(m.key);
            return (
              <div key={m.key} onClick={() => setSelectedMovement(m)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"var(--card)", borderRadius:12, padding:"12px 14px", marginBottom:8, border:"1px solid var(--border)", cursor:"pointer", boxShadow:"var(--shadow)" }}>
                <div style={{ fontWeight:600, fontSize:14, color:"var(--deep)" }}>{m.label}</div>
                <div style={{ textAlign:"right" }}>
                  {latest
                    ? <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:18, fontWeight:700, color:"var(--pink)" }}>{m.unit === "notes" ? "📝" : `${latest.weight} lbs`}</div>
                    : <div style={{ fontSize:12, color:"var(--soft)" }}>Not logged</div>}
                  {latest && <div style={{ fontSize:10, color:"var(--soft)" }}>{fmt(latest.date)}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── INDIVIDUAL MOVEMENT ── */}
      {view === "weights" && selectedMovement && (
        <div>
          <button className="back-btn" onClick={() => setSelectedMovement(null)}>← All Movements</button>
          <div className="card">
            <div className="card-title">{selectedMovement.label}</div>
            <div style={{ fontSize: 11, color: "var(--soft)", textTransform:"uppercase", letterSpacing:1, marginBottom:16 }}>{selectedMovement.category}</div>

            {selectedMovement.unit !== "notes" && (
              <div className="form-group">
                <label>Weight Used (lbs)</label>
                <input className="form-input" type="number" placeholder="e.g. 95" value={weightValue} onChange={e => setWeightValue(e.target.value)} />
              </div>
            )}
            <div className="form-group">
              <label>Notes / Modifications</label>
              <textarea className="form-input form-textarea" placeholder={
                selectedMovement.key === "skater_squat"
                  ? "e.g. Used bench for support, single leg depth, balance notes..."
                  : selectedMovement.key === "bulgarian_split_squat"
                  ? "e.g. Lowered bench height, used PVC for balance, rear foot position..."
                  : selectedMovement.key === "back_squat" || selectedMovement.key === "deadlift"
                  ? "e.g. Used box squat, stance width, belt or no belt..."
                  : "e.g. Modifications used, how it felt, form notes..."
              } value={weightNotes} onChange={e => setWeightNotes(e.target.value)} />
            </div>
            <button className="btn-primary" onClick={logWeight} disabled={weightSaved === "saving"}>
              {weightSaved === "saving" ? "Saving..." : weightSaved === "saved" ? "✓ Saved!" : weightSaved === "error" ? "⚠️ Save failed — try again" : "Save Entry"}
            </button>
          </div>

          {/* History */}
          {(myWeightLogs[selectedMovement.key] || []).length > 0 && (
            <div className="card">
              <div className="card-title" style={{ fontSize:16 }}>History</div>
              {[...(myWeightLogs[selectedMovement.key] || [])].reverse().map((e, i) => (
                <div key={i} style={{ padding:"10px 0", borderBottom:"1px solid var(--border)" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom: e.notes ? 4 : 0 }}>
                    <span style={{ fontSize:13, color:"var(--soft)" }}>{fmt(e.date)}</span>
                    {e.weight && <span style={{ fontFamily:"Cormorant Garamond,serif", fontSize:18, fontWeight:700, color:"var(--pink)" }}>{e.weight} lbs</span>}
                  </div>
                  {e.notes && <div style={{ fontSize:12, color:"var(--mid)", lineHeight:1.5 }}>{e.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MemberNutrition({ user }) {
  const [view, setView] = useState("info");
  const [flyer, setFlyer] = useState(null);
  const [ifThen, setIfThen] = useState("");
  const [profile, setProfile] = useState({ protein: "", carbs: "", fat: "", veggies: "", water: "", allergies: "", intolerances: "", goals: "", coachNotes: "" });
  const [saved, setSaved] = useState(false);
  const [logSaved, setLogSaved] = useState(false);
  const [historyDate, setHistoryDate] = useState(null);
  const [todayMeals, setTodayMeals] = useState({});
  const [photoUrls, setPhotoUrls] = useState({});
  const [historyPhotos, setHistoryPhotos] = useState({});
  const [uploading, setUploading] = useState({});
  const [analyzing, setAnalyzing] = useState({});
  const [pastDates, setPastDates] = useState([]);
  const [historyLog, setHistoryLog] = useState({});
  const [logDate, setLogDate] = useState(today());
  const todayMealsRef = { current: {} };
  const todayPhotosRef = { current: {} };

  const MEAL_SLOTS = [
    { key: "breakfast", label: "🌅 Breakfast" },
    { key: "snack1", label: "🍎 Snack 1" },
    { key: "lunch", label: "☀️ Lunch" },
    { key: "snack2", label: "🥜 Snack 2" },
    { key: "dinner", label: "🌙 Dinner" },
  ];

  useEffect(() => {
    const load = async () => {
      const [prof, logs, f, it] = await Promise.all([
        getNutritionProfile(user.id),
        getNutritionLogs(user.id),
        getSetting('nutrition_flyer'),
        getSetting('nutrition_ifthen'),
      ]);
      if (prof) setProfile({ protein: prof.protein || "", carbs: prof.carbs || "", fat: prof.fat || "", veggies: prof.veggies || "", water: prof.water || "", allergies: prof.allergies || "", intolerances: prof.intolerances || "", goals: prof.goals || "", coachNotes: prof.coach_notes || "" });
      setFlyer(f); setIfThen(it || "");
      const todayLog = logs.find(l => l.date === today());
      if (todayLog) {
        const meals = { breakfast: todayLog.breakfast, snack1: todayLog.snack1, lunch: todayLog.lunch, snack2: todayLog.snack2, dinner: todayLog.dinner };
        setTodayMeals(meals); todayMealsRef.current = meals;
        const photos = todayLog.photo_urls || {};
        setPhotoUrls(photos); todayPhotosRef.current = photos;
      }
      const past = logs.filter(l => l.date !== today());
      setPastDates(past.map(l => l.date));
      const hist = {}; const histPhotos = {};
      past.forEach(l => {
        hist[l.date] = { breakfast: l.breakfast, snack1: l.snack1, lunch: l.lunch, snack2: l.snack2, dinner: l.dinner };
        histPhotos[l.date] = l.photo_urls || {};
      });
      setHistoryLog(hist); setHistoryPhotos(histPhotos);
    };
    load();
  }, [user.id]);

  const saveMeals = async () => {
    setLogSaved("saving");
    const currentPhotos = logDate === today() ? photoUrls : (historyPhotos[logDate] || {});
    const success = await saveNutritionLog(user.id, logDate, todayMeals, currentPhotos);
    if (success) {
      setHistoryLog(prev => ({ ...prev, [logDate]: todayMeals }));
      setHistoryPhotos(prev => ({ ...prev, [logDate]: currentPhotos }));
      if (logDate === today()) { todayMealsRef.current = todayMeals; todayPhotosRef.current = photoUrls; }
      if (!pastDates.includes(logDate) && logDate !== today()) setPastDates(prev => [logDate, ...prev].sort((a,b) => b.localeCompare(a)));
    }
    setLogSaved(success ? "saved" : "error");
    setTimeout(() => setLogSaved(false), 2000);
  };

  const handlePhotoUpload = async (slotKey, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [slotKey]: true }));
    const url = await uploadFoodPhoto(user.id, file);
    if (url) {
      if (logDate === today()) {
        setPhotoUrls(prev => ({ ...prev, [slotKey]: url }));
      } else {
        setHistoryPhotos(prev => ({ ...prev, [logDate]: { ...(prev[logDate] || {}), [slotKey]: url } }));
      }
    }
    setUploading(prev => ({ ...prev, [slotKey]: false }));
  };

  const analyzePhoto = async (slotKey) => {
    const currentPhotos = logDate === today() ? photoUrls : (historyPhotos[logDate] || {});
    const photoUrl = currentPhotos[slotKey];
    if (!photoUrl) { alert("No photo found — try uploading again."); return; }
    const apiKey = await getSetting('openai_key');
    if (!apiKey) { alert("OpenAI key not set — go to Boot Camp Coach → Camp Settings → AI Food Analysis Key."); return; }
    setAnalyzing(prev => ({ ...prev, [slotKey]: true }));
    try {
      const res = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [{ role: 'user', content: [
            { type: 'text', text: 'Identify the food in this photo. Respond in this exact format with no extra text:\n[Food Name]\nCalories: Xkcal | Protein: Xg | Carbs: Xg | Fat: Xg\n\nExample:\nGrilled Chicken & Brown Rice\nCalories: 480kcal | Protein: 45g | Carbs: 45g | Fat: 8g' },
            { type: 'image_url', image_url: { url: photoUrl, detail: 'low' } }
          ]}],
          max_tokens: 200,
        })
      });
      const data = await res.json();
      if (data.error) {
        alert(`AI error: ${data.error.message}`);
      } else {
        const result = data.choices?.[0]?.message?.content || '';
        if (result) {
          setTodayMeals(prev => ({ ...prev, [slotKey]: result }));
        } else {
          alert("AI returned an empty response — try again.");
        }
      }
    } catch(e) {
      alert(`AI analysis failed: ${e.message}`);
    }
    setAnalyzing(prev => ({ ...prev, [slotKey]: false }));
  };

  const saveProf = async () => {
    setSaved("saving");
    const success = await saveNutritionProfile(user.id, profile);
    setSaved(success ? "saved" : "error");
    setTimeout(() => setSaved(false), 2000);
  };

  const servings = [
    { key: "protein", icon: "🥩", label: "Protein", unit: "palm(s) per meal" },
    { key: "carbs", icon: "🌾", label: "Carbs", unit: "cupped hand(s)/meal" },
    { key: "fat", icon: "🥑", label: "Fat", unit: "thumb(s) per meal" },
    { key: "veggies", icon: "🥦", label: "Veggies", unit: "fist(s) per meal" },
  ];

  const getCurrentPhoto = (slotKey) => logDate === today() ? photoUrls[slotKey] : (historyPhotos[logDate] || {})[slotKey];

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {[["info", "My Plan"], ["log", "Today's Log"], ["history", "Past Days"]].map(([v, l]) => (
          <button key={v} className={v === view ? "btn-secondary" : "btn-ghost"} onClick={() => { setView(v); setHistoryDate(null); }} style={{ flex: 1, padding: "9px 6px", fontSize: 12 }}>{l}</button>
        ))}
      </div>

      {view === "info" && (
        <div>
          {profile.coachNotes && (
            <div className="card" style={{ borderLeft: "3px solid var(--forest)" }}>
              <div className="section-label">Note from Madison</div>
              <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.6 }}>{profile.coachNotes}</div>
            </div>
          )}
          <div className="card">
            <div className="card-title">My Daily Serving Targets</div>
            <div className="card-sub">Based on hand measurements</div>
            <div className="serving-grid">
              {servings.map(s => (
                <div key={s.key} className="serving-card">
                  <div className="serving-icon">{s.icon}</div>
                  <div className="serving-label">{s.label}</div>
                  <input className="form-input" style={{ textAlign: "center", background: "white", padding: "6px 8px", marginTop: 6, fontSize: 15, fontWeight: 700 }} placeholder="—" value={profile[s.key]} onChange={e => setProfile({ ...profile, [s.key]: e.target.value })} />
                  <div className="serving-unit">{s.unit}</div>
                </div>
              ))}
            </div>
            <div className="form-group"><label>Daily Water Goal (oz)</label><input className="form-input" placeholder="e.g. 80" value={profile.water} onChange={e => setProfile({ ...profile, water: e.target.value })} /></div>
            <div className="form-group"><label>Allergies</label><input className="form-input" placeholder="e.g. tree nuts, shellfish" value={profile.allergies} onChange={e => setProfile({ ...profile, allergies: e.target.value })} /></div>
            <div className="form-group"><label>Intolerances / Sensitivities</label><input className="form-input" placeholder="e.g. dairy, gluten" value={profile.intolerances} onChange={e => setProfile({ ...profile, intolerances: e.target.value })} /></div>
            <div className="form-group"><label>My Nutrition Goals</label><textarea className="form-input form-textarea" placeholder="e.g. lose body fat, build muscle, more energy..." value={profile.goals} onChange={e => setProfile({ ...profile, goals: e.target.value })} /></div>
            <button className="btn-primary" onClick={saveProf} disabled={saved === "saving"}>{saved === "saving" ? "Saving..." : saved === "saved" ? "✓ Saved!" : saved === "error" ? "⚠️ Save failed" : "Save My Profile"}</button>
          </div>
          {ifThen && <div className="card"><div className="card-title">If/Then Guide</div><div className="card-sub">From Madison</div><div className="ifthen-block">{ifThen}</div></div>}
          {flyer && <div className="card"><div className="card-title">Nutrition Flyer</div><div className="card-sub">From Madison</div><img src={flyer} alt="Nutrition Flyer" className="flyer-img" /></div>}
        </div>
      )}

      {view === "log" && (
        <div>
          <div className="card">
            <div className="card-title">Meal Log</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <input type="date" className="form-input" value={logDate} max={today()} onChange={e => { const d = e.target.value; setLogDate(d); setTodayMeals(d === today() ? (todayMealsRef.current || {}) : (historyLog[d] || {})); }} style={{ flex: 1, fontSize: 13 }} />
              {logDate !== today() && <button className="btn-ghost" style={{ fontSize: 11, whiteSpace: "nowrap" }} onClick={() => { setLogDate(today()); setTodayMeals(todayMealsRef.current || {}); }}>Back to Today</button>}
            </div>
            {logDate !== today() && <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 700, marginBottom: 10, background: "#FFF8EE", borderRadius: 8, padding: "6px 10px" }}>📅 Logging meals for {new Date(logDate + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>}
            <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 16 }}>Type your meal, take a photo, or both — then tap 🤖 to let AI fill it in for you.</div>

            {MEAL_SLOTS.map(slot => {
              const photo = getCurrentPhoto(slot.key);
              return (
                <div key={slot.key} style={{ marginBottom: 18, paddingBottom: 18, borderBottom: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700, color: "var(--soft)", marginBottom: 8 }}>{slot.label}</div>

                  {/* Photo area */}
                  {photo ? (
                    <div style={{ position: "relative", marginBottom: 10 }}>
                      <img src={photo} alt="food" style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 10 }} />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <button
                          onClick={() => analyzePhoto(slot.key)}
                          disabled={analyzing[slot.key]}
                          style={{ flex: 1, padding: "8px 12px", borderRadius: 20, border: "none", background: analyzing[slot.key] ? "#ccc" : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12, cursor: analyzing[slot.key] ? "wait" : "pointer" }}>
                          {analyzing[slot.key] ? "🤖 Analyzing..." : "🤖 Analyze with AI"}
                        </button>
                        <label style={{ padding: "8px 14px", borderRadius: 20, border: "1px solid var(--border)", background: "white", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", color: "var(--mid)" }}>
                          📷 Change
                          <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => handlePhotoUpload(slot.key, e.target.files[0])} />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, border: "2px dashed var(--border)", background: "var(--bg)", cursor: "pointer", marginBottom: 10, color: "var(--soft)", fontSize: 13, fontFamily: "Jost, sans-serif" }}>
                      {uploading[slot.key] ? "⏳ Uploading..." : "📷 Add a photo"}
                      <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => handlePhotoUpload(slot.key, e.target.files[0])} disabled={uploading[slot.key]} />
                    </label>
                  )}

                  <textarea
                    className="form-input form-textarea"
                    style={{ minHeight: 55, fontSize: 14 }}
                    placeholder={`What did you have? (or let AI fill this in from the photo)`}
                    value={todayMeals[slot.key] || ""}
                    onChange={e => setTodayMeals({ ...todayMeals, [slot.key]: e.target.value })}
                  />
                </div>
              );
            })}
            <button className="btn-primary" onClick={saveMeals} disabled={logSaved === "saving"}>
              {logSaved === "saving" ? "Saving..." : logSaved === "saved" ? "✓ Saved!" : logSaved === "error" ? "⚠️ Save failed" : "Save Log"}
            </button>
          </div>
        </div>
      )}

      {view === "history" && !historyDate && (
        <div>
          <div className="card-title" style={{ marginBottom: 4 }}>Past Days</div>
          <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 16 }}>Tap any day to see what you logged</div>
          {pastDates.length === 0
            ? <div className="empty-state"><div className="empty-icon">📅</div>No past logs yet</div>
            : pastDates.map(d => {
              const dayLog = historyLog[d] || {};
              const filled = MEAL_SLOTS.filter(s => dayLog[s.key]).length;
              const hasPhotos = Object.keys(historyPhotos[d] || {}).length > 0;
              return (
                <div key={d} onClick={() => setHistoryDate(d)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"var(--card)", borderRadius:12, padding:"12px 14px", marginBottom:8, border:"1px solid var(--border)", cursor:"pointer", boxShadow:"var(--shadow)" }}>
                  <div style={{ fontWeight:600, fontSize:14, color:"var(--deep)" }}>{new Date(d + 'T12:00:00').toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" })}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {hasPhotos && <span style={{ fontSize: 14 }}>📷</span>}
                    <div style={{ fontSize:12, color:"var(--soft)" }}>{filled} of 5 meals logged</div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {view === "history" && historyDate && (
        <div>
          <button className="back-btn" onClick={() => setHistoryDate(null)}>← Back to Past Days</button>
          <div className="card">
            <div className="card-title">{new Date(historyDate + 'T12:00:00').toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric" })}</div>
            {MEAL_SLOTS.map(slot => {
              const entry = (historyLog[historyDate] || {})[slot.key];
              const photo = (historyPhotos[historyDate] || {})[slot.key];
              return (
                <div key={slot.key} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700, color: "var(--soft)", marginBottom: 6 }}>{slot.label}</div>
                  {photo && <img src={photo} alt="food" style={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 10, marginBottom: 8 }} />}
                  <div style={{ fontSize: 14, color: entry ? "var(--deep)" : "var(--soft)", fontStyle: entry ? "normal" : "italic" }}>{entry || "Not logged"}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MemberWins({ user }) {
  const [type, setType] = useState("win");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [winDate, setWinDate] = useState(today());
  const [filter, setFilter] = useState("all");
  const [saved, setSaved] = useState(false);
  const [allWins, setAllWins] = useState([]);
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});

  useEffect(() => {
    getWins('bc').then(setAllWins);
  }, []);

  const post = async () => {
    if (!text.trim()) return;
    const win = { id: `win_${Date.now()}`, userId: user.id, userName: user.name, type, text: text.trim(), url: url.trim(), date: winDate };
    setSaved("saving");
    const success = await saveWin(win);
    if (success) {
      setAllWins(prev => [{ ...win, user_id: user.id, user_name: user.name, likes: [], comments: [] }, ...prev]);
      setText(""); setUrl("");
    }
    setSaved(success ? "saved" : "error");
    setTimeout(() => setSaved(false), 2000);
  };

  const handleLike = async (id) => {
    await toggleLike('wins', id, user.id);
    setAllWins(prev => prev.map(w => {
      if (w.id !== id) return w;
      const likes = w.likes || [];
      return { ...w, likes: likes.includes(user.id) ? likes.filter(l => l !== user.id) : [...likes, user.id] };
    }));
  };

  const handleComment = async (id) => {
    const text = commentText[id];
    if (!text?.trim()) return;
    await addComment('wins', id, user.id, user.name, text);
    setAllWins(prev => prev.map(w => {
      if (w.id !== id) return w;
      return { ...w, comments: [...(w.comments || []), { userId: user.id, userName: user.name, text, date: today() }] };
    }));
    setCommentText(prev => ({ ...prev, [id]: "" }));
  };

  const handleDelete = async (id) => {
    await deleteWin(id);
    setAllWins(prev => prev.filter(w => w.id !== id));
  };

  const filtered = filter === "all" ? allWins : allWins.filter(w => w.type === filter);
  const winTagLabel = (t) => t === "win" ? "🏆 Win" : t === "gratitude" ? "🙏 Gratitude" : "📲 Share";
  const borderColor = (t) => t === "win" ? "var(--gold)" : t === "gratitude" ? "var(--pink-soft)" : "var(--forest)";

  return (
    <div>
      <div className="card">
        <div className="card-title">Share with the group</div>
        <div className="mood-row" style={{ marginBottom: 12 }}>
          <button className={`mood-btn ${type === "win" ? "selected" : ""}`} onClick={() => setType("win")}>🏆 Win</button>
          <button className={`mood-btn ${type === "gratitude" ? "selected" : ""}`} onClick={() => setType("gratitude")}>🙏 Gratitude</button>
          <button className={`mood-btn ${type === "share" ? "selected" : ""}`} onClick={() => setType("share")}>📲 Share</button>
        </div>
        <textarea className="form-input form-textarea" placeholder={
          type === "win" ? "Share a win — big or small, they all count!" :
          type === "gratitude" ? "What are you grateful for today?" :
          "Share a recipe, an Instagram post, something that inspired you..."
        } value={text} onChange={e => setText(e.target.value)} />
        {type === "share" && (
          <input className="form-input" style={{ marginTop: 8 }} placeholder="Link URL (optional)" value={url} onChange={e => setUrl(e.target.value)} />
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
          <input type="date" className="form-input" value={winDate} max={today()} onChange={e => setWinDate(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
          {winDate !== today() && <button className="btn-ghost" style={{ fontSize: 11, whiteSpace: "nowrap" }} onClick={() => setWinDate(today())}>Today</button>}
        </div>
        {winDate !== today() && <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 700, marginTop: 6, background: "#FFF8EE", borderRadius: 8, padding: "5px 10px" }}>📅 Posting for {new Date(winDate + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>}
        <button className="btn-primary mt-12" onClick={post} disabled={saved === "saving"}>
          {saved === "saving" ? "Posting..." : saved === "saved" ? "✓ Posted!" : saved === "error" ? "⚠️ Failed — try again" : "Share with the Group"}
        </button>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {[["all","All"],["win","Wins"],["gratitude","Gratitudes"],["share","Shares"]].map(([v,l]) => (
          <button key={v} className={filter === v ? "btn-secondary" : "btn-ghost"} onClick={() => setFilter(v)} style={{ flex:1, padding:"7px 4px", fontSize:11 }}>{l}</button>
        ))}
      </div>

      {filtered.length === 0
        ? <div className="empty-state"><div className="empty-icon">✨</div>Nothing here yet — be the first!</div>
        : filtered.map(w => (
          <div key={w.id} className="win-card" style={{ borderLeftColor: borderColor(w.type) }}>
            <div className="win-header">
              <div className="avatar" style={{ width: 28, height: 28, fontSize: 10 }}>{initials(w.user_name || w.userName)}</div>
              <span className="win-name">{w.user_name || w.userName}</span>
              <span className={`win-tag ${w.type}`}>{winTagLabel(w.type)}</span>
              <span className="win-date">{fmt(w.date)}</span>
              {(w.user_id === user.id || w.userId === user.id) && (
                <button onClick={() => handleDelete(w.id)} style={{ marginLeft: "auto", background:"none", border:"none", color:"var(--soft)", cursor:"pointer", fontSize:14 }}>×</button>
              )}
            </div>
            <div className="win-text">{w.text}</div>
            {w.url && <a href={w.url} target="_blank" rel="noreferrer" style={{ display:"inline-block", marginTop:6, fontSize:12, color:"var(--forest)", fontWeight:600 }}>→ View Link</a>}
            <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:10, paddingTop:8, borderTop:"1px solid var(--border)" }}>
              <button onClick={() => handleLike(w.id)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:(w.likes||[]).includes(user.id) ? "var(--pink)" : "var(--soft)", fontWeight:600, display:"flex", alignItems:"center", gap:4 }}>
                ❤️ {(w.likes||[]).length || ""}
              </button>
              <button onClick={() => setShowComments(prev => ({ ...prev, [w.id]: !prev[w.id] }))} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:"var(--soft)", fontWeight:600, display:"flex", alignItems:"center", gap:4 }}>
                💬 {(w.comments||[]).length || ""} {showComments[w.id] ? "Hide" : "Comment"}
              </button>
            </div>
            {showComments[w.id] && (
              <div style={{ marginTop:8 }}>
                {(w.comments||[]).map((c,i) => (
                  <div key={i} style={{ fontSize:12, color:"var(--mid)", padding:"4px 0", borderBottom:"1px solid var(--border)" }}>
                    <span style={{ fontWeight:700, color:"var(--deep)" }}>{c.userName}</span>: {c.text}
                  </div>
                ))}
                <div style={{ display:"flex", gap:6, marginTop:8 }}>
                  <input className="form-input" style={{ flex:1, padding:"8px 12px", fontSize:13 }} placeholder="Add a comment..." value={commentText[w.id] || ""} onChange={e => setCommentText(prev => ({ ...prev, [w.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleComment(w.id)} />
                  <button className="btn-forest" onClick={() => handleComment(w.id)} style={{ padding:"8px 14px" }}>→</button>
                </div>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

function MemberQA({ user }) {
  const [question, setQuestion] = useState("");
  const [isAnon, setIsAnon] = useState(false);
  const [saved, setSaved] = useState(false);
  const [qaData, setQaData] = useState([]);
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});

  useEffect(() => { getQA().then(setQaData); }, []);

  const submit = async () => {
    if (!question.trim()) return;
    const q = { id: `qa_${Date.now()}`, userId: user.id, userName: isAnon ? "Anonymous" : user.name, question: question.trim(), isAnon, date: today() };
    setSaved("saving");
    const success = await saveQuestion(q);
    if (success) {
      setQaData(prev => [{ ...q, user_id: user.id, user_name: q.userName, is_anon: isAnon, likes: [], comments: [] }, ...prev]);
      setQuestion("");
    }
    setSaved(success ? "saved" : "error");
    setTimeout(() => setSaved(false), 2000);
  };

  const handleLike = async (id) => {
    await toggleLike('qa', id, user.id);
    setQaData(prev => prev.map(q => {
      if (q.id !== id) return q;
      const likes = q.likes || [];
      return { ...q, likes: likes.includes(user.id) ? likes.filter(l => l !== user.id) : [...likes, user.id] };
    }));
  };

  const handleComment = async (id) => {
    const text = commentText[id];
    if (!text?.trim()) return;
    await addComment('qa', id, user.id, user.name, text);
    setQaData(prev => prev.map(q => {
      if (q.id !== id) return q;
      return { ...q, comments: [...(q.comments || []), { userId: user.id, userName: user.name, text, date: today() }] };
    }));
    setCommentText(prev => ({ ...prev, [id]: "" }));
  };

  return (
    <div>
      <div className="card">
        <div className="card-title">Ask Madison</div>
        <div className="card-sub">Questions and answers are visible to the whole group</div>
        <div className="form-group">
          <label>Your Question</label>
          <textarea className="form-input form-textarea" placeholder="Ask anything about workouts, nutrition, recovery..." value={question} onChange={e => setQuestion(e.target.value)} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div className={`check-box ${isAnon ? "checked" : ""}`} style={{ cursor: "pointer" }} onClick={() => setIsAnon(!isAnon)}>{isAnon ? "✓" : ""}</div>
          <span style={{ fontSize: 13, color: "var(--mid)", fontWeight: 500 }}>Post anonymously</span>
        </div>
        <button className="btn-primary" onClick={submit} disabled={saved === "saving"}>
          {saved === "saving" ? "Submitting..." : saved === "saved" ? "✓ Question Submitted!" : saved === "error" ? "⚠️ Failed — try again" : "Submit Question"}
        </button>
      </div>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 19, fontWeight: 700, color: "var(--deep)", marginBottom: 10 }}>Q&A Board</div>
      {qaData.length === 0
        ? <div className="empty-state"><div className="empty-icon">💬</div>No questions yet — be the first to ask!</div>
        : qaData.map(q => (
          <div key={q.id} className="qa-item">
            <div className="qa-question">"{q.question}"</div>
            <div className="qa-asker">
              {(q.is_anon || q.isAnon) ? <span className="anon-badge">Anonymous</span> : <span style={{ fontSize: 11 }}>Asked by {q.user_name || q.userName}</span>}
              <span style={{ fontSize: 10, color: "var(--soft)", marginLeft: 8 }}>· {fmt(q.date)}</span>
            </div>
            {q.answer
              ? <div className="qa-answer"><div className="qa-answer-label">Madison's Answer</div>{q.answer}</div>
              : <div style={{ fontSize: 12, color: "var(--soft)", fontStyle: "italic" }}>Madison hasn't answered yet — stay tuned!</div>}
            <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:10, paddingTop:8, borderTop:"1px solid var(--border)" }}>
              <button onClick={() => handleLike(q.id)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:(q.likes||[]).includes(user.id) ? "var(--pink)" : "var(--soft)", fontWeight:600 }}>
                ❤️ {(q.likes||[]).length || ""}
              </button>
              <button onClick={() => setShowComments(prev => ({ ...prev, [q.id]: !prev[q.id] }))} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:"var(--soft)", fontWeight:600 }}>
                💬 {(q.comments||[]).length || ""} {showComments[q.id] ? "Hide" : "Comment"}
              </button>
            </div>
            {showComments[q.id] && (
              <div style={{ marginTop:8 }}>
                {(q.comments||[]).map((c,i) => (
                  <div key={i} style={{ fontSize:12, color:"var(--mid)", padding:"4px 0", borderBottom:"1px solid var(--border)" }}>
                    <span style={{ fontWeight:700, color:"var(--deep)" }}>{c.userName}</span>: {c.text}
                  </div>
                ))}
                <div style={{ display:"flex", gap:6, marginTop:8 }}>
                  <input className="form-input" style={{ flex:1, padding:"8px 12px", fontSize:13 }} placeholder="Add a comment..." value={commentText[q.id] || ""} onChange={e => setCommentText(prev => ({ ...prev, [q.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleComment(q.id)} />
                  <button className="btn-forest" onClick={() => handleComment(q.id)} style={{ padding:"8px 14px" }}>→</button>
                </div>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

// ─── COACH ────────────────────────────────────────────────────────────────────
function CoachDashboard({ user, onLogout, onSwitchToPT }) {
  const [tab, setTab] = useState("today");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [dotWins, setDotWins] = useState(false);
  const [dotQA, setDotQA] = useState(false);
  const [dotMessages, setDotMessages] = useState(false);
  const coachLsKey = (type) => `gt_coach_last_seen_${type}`;

  useEffect(() => {
    const check = async () => {
      const lastWins = localStorage.getItem(coachLsKey('wins'));
      const lastQA = localStorage.getItem(coachLsKey('qa'));
      const lastMsg = localStorage.getItem(coachLsKey('messages'));
      const [wins, qa, msgs] = await Promise.all([
        getWins('bc'),
        getQA(),
        getMessages('bc_group'),
      ]);
      setDotWins(lastWins ? wins.some(w => w.created_at > lastWins) : wins.length > 0);
      setDotQA(lastQA ? qa.some(q => q.created_at > lastQA) : qa.length > 0);
      const otherMsgs = msgs.filter(m => m.sender_id !== "coach");
      setDotMessages(lastMsg ? otherMsgs.some(m => m.created_at > lastMsg) : otherMsgs.length > 0);
    };
    check();
  }, []);

  const handleTab = (id) => {
    if (id === "wins") { localStorage.setItem(coachLsKey('wins'), new Date().toISOString()); setDotWins(false); }
    if (id === "qa") { localStorage.setItem(coachLsKey('qa'), new Date().toISOString()); setDotQA(false); }
    if (id === "messages") { localStorage.setItem(coachLsKey('messages'), new Date().toISOString()); setDotMessages(false); }
    setTab(id);
  };

  const coachTabs = [
    ["today","Today"], ["members","Members"], ["messages","💬 Messages", dotMessages],
    ["wins","Wins & Shares", dotWins], ["workouts","Post Workouts"], ["expworkouts","✨ Expansion Workouts"], ["videos","📹 Videos"],
    ["qa","Q&A", dotQA], ["nutrition","Nutrition Settings"], ["announce","Announcements"],
    ["attendance","Attendance"], ["camp","Camp Settings"], ["summary","End of Camp"],
  ];

  return (
    <div className="coach-shell">
      <style>{styles}</style>
      <div className="coach-top">
        <div className="coach-logo">Get Thicc Boot Camp — Coach</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
          <button onClick={() => setShowMenu(v => !v)} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 13, fontFamily: "Jost, sans-serif", fontWeight: 600, padding: "6px 12px", borderRadius: 8 }}>
            {user.name} ▾
          </button>
          {showMenu && (
            <div style={{ position: "absolute", top: 38, right: 0, background: "white", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", padding: 8, minWidth: 180, zIndex: 100 }}>
              <div style={{ padding: "6px 12px", fontSize: 11, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Switch to</div>
              <button onClick={() => { setShowMenu(false); onSwitchToPT(); }} style={{ width: "100%", padding: "10px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontFamily: "Jost, sans-serif", fontWeight: 600, color: "#5B9BD5", textAlign: "left", borderRadius: 8 }}>🏋️ Personal Coaching</button>
              <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "4px 0" }} />
              <button onClick={() => { setShowMenu(false); onLogout(); }} style={{ width: "100%", padding: "10px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontFamily: "Jost, sans-serif", fontWeight: 600, color: "var(--soft)", textAlign: "left", borderRadius: 8 }}>Sign Out</button>
            </div>
          )}
        </div>
      </div>
      {!selectedMember ? (
        <>
          <div className="coach-tabs">
            {coachTabs.map(([id, label, dot]) => (
              <div key={id} className={`coach-tab ${tab === id ? "active" : ""}`} onClick={() => handleTab(id)} style={{ position: "relative" }}>
                {label}
                {dot && <span style={{ position: "absolute", top: 6, right: 4, width: 8, height: 8, background: "#E8647A", borderRadius: "50%", border: "2px solid var(--surface)" }} />}
              </div>
            ))}
          </div>
          <div className="coach-content">
            {tab === "today" && <CoachToday setSelectedMember={setSelectedMember} />}
            {tab === "members" && <CoachMembers setSelectedMember={setSelectedMember} />}
            {tab === "wins" && <CoachWins user={user} />}
            {tab === "messages" && <BCMessaging user={user} isCoach={true} />}
            {tab === "workouts" && <CoachPostWorkouts />}
            {tab === "expworkouts" && <CoachExpansionWorkouts />}
            {tab === "videos" && <CoachVideoLibrary />}
            {tab === "qa" && <CoachQA user={user} />}
            {tab === "nutrition" && <CoachNutritionSettings />}
            {tab === "announce" && <CoachAnnouncements />}
            {tab === "attendance" && <CoachAttendance />}
            {tab === "camp" && <CoachCampSettings />}
            {tab === "summary" && <CoachEndOfCampSummary />}
          </div>
        </>
      ) : (
        <CoachMemberDetail member={selectedMember} onBack={() => setSelectedMember(null)} />
      )}
    </div>
  );
}

function CoachToday({ setSelectedMember }) {
  const [members, setMembers] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [prs, setPrs] = useState([]);
  const [allCheckins, setAllCheckins] = useState([]);
  const [allWorkouts, setAllWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [m, c, w, p] = await Promise.all([
        getMembers(),
        getCheckins(null, 'bc'),
        getWorkouts(null, 'bc'),
        getPRs(null, 'bc'),
      ]);
      setMembers(m);
      setAllCheckins(c);
      setAllWorkouts(w);
      setCheckins(c.filter(x => x.date === today()));
      setWorkouts(w.filter(x => x.date === today()));
      setPrs(p.filter(x => x.date === today()));
      setLoading(false);
    };
    load();
  }, []);

  const miaMembers = members.filter(m => {
    const mDates = [
      ...allCheckins.filter(c => c.user_id === m.id).map(c => c.date),
      ...allWorkouts.filter(w => w.user_id === m.id).map(w => w.date),
    ];
    return getDaysSince(mDates) >= 3;
  });

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div>Loading...</div>;

  return (
    <div>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, fontWeight: 800, color: "#5B9BD5", marginBottom: 2 }}>
        {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
      </div>
      <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 20 }}>Here's what your ladies are up to today</div>
      <div className="stats-row">
        {[["Checked In", checkins.length],["Workouts", workouts.length],["PRs Today", prs.length]].map(([l,v]) => (
          <div key={l} className="stat-box"><div className="stat-number">{v}</div><div className="stat-label-big">{l}</div></div>
        ))}
      </div>

      {miaMembers.length > 0 && (
        <div className="card" style={{ borderLeft: "3px solid #9B8AA6", marginBottom: 16 }}>
          <div className="section-label" style={{ color: "#9B8AA6" }}>MIA — 3+ Days Inactive</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {miaMembers.map(m => (
              <div key={m.id} onClick={() => setSelectedMember(m)} style={{ display:"flex", alignItems:"center", gap:6, background:"#9B8AA615", borderRadius:8, padding:"6px 10px", cursor:"pointer" }}>
                <div className="avatar" style={{ width:28, height:28, fontSize:10, background:"#9B8AA6" }}>{initials(m.name)}</div>
                <div>
                  <div style={{ fontWeight:700, fontSize:13 }}>{m.name}</div>
                  <div style={{ fontSize:10, color:"var(--soft)" }}>
                    {(() => {
                      const mDates = [
                        ...allCheckins.filter(c => c.user_id === m.id).map(c => c.date),
                        ...allWorkouts.filter(w => w.user_id === m.id).map(w => w.date),
                      ];
                      const days = getDaysSince(mDates);
                      return days === 999 ? "No activity yet" : `${days} days since last activity`;
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Member Activity</div>
      {members.map(m => {
        const ci = checkins.find(c => c.user_id === m.id);
        const wk = workouts.find(w => w.user_id === m.id);
        const pr = prs.find(p => p.user_id === m.id);
        return (
          <div key={m.id} className="today-row" onClick={() => setSelectedMember(m)}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div className="avatar">{initials(m.name)}</div>
              <span className="today-name">{m.name}</span>
            </div>
            <div className="today-chips">
              {ci ? <span className="chip green">Checked in</span> : <span className="chip gray">No check-in</span>}
              {wk && <span className="chip pink">Workout</span>}
              {pr && <span className="chip gold">PR!</span>}
              {ci?.mood && <span className="chip gray">{ci.mood.split(" ")[0]}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CoachMembers({ setSelectedMember }) {
  const [members, setMembers] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [prs, setPrs] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [resetingId, setResetingId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetSaved, setResetSaved] = useState({});

  useEffect(() => {
    const load = async () => {
      const [m, w, p, c] = await Promise.all([getMembers(), getWorkouts(null, 'bc'), getPRs(null, 'bc'), getCheckins(null, 'bc')]);
      setMembers(m); setWorkouts(w); setPrs(p); setCheckins(c);
    };
    load();
  }, []);

  const handleReset = async (userId) => {
    if (!newPassword.trim() || newPassword.length < 6) return;
    const success = await resetMemberPassword(userId, newPassword);
    if (success) {
      setResetSaved(prev => ({ ...prev, [userId]: true }));
      setNewPassword(""); setResetingId(null);
      setTimeout(() => setResetSaved(prev => ({ ...prev, [userId]: false })), 2000);
    }
  };

  return (
    <div>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 18 }}>Your Members</div>
      <div className="member-grid">
        {members.map(m => {
          const mw = workouts.filter(w => w.user_id === m.id);
          const mp = prs.filter(p => p.user_id === m.id);
          const mc = checkins.filter(c => c.user_id === m.id);
          const lastSeen = [...mw, ...mc].sort((a,b) => b.date.localeCompare(a.date))[0]?.date;
          return (
            <div key={m.id} className="member-card">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }} onClick={() => setSelectedMember(m)} >
                <div className="avatar">{initials(m.name)}</div>
                <div className="member-card-name">{m.name}</div>
              </div>
              <div className="member-stat" onClick={() => setSelectedMember(m)}><span className={`stat-dot ${mw.length ? "" : "empty"}`} />{mw.length} workouts</div>
              <div className="member-stat" onClick={() => setSelectedMember(m)}><span className={`stat-dot ${mp.length ? "" : "empty"}`} />{mp.length} PRs</div>
              <div className="member-stat" onClick={() => setSelectedMember(m)}><span className={`stat-dot ${mc.length ? "" : "empty"}`} />{mc.length} check-ins</div>
              {lastSeen && <div style={{ fontSize: 10, color: "var(--soft)", marginTop: 6 }} onClick={() => setSelectedMember(m)}>Last active: {fmt(lastSeen)}</div>}
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
                {resetingId === m.id ? (
                  <div>
                    <input className="form-input" style={{ marginBottom: 6, fontSize: 13 }} type="password" placeholder="New password (min 6 chars)" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                    <div style={{ display: "flex", gap: 6 }}>
                      <div onClick={() => handleReset(m.id)} style={{ flex: 1, padding: "7px", background: "var(--forest)", color: "white", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, textAlign: "center", userSelect: "none" }}>
                        {resetSaved[m.id] ? "✓ Reset!" : "Set Password"}
                      </div>
                      <div onClick={() => { setResetingId(null); setNewPassword(""); }} style={{ padding: "7px 10px", background: "var(--bg)", color: "var(--soft)", borderRadius: 8, cursor: "pointer", fontSize: 12, userSelect: "none" }}>
                        Cancel
                      </div>
                    </div>
                  </div>
                ) : (
                  <div onClick={() => setResetingId(m.id)} style={{ fontSize: 11, color: "var(--soft)", cursor: "pointer", fontWeight: 600 }}>
                    Reset Password
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CoachWins({ user }) {
  const [allWins, setAllWins] = useState([]);
  const [filter, setFilter] = useState("all");
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});
  const [type, setType] = useState("win");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [postSaved, setPostSaved] = useState(false);

  useEffect(() => { getWins('bc').then(setAllWins); }, []);

  const post = async () => {
    if (!text.trim()) return;
    const win = { id: `win_${Date.now()}`, userId: user.id, userName: user.name, type, text: text.trim(), url: url.trim(), date: today() };
    setPostSaved("saving");
    const success = await saveWin(win);
    if (success) {
      setAllWins(prev => [{ ...win, user_id: user.id, user_name: user.name, likes: [], comments: [] }, ...prev]);
      setText(""); setUrl("");
    }
    setPostSaved(success ? "saved" : "error");
    setTimeout(() => setPostSaved(false), 2000);
  };

  const handleLike = async (id) => {
    await toggleLike('wins', id, user.id);
    setAllWins(prev => prev.map(w => {
      if (w.id !== id) return w;
      const likes = w.likes || [];
      return { ...w, likes: likes.includes(user.id) ? likes.filter(l => l !== user.id) : [...likes, user.id] };
    }));
  };

  const handleComment = async (id) => {
    const text = commentText[id];
    if (!text?.trim()) return;
    await addComment('wins', id, user.id, user.name, text);
    setAllWins(prev => prev.map(w => {
      if (w.id !== id) return w;
      return { ...w, comments: [...(w.comments || []), { userId: user.id, userName: user.name, text, date: today() }] };
    }));
    setCommentText(prev => ({ ...prev, [id]: "" }));
  };

  const filtered = filter === "all" ? allWins : allWins.filter(w => w.type === filter);
  const borderColor = (type) => type === "win" ? "var(--gold)" : type === "gratitude" ? "var(--pink-soft)" : "var(--forest)";
  const tagLabel = (type) => type === "win" ? "🏆 Win" : type === "gratitude" ? "🙏 Gratitude" : "📲 Share";

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 14 }}>Wins, Gratitudes & Shares</div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ fontSize: 16 }}>Post to the Community Board</div>
        <div className="mood-row" style={{ margin: "10px 0" }}>
          <button className={`mood-btn ${type === "win" ? "selected" : ""}`} onClick={() => setType("win")}>🏆 Win</button>
          <button className={`mood-btn ${type === "gratitude" ? "selected" : ""}`} onClick={() => setType("gratitude")}>🙏 Gratitude</button>
          <button className={`mood-btn ${type === "share" ? "selected" : ""}`} onClick={() => setType("share")}>📲 Share</button>
        </div>
        <textarea className="form-input form-textarea" style={{ minHeight: 70 }} placeholder={type === "win" ? "Share a win with the group..." : type === "gratitude" ? "Share what you're grateful for..." : "Share a recipe, link, or inspiration..."} value={text} onChange={e => setText(e.target.value)} />
        {type === "share" && <input className="form-input" style={{ marginTop: 8 }} placeholder="Link URL (optional)" value={url} onChange={e => setUrl(e.target.value)} />}
        <div onClick={post} style={{ marginTop: 10, padding: "12px", background: "var(--pink)", color: "white", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, textAlign: "center", userSelect: "none" }}>
          {postSaved === "saving" ? "Posting..." : postSaved === "saved" ? "✓ Posted!" : postSaved === "error" ? "⚠️ Failed — try again" : "Post to Group"}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
        {[["all","All"],["win","Wins"],["gratitude","Gratitudes"],["share","Shares"]].map(([v,l]) => (
          <button key={v} className={filter === v ? "btn-secondary" : "btn-ghost"} onClick={() => setFilter(v)} style={{ padding:"8px 14px", fontSize:12 }}>{l}</button>
        ))}
      </div>
      {filtered.length === 0
        ? <div className="empty-state"><div className="empty-icon">✨</div>Nothing posted yet</div>
        : filtered.map(w => (
          <div key={w.id} style={{ background:"var(--card)", borderRadius:14, padding:14, marginBottom:10, borderLeft:`3px solid ${borderColor(w.type)}`, boxShadow:"var(--shadow)" }}>
            <div className="win-header">
              <div className="avatar" style={{ width: 30, height: 30, fontSize: 11 }}>{initials(w.user_name || w.userName)}</div>
              <span className="win-name">{w.user_name || w.userName}</span>
              <span className={`win-tag ${w.type}`}>{tagLabel(w.type)}</span>
              <span className="win-date">{fmt(w.date)}</span>
            </div>
            <div style={{ fontSize:13, color:"var(--mid)", lineHeight:1.5, marginBottom:8 }}>{w.text}</div>
            {w.url && <a href={w.url} target="_blank" rel="noreferrer" style={{ display:"inline-block", marginBottom:8, fontSize:12, color:"var(--forest)", fontWeight:600 }}>→ View Link</a>}
            <div style={{ display:"flex", gap:16, alignItems:"center", paddingTop:8, borderTop:"1px solid var(--border)" }}>
              <div
                onClick={() => handleLike(w.id)}
                style={{ display:"flex", alignItems:"center", gap:4, cursor:"pointer", padding:"4px 8px", borderRadius:8, background:(w.likes||[]).includes(user.id) ? "var(--pink-pale)" : "var(--bg)", color:(w.likes||[]).includes(user.id) ? "var(--pink)" : "var(--soft)", fontSize:13, fontWeight:600, userSelect:"none" }}
              >
                ❤️ {(w.likes||[]).length || 0}
              </div>
              <div
                onClick={() => setShowComments(prev => ({ ...prev, [w.id]: !prev[w.id] }))}
                style={{ display:"flex", alignItems:"center", gap:4, cursor:"pointer", padding:"4px 8px", borderRadius:8, background:"var(--bg)", color:"var(--soft)", fontSize:13, fontWeight:600, userSelect:"none" }}
              >
                💬 {(w.comments||[]).length || 0} {showComments[w.id] ? "▲" : "▼"}
              </div>
            </div>
            {showComments[w.id] && (
              <div style={{ marginTop:10 }}>
                {(w.comments||[]).map((c,i) => (
                  <div key={i} style={{ fontSize:12, color:"var(--mid)", padding:"4px 0", borderBottom:"1px solid var(--border)" }}>
                    <span style={{ fontWeight:700, color:"var(--deep)" }}>{c.userName}:</span> {c.text}
                  </div>
                ))}
                <div style={{ display:"flex", gap:6, marginTop:8 }}>
                  <input className="form-input" style={{ flex:1, padding:"8px 12px", fontSize:13 }} placeholder="Add a comment..." value={commentText[w.id] || ""} onChange={e => setCommentText(prev => ({ ...prev, [w.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleComment(w.id)} />
                  <div onClick={() => handleComment(w.id)} style={{ padding:"8px 14px", background:"var(--forest)", color:"white", borderRadius:10, cursor:"pointer", fontWeight:600, fontSize:13, userSelect:"none" }}>→</div>
                </div>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

function CoachAnnouncements() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saved, setSaved] = useState(false);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => { getAnnouncements().then(setAnnouncements); }, []);

  const post = async () => {
    if (!title.trim() || !body.trim()) return;
    const ann = { id: `ann_${Date.now()}`, title: title.trim(), body: body.trim(), date: today() };
    await saveAnnouncement(ann);
    setAnnouncements(prev => [ann, ...prev]);
    setSaved(true); setTitle(""); setBody("");
    setTimeout(() => setSaved(false), 2000);
  };

  const remove = async (id) => {
    await deleteAnnouncement(id);
    setAnnouncements(prev => prev.filter(a => a.id !== id));
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 6 }}>Announcements 📣</div>
      <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 18 }}>Posts show up on each member's home screen.</div>
      <div className="card">
        <div className="form-group">
          <label>Title</label>
          <input className="form-input" placeholder="e.g. Class cancelled Saturday" value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Message</label>
          <textarea className="form-input form-textarea" placeholder="Details..." value={body} onChange={e => setBody(e.target.value)} />
        </div>
        <button className="btn-primary" onClick={post}>{saved ? "✓ Posted!" : "Post Announcement"}</button>
      </div>
      {announcements.map(a => (
        <div key={a.id} className="card" style={{ borderLeft: "3px solid var(--gold)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div style={{ fontWeight:700, fontSize:15, color:"var(--deep)", marginBottom:4 }}>{a.title}</div>
            <button onClick={() => remove(a.id)} style={{ background:"none", border:"none", color:"var(--soft)", cursor:"pointer", fontSize:16 }}>×</button>
          </div>
          <div style={{ fontSize:13, color:"var(--mid)", lineHeight:1.5 }}>{a.body}</div>
          <div style={{ fontSize:10, color:"var(--soft)", marginTop:6 }}>{fmt(a.date)}</div>
        </div>
      ))}
    </div>
  );
}

function CoachPostWorkouts() {
  const [week, setWeek] = useState("");
  const [slots, setSlots] = useState([
    { label: "", content: "", notes: "", video_ids: [] },
    { label: "", content: "", notes: "", video_ids: [] },
    { label: "", content: "", notes: "", video_ids: [] },
    { label: "", content: "", notes: "", video_ids: [] },
  ]);
  const [saved, setSaved] = useState(false);
  const [existing, setExisting] = useState([]);
  const [videoLibrary, setVideoLibrary] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showVideoPicker, setShowVideoPicker] = useState(null); // slot index
  const [atSlot, setAtSlot] = useState(null);
  const [atQuery, setAtQuery] = useState("");
  const slotColors = ["var(--forest)", "var(--forest)", "var(--pink)", "var(--pink)"];
  const slotHints = ["At-Home Option 1", "At-Home Option 2", "In-Gym Option 1", "In-Gym Option 2"];

  useEffect(() => {
    Promise.all([getWeeklyWorkouts(), getVideoLibrary()]).then(([ww, vids]) => {
      setExisting(ww); setVideoLibrary(vids);
    });
  }, []);

  const updateSlot = (i, field, val) => setSlots(slots.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  const updateSlotContent = (i, val, cursor) => {
    // Only look at text BEFORE the cursor to find an active @mention
    const before = val.slice(0, cursor);
    const atIdx = before.lastIndexOf('@');
    if (atIdx !== -1) {
      const query = before.slice(atIdx + 1);
      if (!query.includes(' ') && !query.includes('\n')) {
        setAtSlot(i);
        setAtQuery(query);
      } else {
        setAtSlot(null);
      }
    } else {
      setAtSlot(null);
    }
    setSlots(prev => prev.map((s, idx) => idx === i ? { ...s, content: val } : s));
  };

  const pickAtVideo = (i, video) => {
    setSlots(prev => prev.map((s, idx) => {
      if (idx !== i) return s;
      const searchStr = '@' + atQuery;
      const atIdx = s.content.lastIndexOf(searchStr);
      // Remove the @ but keep the movement text
      const newContent = atIdx !== -1
        ? s.content.slice(0, atIdx) + atQuery + s.content.slice(atIdx + searchStr.length)
        : s.content;
      const ids = s.video_ids || [];
      return { ...s, content: newContent, video_ids: ids.includes(video.id) ? ids : [...ids, video.id] };
    }));
    setAtSlot(null);
    setAtQuery("");
  };

  const toggleSlotVideo = (slotIdx, videoId) => {
    setSlots(slots.map((s, idx) => {
      if (idx !== slotIdx) return s;
      const ids = s.video_ids || [];
      return { ...s, video_ids: ids.includes(videoId) ? ids.filter(id => id !== videoId) : [...ids, videoId] };
    }));
  };

  const startEdit = (ww) => {
    setEditingId(ww.id);
    setWeek(ww.week);
    setSlots((ww.slots || []).map(s => ({ label: s.label || "", content: s.content || "", notes: s.notes || "", video_ids: s.video_ids || [] })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setWeek("");
    setSlots([{ label: "", content: "", notes: "", video_ids: [] }, { label: "", content: "", notes: "", video_ids: [] }, { label: "", content: "", notes: "", video_ids: [] }, { label: "", content: "", notes: "", video_ids: [] }]);
  };

  const post = async () => {
    if (!week) return;
    setSaved("saving");
    if (editingId) {
      const success = await updateWeeklyWorkout(editingId, { week, slots });
      if (success) {
        setExisting(prev => prev.map(ww => ww.id === editingId ? { ...ww, week, slots } : ww));
        cancelEdit();
      }
      setSaved(success ? "saved" : "error");
    } else {
      const ww = { id: `ww_${Date.now()}`, week, slots, posted_by: "Madison", posted_date: today() };
      const success = await saveWeeklyWorkout(ww);
      if (success) {
        setExisting(prev => [ww, ...prev]);
        cancelEdit();
      }
      setSaved(success ? "saved" : "error");
    }
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 18 }}>
        {editingId ? "✏️ Edit Workout" : "Post Weekly Workouts"}
      </div>
      <div className="card">
        <div className="form-group">
          <label>Week Label</label>
          <input className="form-input" placeholder="e.g. Week 2 — May 26–June 1" value={week} onChange={e => setWeek(e.target.value)} />
        </div>
        {slots.map((slot, i) => (
          <div key={i} className="slot-form">
            <div className="slot-form-label" style={{ color: slotColors[i] }}>
              Workout {i + 1} {i < 2 ? "(At-Home)" : "(In-Gym)"}
            </div>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label>Label / Name</label>
              <input className="form-input" placeholder={slotHints[i]} value={slot.label} onChange={e => updateSlot(i, "label", e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label>Workout Details</label>
              <textarea className="form-input form-textarea" placeholder="List exercises, sets, reps... (type @ to attach a video)" value={slot.content}
                onChange={e => updateSlotContent(i, e.target.value, e.target.selectionStart)}
                onBlur={() => setTimeout(() => setAtSlot(null), 200)} />
              {atSlot === i && (
                <div style={{ background: "white", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", maxHeight: 200, overflowY: "auto", marginTop: 4 }}>
                  {videoLibrary.filter(v => !atQuery || v.title.toLowerCase().includes(atQuery.toLowerCase()) || (v.exercise || '').toLowerCase().includes(atQuery.toLowerCase())).length === 0
                    ? <div style={{ padding: "10px 14px", fontSize: 13, color: "var(--soft)" }}>No videos match "{atQuery}"</div>
                    : videoLibrary.filter(v => !atQuery || v.title.toLowerCase().includes(atQuery.toLowerCase()) || (v.exercise || '').toLowerCase().includes(atQuery.toLowerCase())).map(v => (
                      <div key={v.id} onMouseDown={e => { e.preventDefault(); pickAtVideo(i, v); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid var(--border)", background: "white" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#FFF0F3"}
                        onMouseLeave={e => e.currentTarget.style.background = "white"}>
                        <span>▶</span>
                        <div><div style={{ fontWeight: 700, fontSize: 13 }}>{v.title}</div><div style={{ fontSize: 11, color: "#999" }}>{v.exercise}</div></div>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label>Coach Notes (optional)</label>
              <input className="form-input" placeholder="Tips, cues, modifications..." value={slot.notes} onChange={e => updateSlot(i, "notes", e.target.value)} />
            </div>
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--soft)", fontWeight: 700, marginBottom: 6 }}>Attached Videos</div>
              {(slot.video_ids || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
                  {(slot.video_ids || []).map(vid => {
                    const v = videoLibrary.find(vl => vl.id === vid);
                    return v ? (
                      <span key={vid} style={{ padding: "4px 10px", background: "#FF000015", borderRadius: 20, fontSize: 11, color: "#CC0000", fontWeight: 600 }}>
                        ▶ {v.title} <span style={{ cursor: "pointer", marginLeft: 4 }} onClick={() => toggleSlotVideo(i, vid)}>×</span>
                      </span>
                    ) : null;
                  })}
                </div>
              )}
              <button className="btn-ghost" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => setShowVideoPicker(showVideoPicker === i ? null : i)}>
                {showVideoPicker === i ? "Done" : "+ Attach Video"}
              </button>
              {showVideoPicker === i && (
                <div style={{ marginTop: 8, background: "var(--bg)", borderRadius: 10, padding: 10, maxHeight: 180, overflowY: "auto" }}>
                  {videoLibrary.length === 0
                    ? <div style={{ fontSize: 12, color: "var(--soft)" }}>No videos in library yet</div>
                    : videoLibrary.map(v => (
                      <div key={v.id} onClick={() => toggleSlotVideo(i, v.id)}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 6px", borderRadius: 7, cursor: "pointer", background: (slot.video_ids || []).includes(v.id) ? "var(--pink-pale)" : "transparent" }}>
                        <span style={{ fontSize: 13 }}>{(slot.video_ids || []).includes(v.id) ? "✓" : "○"}</span>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{v.title}</span>
                        <span style={{ fontSize: 11, color: "var(--soft)" }}>{v.exercise}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-primary mt-8" style={{ flex: 1 }} onClick={post}>
            {saved === "saving" ? "Saving..." : saved === "saved" ? "✓ Saved!" : saved === "error" ? "⚠️ Error" : editingId ? "Save Changes" : "Post to Members"}
          </button>
          {editingId && <button className="btn-ghost mt-8" onClick={cancelEdit}>Cancel</button>}
        </div>
      </div>
      {existing.length > 0 && (
        <div>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Past Posts</div>
          {existing.map(ww => (
            <div key={ww.id} className="week-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div className="week-title">{ww.week}</div>
                  <div className="week-posted">Posted {fmt(ww.posted_date || ww.postedDate)}</div>
                </div>
                <button className="btn-ghost" style={{ fontSize: 12, padding: "5px 12px" }} onClick={() => startEdit(ww)}>✏️ Edit</button>
              </div>
              {(ww.slots || []).map((slot, i) => {
                const attachedVideos = (slot.video_ids || []).map(vid => videoLibrary.find(v => v.id === vid)).filter(Boolean);
                return (
                  <div key={i} className="workout-slot">
                    <div className="slot-label" style={{ color: slotColors[i] }}>{slot.label || slotHints[i]}</div>
                    <div className="slot-content">{slot.content}</div>
                    {slot.notes && <div style={{ fontSize: 12, color: "var(--mid)", marginTop: 4, fontStyle: "italic" }}>📝 {slot.notes}</div>}
                    {attachedVideos.length > 0 && (
                      <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {attachedVideos.map(v => (
                          <span key={v.id} style={{ padding: "3px 10px", background: "#FF000015", borderRadius: 20, fontSize: 11, color: "#CC0000", fontWeight: 600 }}>▶ {v.title}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CoachExpansionWorkouts() {
  const EXP_COLOR = "#5B9BD5";
  const [subTab, setSubTab] = useState("program");

  // Program start date
  const [startDate, setStartDate] = useState("");
  const [startDateSaved, setStartDateSaved] = useState(false);
  const [startDateLoading, setStartDateLoading] = useState(true);

  // Workout programming
  const [track, setTrack] = useState("full_body");
  const [weekNum, setWeekNum] = useState("1");
  const [dayNum, setDayNum] = useState("1");
  const [title, setTitle] = useState("");
  const [exercises, setExercises] = useState([{ movement: "", sets: "3", reps: "10-12", notes: "" }]);
  const [workoutSaving, setWorkoutSaving] = useState(false);
  const [workoutSaved, setWorkoutSaved] = useState(false);
  const [allWorkouts, setAllWorkouts] = useState([]);
  const [workoutsLoading, setWorkoutsLoading] = useState(true);
  const [expandedWorkout, setExpandedWorkout] = useState(null);

  // Movement library
  const [movements, setMovements] = useState([]);
  const [movName, setMovName] = useState("");
  const [movDesc, setMovDesc] = useState("");
  const [movVideo, setMovVideo] = useState("");
  const [movTrack, setMovTrack] = useState("both");
  const [movSaving, setMovSaving] = useState(false);
  const [movSaved, setMovSaved] = useState(false);

  useEffect(() => {
    Promise.all([getProgramStartDate(), getAllExpansionWorkouts(), getMovementLibrary()])
      .then(([sd, wkts, lib]) => {
        setStartDate(sd || "");
        setAllWorkouts(wkts);
        setMovements(lib);
        setStartDateLoading(false);
        setWorkoutsLoading(false);
      });
  }, []);

  const handleSaveStartDate = async () => {
    await saveProgramStartDate(startDate);
    setStartDateSaved(true);
    setTimeout(() => setStartDateSaved(false), 2000);
  };

  const addExercise = () => setExercises(ex => [...ex, { movement: "", sets: "3", reps: "10-12", notes: "" }]);
  const removeExercise = (i) => setExercises(ex => ex.filter((_, idx) => idx !== i));
  const updateExercise = (i, field, val) => setExercises(ex => ex.map((e, idx) => idx === i ? { ...e, [field]: val } : e));

  const handleSaveWorkout = async () => {
    if (!title.trim() || !weekNum || !dayNum) return;
    setWorkoutSaving(true);
    const workout = {
      id: `expw_${track}_w${weekNum}_d${dayNum}_${Date.now()}`,
      track,
      week_number: parseInt(weekNum),
      day_number: parseInt(dayNum),
      title: title.trim(),
      exercises: exercises.filter(e => e.movement.trim()),
    };
    await saveExpansionWorkout(workout);
    const updated = await getAllExpansionWorkouts();
    setAllWorkouts(updated);
    setTitle(""); setExercises([{ movement: "", sets: "3", reps: "10-12", notes: "" }]);
    setWorkoutSaving(false); setWorkoutSaved(true);
    setTimeout(() => setWorkoutSaved(false), 2000);
  };

  const handleDeleteWorkout = async (id) => {
    await deleteExpansionWorkout(id);
    setAllWorkouts(prev => prev.filter(w => w.id !== id));
  };

  const handleSaveMovement = async () => {
    if (!movName.trim() || !movDesc.trim()) return;
    setMovSaving(true);
    const mov = { id: `mov_${Date.now()}`, name: movName.trim(), description: movDesc.trim(), video_url: movVideo.trim() || null, track: movTrack };
    await saveMovement(mov);
    setMovements(prev => [...prev, { ...mov }]);
    setMovName(""); setMovDesc(""); setMovVideo(""); setMovTrack("both");
    setMovSaving(false); setMovSaved(true);
    setTimeout(() => setMovSaved(false), 2000);
  };

  // Group workouts by track → week → day
  const grouped = {};
  allWorkouts.forEach(w => {
    const t = w.track || "full_body";
    if (!grouped[t]) grouped[t] = {};
    if (!grouped[t][w.week_number]) grouped[t][w.week_number] = [];
    grouped[t][w.week_number].push(w);
  });

  const TRACK_LABELS = { full_body: "Full Body", legs_booty: "Legs & Booty" };

  const SUB_TABS = [
    { id: "program", label: "Program Workouts" },
    { id: "library", label: "Movement Library" },
    { id: "settings", label: "Settings" },
  ];

  return (
    <div>
      <div style={{ fontFamily: "Jost, sans-serif", fontSize: 20, fontWeight: 800, marginBottom: 20, color: EXP_COLOR }}>Expansion Workouts</div>

      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, background: "#f0f4f8", borderRadius: 10, padding: 4 }}>
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{ flex: 1, padding: "8px 0", borderRadius: 7, border: "none", background: subTab === t.id ? EXP_COLOR : "transparent", color: subTab === t.id ? "white" : "#666", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* PROGRAM WORKOUTS */}
      {subTab === "program" && (
        <div>
          {/* New workout form */}
          <div style={{ background: "white", border: `1px solid ${EXP_COLOR}30`, borderRadius: 14, padding: 20, marginBottom: 28 }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 16, color: EXP_COLOR }}>Add Workout</div>
            <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              <select value={track} onChange={e => setTrack(e.target.value)} style={{ flex: 1, minWidth: 140, padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13 }}>
                <option value="full_body">Full Body</option>
                <option value="legs_booty">Legs & Booty</option>
              </select>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#666" }}>Week</span>
                <input type="number" min="1" value={weekNum} onChange={e => setWeekNum(e.target.value)} style={{ width: 60, padding: "9px 8px", borderRadius: 8, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13 }} />
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#666" }}>Day</span>
                <input type="number" min="1" max="7" value={dayNum} onChange={e => setDayNum(e.target.value)} style={{ width: 60, padding: "9px 8px", borderRadius: 8, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13 }} />
              </div>
            </div>
            <input placeholder="Workout title (e.g. Upper Body Strength)" value={title} onChange={e => setTitle(e.target.value)} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13, marginBottom: 14, boxSizing: "border-box" }} />

            {/* Exercises */}
            <div style={{ marginBottom: 10 }}>
              {exercises.map((ex, i) => (
                <div key={i} style={{ background: "#f8f9fa", borderRadius: 10, padding: "12px", marginBottom: 8 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input placeholder="Movement name" value={ex.movement} onChange={e => updateExercise(i, "movement", e.target.value)} style={{ flex: 2, padding: "7px 10px", borderRadius: 7, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13 }} />
                    <input placeholder="Sets" value={ex.sets} onChange={e => updateExercise(i, "sets", e.target.value)} style={{ width: 60, padding: "7px 8px", borderRadius: 7, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13 }} />
                    <input placeholder="Reps" value={ex.reps} onChange={e => updateExercise(i, "reps", e.target.value)} style={{ width: 80, padding: "7px 8px", borderRadius: 7, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13 }} />
                    {exercises.length > 1 && <button onClick={() => removeExercise(i)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 18, padding: "0 4px" }}>×</button>}
                  </div>
                  <input placeholder="Coach note for this exercise (optional)" value={ex.notes} onChange={e => updateExercise(i, "notes", e.target.value)} style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 12, boxSizing: "border-box" }} />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={addExercise} style={{ padding: "9px 16px", background: "#f0f4f8", border: "none", borderRadius: 8, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", color: "#555" }}>+ Add Exercise</button>
              <button onClick={handleSaveWorkout} disabled={workoutSaving || !title.trim()} style={{ flex: 1, padding: "9px 16px", background: EXP_COLOR, color: "white", border: "none", borderRadius: 8, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", opacity: !title.trim() ? 0.5 : 1 }}>
                {workoutSaving ? "Saving…" : workoutSaved ? "✓ Saved!" : "Save Workout"}
              </button>
            </div>
          </div>

          {/* Existing workouts */}
          {workoutsLoading ? (
            <div style={{ color: "#999", textAlign: "center", padding: 20 }}>Loading…</div>
          ) : Object.keys(grouped).length === 0 ? (
            <div style={{ color: "#aaa", textAlign: "center", padding: 20, fontSize: 14 }}>No workouts programmed yet.</div>
          ) : (
            Object.entries(grouped).map(([tKey, weeks]) => (
              <div key={tKey} style={{ marginBottom: 28 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: EXP_COLOR, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>{TRACK_LABELS[tKey] || tKey}</div>
                {Object.entries(weeks).sort(([a],[b]) => a-b).map(([wk, wkts]) => (
                  <div key={wk} style={{ marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#555", marginBottom: 8 }}>Week {wk}</div>
                    {wkts.sort((a,b) => a.day_number - b.day_number).map(w => {
                      const isOpen = expandedWorkout === w.id;
                      const exList = Array.isArray(w.exercises) ? w.exercises : [];
                      return (
                        <div key={w.id} style={{ background: "white", border: "1px solid #e8eaed", borderRadius: 10, marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", padding: "12px 14px", cursor: "pointer" }} onClick={() => setExpandedWorkout(isOpen ? null : w.id)}>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontSize: 11, color: EXP_COLOR, fontWeight: 700 }}>Day {w.day_number} · </span>
                              <span style={{ fontWeight: 700, fontSize: 14 }}>{w.title}</span>
                              <span style={{ fontSize: 12, color: "#999", marginLeft: 8 }}>{exList.length} exercises</span>
                            </div>
                            <button onClick={e => { e.stopPropagation(); if (window.confirm(`Delete "${w.title}"? This cannot be undone.`)) handleDeleteWorkout(w.id); }} style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 16, padding: "0 6px", marginRight: 8 }}>🗑</button>
                            <span style={{ color: "#aaa" }}>{isOpen ? "▲" : "▼"}</span>
                          </div>
                          {isOpen && exList.length > 0 && (
                            <div style={{ borderTop: "1px solid #f0f0f0", padding: "10px 14px" }}>
                              {exList.map((ex, i) => (
                                <div key={i} style={{ fontSize: 13, padding: "5px 0", borderBottom: i < exList.length-1 ? "1px solid #f5f5f5" : "none" }}>
                                  <span style={{ fontWeight: 700 }}>{ex.movement}</span>
                                  <span style={{ color: "#888", marginLeft: 8 }}>{ex.sets} sets × {ex.reps}</span>
                                  {ex.notes && <div style={{ fontSize: 12, color: "#aaa", fontStyle: "italic", marginTop: 2 }}>{ex.notes}</div>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}

      {/* MOVEMENT LIBRARY */}
      {subTab === "library" && (
        <div>
          <div style={{ background: "white", border: `1px solid ${EXP_COLOR}30`, borderRadius: 14, padding: 20, marginBottom: 24 }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 16, color: EXP_COLOR }}>Add Movement</div>
            <input placeholder="Movement name (e.g. Romanian Deadlift)" value={movName} onChange={e => setMovName(e.target.value)} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13, marginBottom: 10, boxSizing: "border-box" }} />
            <textarea placeholder="How to perform it — cues, form tips, what to feel" value={movDesc} onChange={e => setMovDesc(e.target.value)} rows={4} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13, marginBottom: 10, resize: "none", boxSizing: "border-box" }} />
            <input placeholder="Video URL (optional)" value={movVideo} onChange={e => setMovVideo(e.target.value)} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13, marginBottom: 10, boxSizing: "border-box" }} />
            <select value={movTrack} onChange={e => setMovTrack(e.target.value)} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13, marginBottom: 14 }}>
              <option value="both">Both Tracks</option>
              <option value="full_body">Full Body Only</option>
              <option value="legs_booty">Legs & Booty Only</option>
            </select>
            <button onClick={handleSaveMovement} disabled={movSaving || !movName.trim() || !movDesc.trim()} style={{ width: "100%", padding: "10px", background: EXP_COLOR, color: "white", border: "none", borderRadius: 8, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", opacity: (!movName.trim() || !movDesc.trim()) ? 0.5 : 1 }}>
              {movSaving ? "Saving…" : movSaved ? "✓ Saved!" : "Add to Library"}
            </button>
          </div>

          {movements.length === 0 ? (
            <div style={{ color: "#aaa", textAlign: "center", padding: 20, fontSize: 14 }}>No movements in library yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {movements.map(m => (
                <div key={m.id} style={{ background: "white", border: "1px solid #e8eaed", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{m.name}</div>
                      {m.track && m.track !== "both" && <div style={{ fontSize: 11, color: EXP_COLOR, fontWeight: 600, marginBottom: 6 }}>{m.track === "legs_booty" ? "Legs & Booty" : "Full Body"}</div>}
                      <div style={{ fontSize: 12, color: "#666", lineHeight: 1.6 }}>{m.description}</div>
                      {m.video_url && <div style={{ fontSize: 11, color: EXP_COLOR, marginTop: 6 }}>📹 Video linked</div>}
                    </div>
                    <button onClick={async () => { if (window.confirm(`Delete "${m.name}" from the library?`)) { await deleteMovement(m.id); setMovements(prev => prev.filter(x => x.id !== m.id)); } }} style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 16, padding: "0 0 0 12px" }}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SETTINGS */}
      {subTab === "settings" && (
        <div>
          <div style={{ background: "white", border: `1px solid ${EXP_COLOR}30`, borderRadius: 14, padding: 20 }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 8, color: EXP_COLOR }}>Program Start Date</div>
            <div style={{ fontSize: 13, color: "#888", marginBottom: 14 }}>Set the date the Expansion program begins. Workouts will drop each Sunday evening based on this date.</div>
            {startDateLoading ? (
              <div style={{ color: "#aaa", fontSize: 13 }}>Loading…</div>
            ) : (
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ flex: 1, padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13 }} />
                <button onClick={handleSaveStartDate} style={{ padding: "9px 20px", background: EXP_COLOR, color: "white", border: "none", borderRadius: 8, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                  {startDateSaved ? "✓ Saved!" : "Save"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CoachVideoLibrary() {
  const [videos, setVideos] = useState([]);
  const [title, setTitle] = useState("");
  const [exercise, setExercise] = useState("");
  const [url, setUrl] = useState("");
  const [saved, setSaved] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => { getVideoLibrary().then(setVideos); }, []);

  const getYouTubeId = (u) => {
    const match = u.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const add = async () => {
    if (!title.trim() || !exercise.trim() || !url.trim()) return;
    if (!getYouTubeId(url)) { setSaved("error"); setTimeout(() => setSaved(false), 2000); return; }
    const video = { id: `vid_${Date.now()}`, title: title.trim(), exercise: exercise.trim(), youtubeUrl: url.trim() };
    setSaved("saving");
    const success = await saveVideo(video);
    if (success) {
      setVideos(prev => [...prev, { ...video, youtube_url: video.youtubeUrl }].sort((a,b) => a.exercise.localeCompare(b.exercise)));
      setTitle(""); setExercise(""); setUrl("");
    }
    setSaved(success ? "saved" : "error");
    setTimeout(() => setSaved(false), 2000);
  };

  const remove = async (id) => {
    await deleteVideo(id);
    setVideos(prev => prev.filter(v => v.id !== id));
  };

  const filtered = videos.filter(v => !search || v.exercise.toLowerCase().includes(search.toLowerCase()) || v.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 6 }}>Video Library 📹</div>
      <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 18 }}>Add YouTube videos — members can watch them in the app and you can attach them to workouts.</div>
      <div className="card">
        <div className="card-title">Add a Video</div>
        <div className="form-group">
          <label>Video Title</label>
          <input className="form-input" placeholder="e.g. RDL Tutorial" value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Exercise Name</label>
          <input className="form-input" placeholder="e.g. Romanian Deadlift" value={exercise} onChange={e => setExercise(e.target.value)} />
        </div>
        <div className="form-group">
          <label>YouTube URL</label>
          <input className="form-input" placeholder="https://www.youtube.com/watch?v=..." value={url} onChange={e => setUrl(e.target.value)} />
        </div>
        <button className="btn-primary" onClick={add}>
          {saved === "saving" ? "Saving..." : saved === "saved" ? "✓ Added!" : saved === "error" ? "⚠️ Check the YouTube URL" : "Add to Library"}
        </button>
      </div>
      <input className="form-input" placeholder="🔍 Search library..." value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 14 }} />
      {filtered.length === 0
        ? <div className="empty-state"><div className="empty-icon">📹</div>{search ? "No matches" : "No videos yet"}</div>
        : filtered.map(v => {
          const ytId = getYouTubeId(v.youtube_url);
          return (
            <div key={v.id} className="card" style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "var(--deep)" }}>{v.title}</div>
                  <div style={{ fontSize: 12, color: "var(--soft)" }}>{v.exercise}</div>
                </div>
                <button onClick={() => remove(v.id)} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 18 }}>×</button>
              </div>
              {ytId && (
                <div style={{ borderRadius: 10, overflow: "hidden" }}>
                  <iframe width="100%" height="200" src={`https://www.youtube.com/embed/${ytId}`}
                    title={v.title} frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen style={{ display: "block" }} />
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
}

function CoachQA({ user }) {
  const [answers, setAnswers] = useState({});
  const [saved, setSaved] = useState({});
  const [qaData, setQaData] = useState([]);
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});
  const [question, setQuestion] = useState("");
  const [postSaved, setPostSaved] = useState(false);

  useEffect(() => { getQA().then(setQaData); }, []);

  const postQuestion = async () => {
    if (!question.trim()) return;
    const q = { id: `qa_${Date.now()}`, userId: user.id, userName: user.name, question: question.trim(), isAnon: false, date: today() };
    setPostSaved("saving");
    const success = await saveQuestion(q);
    if (success) {
      setQaData(prev => [{ ...q, user_id: user.id, user_name: user.name, is_anon: false, likes: [], comments: [] }, ...prev]);
      setQuestion("");
    }
    setPostSaved(success ? "saved" : "error");
    setTimeout(() => setPostSaved(false), 2000);
  };

  const handleSaveAnswer = async (id) => {
    const answer = answers[id];
    if (!answer?.trim()) return;
    await saveAnswer(id, answer);
    setQaData(prev => prev.map(q => q.id === id ? { ...q, answer, answered_date: today() } : q));
    setSaved(s => ({ ...s, [id]: true }));
    setTimeout(() => setSaved(s => ({ ...s, [id]: false })), 2000);
  };

  const handleLike = async (id) => {
    await toggleLike('qa', id, user.id);
    setQaData(prev => prev.map(q => {
      if (q.id !== id) return q;
      const likes = q.likes || [];
      return { ...q, likes: likes.includes(user.id) ? likes.filter(l => l !== user.id) : [...likes, user.id] };
    }));
  };

  const handleComment = async (id) => {
    const text = commentText[id];
    if (!text?.trim()) return;
    await addComment('qa', id, user.id, user.name, text);
    setQaData(prev => prev.map(q => {
      if (q.id !== id) return q;
      return { ...q, comments: [...(q.comments || []), { userId: user.id, userName: user.name, text, date: today() }] };
    }));
    setCommentText(prev => ({ ...prev, [id]: "" }));
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 6 }}>Q&A Board</div>
      <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 14 }}>Answer questions from your members. All answers are visible to the whole group.</div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ fontSize: 16 }}>Post a Question to the Group</div>
        <textarea className="form-input form-textarea" style={{ marginTop: 10, minHeight: 70 }} placeholder="Post a question for your ladies to think about..." value={question} onChange={e => setQuestion(e.target.value)} />
        <div onClick={postQuestion} style={{ marginTop: 10, padding: "12px", background: "var(--pink)", color: "white", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, textAlign: "center", userSelect: "none" }}>
          {postSaved === "saving" ? "Posting..." : postSaved === "saved" ? "✓ Posted!" : postSaved === "error" ? "⚠️ Failed — try again" : "Post Question"}
        </div>
      </div>
      {qaData.length === 0
        ? <div className="empty-state"><div className="empty-icon">💬</div>No questions yet</div>
        : qaData.map(q => (
          <div key={q.id} style={{ background:"var(--card)", borderRadius:14, padding:14, marginBottom:10, border:"1px solid var(--border)", boxShadow:"var(--shadow)" }}>
            <div className="qa-question">"{q.question}"</div>
            <div className="qa-asker">
              {(q.is_anon || q.isAnon) ? <span className="anon-badge">Anonymous</span> : <span style={{ fontSize: 11, color: "var(--soft)" }}>Asked by {q.user_name || q.userName}</span>}
              <span style={{ fontSize: 10, color: "var(--soft)", marginLeft: 8 }}>· {fmt(q.date)}</span>
            </div>
            {q.answer
              ? <div className="qa-answer"><div className="qa-answer-label">Your Answer · {fmt(q.answered_date || q.answeredDate)}</div>{q.answer}</div>
              : (
                <div style={{ marginTop: 8 }}>
                  <textarea className="form-input form-textarea" style={{ minHeight: 60 }} placeholder="Type your answer..." value={answers[q.id] || ""} onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} />
                  <div onClick={() => handleSaveAnswer(q.id)} style={{ marginTop:8, padding:"10px 18px", background:"var(--forest)", color:"white", borderRadius:10, cursor:"pointer", fontWeight:600, fontSize:13, display:"inline-block", userSelect:"none" }}>
                    {saved[q.id] ? "✓ Answered!" : "Post Answer"}
                  </div>
                </div>
              )}
            <div style={{ display:"flex", gap:16, alignItems:"center", paddingTop:8, marginTop:8, borderTop:"1px solid var(--border)" }}>
              <div onClick={() => handleLike(q.id)} style={{ display:"flex", alignItems:"center", gap:4, cursor:"pointer", padding:"4px 8px", borderRadius:8, background:(q.likes||[]).includes(user.id) ? "var(--pink-pale)" : "var(--bg)", color:(q.likes||[]).includes(user.id) ? "var(--pink)" : "var(--soft)", fontSize:13, fontWeight:600, userSelect:"none" }}>
                ❤️ {(q.likes||[]).length || 0}
              </div>
              <div onClick={() => setShowComments(prev => ({ ...prev, [q.id]: !prev[q.id] }))} style={{ display:"flex", alignItems:"center", gap:4, cursor:"pointer", padding:"4px 8px", borderRadius:8, background:"var(--bg)", color:"var(--soft)", fontSize:13, fontWeight:600, userSelect:"none" }}>
                💬 {(q.comments||[]).length || 0} {showComments[q.id] ? "▲" : "▼"}
              </div>
            </div>
            {showComments[q.id] && (
              <div style={{ marginTop:10 }}>
                {(q.comments||[]).map((c,i) => (
                  <div key={i} style={{ fontSize:12, color:"var(--mid)", padding:"4px 0", borderBottom:"1px solid var(--border)" }}>
                    <span style={{ fontWeight:700, color:"var(--deep)" }}>{c.userName}:</span> {c.text}
                  </div>
                ))}
                <div style={{ display:"flex", gap:6, marginTop:8 }}>
                  <input className="form-input" style={{ flex:1, padding:"8px 12px", fontSize:13 }} placeholder="Add a comment..." value={commentText[q.id] || ""} onChange={e => setCommentText(prev => ({ ...prev, [q.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleComment(q.id)} />
                  <div onClick={() => handleComment(q.id)} style={{ padding:"8px 14px", background:"var(--forest)", color:"white", borderRadius:10, cursor:"pointer", fontWeight:600, fontSize:13, userSelect:"none" }}>→</div>
                </div>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

function CoachNutritionSettings() {
  const [flyer, setFlyer] = useState(null);
  const [ifThen, setIfThen] = useState("");
  const [flyerSaved, setFlyerSaved] = useState(false);
  const [ifThenSaved, setIfThenSaved] = useState(false);

  useEffect(() => {
    getSetting('nutrition_flyer').then(f => f && setFlyer(f));
    getSetting('nutrition_ifthen').then(it => it && setIfThen(it));
  }, []);

  const handleFlyerUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      await saveSetting('nutrition_flyer', ev.target.result);
      setFlyer(ev.target.result);
      setFlyerSaved(true);
      setTimeout(() => setFlyerSaved(false), 2000);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveIfThen = async () => {
    await saveSetting('nutrition_ifthen', ifThen);
    setIfThenSaved(true);
    setTimeout(() => setIfThenSaved(false), 2000);
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 18 }}>Nutrition Settings</div>
      <div className="card">
        <div className="card-title">Nutrition Flyer</div>
        <div className="card-sub">Upload a flyer — all members will see it in their Nutrition tab</div>
        <input type="file" accept="image/*" onChange={handleFlyerUpload} style={{ marginBottom: 12, fontSize: 13 }} />
        {flyer && <img src={flyer} alt="Uploaded flyer" className="flyer-img" />}
        {flyerSaved && <div style={{ color: "var(--forest)", fontSize: 13, marginTop: 8 }}>✓ Flyer saved to all members!</div>}
      </div>
      <div className="card">
        <div className="card-title">If/Then Statements</div>
        <div className="card-sub">Guidance for common situations — shown to all members in their Nutrition tab</div>
        <textarea
          className="form-input form-textarea"
          style={{ minHeight: 160 }}
          placeholder={"e.g.\nIf you're hungry between meals → have a palm of protein + a fist of veggies\nIf you're craving sweets → try fruit + a thumb of nut butter\nIf you miss a meal → don't skip the next one, just pick up where you left off"}
          value={ifThen}
          onChange={e => setIfThen(e.target.value)}
        />
        <button className="btn-primary mt-8" onClick={handleSaveIfThen}>{ifThenSaved ? "✓ Saved to Members!" : "Save & Publish to Members"}</button>
      </div>
    </div>
  );
}

function CoachCampSettings() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [saved, setSaved] = useState(false);
  const [campInfo, setCampInfo] = useState(null);
  const [inviteCode, setInviteCode] = useState("");
  const [inviteSaved, setInviteSaved] = useState(false);
  const [kudosPresets, setKudosPresets] = useState(DEFAULT_KUDOS_PRESETS);
  const [newPreset, setNewPreset] = useState("");
  const [presetsSaved, setPresetsSaved] = useState(false);
  const [openaiKey, setOpenaiKey] = useState("");
  const [openaiSaved, setOpenaiSaved] = useState(false);

  useEffect(() => {
    Promise.all([getSetting('camp_start'), getSetting('camp_end'), getSetting('invite_code'), getSetting('kudos_presets'), getSetting('openai_key')]).then(([s, e, code, presets, aiKey]) => {
      if (s) setStartDate(s);
      if (e) setEndDate(e);
      if (s && e) setCampInfo({ start: s, end: e });
      if (code) setInviteCode(code);
      if (presets) { try { setKudosPresets(JSON.parse(presets)); } catch {} }
      if (aiKey) setOpenaiKey(aiKey);
    });
  }, []);

  const saveOpenaiKey = async () => {
    if (!openaiKey.trim()) return;
    await saveSetting('openai_key', openaiKey.trim());
    setOpenaiSaved(true); setTimeout(() => setOpenaiSaved(false), 2000);
  };

  const addPreset = () => {
    if (!newPreset.trim()) return;
    setKudosPresets(prev => [...prev, newPreset.trim()]);
    setNewPreset("");
  };

  const removePreset = (i) => setKudosPresets(prev => prev.filter((_, idx) => idx !== i));

  const savePresets = async () => {
    await saveSetting('kudos_presets', JSON.stringify(kudosPresets));
    setPresetsSaved(true); setTimeout(() => setPresetsSaved(false), 2000);
  };

  const save = async () => {
    await Promise.all([saveSetting('camp_start', startDate), saveSetting('camp_end', endDate)]);
    setCampInfo({ start: startDate, end: endDate });
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const saveInvite = async () => {
    if (!inviteCode.trim()) return;
    await saveSetting('invite_code', inviteCode.trim());
    setInviteSaved(true); setTimeout(() => setInviteSaved(false), 2000);
  };

  const getWeekInfo = () => {
    if (!campInfo) return null;
    const start = new Date(campInfo.start);
    const end = new Date(campInfo.end);
    const now = new Date();
    const totalWeeks = Math.ceil((end - start) / (7 * 86400000));
    const currentWeek = Math.ceil((now - start) / (7 * 86400000));
    const daysLeft = Math.ceil((end - now) / 86400000);
    return { totalWeeks, currentWeek: Math.max(1, Math.min(currentWeek, totalWeeks)), daysLeft: Math.max(0, daysLeft) };
  };

  const info = getWeekInfo();

  return (
    <div style={{ maxWidth: 500 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 18 }}>Camp Settings</div>
      {info && (
        <div className="card" style={{ borderLeft: "3px solid var(--pink)", marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            {[["Week", `${info.currentWeek} of ${info.totalWeeks}`], ["Days Left", info.daysLeft], ["Total Weeks", info.totalWeeks]].map(([l, v]) => (
              <div key={l} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, fontWeight: 800, color: "var(--pink)" }}>{v}</div>
                <div style={{ fontSize: 10, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-title">Camp Dates</div>
        <div className="form-group">
          <label>Camp Start Date</label>
          <input type="date" className="form-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Camp End Date</label>
          <input type="date" className="form-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <button className="btn-primary" onClick={save}>{saved ? "✓ Saved!" : "Save Camp Dates"}</button>
      </div>

      <div className="card">
        <div className="card-title">Member Invite Code 🔑</div>
        <div className="card-sub">Share this code with your ladies so they can create their accounts</div>
        <div className="form-group">
          <label>Invite Code</label>
          <input className="form-input" value={inviteCode} onChange={e => setInviteCode(e.target.value)} placeholder="e.g. GETTHICC2025" autoCapitalize="characters" />
        </div>
        <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 12 }}>
          You can change this anytime — old code will stop working immediately
        </div>
        <button className="btn-primary" onClick={saveInvite}>{inviteSaved ? "✓ Code Saved!" : "Save Invite Code"}</button>
      </div>

      <div className="card">
        <div className="card-title">🤖 AI Food Analysis Key</div>
        <div className="card-sub" style={{ marginBottom: 14 }}>Your OpenAI API key — lets members take a photo of their food and have AI write the description automatically. Used on both Boot Camp and PT sides.</div>
        <div className="form-group">
          <label>OpenAI API Key</label>
          <input className="form-input" type="password" value={openaiKey} onChange={e => setOpenaiKey(e.target.value)} placeholder="sk-..." />
        </div>
        <button className="btn-primary" onClick={saveOpenaiKey}>{openaiSaved ? "✓ Key Saved!" : "Save API Key"}</button>
      </div>

      <div className="card">
        <div className="card-title">Kudos Presets 💌</div>
        <div className="card-sub">Members see these as quick-pick options when sending kudos</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14, marginTop: 10 }}>
          {kudosPresets.map((p, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--bg)", borderRadius: 8, padding: "8px 12px" }}>
              <span style={{ flex: 1, fontSize: 13 }}>{p}</span>
              <button onClick={() => removePreset(i)} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input className="form-input" placeholder="Add a new preset..." value={newPreset}
            onChange={e => setNewPreset(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addPreset()}
            style={{ flex: 1 }} />
          <button className="btn-forest" onClick={addPreset}>+</button>
        </div>
        <button className="btn-primary" onClick={savePresets}>{presetsSaved ? "✓ Saved!" : "Save Presets"}</button>
      </div>
    </div>
  );
}

function CoachAttendance() {
  const [members, setMembers] = useState([]);
  const [date, setDate] = useState(today());
  const [present, setPresent] = useState([]);
  const [history, setHistory] = useState([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getMembers().then(setMembers);
    getAttendance().then(setHistory);
  }, []);

  useEffect(() => {
    const existing = history.find(h => h.date === date);
    setPresent(existing?.present_ids || []);
  }, [date, history]);

  const toggle = (id) => setPresent(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const save = async () => {
    setSaved("saving");
    const success = await saveAttendance(date, present);
    if (success) {
      setHistory(prev => {
        const filtered = prev.filter(h => h.date !== date);
        return [...filtered, { date, present_ids: present }].sort((a,b) => b.date.localeCompare(a.date));
      });
    }
    setSaved(success ? "saved" : "error");
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 18 }}>Saturday Attendance</div>
      <div className="card">
        <div className="form-group">
          <label>Class Date</label>
          <input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="section-label">Who showed up?</div>
        {members.map(m => (
          <div key={m.id} className="check-item" onClick={() => toggle(m.id)}>
            <div className={`check-box ${present.includes(m.id) ? "checked" : ""}`}>{present.includes(m.id) ? "✓" : ""}</div>
            <div className="avatar" style={{ width: 28, height: 28, fontSize: 11, marginRight: 4 }}>{initials(m.name)}</div>
            <span className="check-label">{m.name}</span>
          </div>
        ))}
        <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 8, marginBottom: 12 }}>{present.length} of {members.length} present</div>
        <button className="btn-primary" onClick={save} disabled={saved === "saving"}>
          {saved === "saving" ? "Saving..." : saved === "saved" ? "✓ Attendance Saved!" : saved === "error" ? "⚠️ Failed — try again" : "Save Attendance"}
        </button>
      </div>

      {history.length > 0 && (
        <div>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Attendance History</div>
          {history.map(h => (
            <div key={h.date} className="card" style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{new Date(h.date + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
                <div style={{ fontSize: 13, color: "var(--pink)", fontWeight: 700 }}>{(h.present_ids || []).length}/{members.length}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                {members.map(m => (
                  <span key={m.id} className={`chip ${(h.present_ids || []).includes(m.id) ? "green" : "gray"}`}>
                    {(h.present_ids || []).includes(m.id) ? "✓" : "✗"} {m.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CoachCampSummary({ member, checkins, workouts, prs, attendance, wins }) {
  const totalCheckins = checkins.length;
  const totalWorkouts = workouts.length;
  const totalPRs = prs.length;
  const memberWins = wins.filter(w => w.user_id === member.id && w.type === "win").length;
  const memberGratitudes = wins.filter(w => w.user_id === member.id && w.type === "gratitude").length;
  const classesAttended = attendance.filter(a => (a.present_ids || []).includes(member.id)).length;
  const totalClasses = attendance.length;

  const habitKeys = ["water","sleep","supplements","workout","journaled","meditation","mobilized","nutrition","sharedWin"];
  const habitLabels = { water:"💧 Water", sleep:"😴 Sleep", supplements:"💊 Supplements", workout:"🏋️ Workout", journaled:"📓 Journaled", meditation:"🧘 Meditation", mobilized:"🤸 Mobilized", nutrition:"🥗 Nutrition", sharedWin:"✨ Shared Win" };
  const topHabits = habitKeys.map(k => ({
    label: habitLabels[k],
    rate: totalCheckins ? Math.round((checkins.filter(c => c.checks?.[k]).length / totalCheckins) * 100) : 0
  })).sort((a,b) => b.rate - a.rate).slice(0, 3);

  const bestPRs = prs.filter(p => PR_LIFTS.some(l => l.label === p.exercise));

  return (
    <div style={{ background: "var(--card)", borderRadius: 16, padding: 20, border: "1px solid var(--border)", marginBottom: 16 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, color: "var(--deep)", marginBottom: 14 }}>{member.name}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
        {[["Check-ins", totalCheckins], ["Workouts", totalWorkouts], ["Classes", `${classesAttended}/${totalClasses}`], ["PRs", totalPRs], ["Wins", memberWins], ["Gratitudes", memberGratitudes]].map(([l,v]) => (
          <div key={l} style={{ textAlign: "center", background: "var(--bg)", borderRadius: 10, padding: "10px 4px" }}>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 800, color: "var(--pink)" }}>{v}</div>
            <div style={{ fontSize: 9, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1 }}>{l}</div>
          </div>
        ))}
      </div>
      {topHabits.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div className="section-label">Top Habits</div>
          {topHabits.map(h => (
            <div key={h.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: "var(--mid)" }}>{h.label}</span>
              <span style={{ fontWeight: 700, color: "var(--forest)" }}>{h.rate}%</span>
            </div>
          ))}
        </div>
      )}
      {bestPRs.length > 0 && (
        <div>
          <div className="section-label">PRs Set</div>
          {bestPRs.map(p => (
            <div key={p.id} style={{ fontSize: 12, color: "var(--mid)", marginBottom: 2 }}>
              {p.exercise}: <span style={{ fontWeight: 700, color: "var(--pink)" }}>{p.value} {p.unit}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CoachEndOfCampSummary() {
  const [members, setMembers] = useState([]);
  const [allCheckins, setAllCheckins] = useState([]);
  const [allWorkouts, setAllWorkouts] = useState([]);
  const [allPRs, setAllPRs] = useState([]);
  const [allAttendance, setAllAttendance] = useState([]);
  const [allWins, setAllWins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [m, c, w, p, a, wins] = await Promise.all([
        getMembers(), getCheckins(null, 'bc'), getWorkouts(null, 'bc'), getPRs(null, 'bc'), getAttendance(), getWins('bc')
      ]);
      setMembers(m); setAllCheckins(c); setAllWorkouts(w); setAllPRs(p); setAllAttendance(a); setAllWins(wins);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div>Loading...</div>;

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 6 }}>End of Camp Summary</div>
      <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 20 }}>Full journey recap for each member</div>
      {members.map(m => (
        <CoachCampSummary
          key={m.id}
          member={m}
          checkins={allCheckins.filter(c => c.user_id === m.id)}
          workouts={allWorkouts.filter(w => w.user_id === m.id)}
          prs={allPRs.filter(p => p.user_id === m.id)}
          attendance={allAttendance}
          wins={allWins}
        />
      ))}
    </div>
  );
}

function ExpandableSection({ title, count, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="detail-section">
      <div onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: "10px 0", borderBottom: open ? "none" : "1px solid var(--border)" }}>
        <div className="detail-title" style={{ margin: 0 }}>{title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "var(--soft)" }}>{count} entries</span>
          <span style={{ fontSize: 16, color: "var(--pink)", fontWeight: 700 }}>{open ? "↑" : "↓"}</span>
        </div>
      </div>
      {open && (
        <div style={{ paddingTop: 10 }}>
          {count === 0 ? <div style={{ fontSize: 13, color: "var(--soft)", fontStyle: "italic" }}>Nothing logged yet</div> : children}
        </div>
      )}
    </div>
  );
}

// Coach-side editor for a single client's daily check-in habits and cup fillers.
function ClientActivityEditor({ member, color = "#9B8AA6" }) {
  const [disabled, setDisabled] = useState([]);
  const [custom, setCustom] = useState([]);
  const [newHabit, setNewHabit] = useState("");
  const [fillers, setFillers] = useState([]);
  const [newFiller, setNewFiller] = useState("");
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([getUserPrefs(member.id), getCupFillers(member.id)]).then(([p, f]) => {
      if (!alive) return;
      setDisabled(p?.disabled_checkin_items || []);
      setCustom(p?.custom_checkin_items || []);
      setFillers(f || []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [member.id]);

  const saveHabits = async (nextDisabled, nextCustom) => {
    setSaving(true);
    const ok = await saveUserPrefs(member.id, {
      disabled_checkin_items: nextDisabled,
      custom_checkin_items: nextCustom,
    });
    setSaving(false);
    if (ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    return ok;
  };

  const removeDefault = (key) => {
    const next = [...disabled, key];
    setDisabled(next);
    saveHabits(next, custom);
  };

  const restoreDefault = (key) => {
    const next = disabled.filter(k => k !== key);
    setDisabled(next);
    saveHabits(next, custom);
  };

  // Renaming a built-in stores an override entry keyed to that habit.
  const setDefaultLabel = (key, label) => {
    setCustom(prev => {
      const has = prev.some(c => c.key === key);
      return has ? prev.map(c => (c.key === key ? { ...c, label } : c)) : [...prev, { key, label }];
    });
  };

  const commitDefaultLabel = (key, label, original) => {
    const trimmed = label.trim();
    // Reverting to the original label drops the override entirely.
    const next = (!trimmed || trimmed === original)
      ? custom.filter(c => c.key !== key)
      : custom.some(c => c.key === key)
        ? custom.map(c => (c.key === key ? { ...c, label: trimmed } : c))
        : [...custom, { key, label: trimmed }];
    setCustom(next);
    saveHabits(disabled, next);
  };

  const labelFor = (item) => {
    const o = custom.find(c => c.key === item.key);
    return o ? o.label : item.label;
  };

  // Entries keyed to a built-in are renames, not separate habits.
  const allAdditions = custom.filter(c => !RF_CHECKIN_ITEMS.some(d => d.key === c.key));
  const additions = allAdditions.filter(c => !disabled.includes(c.key));
  const removed = [
    ...RF_CHECKIN_ITEMS.filter(i => disabled.includes(i.key)),
    ...allAdditions.filter(c => disabled.includes(c.key)),
  ];

  const addHabit = () => {
    const label = newHabit.trim();
    if (!label) return;
    const next = [...custom, { key: `cu_${Date.now()}`, label }];
    setCustom(next); setNewHabit("");
    saveHabits(disabled, next);
  };

  // Hide rather than delete, so past check-ins keep their label and it can be restored.
  const removeHabit = (key) => {
    const next = [...disabled, key];
    setDisabled(next);
    saveHabits(next, custom);
  };

  const addFiller = async () => {
    const label = newFiller.trim();
    if (!label) return;
    const f = { id: `cf_${member.id}_${Date.now()}`, userId: member.id, label, sortOrder: fillers.length };
    setFillers(prev => [...prev, { ...f, is_checked: false, user_id: member.id }]);
    setNewFiller("");
    await saveCupFiller(f);
  };

  const removeFiller = async (id) => {
    setFillers(prev => prev.filter(f => f.id !== id));
    await deleteCupFiller(id);
  };

  const renameFiller = async (id, label) => {
    setFillers(prev => prev.map(f => f.id === id ? { ...f, label } : f));
    await updateCupFiller(id, { label });
  };

  const box = { border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px", marginBottom: 6, display: "flex", alignItems: "center", gap: 10 };

  return (
    <div className="card" style={{ borderLeft: `3px solid ${color}`, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
          <Sparkle size={12} color={color} weight="fill" /> {member.name}'s Activities
        </div>
        {saving ? <span style={{ fontSize: 11, color: "var(--soft)" }}>Saving…</span>
          : saved ? <span style={{ fontSize: 11, color: "#48BB78", fontWeight: 700 }}>Saved ✓</span> : null}
      </div>
      <div style={{ fontSize: 11, color: "var(--soft)", marginBottom: 16 }}>Personalize what shows on their home page. Changes save automatically.</div>

      {loading ? <div style={{ fontSize: 13, color: "var(--soft)" }}>Loading…</div> : <>
        {/* Daily check-in habits */}
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Daily Check-In Habits</div>
        {RF_CHECKIN_ITEMS.filter(i => !disabled.includes(i.key)).map(item => (
          <div key={item.key} style={box}>
            <item.Icon size={14} color={color} style={{ flexShrink: 0 }} />
            <input
              value={labelFor(item)}
              onChange={e => setDefaultLabel(item.key, e.target.value)}
              onBlur={e => commitDefaultLabel(item.key, e.target.value, item.label)}
              style={{ flex: 1, fontSize: 12, fontFamily: "Jost, sans-serif", border: "none", background: "transparent", color: "var(--deep)", outline: "none", padding: 0 }}
            />
            <span onClick={() => removeDefault(item.key)} title="Remove this habit" style={{ color: "var(--soft)", cursor: "pointer", fontSize: 16, lineHeight: 1, flexShrink: 0 }}>×</span>
          </div>
        ))}
        {additions.map(c => (
          <div key={c.key} style={{ ...box, borderColor: color, background: color + "0d" }}>
            <Sparkle size={14} color={color} weight="fill" style={{ flexShrink: 0 }} />
            <input
              value={c.label}
              onChange={e => setCustom(prev => prev.map(x => x.key === c.key ? { ...x, label: e.target.value } : x))}
              onBlur={() => saveHabits(disabled, custom)}
              style={{ flex: 1, fontSize: 12, fontFamily: "Jost, sans-serif", border: "none", background: "transparent", color: "var(--deep)", outline: "none", padding: 0 }}
            />
            <span onClick={() => removeHabit(c.key)} style={{ color: "var(--soft)", cursor: "pointer", fontSize: 16, lineHeight: 1, flexShrink: 0 }}>×</span>
          </div>
        ))}
        <div style={{ display: "flex", gap: 6, marginTop: 10, marginBottom: removed.length ? 14 : 22 }}>
          <input className="form-input" value={newHabit} onChange={e => setNewHabit(e.target.value)} placeholder={`Add a habit for ${member.name}…`} style={{ flex: 1, fontSize: 12, padding: "8px 10px" }} onKeyDown={e => e.key === "Enter" && addHabit()} />
          <button onClick={addHabit} style={{ padding: "8px 14px", background: color, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 14 }}>+</button>
        </div>

        {removed.length > 0 && (
          <div style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Removed — tap to restore</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {removed.map(i => (
                <span key={i.key} onClick={() => restoreDefault(i.key)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 20, border: "1px dashed var(--border)", color: "var(--soft)", fontSize: 11, fontFamily: "Jost, sans-serif", cursor: "pointer" }}>
                  + {labelFor(i)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Cup fillers */}
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Cup Fillers</div>
        {fillers.length === 0 && <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 8 }}>None yet — add some below.</div>}
        {fillers.map(f => (
          <div key={f.id} style={box}>
            <Coffee size={14} color={color} style={{ flexShrink: 0 }} />
            <input
              value={f.label}
              onChange={e => setFillers(prev => prev.map(x => x.id === f.id ? { ...x, label: e.target.value } : x))}
              onBlur={e => renameFiller(f.id, e.target.value)}
              style={{ flex: 1, fontSize: 12, fontFamily: "Jost, sans-serif", border: "none", background: "transparent", color: "var(--deep)", outline: "none", padding: 0 }}
            />
            <span onClick={() => removeFiller(f.id)} style={{ color: "var(--soft)", cursor: "pointer", fontSize: 16, lineHeight: 1, flexShrink: 0 }}>×</span>
          </div>
        ))}
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          <input className="form-input" value={newFiller} onChange={e => setNewFiller(e.target.value)} placeholder="Add a cup filler…" style={{ flex: 1, fontSize: 12, padding: "8px 10px" }} onKeyDown={e => e.key === "Enter" && addFiller()} />
          <button onClick={addFiller} style={{ padding: "8px 14px", background: color, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 14 }}>+</button>
        </div>
      </>}
    </div>
  );
}

function CoachMemberDetail({ member, onBack, coachUser = { id: "coach", name: "Madison" } }) {
  const [workouts, setWorkouts] = useState([]);
  const [prs, setPrs] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [nutLogs, setNutLogs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [weightLogs, setWeightLogs] = useState({});
  const [coachNote, setCoachNote] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [memberKudos, setMemberKudos] = useState([]);
  const [wins, setWins] = useState([]);
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});
  const [ciCommentText, setCiCommentText] = useState({});
  const [ciShowComments, setCiShowComments] = useState({});

  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [prefs, setPrefs] = useState({});
  const [ptCheckItems, setPtCheckItems] = useState([]);

  useEffect(() => {
    const load = async () => {
      setRefreshing(true);
      const programType = member.role === 'pt_client' ? 'pt' : member.role === 'expansion' ? 'exp' : member.role === 'rooted' ? 'rooted' : 'bc';
      const [w, p, c, n, prof, wl, kudos, allWins, journals, up, pci] = await Promise.all([
        getWorkouts(member.id),
        getPRs(member.id),
        getCheckins(member.id),
        getNutritionLogs(member.id),
        getNutritionProfile(member.id),
        getWeightLogs(member.id),
        getKudos(member.id),
        getWins(programType),
        getAllJournalEntriesForUsers([member.id]),
        getUserPrefs(member.id),
        getPTCheckItems(member.id),
      ]);
      setPrefs(up || {});
      setPtCheckItems(pci || []);
      // Merge journal_entries (home-page saves) with checkins so a day logged
      // in either place always shows up. checkins wins on conflict.
      const seen = new Set((c || []).map(x => x.date));
      const fromJournal = (journals || [])
        .filter(j => j.date && !j.date.startsWith('nd_') && !seen.has(j.date))
        .map(j => ({
          id: j.id, user_id: j.user_id, user_name: member.name, date: j.date,
          checks: j.checks || {}, mood: j.mood || '', energy: j.energy || '',
          notes: j.response || '', likes: j.likes || [], comments: j.comments || [],
        }));
      const merged = [...(c || []), ...fromJournal].sort((a, b) => b.date.localeCompare(a.date));
      setWorkouts(w); setPrs(p); setCheckins(merged); setNutLogs(n);
      setProfile(prof); setWeightLogs(wl); setMemberKudos(kudos);
      setWins(allWins.filter(win => win.user_id === member.id));
      setCoachNote(prof?.coach_notes || "");
      setLoading(false);
      setRefreshing(false);
    };
    load();
  }, [member.id, refreshKey]);

  const handleLike = async (id) => {
    await toggleLike('wins', id, coachUser.id);
    setWins(prev => prev.map(w => {
      if (w.id !== id) return w;
      const likes = w.likes || [];
      return { ...w, likes: likes.includes(coachUser.id) ? likes.filter(l => l !== coachUser.id) : [...likes, coachUser.id] };
    }));
  };

  const handleComment = async (id) => {
    const text = commentText[id];
    if (!text?.trim()) return;
    await addComment('wins', id, coachUser.id, coachUser.name, text);
    setWins(prev => prev.map(w => {
      if (w.id !== id) return w;
      return { ...w, comments: [...(w.comments || []), { userId: coachUser.id, userName: coachUser.name, text }] };
    }));
    setCommentText(prev => ({ ...prev, [id]: "" }));
  };

  const handleCiLike = async (id) => {
    await toggleLike('checkins', id, coachUser.id);
    setCheckins(prev => prev.map(c => {
      if (c.id !== id) return c;
      const likes = c.likes || [];
      return { ...c, likes: likes.includes(coachUser.id) ? likes.filter(l => l !== coachUser.id) : [...likes, coachUser.id] };
    }));
  };

  const handleCiComment = async (id) => {
    const text = ciCommentText[id];
    if (!text?.trim()) return;
    await addComment('checkins', id, coachUser.id, coachUser.name, text);
    setCheckins(prev => prev.map(c => {
      if (c.id !== id) return c;
      return { ...c, comments: [...(c.comments || []), { userId: coachUser.id, userName: coachUser.name, text }] };
    }));
    setCiCommentText(prev => ({ ...prev, [id]: "" }));
  };

  const saveNote = async () => {
    await saveNutritionProfile(member.id, { ...(profile || {}), coachNotes: coachNote });
    setNoteSaved(true); setTimeout(() => setNoteSaved(false), 2000);
  };

  const servingLabels = { protein: "🥩 Protein", carbs: "🌾 Carbs", fat: "🥑 Fat", veggies: "🥦 Veggies" };

  // Boot Camp uses its own habit keys; the other programs share RF_CHECKIN_ITEMS
  // and can have per-client renames/additions on top.
  const bcLabels = { water:"💧 Water 80oz", sleep:"😴 Sleep 7+hrs", supplements:"💊 Supplements", workout:"🏋️ Workout", journaled:"📓 Journaled", meditation:"🧘 Meditation", mobilized:"🤸 Mobilized", nutrition:"🥗 Nutrition", sharedWin:"✨ Shared Win" };
  const isBootCamp = member.role === 'member';
  const clientItems = buildCheckinItems(prefs);
  const knownItems = allKnownCheckinItems(prefs);

  // Resolve a stored check key to the label the client actually saw. Order
  // matters: keys like "water" exist in both Boot Camp and the other programs.
  const labelForKey = (k) => {
    if (isBootCamp && bcLabels[k]) return bcLabels[k];
    const known = knownItems.find(i => i.key === k);
    if (known) return known.label;
    const legacyPT = ptCheckItems.find(i => i.id === k);
    if (legacyPT) return legacyPT.label;
    if (bcLabels[k]) return bcLabels[k];
    return k.startsWith('cu_') || k.startsWith('ci_') ? 'Removed habit' : k;
  };

  const habitRates = (isBootCamp
    ? Object.keys(bcLabels).map(k => ({ key: k, label: bcLabels[k] }))
    : clientItems.map(i => ({ key: i.key, label: i.label }))
  ).map(({ key, label }) => {
    const total = checkins.length;
    const hits = checkins.filter(c => c.checks?.[key]).length;
    return { key, label, rate: total ? Math.round((hits/total)*100) : 0 };
  });

  if (loading) return <div style={{ padding: 28 }}><button className="back-btn" onClick={onBack}>← Back</button><div className="empty-state"><div className="empty-icon">⏳</div>Loading...</div></div>;

  return (
    <div style={{ padding: 28 }}>
      <style>{styles}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <button className="back-btn" onClick={onBack}>← Back to Members</button>
        <button onClick={() => setRefreshKey(k => k + 1)} disabled={refreshing} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 8, padding: "5px 12px", fontSize: 12, fontFamily: "Jost, sans-serif", fontWeight: 700, color: "var(--soft)", cursor: "pointer" }}>
          {refreshing ? "Refreshing…" : "↻ Refresh"}
        </button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <div className="avatar" style={{ width: 52, height: 52, fontSize: 18 }}>{initials(member.name)}</div>
        <div>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 30, fontWeight: 800, color: "var(--deep)" }}>{member.name}</div>
          <div style={{ fontSize: 12, color: "var(--soft)" }}>{workouts.length} workouts · {prs.length} PRs · {checkins.length} check-ins</div>
        </div>
      </div>

      {/* Ascension + Expansion only. Rooted is self-directed, and Boot Camp
          uses its own separate check-in list with no cup fillers. */}
      {(member.role === 'pt_client' || member.role === 'expansion') && (
        <ClientActivityEditor
          member={member}
          color={member.role === 'pt_client' ? '#9B8AA6' : '#5B9BD5'}
        />
      )}

      {/* Kudos received */}
      {memberKudos.length > 0 && (
        <div className="card" style={{ marginBottom: 20, borderLeft: "3px solid var(--pink)" }}>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 16, fontWeight: 700, marginBottom: 10 }}>💌 Kudos Received</div>
          {memberKudos.map(k => (
            <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
              <div className="avatar" style={{ width: 26, height: 26, fontSize: 10, flexShrink: 0 }}>{initials(k.from_user_name)}</div>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 700, fontSize: 12 }}>{k.from_user_name}</span>
                <span style={{ fontSize: 12, color: "var(--mid)", marginLeft: 6 }}>→</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--pink)", marginLeft: 6 }}>{k.message}</span>
              </div>
              <div style={{ fontSize: 10, color: "var(--soft)", flexShrink: 0 }}>{new Date(k.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
            </div>
          ))}
        </div>
      )}

      {/* Today's Snapshot */}
      {(() => {
        const todayCI = checkins.find(c => c.date === today());
        const todayWK = workouts.find(w => w.date === today());
        const todayNL = nutLogs.find(n => n.date === today());
        const todayPR = prs.find(p => p.date === today());
        return (
          <div className="card" style={{ marginBottom: 20, borderLeft: "3px solid var(--pink)" }}>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Today</div>
            {!todayCI && !todayWK && !todayNL && !todayPR && (
              <div style={{ fontSize: 13, color: "var(--soft)", fontStyle: "italic" }}>No activity logged yet today</div>
            )}
            {todayCI && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Check-in · {todayCI.mood} · {todayCI.energy}</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {Object.entries(todayCI.checks || {}).filter(([,v]) => v).map(([k]) => (
                    <span key={k} className="chip green" style={{ fontSize: 9 }}>✓ {labelForKey(k)}</span>
                  ))}
                </div>
                {todayCI.notes && <div style={{ fontSize: 12, color: "var(--mid)", marginTop: 4 }}>{todayCI.notes}</div>}
              </div>
            )}
            {todayWK && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Workout</div>
                <span className={`workout-type ${todayWK.type}`}>{todayWK.type === "inperson" ? "In-Person" : todayWK.type === "athome" ? "At-Home" : "Alternative"}</span>
                {todayWK.duration && <span style={{ fontSize: 11, color: "var(--soft)", marginLeft: 8 }}>{todayWK.duration} min</span>}
                {todayWK.notes && <div style={{ fontSize: 12, color: "var(--mid)", marginTop: 4 }}>{todayWK.notes}</div>}
              </div>
            )}
            {todayNL && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Meals</div>
                {["breakfast","snack1","lunch","snack2","dinner"].filter(s => todayNL[s]).map(s => (
                  <div key={s} style={{ fontSize: 12, color: "var(--mid)" }}><span style={{ fontWeight: 600 }}>{s.charAt(0).toUpperCase() + s.slice(1)}:</span> {todayNL[s]}</div>
                ))}
              </div>
            )}
            {todayPR && <div style={{ fontSize: 13, color: "var(--pink)", fontWeight: 700 }}>🏆 New PR today: {todayPR.exercise} — {todayPR.value} {todayPR.unit}</div>}
          </div>
        );
      })()}

      {/* Habit Completion Rates — always visible */}
      <div className="detail-section">
        <div className="detail-title">Habit Completion Rates</div>
        {checkins.length === 0 ? <div style={{ fontSize: 13, color: "var(--soft)" }}>No check-ins yet</div> : habitRates.map(h => (
          <div key={h.key} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--deep)" }}>{h.label}</span>
              <span style={{ fontSize: 12, color: h.rate >= 70 ? "var(--forest)" : h.rate >= 40 ? "var(--gold)" : "var(--pink)", fontWeight: 700 }}>{h.rate}%</span>
            </div>
            <div style={{ background: "var(--bg)", borderRadius: 6, height: 6, overflow: "hidden" }}>
              <div style={{ width: `${h.rate}%`, height: "100%", background: h.rate >= 70 ? "var(--forest)" : h.rate >= 40 ? "var(--gold)" : "var(--pink)", borderRadius: 6 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Expandable History Sections */}
      {[
        { title: "Wins & Gratitudes", count: wins.length, content: (
          wins.length === 0
            ? <div style={{ fontSize: 13, color: "var(--soft)", padding: "8px 0" }}>No wins or gratitudes shared yet</div>
            : wins.map(w => (
              <div key={w.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: w.type === "win" ? "var(--gold)" : "var(--pink-soft)", background: w.type === "win" ? "#FFF8E1" : "var(--pink-pale)", padding: "2px 8px", borderRadius: 20 }}>{w.type === "win" ? "Win" : "Gratitude"}</span>
                  <span style={{ fontSize: 11, color: "var(--soft)" }}>{fmt(w.date)}</span>
                </div>
                <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.5, marginBottom: 8 }}>{w.text}</div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div onClick={() => handleLike(w.id)} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 8px", borderRadius: 8, background: (w.likes||[]).includes(coachUser.id) ? "var(--pink-pale)" : "var(--bg)", color: (w.likes||[]).includes(coachUser.id) ? "var(--pink)" : "var(--soft)", fontSize: 12, fontWeight: 600, userSelect: "none" }}>
                    <Heart size={13} weight={(w.likes||[]).includes(coachUser.id) ? "fill" : "regular"} /> {(w.likes||[]).length || 0}
                  </div>
                  <div onClick={() => setShowComments(prev => ({ ...prev, [w.id]: !prev[w.id] }))} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 8px", borderRadius: 8, background: "var(--bg)", color: "var(--soft)", fontSize: 12, fontWeight: 600, userSelect: "none" }}>
                    <ChatCircle size={13} /> {(w.comments||[]).length || 0} {showComments[w.id] ? "▲" : "▼"}
                  </div>
                </div>
                {showComments[w.id] && (
                  <div style={{ marginTop: 8 }}>
                    {(w.comments||[]).map((c,i) => (
                      <div key={i} style={{ fontSize: 12, color: "var(--mid)", padding: "3px 0", borderBottom: "1px solid var(--border)" }}>
                        <span style={{ fontWeight: 700, color: "var(--deep)" }}>{c.userName}:</span> {c.text}
                      </div>
                    ))}
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      <input className="form-input" style={{ flex: 1, padding: "7px 10px", fontSize: 12 }} placeholder="Add a comment..." value={commentText[w.id] || ""} onChange={e => setCommentText(prev => ({ ...prev, [w.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleComment(w.id)} />
                      <div onClick={() => handleComment(w.id)} style={{ padding: "7px 12px", background: "var(--forest)", color: "white", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12, userSelect: "none" }}>→</div>
                    </div>
                  </div>
                )}
              </div>
            ))
        )},
        { title: "Check-in History", count: checkins.length, content: (
          checkins.slice(0, 30).map(c => (
            <div key={c.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{fmt(c.date)}</span>
                <span style={{ fontSize: 11, color: "var(--soft)" }}>{c.mood}{c.energy ? ` · ${c.energy}` : ""}</span>
              </div>
              {c.notes && <div style={{ fontSize: 12, color: "var(--mid)", marginBottom: 6 }}>{c.notes}</div>}
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                {Object.entries(c.checks || {}).filter(([,v]) => v).map(([k]) => (
                  <span key={k} className="chip green" style={{ fontSize: 9 }}>✓ {labelForKey(k)}</span>
                ))}
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div onClick={() => handleCiLike(c.id)} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 8px", borderRadius: 8, background: (c.likes||[]).includes(coachUser.id) ? "var(--pink-pale)" : "var(--bg)", color: (c.likes||[]).includes(coachUser.id) ? "var(--pink)" : "var(--soft)", fontSize: 12, fontWeight: 600, userSelect: "none" }}>
                  <Heart size={13} weight={(c.likes||[]).includes(coachUser.id) ? "fill" : "regular"} /> {(c.likes||[]).length || 0}
                </div>
                <div onClick={() => setCiShowComments(prev => ({ ...prev, [c.id]: !prev[c.id] }))} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 8px", borderRadius: 8, background: "var(--bg)", color: "var(--soft)", fontSize: 12, fontWeight: 600, userSelect: "none" }}>
                  <ChatCircle size={13} /> {(c.comments||[]).length || 0} {ciShowComments[c.id] ? "▲" : "▼"}
                </div>
              </div>
              {ciShowComments[c.id] && (
                <div style={{ marginTop: 8 }}>
                  {(c.comments||[]).map((cm,i) => (
                    <div key={i} style={{ fontSize: 12, color: "var(--mid)", padding: "3px 0", borderBottom: "1px solid var(--border)" }}>
                      <span style={{ fontWeight: 700, color: "var(--deep)" }}>{cm.userName}:</span> {cm.text}
                    </div>
                  ))}
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    <input className="form-input" style={{ flex: 1, padding: "7px 10px", fontSize: 12 }} placeholder="Add a comment..." value={ciCommentText[c.id] || ""} onChange={e => setCiCommentText(prev => ({ ...prev, [c.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleCiComment(c.id)} />
                    <div onClick={() => handleCiComment(c.id)} style={{ padding: "7px 12px", background: "var(--forest)", color: "white", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12, userSelect: "none" }}>→</div>
                  </div>
                </div>
              )}
            </div>
          ))
        )},
        { title: "Workout History", count: workouts.length, content: (
          workouts.slice(0, 10).map(w => (
            <div key={w.id} className="workout-item">
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span className={`workout-type ${w.type}`}>{w.type === "inperson" ? "In-Person" : w.type === "athome" ? "At-Home" : "Alternative"}</span>
                <span style={{ fontSize: 10, color: "var(--soft)" }}>{fmt(w.date)}{w.duration ? ` · ${w.duration}min` : ""}</span>
              </div>
              {w.notes && <div className="workout-note">{w.notes}</div>}
            </div>
          ))
        )},
        { title: "Meal History", count: nutLogs.length, content: (
          nutLogs.slice(0, 10).map(n => (
            <div key={n.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{fmt(n.date)}</div>
              {["breakfast","snack1","lunch","snack2","dinner"].filter(s => n[s]).map(s => (
                <div key={s} style={{ fontSize: 11, color: "var(--mid)" }}><span style={{ fontWeight: 600 }}>{s.charAt(0).toUpperCase() + s.slice(1)}:</span> {n[s]}</div>
              ))}
            </div>
          ))
        )},
        { title: "PRs & Records", count: prs.length, content: (
          PR_LIFTS.map(lift => {
            const entries = prs.filter(p => p.exercise === lift.label).sort((a,b) => b.date.localeCompare(a.date));
            if (!entries.length) return null;
            const best = lift.unit === "lbs" ? entries.reduce((mx,e) => parseFloat(e.value) > parseFloat(mx?.value||0) ? e : mx, null) : entries[0];
            return (
              <div key={lift.key} className="pr-item">
                <div><div className="pr-name">{lift.label}</div><div className="pr-date">{lift.note} · {fmt(best.date)}</div></div>
                <div className="pr-value">{best.value} {lift.unit}</div>
              </div>
            );
          })
        )},
        { title: "Weight Tracker", count: WEIGHTED_MOVEMENTS.filter(m => (weightLogs[m.key]||[]).length > 0).length, content: (
          WEIGHTED_MOVEMENTS.filter(m => (weightLogs[m.key]||[]).length > 0).map(m => {
            const entries = weightLogs[m.key] || [];
            const latest = entries[entries.length - 1];
            return (
              <div key={m.key} style={{ padding:"8px 0", borderBottom:"1px solid var(--border)" }}>
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontSize:13, fontWeight:600 }}>{m.label}</span>
                  {latest.weight && <span style={{ fontFamily:"Cormorant Garamond,serif", fontSize:16, fontWeight:700, color:"var(--pink)" }}>{latest.weight} lbs</span>}
                </div>
                {latest.notes && <div style={{ fontSize:11, color:"var(--mid)", marginTop:2 }}>{latest.notes}</div>}
                <div style={{ fontSize:10, color:"var(--soft)" }}>{entries.length} entries · last {fmt(latest.date)}</div>
              </div>
            );
          })
        )},
      ].map(section => (
        <ExpandableSection key={section.title} title={section.title} count={section.count}>
          {section.content}
        </ExpandableSection>
      ))}

      {/* Nutrition Profile & Coach Note */}
      <div className="detail-section">
        <div className="detail-title">Nutrition Profile</div>
        {profile && Object.keys(servingLabels).some(k => profile[k]) && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            {Object.entries(servingLabels).map(([k, l]) => profile[k] && (
              <span key={k} className="chip pink">{l}: {profile[k]}</span>
            ))}
          </div>
        )}
        {profile?.goals && <div style={{ fontSize: 12, color: "var(--mid)", marginBottom: 8 }}>🎯 {profile.goals}</div>}
        {profile?.allergies && <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 10 }}>⚠️ {profile.allergies}</div>}
        <button className="btn-forest mt-8" onClick={saveNote}>{noteSaved ? "✓ Saved!" : "Save Nutrition Notes"}</button>
      </div>
    </div>
  );
}

// Ascension workouts: coach-assigned programs on top, then Expansion-style log below
function AscensionWorkouts({ user, ascColor, workoutTrack, trainingPlan }) {
  const [programs, setPrograms] = useState([]);
  const [videoLibrary, setVideoLibrary] = useState([]);
  const [slotStates, setSlotStates] = useState({});
  const [showProgram, setShowProgram] = useState(true);

  useEffect(() => {
    Promise.all([getPTPrograms(user.id), getVideoLibrary(), getWorkouts(user.id)]).then(([prog, vids, w]) => {
      setPrograms(prog);
      setVideoLibrary(vids);
      const states = {};
      w.forEach(wk => { if (wk.slot_key) states[wk.slot_key] = { done: true, notes: wk.notes || "", saving: false }; });
      setSlotStates(states);
    });
  }, [user.id]);

  const checkSlot = async (slotKey, slotLabel) => {
    const current = slotStates[slotKey] || { done: false, notes: "", saving: false };
    const newDone = !current.done;
    setSlotStates(prev => ({ ...prev, [slotKey]: { ...current, done: newDone, saving: true } }));
    const id = `wk_slot_${slotKey}_${user.id}`;
    await saveWorkout({ id, userId: user.id, userName: user.name, date: today(), type: slotLabel || "Workout", notes: current.notes || "", duration: "", slot_key: slotKey });
    setSlotStates(prev => ({ ...prev, [slotKey]: { ...prev[slotKey], done: newDone, saving: false } }));
    if (newDone) {
      const existing = await getJournalEntry(user.id, today());
      const checks = { ...(existing?.checks || {}), movement: true };
      await saveJournalEntry(user.id, today(), { ...(existing || {}), checks });
    }
  };

  const updateSlotNotes = (slotKey, text) => setSlotStates(prev => ({ ...prev, [slotKey]: { ...(prev[slotKey] || {}), notes: text } }));
  const saveSlotNotes = async (slotKey, slotLabel) => {
    const state = slotStates[slotKey] || {};
    setSlotStates(prev => ({ ...prev, [slotKey]: { ...state, notesSaving: true } }));
    await saveWorkout({ id: `wk_slot_${slotKey}_${user.id}`, userId: user.id, userName: user.name, date: today(), type: slotLabel || "Workout", notes: state.notes || "", duration: "", slot_key: slotKey });
    setSlotStates(prev => ({ ...prev, [slotKey]: { ...state, notesSaving: false, notesSaved: true } }));
    setTimeout(() => setSlotStates(prev => ({ ...prev, [slotKey]: { ...prev[slotKey], notesSaved: false } })), 2000);
  };

  return (
    <div>
      {(trainingPlan?.label || trainingPlan?.notes) && (
        <div className="card" style={{ borderLeft: `3px solid ${ascColor}`, marginBottom: 12 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: ascColor, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <Barbell size={12} color={ascColor} weight="fill" /> Your Training Plan
          </div>
          {trainingPlan.label && <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: "var(--deep)", marginBottom: trainingPlan.notes ? 6 : 0 }}>{trainingPlan.label}</div>}
          {trainingPlan.notes && <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{trainingPlan.notes}</div>}
        </div>
      )}
      {programs.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <button onClick={() => setShowProgram(p => !p)} style={{ width: "100%", background: "none", border: `1.5px solid ${ascColor}30`, borderRadius: 12, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 14, color: ascColor, marginBottom: showProgram ? 10 : 16 }}>
            <span>My Program ({programs.length} week{programs.length !== 1 ? "s" : ""})</span>
            <span>{showProgram ? "▲" : "▼"}</span>
          </button>
          {showProgram && programs.map((prog) => (
            <div key={prog.id} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: 16, marginBottom: 12 }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, color: "var(--deep)", marginBottom: 2 }}>{prog.week}</div>
              <div style={{ fontSize: 11, color: "var(--soft)", marginBottom: 12 }}>Posted {fmt(prog.posted_date)}</div>
              {(prog.slots || []).map((slot, i) => {
                const slotKey = `${prog.id}_${i}`;
                const state = slotStates[slotKey] || { done: false, notes: "", saving: false };
                const attachedVids = (slot.video_ids || []).map(vid => videoLibrary.find(v => v.id === vid)).filter(Boolean);
                return (
                  <div key={i} style={{ background: state.done ? ascColor + "10" : "var(--surface)", border: `1px solid ${state.done ? ascColor + "40" : "var(--border)"}`, borderRadius: 12, padding: 14, marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <div onClick={() => !state.saving && checkSlot(slotKey, slot.label)} style={{ width: 26, height: 26, borderRadius: 6, border: `2px solid ${state.done ? ascColor : "var(--border)"}`, background: state.done ? ascColor : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: state.saving ? "wait" : "pointer", flexShrink: 0 }}>
                        {state.saving ? <span style={{ fontSize: 10 }}>…</span> : state.done ? <span style={{ color: "white", fontSize: 14, fontWeight: 800 }}>✓</span> : null}
                      </div>
                      <span style={{ fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 14, color: state.done ? ascColor : "var(--deep)" }}>{slot.label || `Workout ${i + 1}`}</span>
                      {state.done && <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, color: ascColor, background: ascColor + "15", padding: "2px 8px", borderRadius: 20 }}>✓ DONE</span>}
                    </div>
                    {slot.content && <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.6, marginBottom: 6 }}>{slot.content}</div>}
                    {slot.notes && <div style={{ fontSize: 12, color: "var(--soft)", fontStyle: "italic", marginBottom: 6 }}>📝 {slot.notes}</div>}
                    {attachedVids.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                        {attachedVids.map(v => <a key={v.id} href={v.youtube_url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", background: "#FF000015", borderRadius: 20, fontSize: 11, fontWeight: 700, color: "#CC0000", textDecoration: "none", border: "1px solid #FF000030" }}>▶ {v.title}</a>)}
                      </div>
                    )}
                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 8 }}>
                      <textarea className="form-input form-textarea" placeholder="How did it go?" value={state.notes || ""} onChange={e => updateSlotNotes(slotKey, e.target.value)} style={{ fontSize: 12, minHeight: 55, marginBottom: 8 }} />
                      <button onClick={() => saveSlotNotes(slotKey, slot.label)} style={{ padding: "6px 14px", borderRadius: 20, border: "none", background: ascColor, color: "white", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                        {state.notesSaving ? "Saving..." : state.notesSaved ? "✓ Saved!" : "Save Notes"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
      <ExpansionWorkouts
        user={user}
        expColor={ascColor}
        workoutTrack={workoutTrack}
        onWorkoutComplete={async () => {
          const existing = await getJournalEntry(user.id, today());
          const checks = { ...(existing?.checks || {}), movement: true };
          await saveJournalEntry(user.id, today(), { ...(existing || {}), checks });
        }}
      />
    </div>
  );
}

// ─── PT Client App ────────────────────────────────────────────────────────────
function PTClientApp({ user, onLogout }) {
  const [tab, setTab] = useState("home");
  const [homeView, setHomeView] = useState("home"); // "home" | "checkin"
  const [trackSub, setTrackSub] = useState("workouts");
  const [connectSub, setConnectSub] = useState("wins");
  const [hasNewReactions, setHasNewReactions] = useState(false);
  const [prefs, setPrefs] = useState({});
  const ptLsKey = (type) => `gt_last_seen_${type}_${user.id}`;
  const lsGet = (k) => localStorage.getItem(`asc_${user.id}_${k}`);

  useEffect(() => {
    getPTReactions(user.id).then(r => setHasNewReactions(r.some(x => !x.seen)));
    getUserPrefs(user.id).then(p => setPrefs(p || {}));
  }, [user.id]);

  const ASC_COLOR = "#9B8AA6";
  const tabs = [
    { id: "home",      label: "Home",      Icon: House },
    { id: "workouts",  label: "Workouts",  Icon: Barbell },
    { id: "nutrition", label: "Nutrition", Icon: Acorn },
    { id: "resources", label: "Resources", Icon: Book },
  ];

  const handleTab = (t) => {
    setTab(t);
    if (t === "home") { setHomeView("home"); markPTReactionsSeen(user.id); setHasNewReactions(false); }
  };

  // Allow PTHome to trigger check-in within the Home tab
  const handleSetTab = (t) => {
    if (t === "checkin") { setHomeView("checkin"); return; }
    handleTab(t);
  };

  const SubTabs = ({ options, active, onSelect }) => (
    <div style={{ display: "flex", gap: 0, margin: "0 16px 16px", background: "var(--card)", borderRadius: 12, padding: 4, border: "1px solid var(--border)" }}>
      {options.map(({ id, label }) => (
        <button key={id} onClick={() => onSelect(id)} style={{ flex: 1, padding: "8px 4px", borderRadius: 9, border: "none", background: active === id ? ASC_COLOR : "transparent", color: active === id ? "white" : "var(--soft)", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="app-shell pt-shell">
      <style>{styles}</style>
      <div className="top-bar">
        <div>
          <div className="top-bar-title">Ascension</div>
          <div className="top-bar-sub">{user.name}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="avatar">{initials(user.name)}</div>
          <button className="btn-ghost" onClick={onLogout} style={{ fontSize: 11 }}>Out</button>
        </div>
      </div>
      <div className="bottom-nav" style={{ borderBottom: `2px solid ${ASC_COLOR}20` }}>
        {tabs.map(t => (
          <div key={t.id} className={`nav-item ${tab === t.id ? "active" : ""}`} onClick={() => handleTab(t.id)} style={{ position: "relative", color: tab === t.id ? ASC_COLOR : undefined }}>
            <t.Icon size={20} weight={tab === t.id ? "fill" : "regular"} />
            {t.id === "home" && hasNewReactions && <span style={{ position: "absolute", top: 6, right: "calc(50% - 14px)", width: 8, height: 8, background: ASC_COLOR, borderRadius: "50%", border: "2px solid var(--surface)" }} />}
            {t.label}
          </div>
        ))}
      </div>
      <div className="page-content">
        {tab === "home" && homeView === "home" && <PTHome user={user} setTab={handleSetTab} setHasNewReactions={setHasNewReactions} />}
        {tab === "home" && homeView === "checkin" && (
          <div>
            <button onClick={() => setHomeView("home")} style={{ background: "none", border: "none", color: ASC_COLOR, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: "16px 16px 0", display: "block" }}>← Back</button>
            <PTCheckin user={user} />
          </div>
        )}
        {tab === "workouts" && (
          <AscensionWorkouts
            user={user}
            ascColor={ASC_COLOR}
            workoutTrack={prefs?.workout_track || lsGet('workout_track') || "full_body"}
            trainingPlan={prefs?.custom_training}
          />
        )}
        {tab === "nutrition" && (
          <ExpansionNutrition
            user={user}
            expColor={ASC_COLOR}
            nutritionPath={prefs?.nutrition_path || lsGet('nutrition_path') || "maintenance"}
            customPlan={prefs?.custom_nutrition}
          />
        )}
        {tab === "resources" && <ExpansionResources user={user} expColor={ASC_COLOR} />}
      </div>
    </div>
  );
}

function PTHome({ user, setTab, setHasNewReactions }) {
  const ASC_COLOR = "#9B8AA6";

  const [myCheckins, setMyCheckins] = useState([]);
  const [myWorkouts, setMyWorkouts] = useState([]);
  const [nextAppt, setNextAppt] = useState(null);
  const [unseenReactions, setUnseenReactions] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [coachNote, setCoachNote] = useState("");
  const [loading, setLoading] = useState(true);

  // date for checklist — defaults to today, user can go back
  const [checkDate, setCheckDate] = useState(today());

  // check-in state
  const [checks, setChecks] = useState({});
  const [mood, setMood] = useState("");
  const [energy, setEnergy] = useState("");
  const [journalText, setJournalText] = useState("");
  const [checksSaved, setChecksSaved] = useState(false);
  const [checksSaving, setChecksSaving] = useState(false);
  const [infoPopup, setInfoPopup] = useState(null);

  // cup fillers state
  const [cupFillers, setCupFillers] = useState([]);
  const [newCupLabel, setNewCupLabel] = useState("");

  // coach-personalized check-in list
  const [checkItems, setCheckItems] = useState(RF_CHECKIN_ITEMS);

  // load static data once
  useEffect(() => {
    const load = async () => {
      const [c, w, appts, reactions, ann, prefs, fillers] = await Promise.all([
        getCheckins(user.id), getWorkouts(user.id),
        getPTAppointments(user.id), getPTReactions(user.id),
        getAnnouncements('pt', user.id),
        getUserPrefs(user.id),
        getCupFillers(user.id),
      ]);
      setMyCheckins(c); setMyWorkouts(w);
      const upcoming = appts.filter(a => a.date >= today()).sort((a,b) => a.date.localeCompare(b.date));
      setNextAppt(upcoming[0] || null);
      // Coach hearts/comments written from the coach panel live on the check-in
      // row itself, not in pt_reactions — surface those here too.
      const rxSeen = (localStorage.getItem(`asc_rx_seen_${user.id}`) || "").slice(0, 10);
      const fromCheckins = (c || []).filter(ci => !rxSeen || (ci.date || "") > rxSeen).flatMap(ci => [
        ...((ci.likes || []).length ? [{ id: `${ci.id}_l`, reaction_type: "like", target_type: "check-in", date: ci.date }] : []),
        ...(ci.comments || []).map((cm, i) => ({
          id: `${ci.id}_c${i}`, reaction_type: "comment", target_type: "check-in",
          comment_text: cm.text, date: ci.date,
        })),
      ]).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setUnseenReactions([...reactions.filter(r => !r.seen), ...fromCheckins]);
      setAnnouncements(ann);
      setCoachNote(prefs?.coach_note || "");
      setCupFillers(fillers);
      setCheckItems(buildCheckinItems(prefs));
      setLoading(false);
    };
    load();
  }, [user.id]);

  // reload journal entry whenever checkDate changes
  useEffect(() => {
    setChecks({}); setMood(""); setEnergy(""); setJournalText("");
    getJournalEntry(user.id, checkDate).then(entry => {
      if (entry) {
        setChecks(entry.checks || {});
        setMood(entry.mood || "");
        setEnergy(entry.energy || "");
        setJournalText(entry.response || "");
      }
    });
  }, [user.id, checkDate]);

  const promptIdx = Math.floor(Date.now() / 86400000) % RF_PROMPTS.length;
  const prompt = RF_PROMPTS[promptIdx];

  const MOOD_OPTIONS = ["Fantastic","Good","Okay","Low","Struggling"];
  const ENERGY_OPTIONS = ["High","Medium","Low","Drained"];

  const markSeen = async () => {
    await markPTReactionsSeen(user.id);
    // Check-in hearts/comments have no "seen" flag of their own, so remember
    // the dismissal locally or the card would come back on every reload.
    localStorage.setItem(`asc_rx_seen_${user.id}`, new Date().toISOString());
    setUnseenReactions([]); setHasNewReactions(false);
  };

  const toggleCheck = (key) => setChecks(prev => ({ ...prev, [key]: !prev[key] }));

  const saveChecks = async () => {
    setChecksSaving(true);
    await Promise.all([
      saveJournalEntry(user.id, checkDate, { checks, mood, energy, response: journalText, prompt_text: prompt }),
      saveCheckin({ id: `ci_${user.id}_${checkDate}`, userId: user.id, userName: user.name, date: checkDate, checks, mood, energy, notes: journalText, customItems: [] }),
    ]);
    setChecksSaving(false); setChecksSaved(true); setTimeout(() => setChecksSaved(false), 2000);
  };

  const toggleCupCheck = async (filler) => {
    const newVal = !filler.is_checked;
    setCupFillers(prev => prev.map(f => f.id === filler.id ? { ...f, is_checked: newVal } : f));
    await updateCupFiller(filler.id, { is_checked: newVal });
  };

  const addCupFiller = async (label) => {
    if (!label.trim()) return;
    const f = { id: `cf_${user.id}_${Date.now()}`, userId: user.id, label: label.trim(), sortOrder: cupFillers.length };
    await saveCupFiller(f);
    setCupFillers(prev => [...prev, { ...f, is_checked: false }]);
    setNewCupLabel("");
  };

  const removeCupFiller = async (id) => {
    await deleteCupFiller(id);
    setCupFillers(prev => prev.filter(f => f.id !== id));
  };

  const streak = getStreakFromCheckins(myCheckins.map(c => c.date));

  if (loading) return <AppSkeleton color={ASC_COLOR} />;

  return (
    <div>
      {infoPopup && <InfoPopup title={infoPopup.title} body={infoPopup.body} onClose={() => setInfoPopup(null)} />}

      {/* Header */}
      <div className="card" style={{ borderLeft: `3px solid ${ASC_COLOR}`, marginBottom: 16 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 700, color: "var(--deep)", marginBottom: 2 }}>{user.name}</div>
        <div style={{ fontSize: 11, color: "var(--soft)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 14 }}>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {[["Workouts", myWorkouts.length, Barbell], ["Check-ins", myCheckins.length, Check], ["Streak", streak, Fire]].map(([l, v, Icon]) => (
            <div key={l} style={{ textAlign: "center", background: `${ASC_COLOR}10`, borderRadius: 10, padding: "10px 4px" }}>
              <Icon size={14} color={ASC_COLOR} style={{ marginBottom: 2 }} />
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 800, color: ASC_COLOR }}>{v}</div>
              <div style={{ fontSize: 9, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {coachNote && (
        <div className="card" style={{ borderLeft: `3px solid ${ASC_COLOR}`, marginBottom: 16 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: ASC_COLOR, fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Feather size={12} color={ASC_COLOR} weight="fill" /> Note from Madison
          </div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, color: "var(--deep)", lineHeight: 1.7, whiteSpace: "pre-line" }}>{coachNote}</div>
        </div>
      )}

      {unseenReactions.length > 0 && (
        <div className="card" style={{ borderLeft: `3px solid ${ASC_COLOR}`, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: ASC_COLOR, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}><Heart size={12} color={ASC_COLOR} weight="fill" /> Madison noticed you</div>
            <button onClick={markSeen} style={{ fontSize: 11, background: "none", border: "none", color: "var(--soft)", cursor: "pointer" }}>Dismiss</button>
          </div>
          {unseenReactions.slice(0, 3).map(r => (
            <div key={r.id} style={{ padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 13, color: "var(--mid)", display: "flex", alignItems: "center", gap: 6 }}>
              {r.reaction_type === "like" ? <Heart size={12} color={ASC_COLOR} weight="fill" /> : <ChatCircle size={12} color={ASC_COLOR} weight="fill" />}
              <span>{r.reaction_type === "like" ? "Liked your " : "Commented on your "}<strong>{r.target_type}</strong></span>
              {r.comment_text && <span style={{ fontStyle: "italic" }}>: "{r.comment_text}"</span>}
            </div>
          ))}
          {unseenReactions.length > 3 && <div style={{ fontSize: 11, color: "var(--soft)", marginTop: 6 }}>+{unseenReactions.length - 3} more</div>}
        </div>
      )}

      {announcements.length > 0 && (
        <div className="card" style={{ borderLeft: `3px solid ${ASC_COLOR}`, marginBottom: 16 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: ASC_COLOR, fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><Envelope size={12} color={ASC_COLOR} /> From Madison</div>
          {announcements.map(a => (
            <div key={a.id} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--deep)", marginBottom: 2 }}>{a.title}</div>
              <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.5 }}>{a.body}</div>
              <div style={{ fontSize: 11, color: "var(--soft)", marginTop: 4 }}>{a.date}</div>
            </div>
          ))}
        </div>
      )}

      {nextAppt && (
        <div className="card" style={{ borderLeft: `3px solid ${ASC_COLOR}`, marginBottom: 16 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: ASC_COLOR, fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><Star size={12} color={ASC_COLOR} weight="fill" /> Next Session</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, color: "var(--deep)" }}>
            {new Date(nextAppt.date + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            {nextAppt.time && <span style={{ color: "var(--soft)", fontWeight: 400, fontSize: 14, marginLeft: 8 }}>at {nextAppt.time}</span>}
          </div>
          {nextAppt.notes && <div style={{ fontSize: 12, color: "var(--mid)", marginTop: 4 }}>{nextAppt.notes}</div>}
        </div>
      )}

      {/* Date picker */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: ASC_COLOR, textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap" }}>Log for:</label>
        <input type="date" className="form-input" value={checkDate} max={today()} onChange={e => setCheckDate(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
        {checkDate !== today() && <button onClick={() => setCheckDate(today())} style={{ fontSize: 11, background: "none", border: `1px solid ${ASC_COLOR}`, borderRadius: 8, color: ASC_COLOR, padding: "6px 10px", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "Jost, sans-serif", fontWeight: 700 }}>Today</button>}
      </div>
      {checkDate !== today() && <div style={{ fontSize: 12, color: ASC_COLOR, fontWeight: 700, marginBottom: 12, background: ASC_COLOR + "12", borderRadius: 8, padding: "8px 12px" }}>📅 Logging for {new Date(checkDate + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>}

      {/* Mood + Energy */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, marginBottom: 16 }}>How are you today?</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--soft)", fontWeight: 700, marginBottom: 8 }}>Feeling</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {MOOD_OPTIONS.map(o => (
              <button key={o} onClick={() => setMood(o.toLowerCase())} style={{ padding: "7px 14px", borderRadius: 6, border: `1.5px solid ${mood === o.toLowerCase() ? ASC_COLOR : "var(--border)"}`, background: mood === o.toLowerCase() ? ASC_COLOR + "18" : "transparent", color: mood === o.toLowerCase() ? ASC_COLOR : "var(--mid)", fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{o}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--soft)", fontWeight: 700, marginBottom: 8 }}>Energy Level</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {ENERGY_OPTIONS.map(o => (
              <button key={o} onClick={() => setEnergy(o.toLowerCase())} style={{ padding: "7px 14px", borderRadius: 6, border: `1.5px solid ${energy === o.toLowerCase() ? ASC_COLOR : "var(--border)"}`, background: energy === o.toLowerCase() ? ASC_COLOR + "18" : "transparent", color: energy === o.toLowerCase() ? ASC_COLOR : "var(--mid)", fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{o}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Journal Prompt */}
      <div className="card" style={{ borderLeft: `3px solid ${ASC_COLOR}`, marginBottom: 16 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: ASC_COLOR, fontWeight: 700, marginBottom: 8 }}>Today's Reflection</div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 16, color: "var(--deep)", lineHeight: 1.7, marginBottom: 12 }}>"{prompt}"</div>
        <textarea className="form-input form-textarea" placeholder="Write your thoughts here..." value={journalText} onChange={e => setJournalText(e.target.value)} style={{ minHeight: 100 }} />
      </div>

      {/* Daily Check-In + Cup Fillers side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16, alignItems: "start" }}>

        {/* Daily Check-In */}
        <div className="card" style={{ margin: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 700 }}>Daily Check-In</div>
            <div style={{ color: ASC_COLOR, fontWeight: 700, fontSize: 12 }}>{Object.values(checks).filter(Boolean).length} / {checkItems.length}</div>
          </div>
          {checkItems.map(item => (
            <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0", borderBottom: "1px solid var(--border)" }}>
              <input type="checkbox" checked={!!checks[item.key]} onChange={() => toggleCheck(item.key)} style={{ width: 16, height: 16, accentColor: ASC_COLOR, flexShrink: 0 }} />
              <item.Icon size={14} weight="regular" color={ASC_COLOR} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 12, color: "var(--deep)", fontFamily: "'Jost', sans-serif" }}>{item.label}</span>
              <button onClick={() => setInfoPopup({ title: item.label, body: item.info })} style={{ background: "none", border: "1px solid var(--border)", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--soft)", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>i</button>
            </div>
          ))}
        </div>

        {/* Cup Fillers */}
        <div className="card" style={{ margin: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Coffee size={15} color={ASC_COLOR} />
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 700, color: "var(--deep)" }}>Cup Fillers</span>
              <button onClick={() => setInfoPopup({ title: CUP_FILLER_INFO.title, body: CUP_FILLER_INFO.body })} style={{ background: "none", border: "1px solid var(--border)", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--soft)", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>i</button>
            </div>
            <div style={{ color: ASC_COLOR, fontWeight: 700, fontSize: 12 }}>{cupFillers.filter(f => f.is_checked).length} / {cupFillers.length}</div>
          </div>
          {cupFillers.map(f => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0", borderBottom: "1px solid var(--border)" }}>
              <input type="checkbox" checked={!!f.is_checked} onChange={() => toggleCupCheck(f)} style={{ width: 16, height: 16, accentColor: ASC_COLOR, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 12, color: "var(--mid)" }}>{f.label}</span>
              <span onClick={() => removeCupFiller(f.id)} style={{ color: "var(--soft)", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</span>
            </div>
          ))}
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            <input className="form-input" value={newCupLabel} onChange={e => setNewCupLabel(e.target.value)} placeholder="Add one…" style={{ flex: 1, fontSize: 12, padding: "7px 10px" }} onKeyDown={e => e.key === "Enter" && addCupFiller(newCupLabel)} />
            <button onClick={() => addCupFiller(newCupLabel)} style={{ padding: "7px 12px", background: ASC_COLOR, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 14 }}>+</button>
          </div>
        </div>

      </div>

      {/* Full-page save button */}
      <button onClick={saveChecks} disabled={checksSaving} style={{ marginTop: 20, width: "100%", padding: "13px", background: ASC_COLOR, color: "white", border: "none", borderRadius: 10, fontFamily: "'Jost', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
        {checksSaving ? "Saving…" : checksSaved ? "Saved ✓" : "Save Today's Check-In"}
      </button>
    </div>
  );
}

function PTCheckin({ user }) {
  const [selectedDate, setSelectedDate] = useState(today());
  const [customItems, setCustomItems] = useState([]);
  const [checks, setChecks] = useState({});
  const [mood, setMood] = useState("");
  const [energy, setEnergy] = useState("");
  const [notes, setNotes] = useState("");
  const [allCheckins, setAllCheckins] = useState([]);
  const [saved, setSaved] = useState(false);
  // goals
  const [goals, setGoals] = useState([]);
  const [newGoal, setNewGoal] = useState("");
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editGoalText, setEditGoalText] = useState("");
  // cup fillers
  const [cupFillers, setCupFillers] = useState([]);
  const [newCupLabel, setNewCupLabel] = useState("");
  const [cupOpen, setCupOpen] = useState(false);

  const ASC_COLOR = "#9B8AA6";
  const moods = ["Great", "Good", "Okay", "Low", "Stressed"];
  const energies = ["High", "Medium", "Low", "Drained"];

  useEffect(() => {
    Promise.all([
      getPTCheckItems(user.id),
      getCheckins(user.id),
      getPTGoals(user.id),
      getCupFillers(user.id),
    ]).then(([items, c, g, cf]) => {
      setCustomItems(items);
      setAllCheckins(c);
      setGoals(g);
      setCupFillers(cf);
    });
  }, [user.id]);

  const addGoal = async () => {
    if (!newGoal.trim()) return;
    const goal = { id: `goal_${Date.now()}`, clientId: user.id, goalText: newGoal.trim() };
    const ok = await savePTGoal(goal);
    if (ok) { setGoals(prev => [{ id: goal.id, client_id: user.id, goal_text: goal.goalText, completed: false, created_at: new Date().toISOString() }, ...prev]); setNewGoal(""); }
  };
  const toggleGoal = async (g) => {
    const next = !g.completed;
    setGoals(prev => prev.map(x => x.id === g.id ? { ...x, completed: next } : x));
    await updatePTGoal(g.id, { completed: next });
  };
  const saveGoalEdit = async (id) => {
    if (!editGoalText.trim()) return;
    await updatePTGoal(id, { goal_text: editGoalText.trim() });
    setGoals(prev => prev.map(g => g.id === id ? { ...g, goal_text: editGoalText.trim() } : g));
    setEditingGoalId(null);
  };
  const removeGoal = async (id) => { await deletePTGoal(id); setGoals(prev => prev.filter(g => g.id !== id)); };

  const addCupFiller = async (label) => {
    if (!label.trim()) return;
    const f = { id: `cf_${user.id}_${Date.now()}`, userId: user.id, label: label.trim(), sortOrder: cupFillers.length };
    await saveCupFiller(f);
    setCupFillers(prev => [...prev, { ...f, user_id: user.id, is_checked: false }]);
    setNewCupLabel("");
  };
  const toggleCup = async (f) => {
    const next = !f.is_checked;
    setCupFillers(prev => prev.map(x => x.id === f.id ? { ...x, is_checked: next } : x));
    await updateCupFiller(f.id, { is_checked: next });
  };
  const removeCup = async (id) => { await deleteCupFiller(id); setCupFillers(prev => prev.filter(f => f.id !== id)); };

  useEffect(() => {
    const existing = allCheckins.find(c => c.date === selectedDate);
    if (existing) {
      // Translate any old ID-based keys (ci_xxx) to label-based keys
      const rawChecks = existing.checks || {};
      const cleanChecks = {};
      Object.entries(rawChecks).forEach(([k, v]) => {
        if (k.startsWith('ci_')) {
          // Old format: look up label from customItems
          const item = customItems.find(i => i.id === k);
          if (item && v) cleanChecks[item.label] = true;
        } else {
          // New format: already a label
          cleanChecks[k] = v;
        }
      });
      setChecks(cleanChecks);
      setMood(existing.mood || "");
      setEnergy(existing.energy || "");
      setNotes(existing.notes || "");
    } else { setChecks({}); setMood(""); setEnergy(""); setNotes(""); }
  }, [selectedDate, allCheckins, customItems]);

  const save = async () => {
    setSaved("saving");
    const success = await saveCheckin({ id: `ci_${user.id}_${selectedDate}`, userId: user.id, userName: user.name, date: selectedDate, checks, mood, energy, notes, customItems: [] });
    if (success) setAllCheckins(prev => [...prev.filter(c => c.date !== selectedDate), { id: `ci_${user.id}_${selectedDate}`, user_id: user.id, date: selectedDate, checks, mood, energy, notes }]);
    setSaved(success ? "saved" : "error");
    setTimeout(() => setSaved(false), 2500);
  };

  const pill = (active) => ({ padding: "7px 14px", borderRadius: 6, border: `1.5px solid ${active ? ASC_COLOR : "var(--border)"}`, background: active ? ASC_COLOR + "18" : "transparent", color: active ? ASC_COLOR : "var(--mid)", fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" });

  return (
    <div>
      {/* Date picker */}
      <div className="card">
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: "var(--deep)", marginBottom: 12 }}>Daily Check-in</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input type="date" className="form-input" value={selectedDate} max={today()} onChange={e => setSelectedDate(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
          {selectedDate !== today() && <button className="btn-ghost" style={{ fontSize: 11, whiteSpace: "nowrap" }} onClick={() => setSelectedDate(today())}>Today</button>}
        </div>
        {selectedDate !== today() && <div style={{ fontSize: 11, color: ASC_COLOR, fontWeight: 700, marginTop: 10, background: ASC_COLOR + "12", borderRadius: 8, padding: "6px 10px" }}>Logging for {new Date(selectedDate + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>}
      </div>

      {/* Habits + Cup Fillers side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16, alignItems: "start" }}>

        {/* Habits */}
        <div className="card" style={{ margin: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 700 }}>Daily Habits</div>
            {customItems.length > 0 && <div style={{ color: ASC_COLOR, fontWeight: 700, fontSize: 12 }}>{Object.values(checks).filter(Boolean).length}/{customItems.length}</div>}
          </div>
          {customItems.length > 0 ? customItems.map(item => (
            <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0", borderBottom: "1px solid var(--border)", cursor: "pointer" }} onClick={() => setChecks(prev => ({ ...prev, [item.label]: !prev[item.label] }))}>
              <input type="checkbox" checked={!!checks[item.label]} onChange={() => {}} style={{ width: 16, height: 16, accentColor: ASC_COLOR, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, color: "var(--deep)", fontFamily: "'Jost', sans-serif" }}>{item.label}</span>
            </div>
          )) : <div style={{ fontSize: 13, color: "var(--soft)", fontStyle: "italic" }}>Madison will set up your habits soon!</div>}
        </div>

        {/* Cup Fillers */}
        <div className="card" style={{ margin: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Drop size={15} color={ASC_COLOR} />
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 700, color: "var(--deep)" }}>Cup Fillers</span>
            </div>
            <div style={{ color: ASC_COLOR, fontWeight: 700, fontSize: 12 }}>{cupFillers.filter(f => f.is_checked).length}/{cupFillers.length}</div>
          </div>
          {cupFillers.map(f => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0", borderBottom: "1px solid var(--border)" }}>
              <input type="checkbox" checked={!!f.is_checked} onChange={() => toggleCup(f)} style={{ width: 16, height: 16, accentColor: ASC_COLOR, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, color: "var(--mid)" }}>{f.label}</span>
              <span onClick={() => removeCup(f.id)} style={{ color: "var(--soft)", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</span>
            </div>
          ))}
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            <input className="form-input" value={newCupLabel} onChange={e => setNewCupLabel(e.target.value)} placeholder="Add one…" style={{ flex: 1, fontSize: 12, padding: "7px 10px" }} onKeyDown={e => e.key === "Enter" && addCupFiller(newCupLabel)} />
            <button onClick={() => addCupFiller(newCupLabel)} style={{ padding: "7px 12px", background: ASC_COLOR, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "'Jost', sans-serif", fontWeight: 700, fontSize: 14 }}>+</button>
          </div>
        </div>

      </div>

      {/* Mood + Energy */}
      <div className="card">
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--soft)", fontWeight: 700, marginBottom: 8 }}>Feeling</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {moods.map(m => <button key={m} onClick={() => setMood(m)} style={pill(mood === m)}>{m}</button>)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--soft)", fontWeight: 700, marginBottom: 8 }}>Energy Level</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {energies.map(e => <button key={e} onClick={() => setEnergy(e)} style={pill(energy === e)}>{e}</button>)}
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="card">
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--soft)", fontWeight: 700, marginBottom: 8 }}>Notes</div>
        <textarea className="form-input form-textarea" placeholder="How are you feeling? Recovery, life stuff..." value={notes} onChange={e => setNotes(e.target.value)} />
      </div>

      <button onClick={save} style={{ width: "100%", padding: "13px", background: saved === "saved" ? "#48BB78" : ASC_COLOR, color: "white", border: "none", borderRadius: 12, fontFamily: "'Jost', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer", marginBottom: 14 }}>
        {saved === "saving" ? "Saving..." : saved === "saved" ? "Saved!" : "Save Check-in"}
      </button>

      {/* ── Goals ── */}
      <div className="card" style={{ marginBottom: 14 }}>
        <button onClick={() => setGoalsOpen(o => !o)} style={{ width: "100%", background: "none", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: 0, fontFamily: "'Jost', sans-serif" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Target size={16} color={ASC_COLOR} weight="fill" />
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, color: "var(--deep)" }}>Goals</span>
            {goals.filter(g => g.completed).length > 0 && (
              <span style={{ fontSize: 11, background: "#48BB7820", color: "#48BB78", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>{goals.filter(g => g.completed).length} done</span>
            )}
          </div>
          <span style={{ color: "var(--soft)", fontSize: 16 }}>{goalsOpen ? "▲" : "▼"}</span>
        </button>
        {goalsOpen && (
          <div style={{ marginTop: 14 }}>
            {/* Active goals */}
            {goals.filter(g => !g.completed).map(g => (
              <div key={g.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <div onClick={() => toggleGoal(g)} style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${ASC_COLOR}`, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, marginTop: 1 }} />
                {editingGoalId === g.id ? (
                  <div style={{ flex: 1, display: "flex", gap: 6 }}>
                    <input className="form-input" value={editGoalText} onChange={e => setEditGoalText(e.target.value)} style={{ flex: 1, fontSize: 13 }} autoFocus onKeyDown={e => e.key === "Enter" && saveGoalEdit(g.id)} />
                    <button onClick={() => saveGoalEdit(g.id)} style={{ padding: "4px 10px", background: ASC_COLOR, color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "'Jost', sans-serif", fontWeight: 700, fontSize: 12 }}>Save</button>
                    <button onClick={() => setEditingGoalId(null)} style={{ padding: "4px 8px", background: "none", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", color: "var(--soft)", fontSize: 12 }}>✕</button>
                  </div>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 14, color: "var(--deep)", lineHeight: 1.5 }}>{g.goal_text}</span>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => { setEditingGoalId(g.id); setEditGoalText(g.goal_text); }} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 12 }}>edit</button>
                      <button onClick={() => removeGoal(g.id)} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
                    </div>
                  </>
                )}
              </div>
            ))}
            {/* Completed goals */}
            {goals.filter(g => g.completed).length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--soft)", fontWeight: 700, marginBottom: 6 }}>Completed</div>
                {goals.filter(g => g.completed).map(g => (
                  <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid var(--border)", opacity: 0.6 }}>
                    <div onClick={() => toggleGoal(g)} style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid #48BB78`, background: "#48BB78", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                      <Check size={13} color="white" weight="bold" />
                    </div>
                    <span style={{ flex: 1, fontSize: 13, color: "var(--soft)", textDecoration: "line-through" }}>{g.goal_text}</span>
                    <button onClick={() => removeGoal(g.id)} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
            {goals.length === 0 && <div style={{ fontSize: 13, color: "var(--soft)", fontStyle: "italic", padding: "10px 0" }}>No goals yet — add one below.</div>}
            {/* Add goal */}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <input className="form-input" value={newGoal} onChange={e => setNewGoal(e.target.value)} placeholder="Add a goal…" style={{ flex: 1, fontSize: 13 }} onKeyDown={e => e.key === "Enter" && addGoal()} />
              <button onClick={addGoal} style={{ padding: "0 14px", background: ASC_COLOR, color: "white", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 16, cursor: "pointer", fontFamily: "'Jost', sans-serif" }}>+</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const PT_MOVEMENTS = [
  "Back Squat", "Front Squat", "Overhead Squat",
  "Deadlift", "Sumo Deadlift",
  "Bench Press", "Strict Press", "Push Press", "Push Jerk",
  "Clean & Jerk", "Snatch",
];
const PT_REP_TYPES = ["1 Rep Max", "2 Rep Max", "3 Rep Max", "5 Rep Max"];

function PTWorkouts({ user }) {
  const [view, setView] = useState("program");
  const [myWorkouts, setMyWorkouts] = useState([]);
  const [myPrograms, setMyPrograms] = useState([]);
  const [videoLibrary, setVideoLibrary] = useState([]);
  const [videoSearch, setVideoSearch] = useState("");
  // slot completion: key = `${progId}_${slotIndex}`, value = { done, notes, saving }
  const [slotStates, setSlotStates] = useState({});
  // PRs
  const [myPRs, setMyPRs] = useState([]);
  const [prMovement, setPrMovement] = useState(PT_MOVEMENTS[0]);
  const [prRepType, setPrRepType] = useState(PT_REP_TYPES[0]);
  const [prWeight, setPrWeight] = useState("");
  const [prNotes, setPrNotes] = useState("");
  const [prSaved, setPrSaved] = useState(false);
  const [expandedMovement, setExpandedMovement] = useState(null);
  const [expandedWorkout, setExpandedWorkout] = useState(null);

  useEffect(() => {
    Promise.all([getWorkouts(user.id), getPTPrograms(user.id), getVideoLibrary(), getPRs(user.id)]).then(([w, prog, vids, prs]) => {
      setMyWorkouts(w); setMyPrograms(prog); setVideoLibrary(vids); setMyPRs(prs);
      const states = {};
      w.forEach(wk => {
        if (wk.slot_key) states[wk.slot_key] = { done: true, notes: wk.notes || "", saving: false };
      });
      setSlotStates(states);
    });
  }, [user.id]);

  const checkSlot = async (slotKey, slotLabel) => {
    const current = slotStates[slotKey] || { done: false, notes: "", saving: false };
    const newDone = !current.done;
    setSlotStates(prev => ({ ...prev, [slotKey]: { ...current, done: newDone, saving: true } }));
    const id = `wk_slot_${slotKey}_${user.id}`;
    const success = await saveWorkout({
      id, userId: user.id, userName: user.name,
      date: today(), type: slotLabel || "Workout", notes: current.notes || "", duration: "",
      slot_key: slotKey,
    });
    setSlotStates(prev => ({ ...prev, [slotKey]: { ...current, done: newDone, saving: false, saved: success } }));
    if (success) {
      setMyWorkouts(prev => {
        const filtered = prev.filter(w => w.slot_key !== slotKey);
        return newDone
          ? [{ id, user_id: user.id, date: today(), type: slotLabel, notes: current.notes, slot_key: slotKey }, ...filtered]
          : filtered;
      });
    }
    setTimeout(() => setSlotStates(prev => ({ ...prev, [slotKey]: { ...prev[slotKey], saved: false } })), 2000);
  };

  const updateSlotNotes = (slotKey, text) => {
    setSlotStates(prev => ({ ...prev, [slotKey]: { ...(prev[slotKey] || { done: false, saving: false }), notes: text } }));
  };

  const saveSlotNotes = async (slotKey, slotLabel) => {
    const state = slotStates[slotKey] || {};
    setSlotStates(prev => ({ ...prev, [slotKey]: { ...state, notesSaving: true } }));
    const id = `wk_slot_${slotKey}_${user.id}`;
    const success = await saveWorkout({
      id, userId: user.id, userName: user.name,
      date: today(), type: slotLabel || "Workout", notes: state.notes || "", duration: "",
      slot_key: slotKey,
    });
    setSlotStates(prev => ({ ...prev, [slotKey]: { ...state, notesSaving: false, notesSaved: success } }));
    if (success) {
      setMyWorkouts(prev => prev.map(w => w.slot_key === slotKey ? { ...w, notes: state.notes } : w));
    }
    setTimeout(() => setSlotStates(prev => ({ ...prev, [slotKey]: { ...prev[slotKey], notesSaved: false } })), 2000);
  };

  const savePREntry = async () => {
    if (!prWeight.trim()) return;
    const pr = { id: `pr_${Date.now()}`, userId: user.id, userName: user.name, movement: prMovement, weight: prWeight.trim(), reps: prRepType, date: today(), notes: prNotes.trim() };
    setPrSaved("saving");
    const success = await savePR(pr);
    if (success) {
      setMyPRs(prev => [{ ...pr, user_id: user.id }, ...prev]);
      setPrWeight(""); setPrNotes("");
    }
    setPrSaved(success ? "saved" : "error");
    setTimeout(() => setPrSaved(false), 2000);
  };

  const removePR = async (id) => { await deletePR(id); setMyPRs(prev => prev.filter(p => p.id !== id)); };

  const getYTId = (url) => { const m = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/); return m ? m[1] : null; };
  const filteredVids = videoLibrary.filter(v => !videoSearch || v.exercise.toLowerCase().includes(videoSearch.toLowerCase()) || v.title.toLowerCase().includes(videoSearch.toLowerCase()));

  // Group PRs by movement
  const prsByMovement = PT_MOVEMENTS.map(m => ({
    movement: m,
    prs: myPRs.filter(p => p.movement === m).sort((a,b) => b.date.localeCompare(a.date)),
  })).filter(m => m.prs.length > 0);

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {[["program","My Program"],["prs","🏆 PRs"],["history","History"],["videos","📹 Videos"]].map(([v,l]) => (
          <button key={v} className={v === view ? "btn-secondary" : "btn-ghost"} onClick={() => setView(v)} style={{ flex: 1, padding: "9px 6px", fontSize: 12 }}>{l}</button>
        ))}
      </div>
      {view === "program" && (
        <div>
          {myPrograms.length === 0
            ? <div className="empty-state"><div className="empty-icon">💪</div>Madison is building your program!</div>
            : myPrograms.map((prog) => (
              <div key={prog.id} className="week-card" style={{ marginBottom: 14 }}>
                <div className="week-title">{prog.week}</div>
                <div className="week-posted">Posted {fmt(prog.posted_date)}</div>
                {(prog.slots || []).map((slot, i) => {
                  const slotKey = `${prog.id}_${i}`;
                  const state = slotStates[slotKey] || { done: false, notes: "", saving: false };
                  const attachedVids = (slot.video_ids || []).map(vid => videoLibrary.find(v => v.id === vid)).filter(Boolean);
                  return (
                    <div key={i} className="workout-slot" style={{ background: state.done ? "#F0FAF4" : "var(--surface)", border: state.done ? "1px solid #48BB7840" : "1px solid var(--border)", borderRadius: 12, padding: 14, marginBottom: 10 }}>
                      {/* Header row: checkbox + label */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        <div
                          onClick={() => !state.saving && checkSlot(slotKey, slot.label)}
                          style={{ width: 26, height: 26, borderRadius: 6, border: `2px solid ${state.done ? "#48BB78" : "#5B9BD5"}`, background: state.done ? "#48BB78" : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: state.saving ? "wait" : "pointer", flexShrink: 0, transition: "all 0.15s" }}>
                          {state.saving ? <span style={{ fontSize: 10 }}>…</span> : state.done ? <span style={{ color: "white", fontSize: 14, fontWeight: 800 }}>✓</span> : null}
                        </div>
                        <div className="slot-label" style={{ color: state.done ? "#48BB78" : "#5B9BD5", margin: 0 }}>{slot.label || `Workout ${i + 1}`}</div>
                        {state.done && <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, color: "#48BB78", background: "#E6FAF0", padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>✓ DONE</span>}
                        {state.saved && !state.done && <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--soft)" }}>Unchecked</span>}
                      </div>

                      <div className="slot-content">{slot.content}</div>
                      {slot.notes && <div style={{ fontSize: 12, color: "var(--mid)", marginTop: 6, fontStyle: "italic" }}>📝 Coach note: {slot.notes}</div>}

                      {attachedVids.length > 0 && (
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {attachedVids.map(v => <a key={v.id} href={v.youtube_url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", background: "#FF000015", borderRadius: 20, fontSize: 11, fontWeight: 700, color: "#CC0000", textDecoration: "none", border: "1px solid #FF000030" }}>▶ {v.title}</a>)}
                        </div>
                      )}

                      {/* Notes row — separate from checkbox */}
                      <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
                        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--soft)", fontWeight: 700, marginBottom: 6 }}>My Notes</div>
                        <textarea
                          className="form-input form-textarea"
                          placeholder="How did it go? PRs, how you felt, modifications..."
                          value={state.notes || ""}
                          onChange={e => updateSlotNotes(slotKey, e.target.value)}
                          style={{ fontSize: 12, minHeight: 60, marginBottom: 8 }}
                        />
                        <button
                          onClick={() => saveSlotNotes(slotKey, slot.label)}
                          style={{ padding: "7px 16px", borderRadius: 20, border: "none", background: "#5B9BD5", color: "white", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                          {state.notesSaving ? "Saving..." : state.notesSaved ? "✓ Notes Saved!" : "Save Notes"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      )}
      {view === "prs" && (
        <div>
          {/* Log a new PR */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title">Log a PR 🏆</div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label>Movement</label>
              <select className="form-input" value={prMovement} onChange={e => setPrMovement(e.target.value)}>
                {PT_MOVEMENTS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Rep Type</label>
                <select className="form-input" value={prRepType} onChange={e => setPrRepType(e.target.value)}>
                  {PT_REP_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Weight (lbs)</label>
                <input className="form-input" type="number" placeholder="135" value={prWeight} onChange={e => setPrWeight(e.target.value)} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Notes (optional)</label>
              <input className="form-input" placeholder="How it felt, technique notes..." value={prNotes} onChange={e => setPrNotes(e.target.value)} />
            </div>
            <button className="btn-primary" onClick={savePREntry}>
              {prSaved === "saving" ? "Saving..." : prSaved === "saved" ? "✓ PR Logged!" : "Log PR"}
            </button>
          </div>

          {/* PRs by movement */}
          {prsByMovement.length === 0
            ? <div className="empty-state"><div className="empty-icon">🏆</div>No PRs logged yet — start tracking your lifts!</div>
            : PT_MOVEMENTS.filter(m => myPRs.some(p => p.movement === m)).map(movement => {
              const prs = myPRs.filter(p => p.movement === movement).sort((a,b) => b.date.localeCompare(a.date));
              const isOpen = expandedMovement === movement;
              // Best per rep type
              const bests = PT_REP_TYPES.map(rt => {
                const matches = prs.filter(p => p.reps === rt);
                if (!matches.length) return null;
                const best = matches.reduce((a,b) => parseFloat(a.weight) >= parseFloat(b.weight) ? a : b);
                return { repType: rt, weight: best.weight, date: best.date };
              }).filter(Boolean);

              return (
                <div key={movement} className="card" style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={() => setExpandedMovement(isOpen ? null : movement)}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: 15, color: "var(--deep)" }}>{movement}</div>
                      <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                        {bests.map(b => (
                          <span key={b.repType} style={{ fontSize: 11, background: "#E8F2FA", color: "#5B9BD5", fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>
                            {b.repType}: {b.weight} lbs
                          </span>
                        ))}
                      </div>
                    </div>
                    <span style={{ fontSize: 18, color: "var(--soft)", marginLeft: 8 }}>{isOpen ? "▲" : "▼"}</span>
                  </div>

                  {isOpen && (
                    <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
                      {PT_REP_TYPES.map(rt => {
                        const repPRs = prs.filter(p => p.reps === rt);
                        if (!repPRs.length) return null;
                        return (
                          <div key={rt} style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, color: "#5B9BD5", marginBottom: 6 }}>{rt}</div>
                            {repPRs.map(pr => (
                              <div key={pr.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                                <div style={{ flex: 1 }}>
                                  <span style={{ fontWeight: 700, fontSize: 14, color: "var(--deep)" }}>{pr.weight} lbs</span>
                                  <span style={{ fontSize: 11, color: "var(--soft)", marginLeft: 8 }}>{fmt(pr.date)}</span>
                                  {pr.notes && <div style={{ fontSize: 11, color: "var(--mid)", fontStyle: "italic", marginTop: 2 }}>{pr.notes}</div>}
                                </div>
                                <button onClick={() => removePR(pr.id)} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 16 }}>×</button>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
      {view === "history" && (
        <div>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, color: "var(--deep)", marginBottom: 4 }}>Workout History</div>
          <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 16 }}>Tap any workout to see the full details</div>
          {myWorkouts.length === 0 ? <div className="empty-state"><div className="empty-icon">💪</div>No workouts logged yet — check off your first workout in My Program!</div>
            : myWorkouts.map(w => {
              const workoutName = w.type && w.type !== "workout" ? w.type : "Workout";
              const slotData = w.slot_key ? (() => {
                const parts = w.slot_key.split("_");
                const slotIdx = parseInt(parts[parts.length - 1]);
                const progId = parts.slice(0, -1).join("_");
                const prog = myPrograms.find(p => p.id === progId);
                return prog ? prog.slots?.[slotIdx] : null;
              })() : null;
              const isOpen = expandedWorkout === w.id;
              return (
                <div key={w.id} className="card" style={{ marginBottom: 10, cursor: "pointer", borderLeft: "3px solid #5B9BD5" }} onClick={() => setExpandedWorkout(isOpen ? null : w.id)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--deep)" }}>💪 {workoutName}</div>
                      <div style={{ fontSize: 11, color: "var(--soft)", marginTop: 2 }}>{fmt(w.date)}{w.duration ? ` · ${w.duration} min` : ""}</div>
                    </div>
                    <span style={{ fontSize: 16, color: "var(--soft)" }}>{isOpen ? "▲" : "▼"}</span>
                  </div>
                  {isOpen && (
                    <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                      {slotData?.content && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--soft)", fontWeight: 700, marginBottom: 6 }}>Workout</div>
                          <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{slotData.content}</div>
                        </div>
                      )}
                      {slotData?.notes && (
                        <div style={{ marginBottom: 10, background: "#E8F2FA", borderRadius: 8, padding: "8px 12px" }}>
                          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#5B9BD5", fontWeight: 700, marginBottom: 4 }}>Coach Note</div>
                          <div style={{ fontSize: 12, color: "var(--mid)", fontStyle: "italic" }}>{slotData.notes}</div>
                        </div>
                      )}
                      {w.notes && (
                        <div style={{ background: "var(--bg)", borderRadius: 8, padding: "8px 12px" }}>
                          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--soft)", fontWeight: 700, marginBottom: 4 }}>My Notes</div>
                          <div style={{ fontSize: 12, color: "var(--mid)" }}>{w.notes}</div>
                        </div>
                      )}
                      {!slotData?.content && !w.notes && <div style={{ fontSize: 12, color: "var(--soft)", fontStyle: "italic" }}>No details recorded</div>}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
      {view === "videos" && (
        <div>
          <input className="form-input" placeholder="🔍 Search exercises..." value={videoSearch} onChange={e => setVideoSearch(e.target.value)} style={{ marginBottom: 14 }} />
          {filteredVids.length === 0 ? <div className="empty-state"><div className="empty-icon">📹</div>No videos yet</div>
            : filteredVids.map(v => {
              const ytId = getYTId(v.youtube_url);
              return (
                <div key={v.id} className="card" style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--deep)", marginBottom: 2 }}>{v.title}</div>
                  <div style={{ fontSize: 11, color: "var(--soft)", marginBottom: 10 }}>{v.exercise}</div>
                  {ytId && <div style={{ borderRadius: 10, overflow: "hidden" }}><iframe width="100%" height="200" src={`https://www.youtube.com/embed/${ytId}`} title={v.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ display: "block" }} /></div>}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

function PTWins({ user }) {
  const [wins, setWins] = useState([]);
  const [type, setType] = useState("win");
  const [text, setText] = useState("");
  const [winDate, setWinDate] = useState(today());
  const [saved, setSaved] = useState(false);
  const [reactions, setReactions] = useState([]);

  useEffect(() => {
    Promise.all([getWins('pt'), getPTReactions(user.id)]).then(([w, r]) => {
      setWins(w.filter(x => x.user_id === user.id && (x.type === "win" || x.type === "gratitude")));
      setReactions(r);
    });
  }, [user.id]);

  const post = async () => {
    if (!text.trim()) return;
    const win = { id: `win_${Date.now()}`, userId: user.id, userName: user.name, type, text: text.trim(), date: winDate };
    setSaved("saving");
    const success = await saveWin(win);
    if (success) { setWins(prev => [{ ...win, user_id: user.id, likes: [], comments: [] }, ...prev]); setText(""); }
    setSaved(success ? "saved" : "error");
    setTimeout(() => setSaved(false), 2000);
  };

  const ASC_COLOR = "#9B8AA6";
  const pill = (active) => ({ padding: "7px 14px", borderRadius: 6, border: `1.5px solid ${active ? ASC_COLOR : "var(--border)"}`, background: active ? ASC_COLOR + "18" : "transparent", color: active ? ASC_COLOR : "var(--mid)", fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" });

  return (
    <div>
      <div className="card">
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: "var(--deep)", marginBottom: 12 }}>Share with Madison</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {[["win","Win"],["gratitude","Gratitude"]].map(([t,l]) => (
            <button key={t} onClick={() => setType(t)} style={pill(type === t)}>{l}</button>
          ))}
        </div>
        <textarea className="form-input form-textarea" placeholder={type === "win" ? "What did you crush?" : "What are you grateful for?"} value={text} onChange={e => setText(e.target.value)} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
          <input type="date" className="form-input" value={winDate} max={today()} onChange={e => setWinDate(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
          {winDate !== today() && <button style={{ fontSize: 11, background: "none", border: "none", color: "var(--soft)", cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => setWinDate(today())}>Today</button>}
        </div>
        {winDate !== today() && <div style={{ fontSize: 11, color: ASC_COLOR, fontWeight: 700, marginTop: 6, background: ASC_COLOR + "12", borderRadius: 8, padding: "5px 10px" }}>📅 Posting for {new Date(winDate + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>}
        <button onClick={post} style={{ width: "100%", padding: "12px", background: saved === "saved" ? "#48BB78" : ASC_COLOR, color: "white", border: "none", borderRadius: 10, fontFamily: "'Jost', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer", marginTop: 10 }}>{saved === "saving" ? "Posting..." : saved === "saved" ? "Posted!" : "Post"}</button>
      </div>
      {wins.map(w => {
        // Coach reactions live in two places: the older pt_reactions table and
        // the likes/comments columns written from the coach panel. Show both.
        const itemReactions = [
          ...reactions.filter(r => r.target_id === w.id),
          ...((w.likes || []).length ? [{ id: `${w.id}_l`, reaction_type: "like" }] : []),
          ...(w.comments || []).map((c, i) => ({
            id: `${w.id}_c${i}`,
            reaction_type: "comment",
            comment_text: c.text,
            user_name: c.userName,
          })),
        ];
        return (
          <div key={w.id} className="card" style={{ borderLeft: `3px solid ${w.type === "win" ? ASC_COLOR : "var(--forest)"}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: w.type === "win" ? ASC_COLOR : "var(--forest)", textTransform: "uppercase", letterSpacing: 1.5, display: "flex", alignItems: "center", gap: 4 }}>
                {w.type === "win" ? <Trophy size={11} weight="fill" color={ASC_COLOR} /> : <Sparkle size={11} weight="fill" color="var(--forest)" />}
                {w.type === "win" ? "Win" : "Gratitude"}
              </span>
              <span style={{ fontSize: 10, color: "var(--soft)" }}>{fmt(w.date)}</span>
            </div>
            <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.5, marginBottom: itemReactions.length ? 8 : 0 }}>{w.text}</div>
            {itemReactions.length > 0 && (
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: 8 }}>
                {itemReactions.map(r => (
                  <div key={r.id} style={{ fontSize: 12, color: "var(--mid)", padding: "3px 0", display: "flex", alignItems: "center", gap: 5 }}>
                    {r.reaction_type === "like"
                      ? <><Heart size={11} color={ASC_COLOR} weight="fill" /> {r.user_name || "Madison"} liked this</>
                      : <><ChatCircle size={11} color={ASC_COLOR} weight="fill" /> {r.user_name || "Madison"}: "{r.comment_text}"</>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PTGoals({ user }) {
  const [goals, setGoals] = useState([]);
  const [newGoal, setNewGoal] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  useEffect(() => { getPTGoals(user.id).then(setGoals); }, [user.id]);

  const add = async () => {
    if (!newGoal.trim()) return;
    const goal = { id: `goal_${Date.now()}`, clientId: user.id, goalText: newGoal.trim() };
    const success = await savePTGoal(goal);
    if (success) { setGoals(prev => [{ id: goal.id, client_id: user.id, goal_text: goal.goalText, created_at: new Date().toISOString() }, ...prev]); setNewGoal(""); }
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    await updatePTGoal(id, editText.trim());
    setGoals(prev => prev.map(g => g.id === id ? { ...g, goal_text: editText.trim() } : g));
    setEditingId(null);
  };

  const remove = async (id) => { await deletePTGoal(id); setGoals(prev => prev.filter(g => g.id !== id)); };

  const ASC_COLOR = "#9B8AA6";

  return (
    <div>
      <div className="card">
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: "var(--deep)", marginBottom: 4 }}>My Goals</div>
        <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 14 }}>Your goals are visible to Madison too</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="form-input" placeholder="Add a goal..." value={newGoal} onChange={e => setNewGoal(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} style={{ flex: 1 }} />
          <button onClick={add} style={{ padding: "0 16px", background: ASC_COLOR, color: "white", border: "none", borderRadius: 10, fontFamily: "'Jost', sans-serif", fontWeight: 700, fontSize: 18, cursor: "pointer" }}>+</button>
        </div>
      </div>
      {goals.length === 0
        ? <div className="empty-state"><Target size={32} color={ASC_COLOR} weight="duotone" /><div style={{ marginTop: 10 }}>No goals yet — add one above</div></div>
        : goals.map(g => (
          <div key={g.id} className="card" style={{ borderLeft: `3px solid ${ASC_COLOR}` }}>
            {editingId === g.id ? (
              <div style={{ display: "flex", gap: 8 }}>
                <input className="form-input" value={editText} onChange={e => setEditText(e.target.value)} style={{ flex: 1 }} autoFocus />
                <button onClick={() => saveEdit(g.id)} style={{ padding: "8px 14px", background: ASC_COLOR, color: "white", border: "none", borderRadius: 8, fontFamily: "'Jost', sans-serif", fontWeight: 700, cursor: "pointer" }}>Save</button>
                <button className="btn-ghost" style={{ padding: "8px 14px" }} onClick={() => setEditingId(null)}>✕</button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <Target size={16} color={ASC_COLOR} weight="duotone" style={{ marginTop: 2, flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 14, color: "var(--deep)", lineHeight: 1.5 }}>{g.goal_text}</div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => { setEditingId(g.id); setEditText(g.goal_text); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--soft)", fontSize: 13 }}>edit</button>
                  <button onClick={() => remove(g.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--soft)", fontSize: 18, lineHeight: 1 }}>×</button>
                </div>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

// ─── PT Coach Dashboard ───────────────────────────────────────────────────────
function PTCoachAnnouncements() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saved, setSaved] = useState(false);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => { getAnnouncements('pt').then(setAnnouncements); }, []);

  const post = async () => {
    if (!title.trim() || !body.trim()) return;
    const ann = { id: `pt_ann_${Date.now()}`, title: title.trim(), body: body.trim(), date: today(), audience: 'pt' };
    const success = await saveAnnouncement(ann);
    if (success) setAnnouncements(prev => [ann, ...prev]);
    setSaved(true); setTitle(""); setBody("");
    setTimeout(() => setSaved(false), 2000);
  };

  const remove = async (id) => { await deleteAnnouncement(id); setAnnouncements(prev => prev.filter(a => a.id !== id)); };

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 6 }}>PT Announcements 📣</div>
      <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 18 }}>These only show up for your PT clients — completely separate from Boot Camp.</div>
      <div className="card">
        <div className="form-group"><label>Title</label><input className="form-input" placeholder="e.g. Session moved to Thursday" value={title} onChange={e => setTitle(e.target.value)} /></div>
        <div className="form-group"><label>Message</label><textarea className="form-input form-textarea" placeholder="Details..." value={body} onChange={e => setBody(e.target.value)} /></div>
        <button className="btn-primary" onClick={post}>{saved ? "✓ Posted!" : "Post to PT Clients"}</button>
      </div>
      {announcements.length === 0 && <div className="empty-state" style={{ marginTop: 20 }}><div className="empty-icon">📣</div>No PT announcements yet</div>}
      {announcements.map(a => (
        <div key={a.id} className="card" style={{ borderLeft: "3px solid #5B9BD5" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--deep)", marginBottom: 4 }}>{a.title}</div>
            <button onClick={() => remove(a.id)} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 16 }}>×</button>
          </div>
          <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.5 }}>{a.body}</div>
          <div style={{ fontSize: 10, color: "var(--soft)", marginTop: 6 }}>{fmt(a.date)}</div>
        </div>
      ))}
    </div>
  );
}

function PTCoachDashboard({ user, onLogout, onSwitchToBc }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [coachTab, setCoachTab] = useState("clients");
  const [showMenu, setShowMenu] = useState(false);
  const [allCheckins, setAllCheckins] = useState([]);
  const [allWorkouts, setAllWorkouts] = useState([]);
  const [allWins, setAllWins] = useState([]);
  const [dotMessages, setDotMessages] = useState(false);
  const ptCoachLsKey = (type) => `gt_ptcoach_last_seen_${type}`;

  useEffect(() => {
    Promise.all([getPTClients(), getCheckins(null, 'pt'), getWorkouts(null, 'pt'), getWins('pt')]).then(([c, ci, w, wn]) => {
      setClients(c); setAllCheckins(ci); setAllWorkouts(w);
      setAllWins(wn.filter(x => x.type === "win" || x.type === "gratitude"));
      // Check for new PT messages across all client DMs
      const lastMsg = localStorage.getItem(ptCoachLsKey('messages'));
      Promise.all(c.map(client => getMessages(`pt_dm_${[client.id, "coach"].sort().join("_")}`))).then(allMsgArrays => {
        const allMsgs = allMsgArrays.flat().filter(m => m.sender_id !== "coach");
        setDotMessages(lastMsg ? allMsgs.some(m => m.created_at > lastMsg) : allMsgs.length > 0);
      });
    });
  }, []);

  const handleCoachTab = (id) => {
    if (id === "messages") { localStorage.setItem(ptCoachLsKey('messages'), new Date().toISOString()); setDotMessages(false); }
    setCoachTab(id);
  };

  const getMIA = (clientId) => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 3);
    const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth()+1).padStart(2,'0')}-${String(cutoff.getDate()).padStart(2,'0')}`;
    const last = allCheckins.filter(c => c.user_id === clientId).sort((a,b) => b.date.localeCompare(a.date))[0];
    return !last || last.date < cutoffStr;
  };

  const getLastCheckin = (clientId) => allCheckins.filter(c => c.user_id === clientId).sort((a,b) => b.date.localeCompare(a.date))[0];
  const getClientWorkouts = (clientId) => allWorkouts.filter(w => w.user_id === clientId);
  const getClientWins = (clientId) => allWins.filter(w => w.user_id === clientId);

  // Recent activity feed across all clients (last 10 items)
  const recentActivity = [
    ...allCheckins.map(c => ({ ...c, _type: "check-in", _date: c.date, _clientId: c.user_id })),
    ...allWorkouts.map(w => ({ ...w, _type: "workout", _date: w.date, _clientId: w.user_id })),
    ...allWins.map(w => ({ ...w, _type: w.type, _date: w.date, _clientId: w.user_id })),
  ]
    .filter(a => clients.some(c => c.id === a._clientId))
    .sort((a,b) => b._date.localeCompare(a._date))
    .slice(0, 15);

  return (
    <div className="coach-shell pt-coach-shell">
      <style>{styles}</style>
      <div className="coach-top" style={{ background: "linear-gradient(135deg, #1A2332 0%, #2C3E55 100%)" }}>
        <div className="coach-logo" style={{ color: "#5B9BD5" }}>Personal Coaching 🏋️</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
          <button onClick={() => setShowMenu(v => !v)} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 13, fontFamily: "Jost, sans-serif", fontWeight: 600, padding: "6px 12px", borderRadius: 8 }}>
            {user.name} ▾
          </button>
          {showMenu && (
            <div style={{ position: "absolute", top: 38, right: 0, background: "white", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", padding: 8, minWidth: 180, zIndex: 100 }}>
              <div style={{ padding: "6px 12px", fontSize: 11, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Switch to</div>
              <button onClick={() => { setShowMenu(false); onSwitchToBc(); }} style={{ width: "100%", padding: "10px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontFamily: "Jost, sans-serif", fontWeight: 600, color: "var(--pink)", textAlign: "left", borderRadius: 8 }}>💪 Boot Camp</button>
              <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "4px 0" }} />
              <button onClick={() => { setShowMenu(false); onLogout(); }} style={{ width: "100%", padding: "10px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontFamily: "Jost, sans-serif", fontWeight: 600, color: "var(--soft)", textAlign: "left", borderRadius: 8 }}>Sign Out</button>
            </div>
          )}
        </div>
      </div>
      {!selectedClient ? (
        <>
          <div className="coach-tabs">
            {[["clients","👥 Clients"],["messages","💬 Messages", dotMessages],["activity","📊 Activity"],["videos","📹 Videos"],["settings","⚙️ PT Settings"]].map(([id,label,dot]) => (
              <div key={id} className={`coach-tab ${coachTab === id ? "active" : ""}`} onClick={() => handleCoachTab(id)} style={{ position: "relative" }}>
                {label}
                {dot && <span style={{ position: "absolute", top: 6, right: 4, width: 8, height: 8, background: "#E8647A", borderRadius: "50%", border: "2px solid var(--surface)" }} />}
              </div>
            ))}
          </div>
          <div className="coach-content">

            {coachTab === "clients" && (
              <div style={{ maxWidth: 720 }}>
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 28, fontWeight: 800, color: "var(--deep)", marginBottom: 6 }}>Your Clients</div>
                <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 20 }}>Click any client to view their full profile, build their program, set habits, schedule sessions, and leave reactions.</div>

                {/* Stats row */}
                {clients.length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
                    {[
                      ["Total Clients", clients.length, "#5B9BD5"],
                      ["Check-ins Today", allCheckins.filter(c => c.date === today() && clients.some(cl => cl.id === c.user_id)).length, "#48BB78"],
                      ["MIA 3+ Days", clients.filter(c => getMIA(c.id)).length, "#E8647A"],
                    ].map(([label, val, color]) => (
                      <div key={label} style={{ background: "white", borderRadius: 14, padding: "16px 12px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderTop: `3px solid ${color}` }}>
                        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 32, fontWeight: 800, color }}>{val}</div>
                        <div style={{ fontSize: 10, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {clients.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">👤</div>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>No PT clients yet</div>
                    <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 16 }}>Go to ⚙️ PT Settings to set your invite code, then share it with your clients so they can sign up.</div>
                    <div style={{ background: "#E8F2FA", borderRadius: 12, padding: "14px 18px", textAlign: "left", fontSize: 12, color: "var(--mid)", lineHeight: 1.8 }}>
                      <div style={{ fontWeight: 800, color: "#5B9BD5", marginBottom: 6, fontSize: 13 }}>Once a client is added, clicking their card unlocks:</div>
                      <div>📋 <strong>Program</strong> — build their weekly workouts (up to 7/week) with videos</div>
                      <div>✅ <strong>Check-ins</strong> — see daily habits + mood + notes, leave reactions</div>
                      <div>💪 <strong>Workouts</strong> — see every logged session, like & comment</div>
                      <div>🥗 <strong>Nutrition</strong> — view their logs, add entries for them</div>
                      <div>✨ <strong>Wins & Gratitudes</strong> — like & comment, they get notified</div>
                      <div>🎯 <strong>Goals</strong> — see what they've set for themselves</div>
                      <div>📅 <strong>Sessions</strong> — schedule upcoming appointments (shows on their home)</div>
                      <div>⚙️ <strong>Habits</strong> — set their custom daily check-in items</div>
                    </div>
                  </div>
                ) : clients.map(c => {
                  const mia = getMIA(c.id);
                  const lastCI = getLastCheckin(c.id);
                  const wkCount = getClientWorkouts(c.id).length;
                  const winCount = getClientWins(c.id).length;
                  return (
                    <div key={c.id} className="card" style={{ cursor: "pointer", borderLeft: mia ? "4px solid #E8647A" : "4px solid #5B9BD5", marginBottom: 12, transition: "box-shadow 0.2s" }}
                      onClick={() => setSelectedClient(c)}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 24px rgba(91,155,213,0.18)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = ""}>
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <div className="avatar" style={{ width: 50, height: 50, fontSize: 18, background: mia ? "#E8647A" : "#5B9BD5", flexShrink: 0 }}>{initials(c.name)}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                            <span style={{ fontWeight: 800, fontSize: 17, color: "var(--deep)" }}>{c.name}</span>
                            {mia && <span style={{ fontSize: 10, fontWeight: 700, color: "#E8647A", background: "#FFE8EC", padding: "2px 8px", borderRadius: 20 }}>⚠️ MIA 3+ days</span>}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 6 }}>@{c.username}</div>
                          <div style={{ display: "flex", gap: 14, fontSize: 11, color: "var(--mid)" }}>
                            <span>✅ {allCheckins.filter(ci => ci.user_id === c.id).length} check-ins</span>
                            <span>💪 {wkCount} workouts</span>
                            <span>✨ {winCount} wins</span>
                            {lastCI && <span>Last: {fmt(lastCI.date)}</span>}
                          </div>
                        </div>
                        <div style={{ fontSize: 22, color: "#5B9BD5", flexShrink: 0 }}>→</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {coachTab === "activity" && (
              <div style={{ maxWidth: 680 }}>
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 28, fontWeight: 800, color: "var(--deep)", marginBottom: 6 }}>Recent Activity</div>
                <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 20 }}>Everything happening across all your PT clients — click a client name to open their full profile.</div>
                {recentActivity.length === 0
                  ? <div className="empty-state"><div className="empty-icon">📊</div>No activity yet — activity will show here once clients start checking in, logging workouts, and posting wins.</div>
                  : recentActivity.map((item, i) => {
                    const client = clients.find(c => c.id === item._clientId);
                    if (!client) return null;
                    const typeLabel = item._type === "check-in" ? "✅ Check-in" : item._type === "workout" ? "💪 Workout" : item._type === "win" ? "🏆 Win" : "🙏 Gratitude";
                    const typeColor = item._type === "check-in" ? "#48BB78" : item._type === "workout" ? "#5B9BD5" : item._type === "win" ? "#E8A020" : "#9B59B6";
                    return (
                      <div key={`${item._type}_${item.id}_${i}`} className="card" style={{ marginBottom: 10, borderLeft: `3px solid ${typeColor}`, cursor: "pointer" }} onClick={() => setSelectedClient(client)}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                          <div className="avatar" style={{ width: 32, height: 32, fontSize: 12, background: "#5B9BD5", flexShrink: 0 }}>{initials(client.name)}</div>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: 700, fontSize: 13, color: "var(--deep)" }}>{client.name}</span>
                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: typeColor }}>{typeLabel}</span>
                          </div>
                          <div style={{ fontSize: 11, color: "var(--soft)" }}>{fmt(item._date)}</div>
                        </div>
                        {item._type === "check-in" && (
                          <div style={{ fontSize: 12, color: "var(--mid)" }}>
                            {item.mood && <span style={{ marginRight: 8 }}>{item.mood}</span>}
                            {item.energy && <span style={{ marginRight: 8 }}>{item.energy}</span>}
                            {item.notes && <span style={{ fontStyle: "italic" }}>{item.notes.slice(0, 80)}{item.notes.length > 80 ? "..." : ""}</span>}
                          </div>
                        )}
                        {item._type === "workout" && item.notes && <div style={{ fontSize: 12, color: "var(--mid)", fontStyle: "italic" }}>{item.notes.slice(0, 80)}{item.notes.length > 80 ? "..." : ""}</div>}
                        {(item._type === "win" || item._type === "gratitude") && <div style={{ fontSize: 12, color: "var(--mid)" }}>{item.text?.slice(0, 100)}{item.text?.length > 100 ? "..." : ""}</div>}
                      </div>
                    );
                  })}
              </div>
            )}

            {coachTab === "messages" && <PTCoachMessaging user={user} clients={clients} />}
            {coachTab === "videos" && <CoachVideoLibrary />}
            {coachTab === "settings" && <PTCoachSettings />}
          </div>
        </>
      ) : (
        <PTCoachClientDetail client={selectedClient} coach={user} onBack={() => setSelectedClient(null)} />
      )}
    </div>
  );
}

function PTCoachSettings() {
  const [ptInviteCode, setPtInviteCode] = useState("");
  const [saved, setSaved] = useState(false);
  const [testStatus, setTestStatus] = useState(null); // null | "creating" | "exists" | "done"
  const TEST_USER = { username: "testclient", password: "test123", name: "Test Client" };

  useEffect(() => { getSetting('pt_invite_code').then(c => { if (c) setPtInviteCode(c); }); }, []);

  const save = async () => {
    if (!ptInviteCode.trim()) return;
    await saveSetting('pt_invite_code', ptInviteCode.trim());
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const createTestAccount = async () => {
    setTestStatus("creating");
    // Check if already exists
    const existing = await loginUser(TEST_USER.username, TEST_USER.password);
    if (existing) { setTestStatus("exists"); return; }
    // Need a valid invite code to sign up
    const code = ptInviteCode.trim();
    if (!code) { setTestStatus("nocode"); return; }
    const result = await signUpUser(TEST_USER.name, TEST_USER.username, TEST_USER.password, code, "pt_client");
    setTestStatus(result.error ? "error" : "done");
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 800, color: "var(--deep)", marginBottom: 18 }}>PT Settings ⚙️</div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">PT Client Invite Code 🔑</div>
        <div className="card-sub">Share this with personal training clients so they can create their accounts</div>
        <div className="form-group" style={{ marginTop: 14 }}>
          <label>Invite Code</label>
          <input className="form-input" value={ptInviteCode} onChange={e => setPtInviteCode(e.target.value)} placeholder="e.g. MADISON1ON1" style={{ textTransform: "uppercase" }} />
        </div>
        <button className="btn-primary" onClick={save}>{saved ? "✓ Saved!" : "Save PT Invite Code"}</button>
      </div>

      <div className="card">
        <div className="card-title">🤖 AI Food Analysis Key</div>
        <div className="card-sub" style={{ marginBottom: 14 }}>Same key used across both Boot Camp and PT. Set it in either Camp Settings or here.</div>
        <div className="form-group">
          <label>OpenAI API Key</label>
          <input className="form-input" type="password" value={ptInviteCode.startsWith("sk-") ? "" : ""} placeholder="Set in Boot Camp → Camp Settings" disabled style={{ opacity: 0.6 }} />
        </div>
        <div style={{ fontSize: 12, color: "var(--soft)" }}>Go to Boot Camp → Camp Settings → 🤖 AI Food Analysis Key to set or update it.</div>
      </div>

      <div className="card" style={{ borderLeft: "3px solid #5B9BD5" }}>
        <div className="card-title">🧪 Preview Client Experience</div>
        <div className="card-sub" style={{ marginBottom: 14 }}>Creates a test PT client account so you can log in and see exactly what your clients see. Save your invite code first, then click below.</div>
        <div style={{ background: "var(--bg)", borderRadius: 10, padding: "12px 14px", marginBottom: 14, fontFamily: "monospace", fontSize: 13 }}>
          <div><strong>Username:</strong> testclient</div>
          <div><strong>Password:</strong> test123</div>
        </div>
        <button
          onClick={createTestAccount}
          style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "#5B9BD5", color: "white", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
          {testStatus === "creating" ? "Creating..." : "Create Test Account"}
        </button>
        {testStatus === "done" && <div style={{ marginTop: 10, fontSize: 13, color: "#48BB78", fontWeight: 700 }}>✓ Done! Sign out, choose "Sign in to Personal Coaching", and log in with testclient / test123</div>}
        {testStatus === "exists" && <div style={{ marginTop: 10, fontSize: 13, color: "#5B9BD5", fontWeight: 700 }}>✓ Test account already exists — log in with testclient / test123</div>}
        {testStatus === "nocode" && <div style={{ marginTop: 10, fontSize: 13, color: "#E8647A", fontWeight: 700 }}>⚠️ Save your PT invite code first, then try again</div>}
        {testStatus === "error" && <div style={{ marginTop: 10, fontSize: 13, color: "#E8647A", fontWeight: 700 }}>⚠️ Something went wrong — try again</div>}
      </div>
    </div>
  );
}

function PTCoachClientDetail({ client, coach, onBack }) {
  const [tab, setTab] = useState("overview");
  const [checkins, setCheckins] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [wins, setWins] = useState([]);
  const [goals, setGoals] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [checkItems, setCheckItems] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [clientAnnouncements, setClientAnnouncements] = useState([]);
  const [annTitle, setAnnTitle] = useState("");
  const [annBody, setAnnBody] = useState("");
  const [annSaved, setAnnSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  // Program builder
  const [editProgramId, setEditProgramId] = useState(null);
  const [progWeek, setProgWeek] = useState("");
  const [progSlots, setProgSlots] = useState(Array(7).fill(null).map(() => ({ label: "", content: "", notes: "", video_ids: [] })));
  const [progSaved, setProgSaved] = useState(false);
  const [videoLibrary, setVideoLibrary] = useState([]);
  const [showVideoPicker, setShowVideoPicker] = useState(null);
  const [progAtSlot, setProgAtSlot] = useState(null);
  const [progAtQuery, setProgAtQuery] = useState("");

  // Check items
  const [newCheckItem, setNewCheckItem] = useState("");

  // Appointments
  const [apptDate, setApptDate] = useState("");
  const [apptTime, setApptTime] = useState("");
  const [apptNotes, setApptNotes] = useState("");
  const [apptSaved, setApptSaved] = useState(false);

  // Reactions
  const [commentTexts, setCommentTexts] = useState({});
  const [expandedWorkout, setExpandedWorkout] = useState(null);

  useEffect(() => {
    const load = async () => {
      const [c, w, wn, g, prog, ci, appts, r, vids, ann] = await Promise.all([
        getCheckins(client.id), getWorkouts(client.id),
        getWins('pt').then(all => all.filter(x => x.user_id === client.id && (x.type === "win" || x.type === "gratitude"))),
        getPTGoals(client.id), getPTPrograms(client.id),
        getPTCheckItems(client.id), getPTAppointments(client.id),
        getPTReactions(client.id), getVideoLibrary(),
        getAnnouncements('pt', client.id),
      ]);
      setCheckins(c); setWorkouts(w); setWins(wn); setGoals(g);
      setPrograms(prog); setCheckItems(ci); setAppointments(appts);
      setReactions(r); setVideoLibrary(vids); setClientAnnouncements(ann);
      setLoading(false);
    };
    load();
  }, [client.id]);

  const addReaction = async (targetType, targetId, reactionType, commentText = "") => {
    const r = { id: `pr_${Date.now()}`, coachId: coach.id, clientId: client.id, targetType, targetId, reactionType, commentText };
    await savePTReaction(r);
    setReactions(prev => [{ ...r, target_type: targetType, target_id: targetId, reaction_type: reactionType, comment_text: commentText, seen: false, created_at: new Date().toISOString() }, ...prev]);
    setCommentTexts(prev => ({ ...prev, [targetId]: "" }));
  };

  const getItemReactions = (id) => reactions.filter(r => r.target_id === id);

  const postProgram = async () => {
    if (!progWeek) return;
    setProgSaved("saving");
    const filledSlots = progSlots.filter(s => s.content.trim() || s.label.trim());
    if (editProgramId) {
      const success = await updatePTProgram(editProgramId, { week: progWeek, slots: filledSlots });
      if (success) { setPrograms(prev => prev.map(p => p.id === editProgramId ? { ...p, week: progWeek, slots: filledSlots } : p)); setEditProgramId(null); }
      setProgSaved(success ? "saved" : "error");
    } else {
      const prog = { id: `prog_${Date.now()}`, clientId: client.id, week: progWeek, slots: filledSlots, postedDate: today() };
      const success = await savePTProgram(prog);
      if (success) setPrograms(prev => [{ ...prog, id: prog.id, client_id: client.id, posted_date: prog.postedDate }, ...prev]);
      setProgSaved(success ? "saved" : "error");
    }
    setTimeout(() => { setProgSaved(false); if (!editProgramId) { setProgWeek(""); setProgSlots(Array(7).fill(null).map(() => ({ label: "", content: "", notes: "", video_ids: [] }))); } }, 2000);
  };

  const startEditProgram = (prog) => {
    setEditProgramId(prog.id);
    setProgWeek(prog.week);
    const slots = Array(7).fill(null).map((_, i) => prog.slots[i] || { label: "", content: "", notes: "", video_ids: [] });
    setProgSlots(slots);
    setTab("program");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateProgSlot = (i, field, val) => setProgSlots(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));
  const updateProgSlotContent = (i, val, cursor) => {
    const before = val.slice(0, cursor);
    const atIdx = before.lastIndexOf('@');
    if (atIdx !== -1) {
      const query = before.slice(atIdx + 1);
      if (!query.includes(' ') && !query.includes('\n')) {
        setProgAtSlot(i);
        setProgAtQuery(query);
      } else {
        setProgAtSlot(null);
      }
    } else {
      setProgAtSlot(null);
    }
    setProgSlots(prev => prev.map((s, idx) => idx === i ? { ...s, content: val } : s));
  };

  const pickProgAtVideo = (i, video) => {
    setProgSlots(prev => prev.map((s, idx) => {
      if (idx !== i) return s;
      const searchStr = '@' + progAtQuery;
      const atIdx = s.content.lastIndexOf(searchStr);
      // Remove the @ but keep the movement text
      const newContent = atIdx !== -1
        ? s.content.slice(0, atIdx) + progAtQuery + s.content.slice(atIdx + searchStr.length)
        : s.content;
      const ids = s.video_ids || [];
      return { ...s, content: newContent, video_ids: ids.includes(video.id) ? ids : [...ids, video.id] };
    }));
    setProgAtSlot(null);
    setProgAtQuery("");
  };
  const toggleProgVideo = (slotIdx, videoId) => setProgSlots(prev => prev.map((s, idx) => {
    if (idx !== slotIdx) return s;
    const ids = s.video_ids || [];
    return { ...s, video_ids: ids.includes(videoId) ? ids.filter(id => id !== videoId) : [...ids, videoId] };
  }));

  const addCheckItem = async () => {
    if (!newCheckItem.trim()) return;
    const item = { id: `ci_${Date.now()}`, clientId: client.id, label: newCheckItem.trim() };
    await savePTCheckItem(item);
    setCheckItems(prev => [...prev, { id: item.id, client_id: client.id, label: item.label }]);
    setNewCheckItem("");
  };

  const removeCheckItem = async (id) => { await deletePTCheckItem(id); setCheckItems(prev => prev.filter(i => i.id !== id)); };

  const addAppointment = async () => {
    if (!apptDate) return;
    const appt = { id: `appt_${Date.now()}`, clientId: client.id, date: apptDate, time: apptTime, notes: apptNotes };
    setApptSaved("saving");
    const success = await savePTAppointment(appt);
    if (success) { setAppointments(prev => [...prev, { ...appt, client_id: client.id }].sort((a,b) => a.date.localeCompare(b.date))); setApptDate(""); setApptTime(""); setApptNotes(""); }
    setApptSaved(success ? "saved" : "error");
    setTimeout(() => setApptSaved(false), 2000);
  };

  const removeAppt = async (id) => { await deletePTAppointment(id); setAppointments(prev => prev.filter(a => a.id !== id)); };

  const getYTId = (url) => { const m = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/); return m ? m[1] : null; };

  if (loading) return <div style={{ padding: 28 }}><button className="back-btn" onClick={onBack}>← Back</button><div className="empty-state"><div className="empty-icon">⏳</div>Loading...</div></div>;

  const tabs = [["overview","Overview"],["program","Program"],["checkins","Check-ins"],["workouts","Workouts"],["wins","Wins"],["goals","Goals"],["announce","📣 Messages"],["manage","Manage"]];

  return (
    <div style={{ padding: 28 }}>
      <style>{styles}</style>
      <button className="back-btn" onClick={onBack}>← Back to Clients</button>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <div className="avatar" style={{ width: 52, height: 52, fontSize: 18, background: "#5B9BD5" }}>{initials(client.name)}</div>
        <div>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 28, fontWeight: 800, color: "var(--deep)" }}>{client.name}</div>
          <div style={{ fontSize: 12, color: "var(--soft)" }}>{checkins.length} check-ins · {workouts.length} workouts · {goals.length} goals</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {tabs.map(([id, label]) => (
          <button key={id} className={id === tab ? "btn-secondary" : "btn-ghost"} onClick={() => setTab(id)} style={{ padding: "8px 14px", fontSize: 12 }}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div>
          {/* Goals */}
          {goals.length > 0 && (
            <div className="card" style={{ borderLeft: "3px solid #5B9BD5", marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>🎯 Goals</div>
              {goals.map(g => <div key={g.id} style={{ fontSize: 13, color: "var(--mid)", padding: "4px 0", borderBottom: "1px solid var(--border)" }}>• {g.goal_text}</div>)}
            </div>
          )}
          {/* Next appointment */}
          {appointments.filter(a => a.date >= today()).length > 0 && (
            <div className="card" style={{ borderLeft: "3px solid #5B9BD5", marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>📅 Next Session</div>
              {(() => { const next = appointments.filter(a => a.date >= today()).sort((a,b) => a.date.localeCompare(b.date))[0];
                return <div style={{ fontSize: 14, color: "var(--deep)", fontWeight: 600 }}>{new Date(next.date + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}{next.time && <span style={{ color: "var(--soft)", fontWeight: 400 }}> at {next.time}</span>}{next.notes && <div style={{ fontSize: 12, color: "var(--mid)", marginTop: 4 }}>{next.notes}</div>}</div>;
              })()}
            </div>
          )}
          {/* Recent check-in */}
          {checkins.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Latest Check-in</div>
              {(() => { const latest = checkins[0]; return (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--deep)", marginBottom: 4 }}>{fmt(latest.date)} · {latest.mood} · {latest.energy}</div>
                  {Object.entries(latest.checks || {}).filter(([,v]) => v).length > 0 && (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                      {Object.entries(latest.checks || {}).filter(([,v]) => v).map(([k]) => <span key={k} className="chip green" style={{ fontSize: 10 }}>✓ {k}</span>)}
                    </div>
                  )}
                  {latest.notes && <div style={{ fontSize: 12, color: "var(--mid)", marginBottom: 10 }}>{latest.notes}</div>}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => addReaction("check-in", latest.id, "like")} style={{ padding: "5px 12px", borderRadius: 20, border: "1px solid var(--border)", background: getItemReactions(latest.id).some(r => r.reaction_type === "like") ? "#FFE8EC" : "white", cursor: "pointer", fontSize: 12, fontFamily: "Jost, sans-serif" }}>❤️ Like</button>
                    <input className="form-input" style={{ flex: 1, padding: "5px 10px", fontSize: 12 }} placeholder="Leave a comment..." value={commentTexts[latest.id] || ""} onChange={e => setCommentTexts(prev => ({ ...prev, [latest.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && addReaction("check-in", latest.id, "comment", commentTexts[latest.id])} />
                    <button onClick={() => addReaction("check-in", latest.id, "comment", commentTexts[latest.id])} style={{ padding: "5px 12px", borderRadius: 20, border: "none", background: "#5B9BD5", color: "white", cursor: "pointer", fontSize: 12, fontFamily: "Jost, sans-serif", fontWeight: 700 }}>Send</button>
                  </div>
                </div>
              );})()}
            </div>
          )}
        </div>
      )}

      {tab === "program" && <PTProgramEditor client={client} color="#5B9BD5" />}

      {tab === "checkins" && (
        <div>
          {checkins.length === 0 ? <div className="empty-state"><div className="empty-icon">✅</div>No check-ins yet</div>
            : checkins.map(c => {
              const itemReactions = getItemReactions(c.id);
              return (
                <div key={c.id} className="card" style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{fmt(c.date)}</span>
                    <span style={{ fontSize: 11, color: "var(--soft)" }}>{c.mood} · {c.energy}</span>
                  </div>
                  {(() => { const done = Object.entries(c.checks || {}).filter(([,v]) => v).map(([k]) => k.startsWith('ci_') ? checkItems.find(i => i.id === k)?.label : k).filter(Boolean); return done.length > 0 ? <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>{[...new Set(done)].map(label => <span key={label} className="chip green" style={{ fontSize: 9 }}>✓ {label}</span>)}</div> : null; })()}
                  {c.notes && <div style={{ fontSize: 12, color: "var(--mid)", marginBottom: 8 }}>{c.notes}</div>}
                  {itemReactions.map(r => <div key={r.id} style={{ fontSize: 11, color: "var(--mid)", padding: "2px 0" }}>{r.reaction_type === "like" ? "❤️ You liked this" : `💬 You: "${r.comment_text}"`}</div>)}
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button onClick={() => addReaction("check-in", c.id, "like")} style={{ padding: "4px 10px", borderRadius: 20, border: "1px solid var(--border)", background: itemReactions.some(r => r.reaction_type === "like") ? "#FFE8EC" : "white", cursor: "pointer", fontSize: 11, fontFamily: "Jost, sans-serif" }}>❤️</button>
                    <input className="form-input" style={{ flex: 1, padding: "4px 10px", fontSize: 11 }} placeholder="Comment..." value={commentTexts[c.id] || ""} onChange={e => setCommentTexts(prev => ({ ...prev, [c.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && addReaction("check-in", c.id, "comment", commentTexts[c.id])} />
                    <button onClick={() => addReaction("check-in", c.id, "comment", commentTexts[c.id])} style={{ padding: "4px 10px", borderRadius: 20, border: "none", background: "#5B9BD5", color: "white", cursor: "pointer", fontSize: 11, fontFamily: "Jost, sans-serif", fontWeight: 700 }}>→</button>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {tab === "workouts" && (
        <div>
          <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 14 }}>Tap any workout to see the full details</div>
          {workouts.length === 0 ? <div className="empty-state"><div className="empty-icon">💪</div>No workouts logged yet</div>
            : workouts.map(w => {
              const itemReactions = getItemReactions(w.id);
              const workoutName = w.type && w.type !== "workout" ? w.type : "Workout";
              const isOpen = expandedWorkout === w.id;
              const slotData = w.slot_key ? (() => {
                const parts = w.slot_key.split("_");
                const slotIdx = parseInt(parts[parts.length - 1]);
                const progId = parts.slice(0, -1).join("_");
                const prog = programs.find(p => p.id === progId);
                return prog ? prog.slots?.[slotIdx] : null;
              })() : null;
              return (
                <div key={w.id} className="card" style={{ marginBottom: 10, borderLeft: "3px solid #5B9BD5" }}>
                  {/* Clickable header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer", marginBottom: isOpen ? 0 : 4 }} onClick={() => setExpandedWorkout(isOpen ? null : w.id)}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--deep)", marginBottom: 2 }}>💪 {workoutName}</div>
                      <span style={{ fontSize: 11, color: "var(--soft)" }}>{fmt(w.date)}{w.duration ? ` · ${w.duration} min` : ""}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {w.slot_key && <span style={{ fontSize: 10, background: "#E8F2FA", color: "#5B9BD5", fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>✓ Done</span>}
                      <span style={{ fontSize: 14, color: "var(--soft)" }}>{isOpen ? "▲" : "▼"}</span>
                    </div>
                  </div>
                  {/* Expanded details */}
                  {isOpen && (
                    <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12, marginBottom: 10 }}>
                      {slotData?.content && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--soft)", fontWeight: 700, marginBottom: 6 }}>Workout</div>
                          <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{slotData.content}</div>
                        </div>
                      )}
                      {slotData?.notes && (
                        <div style={{ marginBottom: 10, background: "#E8F2FA", borderRadius: 8, padding: "8px 12px" }}>
                          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#5B9BD5", fontWeight: 700, marginBottom: 4 }}>Your Coach Note</div>
                          <div style={{ fontSize: 12, color: "var(--mid)", fontStyle: "italic" }}>{slotData.notes}</div>
                        </div>
                      )}
                      {w.notes && (
                        <div style={{ background: "var(--bg)", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--soft)", fontWeight: 700, marginBottom: 4 }}>Client Notes</div>
                          <div style={{ fontSize: 12, color: "var(--mid)" }}>{w.notes}</div>
                        </div>
                      )}
                      {!slotData?.content && !w.notes && <div style={{ fontSize: 12, color: "var(--soft)", fontStyle: "italic", marginBottom: 8 }}>No details recorded</div>}
                    </div>
                  )}
                  {/* Reactions always visible */}
                  {itemReactions.map(r => <div key={r.id} style={{ fontSize: 11, color: "var(--mid)", padding: "2px 0" }}>{r.reaction_type === "like" ? "❤️ You liked this" : `💬 You: "${r.comment_text}"`}</div>)}
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button onClick={() => addReaction("workout", w.id, "like")} style={{ padding: "4px 10px", borderRadius: 20, border: "1px solid var(--border)", background: itemReactions.some(r => r.reaction_type === "like") ? "#FFE8EC" : "white", cursor: "pointer", fontSize: 11, fontFamily: "Jost, sans-serif" }}>❤️</button>
                    <input className="form-input" style={{ flex: 1, padding: "4px 10px", fontSize: 11 }} placeholder="Comment..." value={commentTexts[w.id] || ""} onChange={e => setCommentTexts(prev => ({ ...prev, [w.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && addReaction("workout", w.id, "comment", commentTexts[w.id])} />
                    <button onClick={() => addReaction("workout", w.id, "comment", commentTexts[w.id])} style={{ padding: "4px 10px", borderRadius: 20, border: "none", background: "#5B9BD5", color: "white", cursor: "pointer", fontSize: 11, fontFamily: "Jost, sans-serif", fontWeight: 700 }}>→</button>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {tab === "wins" && (
        <div>
          {wins.length === 0 ? <div className="empty-state"><div className="empty-icon">✨</div>No wins or gratitudes yet</div>
            : wins.map(w => {
              const itemReactions = getItemReactions(w.id);
              return (
                <div key={w.id} className="card" style={{ borderLeft: `3px solid ${w.type === "win" ? "#5B9BD5" : "var(--forest)"}`, marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: w.type === "win" ? "#5B9BD5" : "var(--forest)", textTransform: "uppercase" }}>{w.type === "win" ? "🏆 Win" : "🙏 Gratitude"}</span>
                    <span style={{ fontSize: 10, color: "var(--soft)" }}>{fmt(w.date)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.5, marginBottom: 8 }}>{w.text}</div>
                  {itemReactions.map(r => <div key={r.id} style={{ fontSize: 11, color: "var(--mid)", padding: "2px 0" }}>{r.reaction_type === "like" ? "❤️ You liked this" : `💬 You: "${r.comment_text}"`}</div>)}
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button onClick={() => addReaction("win", w.id, "like")} style={{ padding: "4px 10px", borderRadius: 20, border: "1px solid var(--border)", background: itemReactions.some(r => r.reaction_type === "like") ? "#FFE8EC" : "white", cursor: "pointer", fontSize: 11, fontFamily: "Jost, sans-serif" }}>❤️</button>
                    <input className="form-input" style={{ flex: 1, padding: "4px 10px", fontSize: 11 }} placeholder="Comment..." value={commentTexts[w.id] || ""} onChange={e => setCommentTexts(prev => ({ ...prev, [w.id]: e.target.value }))} onKeyDown={e => e.key === "Enter" && addReaction("win", w.id, "comment", commentTexts[w.id])} />
                    <button onClick={() => addReaction("win", w.id, "comment", commentTexts[w.id])} style={{ padding: "4px 10px", borderRadius: 20, border: "none", background: "#5B9BD5", color: "white", cursor: "pointer", fontSize: 11, fontFamily: "Jost, sans-serif", fontWeight: 700 }}>→</button>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {tab === "goals" && (
        <div>
          {goals.length === 0 ? <div className="empty-state"><div className="empty-icon">🎯</div>No goals set yet</div>
            : goals.map(g => <div key={g.id} className="card" style={{ borderLeft: "3px solid #5B9BD5", marginBottom: 10 }}><div style={{ display: "flex", gap: 10 }}><span style={{ fontSize: 16 }}>🎯</span><div style={{ flex: 1, fontSize: 14, color: "var(--deep)", lineHeight: 1.5 }}>{g.goal_text}</div></div></div>)}
        </div>
      )}

      {tab === "announce" && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Send a Message to {client.name}</div>
            <div className="card-sub" style={{ marginBottom: 14 }}>This will appear on their home screen. Only they can see it.</div>
            <input className="form-input" placeholder="Title (e.g. This Week's Focus)" value={annTitle} onChange={e => setAnnTitle(e.target.value)} style={{ marginBottom: 8 }} />
            <textarea className="form-input form-textarea" placeholder="Write your message..." value={annBody} onChange={e => setAnnBody(e.target.value)} style={{ marginBottom: 10 }} />
            <button className="btn-primary" onClick={async () => {
              if (!annTitle.trim() || !annBody.trim()) return;
              const ann = { id: `ann_${Date.now()}`, title: annTitle.trim(), body: annBody.trim(), date: today(), audience: 'pt', recipient_id: client.id };
              setAnnSaved("saving");
              const ok = await saveAnnouncement(ann);
              if (ok) { setClientAnnouncements(prev => [ann, ...prev]); setAnnTitle(""); setAnnBody(""); }
              setAnnSaved(ok ? "saved" : "error");
              setTimeout(() => setAnnSaved(false), 2500);
            }}>{annSaved === "saving" ? "Sending..." : annSaved === "saved" ? "✓ Sent!" : annSaved === "error" ? "⚠️ Error" : "Send Message"}</button>
          </div>
          {clientAnnouncements.length === 0
            ? <div className="empty-state"><div className="empty-icon">📣</div>No messages sent yet</div>
            : clientAnnouncements.map(a => (
              <div key={a.id} className="card" style={{ marginBottom: 10, borderLeft: "3px solid #5B9BD5" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "var(--deep)", marginBottom: 2 }}>{a.title}</div>
                    <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.5 }}>{a.body}</div>
                    <div style={{ fontSize: 11, color: "var(--soft)", marginTop: 4 }}>{a.date}</div>
                  </div>
                  <button onClick={async () => { await deleteAnnouncement(a.id); setClientAnnouncements(prev => prev.filter(x => x.id !== a.id)); }} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 18, marginLeft: 8 }}>×</button>
                </div>
              </div>
            ))}
        </div>
      )}

      {tab === "manage" && (
        <div>
          {/* Check-in Items */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Custom Check-in Habits</div>
            <div className="card-sub" style={{ marginBottom: 12 }}>Set the habits this client tracks in their daily check-in</div>
            {checkItems.map(item => (
              <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ flex: 1, fontSize: 13 }}>✓ {item.label}</span>
                <button onClick={() => removeCheckItem(item.id)} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 16 }}>×</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <input className="form-input" placeholder="Add a habit..." value={newCheckItem} onChange={e => setNewCheckItem(e.target.value)} onKeyDown={e => e.key === "Enter" && addCheckItem()} style={{ flex: 1 }} />
              <button className="btn-forest" onClick={addCheckItem}>+</button>
            </div>
          </div>

          {/* Appointments */}
          <div className="card">
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Sessions & Appointments</div>
            <div className="card-sub" style={{ marginBottom: 12 }}>Upcoming sessions show on the client's home page</div>
            {appointments.filter(a => a.date >= today()).length === 0
              ? <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 12 }}>No upcoming sessions scheduled</div>
              : appointments.filter(a => a.date >= today()).map(a => (
                <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{new Date(a.date + 'T12:00:00').toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}{a.time && ` at ${a.time}`}</div>
                    {a.notes && <div style={{ fontSize: 11, color: "var(--soft)" }}>{a.notes}</div>}
                  </div>
                  <button onClick={() => removeAppt(a.id)} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 16 }}>×</button>
                </div>
              ))}
            <div style={{ marginTop: 14 }}>
              <div className="section-label">Add Session</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                <input type="date" className="form-input" value={apptDate} onChange={e => setApptDate(e.target.value)} style={{ flex: 1, minWidth: 140 }} />
                <input type="time" className="form-input" value={apptTime} onChange={e => setApptTime(e.target.value)} style={{ flex: 1, minWidth: 120 }} />
              </div>
              <input className="form-input" placeholder="Notes (optional)" value={apptNotes} onChange={e => setApptNotes(e.target.value)} style={{ marginTop: 8 }} />
              <button className="btn-primary" style={{ marginTop: 10 }} onClick={addAppointment}>{apptSaved === "saving" ? "Saving..." : apptSaved === "saved" ? "✓ Added!" : "Add Session"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Messaging ────────────────────────────────────────────────────────────────
function ChatWindow({ conversationId, currentUser, otherName, onBack }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!conversationId) return;
    // Mark this conversation as seen
    localStorage.setItem(`gt_msg_conv_${conversationId}`, new Date().toISOString());
    getMessages(conversationId).then(setMessages);
    const sub = supabase
      .channel(`chat_${conversationId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
        (payload) => {
          setMessages(prev => [...prev, payload.new]);
          localStorage.setItem(`gt_msg_conv_${conversationId}`, new Date().toISOString());
        })
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [conversationId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const msg = { id: `msg_${Date.now()}`, conversationId, senderId: currentUser.id, senderName: currentUser.name, body: text.trim() };
    setText("");
    await sendMessage(msg);
    setSending(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 400 }}>
      {onBack && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--soft)", padding: "4px 0" }}>←</button>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, color: "var(--deep)" }}>{otherName}</div>
        </div>
      )}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, marginBottom: 12, maxHeight: 420, padding: "4px 0" }}>
        {messages.length === 0 && <div style={{ fontSize: 13, color: "var(--soft)", textAlign: "center", marginTop: 40 }}>No messages yet — say hi! 👋</div>}
        {messages.map(m => {
          const isMe = m.sender_id === currentUser.id;
          return (
            <div key={m.id} style={{ display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start" }}>
              {!isMe && <div style={{ fontSize: 10, color: "var(--soft)", marginBottom: 2, marginLeft: 4 }}>{m.sender_name}</div>}
              <div style={{ maxWidth: "75%", padding: "10px 14px", borderRadius: isMe ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: isMe ? "var(--pink)" : "var(--bg)", color: isMe ? "white" : "var(--deep)", fontSize: 14, lineHeight: 1.4 }}>
                {m.body}
              </div>
              <div style={{ fontSize: 10, color: "var(--soft)", marginTop: 2, marginLeft: 4, marginRight: 4 }}>
                {new Date(m.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea
          className="form-input"
          placeholder="Type a message..."
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={2}
          style={{ flex: 1, resize: "none", fontSize: 14 }}
        />
        <button onClick={send} disabled={!text.trim() || sending} style={{ padding: "10px 18px", borderRadius: 20, border: "none", background: "var(--pink)", color: "white", cursor: "pointer", fontSize: 16, fontFamily: "Jost, sans-serif", fontWeight: 700, opacity: text.trim() ? 1 : 0.5 }}>→</button>
      </div>
    </div>
  );
}

function PTMessaging({ user }) {
  // PT client 1-on-1 with coach
  const coachId = "coach";
  const convId = `pt_dm_${[user.id, coachId].sort().join("_")}`;
  return (
    <div className="card">
      <div className="card-title">💬 Messages with Madison</div>
      <ChatWindow conversationId={convId} currentUser={user} otherName="Madison" />
    </div>
  );
}

function PTCoachMessaging({ user, clients }) {
  const [selectedClient, setSelectedClient] = useState(null);
  const [unreadConvs, setUnreadConvs] = useState({});
  const coachUser = { ...user, id: "coach" };

  useEffect(() => {
    if (clients.length === 0) return;
    // Fetch latest message per client conv and check against per-conv last-seen
    Promise.all(clients.map(async c => {
      const convId = `pt_dm_${[c.id, "coach"].sort().join("_")}`;
      const msgs = await getMessages(convId);
      const lastSeen = localStorage.getItem(`gt_msg_conv_${convId}`);
      const hasUnread = msgs.some(m => m.sender_id !== "coach" && (!lastSeen || m.created_at > lastSeen));
      return [c.id, hasUnread];
    })).then(results => setUnreadConvs(Object.fromEntries(results)));
  }, [clients]);

  if (selectedClient) {
    const convId = `pt_dm_${[selectedClient.id, "coach"].sort().join("_")}`;
    return (
      <div className="card">
        <ChatWindow conversationId={convId} currentUser={coachUser} otherName={selectedClient.name} onBack={() => { setSelectedClient(null); setUnreadConvs(prev => ({ ...prev, [selectedClient.id]: false })); }} />
      </div>
    );
  }

  const sorted = [...clients].sort((a, b) => (unreadConvs[b.id] ? 1 : 0) - (unreadConvs[a.id] ? 1 : 0));

  return (
    <div>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 800, color: "var(--deep)", marginBottom: 6 }}>Messages</div>
      <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 16 }}>Select a client to open your conversation</div>
      {sorted.length === 0 && <div className="empty-state"><div className="empty-icon">💬</div>No clients yet</div>}
      {sorted.map(c => (
        <div key={c.id} className="card" style={{ marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderLeft: unreadConvs[c.id] ? "3px solid #E8647A" : undefined }} onClick={() => setSelectedClient(c)}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {unreadConvs[c.id] && <span style={{ width: 10, height: 10, background: "#E8647A", borderRadius: "50%", flexShrink: 0 }} />}
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "var(--deep)" }}>{c.name}</div>
              <div style={{ fontSize: 12, color: unreadConvs[c.id] ? "#E8647A" : "var(--soft)", fontWeight: unreadConvs[c.id] ? 700 : 400 }}>{unreadConvs[c.id] ? "New message!" : "Tap to open chat"}</div>
            </div>
          </div>
          <div style={{ fontSize: 20, color: "var(--soft)" }}>→</div>
        </div>
      ))}
    </div>
  );
}

function BCMessaging({ user, isCoach }) {
  const [view, setView] = useState("list");
  const [members, setMembers] = useState([]);
  const [unreadConvs, setUnreadConvs] = useState({});
  const coachId = "coach";
  const myId = isCoach ? coachId : user.id;
  const myUser = isCoach ? { ...user, id: coachId } : user;

  useEffect(() => {
    if (!isCoach) return;
    getMembers().then(async ms => {
      setMembers(ms);
      // Check unread for group + each member DM
      const groupLastSeen = localStorage.getItem(`gt_msg_conv_bc_group`);
      const groupMsgs = await getMessages("bc_group");
      const groupUnread = groupMsgs.some(m => m.sender_id !== "coach" && (!groupLastSeen || m.created_at > groupLastSeen));
      const dmResults = await Promise.all(ms.map(async m => {
        const convId = `bc_dm_${[coachId, m.id].sort().join("_")}`;
        const msgs = await getMessages(convId);
        const lastSeen = localStorage.getItem(`gt_msg_conv_${convId}`);
        return [m.id, msgs.some(msg => msg.sender_id !== "coach" && (!lastSeen || msg.created_at > lastSeen))];
      }));
      setUnreadConvs({ group: groupUnread, ...Object.fromEntries(dmResults) });
    });
  }, [isCoach]);

  const clearUnread = (key) => setUnreadConvs(prev => ({ ...prev, [key]: false }));

  if (view === "group") {
    return (
      <div className="card">
        <ChatWindow conversationId="bc_group" currentUser={myUser} otherName="Boot Camp Group Chat 💪" onBack={() => { setView("list"); clearUnread("group"); }} />
      </div>
    );
  }

  if (view.startsWith("dm_")) {
    const otherId = view.replace("dm_", "");
    const other = isCoach ? members.find(m => m.id === otherId) : { name: "Madison", id: coachId };
    const convId = `bc_dm_${[myId, otherId].sort().join("_")}`;
    return (
      <div className="card">
        <ChatWindow conversationId={convId} currentUser={myUser} otherName={other?.name || "Coach"} onBack={() => { setView("list"); clearUnread(otherId); }} />
      </div>
    );
  }

  const sortedMembers = [...members].sort((a, b) => (unreadConvs[b.id] ? 1 : 0) - (unreadConvs[a.id] ? 1 : 0));

  return (
    <div>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 800, color: "var(--deep)", marginBottom: 16 }}>Messages</div>
      {/* Group chat */}
      <div className="card" style={{ marginBottom: 10, cursor: "pointer", borderLeft: unreadConvs.group ? "3px solid #E8647A" : "3px solid var(--pink)", display: "flex", alignItems: "center", justifyContent: "space-between" }} onClick={() => setView("group")}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {unreadConvs.group && <span style={{ width: 10, height: 10, background: "#E8647A", borderRadius: "50%", flexShrink: 0 }} />}
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--deep)" }}>💪 Boot Camp Group Chat</div>
            <div style={{ fontSize: 12, color: unreadConvs.group ? "#E8647A" : "var(--soft)", fontWeight: unreadConvs.group ? 700 : 400 }}>{unreadConvs.group ? "New message!" : "Everyone in the camp"}</div>
          </div>
        </div>
        <div style={{ fontSize: 20, color: "var(--soft)" }}>→</div>
      </div>
      {/* 1-on-1 with coach (member view) or list of members (coach view) */}
      {!isCoach && (
        <div className="card" style={{ marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }} onClick={() => setView(`dm_${coachId}`)}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--deep)" }}>Madison (Coach)</div>
            <div style={{ fontSize: 12, color: "var(--soft)" }}>Private conversation</div>
          </div>
          <div style={{ fontSize: 20, color: "var(--soft)" }}>→</div>
        </div>
      )}
      {isCoach && sortedMembers.map(m => (
        <div key={m.id} className="card" style={{ marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderLeft: unreadConvs[m.id] ? "3px solid #E8647A" : undefined }} onClick={() => setView(`dm_${m.id}`)}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {unreadConvs[m.id] && <span style={{ width: 10, height: 10, background: "#E8647A", borderRadius: "50%", flexShrink: 0 }} />}
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "var(--deep)" }}>{m.name}</div>
              <div style={{ fontSize: 12, color: unreadConvs[m.id] ? "#E8647A" : "var(--soft)", fontWeight: unreadConvs[m.id] ? 700 : 400 }}>{unreadConvs[m.id] ? "New message!" : "Private message"}</div>
            </div>
          </div>
          <div style={{ fontSize: 20, color: "var(--soft)" }}>→</div>
        </div>
      ))}
    </div>
  );
}

// ─── Coach App Wrapper ────────────────────────────────────────────────────────
function CoachApp({ user, onLogout }) {
  const [coachMode, setCoachMode] = useState("bootcamp");
  if (coachMode === "pt") return <PTCoachDashboard user={user} onLogout={onLogout} onSwitchToBc={() => setCoachMode("bootcamp")} />;
  return <CoachDashboard user={user} onLogout={onLogout} onSwitchToPT={() => setCoachMode("pt")} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── ROOTED FOUNDATION ────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

const RF_PROMPTS = [
  "What is taking up the most space in my mind right now? What would it feel like to set it down, even just for today?",
  "What is one thing I can control today and one thing I need to let go of?",
  "Where in my body am I holding tension right now? What do I think it's connected to?",
  "What has been draining my cup lately? What can I do about it?",
  "What story am I telling myself right now that might not be true?",
  "Who was I today? Who do I want to be tomorrow?",
  "What did I do today that I'm proud of, even if it was small?",
  "What triggers me and why? What does that tell me about myself?",
  "What do I need more of right now — and what do I need less of?",
  "How did I show up for myself today?",
  "What are three things I'm grateful for that I usually overlook?",
  "What's a win from this week that I haven't given myself credit for?",
  "Who in my life fills my cup and have I told them lately?",
  "What is something about my body I'm grateful for today?",
  "What made me smile today, even briefly?",
];

const CUP_FILLER_DEFAULTS = ["Working out","Meditating","Journaling","Morning coffee","Warm bath","Reading","Listening to music","Dancing","Singing","Taking a class","Making art","Walking in nature","Hiking","Running"];

const RF_CHECKIN_ITEMS = [
  { key: "water",    label: "Drank 80oz of water or more", Icon: Drop,      info: "Water is the foundation of everything — energy, digestion, mood, skin, recovery. Most people are chronically dehydrated without knowing it. 80oz is your daily baseline." },
  { key: "sleep",    label: "Got 7-8 hours of sleep",       Icon: Moon,      info: "Sleep is when your body repairs itself, your hormones reset, and your brain processes the day. Skimping on sleep makes everything harder — workouts, food choices, mood, focus. 7-8 hours is non-negotiable." },
  { key: "meditate", label: "Meditated for 3+ minutes",     Icon: Wind,      info: "Meditation trains your nervous system to slow down. Even 3 minutes a day builds your capacity to respond instead of react, reduces anxiety, and improves focus over time. Start small and build." },
  { key: "movement", label: "Completed body movement",      Icon: Waves,     info: "Movement doesn't have to mean a full workout. A walk counts. Stretching counts. Dancing in your kitchen counts. The goal is to move your body intentionally every single day." },
  { key: "journal",  label: "Journaled",                    Icon: Notebook,  info: "Journaling is how you process what's happening inside — not just what happened, but how you felt about it. It builds self-awareness, reduces stress, and helps you notice patterns you'd otherwise miss." },
  { key: "nutrition",label: "Followed my nutrition basics", Icon: Leaf,      info: "This isn't about being perfect. It's about building a consistent relationship with food that fuels your body. Protein, fat, carbs, and vegetables at every meal. Eat slowly. Stop at 80% full." },
  { key: "win",      label: "Shared a win or gratitude",    Icon: Sparkle,   info: "Community accountability changes everything. Sharing your wins — no matter how small — rewires your brain to notice progress. Gratitude does the same. This habit builds momentum." },
];

// Builds the effective daily check-in list for a client: the shared defaults
// minus any the coach turned off, plus any custom habits the coach added.
function buildCheckinItems(prefs) {
  const disabled = prefs?.disabled_checkin_items || [];
  const custom = prefs?.custom_checkin_items || [];

  // An entry whose key matches a built-in habit is a rename of that habit;
  // any other entry is a brand-new habit the coach added.
  const renames = {};
  const additions = [];
  custom.forEach(c => {
    if (RF_CHECKIN_ITEMS.some(d => d.key === c.key)) renames[c.key] = c.label;
    else additions.push(c);
  });

  return [
    ...RF_CHECKIN_ITEMS
      .filter(i => !disabled.includes(i.key))
      .map(i => (renames[i.key] ? { ...i, label: renames[i.key] } : i)),
    ...additions.filter(c => !disabled.includes(c.key)).map(c => ({
      key: c.key,
      label: c.label,
      Icon: Sparkle,
      info: c.info || "A personalized habit your coach set just for you.",
      custom: true,
    })),
  ];
}

// Every habit a client has ever had, including removed ones, so historical
// check-ins can still be labelled after a habit is taken off their list.
function allKnownCheckinItems(prefs) {
  const custom = prefs?.custom_checkin_items || [];
  const additions = custom.filter(c => !RF_CHECKIN_ITEMS.some(d => d.key === c.key));
  const renames = {};
  custom.forEach(c => { if (RF_CHECKIN_ITEMS.some(d => d.key === c.key)) renames[c.key] = c.label; });
  return [
    ...RF_CHECKIN_ITEMS.map(i => (renames[i.key] ? { ...i, label: renames[i.key] } : i)),
    ...additions.map(c => ({ key: c.key, label: c.label, custom: true })),
  ];
}

const CUP_FILLER_INFO = {
  title: "What are Cup Fillers?",
  body: `Your cup represents your energy, joy, and capacity to show up for yourself and others. Cup fillers are the activities and experiences that restore you — the things that leave you feeling recharged, lighter, or more like yourself.\n\nWhen your cup is full, everything is easier. You're more patient, more present, more motivated. When it's empty, everything feels harder.\n\nThe practice: identify at least 5 things that fill your cup, and check in with yourself daily about whether you've done any of them. This isn't self-indulgence — it's maintenance. A full cup is the only one worth pouring from.`,
};

const CHECKIN_WALKTHROUGH = [
  { Icon: Leaf,     title: "Your Daily Check-In",  body: "Every day, you'll check in with 7 simple habits. These aren't rules — they're anchors. Small, consistent actions that compound into real change over time. You don't have to be perfect. You just have to show up." },
  { Icon: Drop,     title: "Hydration",             body: "80oz of water a day is your baseline. Most people are dehydrated without knowing it — and dehydration looks like fatigue, brain fog, hunger, and headaches. Start your morning with a full glass before anything else." },
  { Icon: Moon,     title: "Sleep",                 body: "7-8 hours is not a luxury. It's when your body repairs, your hormones reset, and your brain catches up. Poor sleep undermines every other healthy habit. Protect it like it matters — because it does." },
  { Icon: Wind,     title: "Meditation",            body: "Start with 3 minutes. That's it. Apps like Insight Timer, Calm, and Headspace are in your Resources tab. Meditation isn't about emptying your mind — it's about noticing it. That awareness changes everything." },
  { Icon: Waves,    title: "Body Movement",         body: "This can be a full workout, a walk, mobility work, dancing, or anything that gets you moving intentionally. The goal isn't intensity — it's consistency. Move your body every day in a way that feels good." },
  { Icon: Notebook, title: "Journaling",            body: "Your Home tab has a daily reflection prompt waiting for you. Write freely — there are no wrong answers. Journaling builds self-awareness in a way nothing else can. Even a few sentences counts." },
  { Icon: Acorn,    title: "Nutrition Basics",      body: "The Nutrition tab has everything you need — a simple meal formula, food group guides, and an optional food diary. No calorie counting. Just protein + fat + carb + veggies at each meal, eaten slowly, to 80% full." },
  { Icon: Sparkle,  title: "Wins & Gratitude",      body: "Sharing a win or a gratitude in the Community tab every day rewires your brain toward progress. It doesn't have to be big. 'I drank my water today' is a win. 'I'm grateful for my morning coffee' counts. Post it." },
  { Icon: Coffee,   title: "Cup Fillers",           body: "These are YOUR things — the activities that restore you. Working out, reading, music, a bath, a walk — whatever fills you up. You'll set up your personal list in the check-in. Aim to do at least one every day." },
];

function AppSkeleton({ color }) {
  const pulse = `
    @keyframes skel-pulse {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 1; }
    }
    .skel { background: currentColor; border-radius: 8px; animation: skel-pulse 1.6s ease-in-out infinite; }
  `;
  const bar = (w, h, mb = 10, delay = 0) => (
    <div className="skel" style={{ width: w, height: h, marginBottom: mb, animationDelay: `${delay}s`, color: color + "22" }} />
  );
  return (
    <div style={{ padding: "0 20px", paddingTop: 80, maxWidth: 480, margin: "0 auto" }}>
      <style>{pulse}</style>
      {bar("60%", 12, 8, 0)}
      {bar("40%", 10, 28, 0.1)}
      <div style={{ background: color + "08", borderRadius: 14, padding: 20, marginBottom: 16 }}>
        {bar("80%", 12, 12, 0.15)}
        {bar("100%", 10, 8, 0.2)}
        {bar("70%", 10, 0, 0.25)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        {[0.3, 0.4].map((d, i) => (
          <div key={i} style={{ background: color + "08", borderRadius: 14, padding: 16 }}>
            {bar("60%", 11, 10, d)}
            {bar("100%", 9, 6, d + 0.05)}
            {bar("100%", 9, 6, d + 0.1)}
            {bar("80%", 9, 0, d + 0.15)}
          </div>
        ))}
      </div>
      <div style={{ background: color + "08", borderRadius: 14, padding: 20 }}>
        {bar("50%", 12, 12, 0.5)}
        {bar("100%", 9, 8, 0.55)}
        {bar("100%", 9, 8, 0.6)}
        {bar("60%", 9, 0, 0.65)}
      </div>
    </div>
  );
}

function InfoPopup({ title, body, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 600, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: "0 0 0 0" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: "20px 20px 0 0", padding: "28px 24px 40px", width: "100%", maxWidth: 480, maxHeight: "70vh", overflowY: "auto" }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, marginBottom: 12 }}>{title}</div>
        <div style={{ fontSize: 14, color: "var(--mid)", lineHeight: 1.8, whiteSpace: "pre-line" }}>{body}</div>
        <button onClick={onClose} style={{ marginTop: 20, width: "100%", padding: 13, background: "var(--bg)", border: "none", borderRadius: 12, fontFamily: "'Jost', sans-serif", fontWeight: 700, fontSize: 14, color: "var(--soft)", cursor: "pointer" }}>Got it</button>
      </div>
    </div>
  );
}

function WalkthroughScreen({ onDone, rfColor }) {
  const [step, setStep] = useState(0);
  const item = CHECKIN_WALKTHROUGH[step];
  const isLast = step === CHECKIN_WALKTHROUGH.length - 1;
  return (
    <div style={{ position: "fixed", inset: 0, background: "linear-gradient(160deg, #F7F4F0 0%, #EDE8E0 100%)", zIndex: 400, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 28 }}>
      <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
        <item.Icon size={64} weight="duotone" color={rfColor} style={{ marginBottom: 20 }} />
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 700, color: rfColor, marginBottom: 14 }}>{item.title}</div>
        <div style={{ fontSize: 15, color: "var(--mid)", lineHeight: 1.8, marginBottom: 32 }}>{item.body}</div>
        <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 28 }}>
          {CHECKIN_WALKTHROUGH.map((_, i) => (
            <div key={i} style={{ width: i === step ? 20 : 7, height: 7, borderRadius: 4, background: i === step ? rfColor : rfColor + "40", transition: "all 0.3s" }} />
          ))}
        </div>
        <button onClick={() => isLast ? onDone() : setStep(s => s + 1)} style={{ width: "100%", padding: 16, background: rfColor, color: "white", border: "none", borderRadius: 14, fontFamily: "'Jost', sans-serif", fontSize: 16, fontWeight: 700, cursor: "pointer" }}>
          {isLast ? "Let's go! →" : "Next →"}
        </button>
        {step > 0 && <button onClick={() => setStep(s => s - 1)} style={{ marginTop: 12, background: "none", border: "none", color: "var(--soft)", fontFamily: "'Jost', sans-serif", fontSize: 13, cursor: "pointer" }}>← Back</button>}
        {!isLast && <button onClick={onDone} style={{ display: "block", marginTop: 10, background: "none", border: "none", color: "var(--soft)", fontFamily: "'Jost', sans-serif", fontSize: 13, cursor: "pointer" }}>Skip walkthrough</button>}
      </div>
    </div>
  );
}

function RootedWelcome({ onDone }) {
  const [checked, setChecked] = useState(false);
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 28, background: "linear-gradient(160deg, #F7F4F0 0%, #EDE8E0 100%)" }}>
      <style>{styles}</style>
      <div style={{ maxWidth: 480, width: "100%" }}>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 28, fontWeight: 800, color: "#3D5A47", marginBottom: 6 }}>Welcome to The Rooted Foundation</div>
        <div style={{ fontStyle: "italic", fontSize: 14, color: "#9CAF88", marginBottom: 24 }}>Your new beginning. Your becoming.</div>
        <div style={{ fontSize: 15, color: "#4A5568", lineHeight: 1.9, marginBottom: 28 }}>
          <p style={{ marginBottom: 16 }}>This is the start of something that has always been waiting for you — the version of yourself you can already see. The human, the soul, you know lives within you. The one who has been patient. Or maybe not so patient.</p>
          <p style={{ marginBottom: 16 }}>This is where that person finally gets to show up.</p>
          <p style={{ marginBottom: 16 }}>Here, the goals you've been carrying — the ones you've written down, thought about, and quietly set aside — finally have a place to land. This is where taking care of yourself stops being an afterthought and becomes your non-negotiable. Where the oxygen mask goes on you first, not because you're selfish, but because you understand that a full cup is the only one worth pouring from.</p>
          <p style={{ marginBottom: 16 }}>This isn't a program you do for thirty days and abandon. It's not a diet, a quick fix, or a challenge with an end date. It's the foundation of a life you actually want to live — built one small, intentional habit at a time.</p>
          <p style={{ marginBottom: 16 }}>The more love and care you give yourself, the more you have to give to the people, the work, and the world that matter most to you.</p>
          <p style={{ fontWeight: 700, color: "#3D5A47" }}>You're not starting over. You're starting from a place of intention.</p>
          <p style={{ fontWeight: 700, color: "#3D5A47", marginTop: 8 }}>Welcome home.</p>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", marginBottom: 24 }}>
          <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ width: 20, height: 20, accentColor: "#9CAF88" }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: "#3D5A47" }}>I have read this and I am ready to move forward.</span>
        </label>
        <button onClick={onDone} disabled={!checked} style={{ width: "100%", padding: "16px", background: checked ? "#9CAF88" : "#ccc", color: "white", border: "none", borderRadius: 14, fontFamily: "Jost, sans-serif", fontSize: 16, fontWeight: 700, cursor: checked ? "pointer" : "not-allowed" }}>
          Begin →
        </button>
      </div>
    </div>
  );
}

function RootedApp({ user, onLogout, isPreview = false }) {
  const [tab, setTab] = useState("home");
  const [prefs, setPrefs] = useState(null);
  const RF_COLOR = "#9CAF88";

  const rfLs = (k) => `rf_${user.id}_${k}`;

  useEffect(() => {
    getUserPrefs(user.id).then(p => {
      const merged = {
        ...(p || {}),
        welcome_seen:     (p?.welcome_seen)     || localStorage.getItem(rfLs('welcome_seen'))     === '1',
        walkthrough_seen: (p?.walkthrough_seen) || localStorage.getItem(rfLs('walkthrough_seen')) === '1',
      };
      setPrefs(merged);
    });
  }, [user.id]);

  const handleWelcomeDone = async () => {
    localStorage.setItem(rfLs('welcome_seen'), '1');
    await saveUserPrefs(user.id, { welcome_seen: true });
    setPrefs(p => ({ ...p, welcome_seen: true }));
  };

  const handleWalkthroughDone = async () => {
    localStorage.setItem(rfLs('walkthrough_seen'), '1');
    await saveUserPrefs(user.id, { walkthrough_seen: true });
    setPrefs(p => ({ ...p, walkthrough_seen: true }));
  };

  if (!prefs) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}><style>{styles}</style><AppSkeleton color={RF_COLOR} /></div>;

  const trialExpired = !isPreview && user.created_at && (Date.now() - new Date(user.created_at).getTime()) > 30 * 24 * 60 * 60 * 1000;
  if (trialExpired) return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, background: "linear-gradient(160deg, #F7F4F0 0%, #EDE8E0 100%)", textAlign: "center" }}>
      <style>{styles}</style>
      <Leaf size={48} color="#9CAF88" weight="duotone" style={{ marginBottom: 20 }} />
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: "#3D5A47", marginBottom: 10 }}>Your free trial has ended</div>
      <p style={{ fontSize: 15, color: "var(--mid)", lineHeight: 1.7, maxWidth: 340, marginBottom: 28 }}>
        We hope these 30 days gave you a taste of what's possible. To continue your journey and keep access to The Rooted Foundation, reach out to Madison to set up your membership.
      </p>
      <a href="mailto:madisonbelmondo@gmail.com" style={{ display: "inline-block", padding: "14px 28px", background: "#9CAF88", color: "white", borderRadius: 8, fontFamily: "'Jost', sans-serif", fontWeight: 700, fontSize: 15, textDecoration: "none", marginBottom: 16 }}>
        Contact Madison
      </a>
      <button onClick={onLogout} style={{ background: "none", border: "none", color: "var(--soft)", fontFamily: "'Jost', sans-serif", fontSize: 13, cursor: "pointer" }}>Sign out</button>
    </div>
  );

  if (!prefs.welcome_seen) return <RootedWelcome onDone={handleWelcomeDone} />;
  if (!prefs.walkthrough_seen) return <><style>{styles}</style><WalkthroughScreen onDone={handleWalkthroughDone} rfColor={RF_COLOR} /></>;
  if (!prefs.cup_fillers_setup) return <CupFillerSetup color={RF_COLOR} onDone={async (selected) => {
    for (const label of selected) {
      const f = { id: `cf_${user.id}_${Date.now()}_${Math.random().toString(36).slice(2)}`, userId: user.id, label, sortOrder: 0 };
      await saveCupFiller(f);
    }
    await saveUserPrefs(user.id, { cup_fillers_setup: true });
    setPrefs(p => ({ ...p, cup_fillers_setup: true }));
  }} />;

  const RF_TABS = [
    { id: "home",      label: "Home",      Icon: House },
    { id: "community", label: "Community", Icon: Leaf },
    { id: "nutrition", label: "Nutrition", Icon: Acorn },
    { id: "resources", label: "Resources", Icon: Book },
  ];

  return (
    <div className="app-shell">
      <style>{styles}</style>
      <div className="top-bar" style={{ borderBottom: `1px solid ${RF_COLOR}30` }}>
        <div>
          <div className="top-bar-title" style={{ color: RF_COLOR }}>The Rooted Foundation</div>
          <div className="top-bar-sub">{user.name}</div>
        </div>
        <button className="btn-ghost" onClick={onLogout} style={{ fontSize: 11 }}>Sign out</button>
      </div>
      <div className="bottom-nav" style={{ borderBottom: `1px solid ${RF_COLOR}30` }}>
        {RF_TABS.map(t => (
          <div key={t.id} className={`nav-item ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)} style={{ color: tab === t.id ? RF_COLOR : undefined }}>
            <t.Icon size={20} weight={tab === t.id ? "fill" : "regular"} />
            {t.label}
          </div>
        ))}
      </div>
      <div className="page-content">
        {tab === "home"      && <RootedHome user={user} rfColor={RF_COLOR} />}
        {tab === "community" && <RootedCommunity user={user} rfColor={RF_COLOR} />}
        {tab === "nutrition" && <RootedNutrition user={user} rfColor={RF_COLOR} />}
        {tab === "resources" && <RootedResources user={user} rfColor={RF_COLOR} />}
      </div>
    </div>
  );
}

function RootedHome({ user, rfColor }) {
  const [selectedDate, setSelectedDate] = useState(today());
  const promptIdx = Math.floor(Date.now() / 86400000) % RF_PROMPTS.length;
  const prompt = RF_PROMPTS[promptIdx];

  const [journalEntry, setJournalEntry] = useState(null);
  const [journalText, setJournalText] = useState("");
  const [checks, setChecks] = useState({});
  const [mood, setMood] = useState("");
  const [energy, setEnergy] = useState("");
  const [cupFillers, setCupFillers] = useState([]);
  const [showCupPopup, setShowCupPopup] = useState(false);
  const [newCupLabel, setNewCupLabel] = useState("");
  const [cupSetupDone, setCupSetupDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [checkItems, setCheckItems] = useState(RF_CHECKIN_ITEMS);

  useEffect(() => {
    const load = async () => {
      const [entry, fillers, prefs] = await Promise.all([
        getJournalEntry(user.id, selectedDate),
        getCupFillers(user.id),
        getUserPrefs(user.id),
      ]);
      // Reset fields when date changes
      setJournalText("");
      setChecks({});
      setMood("");
      setEnergy("");
      if (entry) {
        setJournalEntry(entry);
        setJournalText(entry.response || "");
        setChecks(entry.checks || {});
        setMood(entry.mood || "");
        setEnergy(entry.energy || "");
      }
      setCupFillers(fillers);
      setCheckItems(buildCheckinItems(prefs));
      setCupSetupDone(!!prefs.cup_fillers_setup);
    };
    load();
  }, [user.id, selectedDate]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    const { data } = await supabase.from('journal_entries').select('*').eq('user_id', user.id).not('date', 'like', 'nd_%').order('date', { ascending: false }).limit(30);
    setHistory(data || []);
    setHistoryLoading(false);
  };

  const toggleCheck = (key) => setChecks(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleCupCheck = async (filler) => {
    const newVal = !filler.is_checked;
    setCupFillers(prev => prev.map(f => f.id === filler.id ? { ...f, is_checked: newVal } : f));
    await updateCupFiller(filler.id, { is_checked: newVal });
  };

  const addCupFiller = async (label) => {
    if (!label.trim()) return;
    const newFiller = { id: `cf_${user.id}_${Date.now()}`, userId: user.id, label: label.trim(), sortOrder: cupFillers.length };
    await saveCupFiller(newFiller);
    setCupFillers(prev => [...prev, { ...newFiller, is_checked: false }]);
    setNewCupLabel("");
  };

  const removeCupFiller = async (id) => {
    await deleteCupFiller(id);
    setCupFillers(prev => prev.filter(f => f.id !== id));
  };

  const handleCupPopupDone = async (selected) => {
    for (const label of selected) { await addCupFiller(label); }
    await saveUserPrefs(user.id, { cup_fillers_setup: true });
    setCupSetupDone(true);
    setShowCupPopup(false);
  };

  const saveAll = async () => {
    setSaving(true);
    await saveJournalEntry(user.id, selectedDate, { response: journalText, checks, mood, energy, prompt_text: prompt });
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const [infoPopup, setInfoPopup] = useState(null);
  const completedCount = Object.values(checks).filter(Boolean).length + cupFillers.filter(f => f.is_checked).length;
  const totalCount = checkItems.length + 1;

  const MOOD_OPTIONS = [
    { value: "fantastic", label: "Fantastic" },
    { value: "good",      label: "Good" },
    { value: "okay",      label: "Okay" },
    { value: "low",       label: "Low" },
    { value: "struggling",label: "Struggling" },
  ];
  const ENERGY_OPTIONS = [
    { value: "high",    label: "High" },
    { value: "medium",  label: "Medium" },
    { value: "low",     label: "Low" },
    { value: "drained", label: "Drained" },
  ];

  return (
    <div>
      {infoPopup && <InfoPopup title={infoPopup.title} body={infoPopup.body} onClose={() => setInfoPopup(null)} />}

      {/* Date picker */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: rfColor, textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap" }}>Log for:</label>
        <input type="date" className="form-input" value={selectedDate} max={today()} onChange={e => setSelectedDate(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
        {selectedDate !== today() && <button onClick={() => setSelectedDate(today())} style={{ fontSize: 11, background: "none", border: `1px solid ${rfColor}`, borderRadius: 8, color: rfColor, padding: "6px 10px", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "Jost, sans-serif", fontWeight: 700 }}>Today</button>}
      </div>
      {selectedDate !== today() && <div style={{ fontSize: 12, color: rfColor, fontWeight: 700, marginBottom: 12, background: rfColor + "12", borderRadius: 8, padding: "8px 12px" }}>📅 Logging for {new Date(selectedDate + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>}

      {/* Mood + Energy */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, marginBottom: 16 }}>How are you today?</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--soft)", fontWeight: 700, marginBottom: 8 }}>Feeling</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {MOOD_OPTIONS.map(o => (
              <button key={o.value} onClick={() => setMood(o.value)} style={{ padding: "7px 14px", borderRadius: 6, border: `1.5px solid ${mood === o.value ? rfColor : "var(--border)"}`, background: mood === o.value ? rfColor + "18" : "transparent", color: mood === o.value ? rfColor : "var(--mid)", fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{o.label}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--soft)", fontWeight: 700, marginBottom: 8 }}>Energy Level</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {ENERGY_OPTIONS.map(o => (
              <button key={o.value} onClick={() => setEnergy(o.value)} style={{ padding: "7px 14px", borderRadius: 6, border: `1.5px solid ${energy === o.value ? rfColor : "var(--border)"}`, background: energy === o.value ? rfColor + "18" : "transparent", color: energy === o.value ? rfColor : "var(--mid)", fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{o.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Journal Prompt */}
      <div className="card" style={{ borderLeft: `3px solid ${rfColor}`, marginBottom: 16 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: rfColor, fontWeight: 700, marginBottom: 8 }}>Today's Reflection</div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 16, color: "var(--deep)", lineHeight: 1.7, marginBottom: 12 }}>"{prompt}"</div>
        <textarea className="form-input form-textarea" placeholder="Write your thoughts here..." value={journalText} onChange={e => setJournalText(e.target.value)} style={{ minHeight: 100 }} />
      </div>

      {/* Check-in + Cup Fillers side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16, alignItems: "start" }}>

        {/* Daily Check-In */}
        <div className="card" style={{ margin: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 700 }}>Daily Check-In</div>
            <div style={{ color: rfColor, fontWeight: 700, fontSize: 12 }}>{Object.values(checks).filter(Boolean).length} / {checkItems.length}</div>
          </div>
          {checkItems.map(item => (
            <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0", borderBottom: "1px solid var(--border)" }}>
              <input type="checkbox" checked={!!checks[item.key]} onChange={() => toggleCheck(item.key)} style={{ width: 16, height: 16, accentColor: rfColor, flexShrink: 0 }} />
              <item.Icon size={14} weight="regular" color={rfColor} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, color: "var(--deep)", fontFamily: "'Jost', sans-serif" }}>{item.label}</span>
              <button onClick={() => setInfoPopup({ title: item.label, body: item.info })} style={{ background: "none", border: "1px solid var(--border)", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--soft)", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>i</button>
            </div>
          ))}
        </div>

        {/* Cup Fillers */}
        <div className="card" style={{ margin: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Coffee size={15} color={rfColor} />
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 700, color: "var(--deep)" }}>Cup Fillers</span>
              <button onClick={() => setInfoPopup({ title: CUP_FILLER_INFO.title, body: CUP_FILLER_INFO.body })} style={{ background: "none", border: "1px solid var(--border)", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--soft)", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>i</button>
            </div>
            <div style={{ color: rfColor, fontWeight: 700, fontSize: 12 }}>{cupFillers.filter(f => f.is_checked).length} / {cupFillers.length}</div>
          </div>
          {cupFillers.map(f => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0", borderBottom: "1px solid var(--border)" }}>
              <input type="checkbox" checked={!!f.is_checked} onChange={() => toggleCupCheck(f)} style={{ width: 16, height: 16, accentColor: rfColor, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, color: "var(--mid)" }}>{f.label}</span>
              <span onClick={() => removeCupFiller(f.id)} style={{ color: "var(--soft)", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</span>
            </div>
          ))}
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            <input className="form-input" value={newCupLabel} onChange={e => setNewCupLabel(e.target.value)} placeholder="Add one…" style={{ flex: 1, fontSize: 12, padding: "7px 10px" }} onKeyDown={e => e.key === "Enter" && addCupFiller(newCupLabel)} />
            <button onClick={() => addCupFiller(newCupLabel)} style={{ padding: "7px 12px", background: rfColor, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 14 }}>+</button>
          </div>
        </div>

      </div>

      <button className="btn-primary" onClick={saveAll} style={{ width: "100%", background: rfColor, borderRadius: 8, fontFamily: "'Jost', sans-serif" }} disabled={saving}>
        {saving ? "Saving..." : saved ? "Saved" : "Save Today's Check-In"}
      </button>

      {/* Past Days History */}
      <div style={{ marginTop: 24 }}>
        <button onClick={() => { setShowHistory(h => { if (!h) loadHistory(); return !h; }); }} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: "none", border: "none", cursor: "pointer", padding: "12px 0", borderTop: "1px solid var(--border)" }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 700, color: "var(--deep)" }}>Past Days</div>
          <span style={{ color: "var(--soft)", fontSize: 13 }}>{showHistory ? "▲ Hide" : "▼ Show"}</span>
        </button>
        {showHistory && (
          <div>
            {historyLoading && <div style={{ color: "var(--soft)", fontSize: 13, padding: "12px 0" }}>Loading...</div>}
            {!historyLoading && history.length === 0 && <div style={{ color: "var(--soft)", fontSize: 13, padding: "12px 0" }}>No past entries yet — save today's check-in to get started.</div>}
            {history.filter(e => e.date !== selectedDate).map(e => (
              <details key={e.date} style={{ borderBottom: "1px solid var(--border)", padding: "10px 0" }}>
                <summary style={{ cursor: "pointer", fontFamily: "'Jost', sans-serif", fontSize: 14, fontWeight: 600, color: "var(--deep)", listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>{new Date(e.date + 'T12:00:00').toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>
                  <span style={{ fontSize: 12, color: rfColor, fontWeight: 600 }}>
                    {e.checks ? Object.values(e.checks).filter(Boolean).length : 0}/{checkItems.length} habits
                  </span>
                </summary>
                <div style={{ paddingTop: 10 }}>
                  {(e.mood || e.energy) && (
                    <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                      {e.mood   && <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 4, background: rfColor + "15", color: rfColor, fontWeight: 600 }}>Feeling: {e.mood}</span>}
                      {e.energy && <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 4, background: rfColor + "15", color: rfColor, fontWeight: 600 }}>Energy: {e.energy}</span>}
                    </div>
                  )}
                  {e.prompt_text && <div style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 14, color: "var(--mid)", marginBottom: 6, lineHeight: 1.5 }}>"{e.prompt_text}"</div>}
                  {e.response && <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.6, marginBottom: 8, whiteSpace: "pre-line" }}>{e.response}</div>}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {checkItems.map(item => (
                      <span key={item.key} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, background: e.checks?.[item.key] ? rfColor + "20" : "var(--bg)", color: e.checks?.[item.key] ? rfColor : "var(--soft)", fontWeight: 600 }}>
                        {e.checks?.[item.key] ? "✓" : "–"} {item.label}
                      </span>
                    ))}
                  </div>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CupFillerSetup({ color, onDone }) {
  const [selected, setSelected] = useState([]);
  const [custom, setCustom] = useState("");
  const [extras, setExtras] = useState([]);
  const toggle = (label) => setSelected(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);
  const addCustom = () => {
    if (!custom.trim()) return;
    setExtras(e => [...e, custom.trim()]);
    setSelected(s => [...s, custom.trim()]);
    setCustom("");
  };
  const allSelected = [...selected];
  const canContinue = allSelected.length >= 1;
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px", background: "var(--bg)" }}>
      <style>{styles}</style>
      <Coffee size={44} weight="duotone" color={color} style={{ marginBottom: 16 }} />
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: "var(--deep)", marginBottom: 8, textAlign: "center" }}>What fills your cup?</div>
      <p style={{ fontSize: 14, color: "var(--soft)", marginBottom: 24, textAlign: "center", maxWidth: 340, lineHeight: 1.7 }}>
        These are the things that restore you. Pick at least one to get started — you can always add more later.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20, maxWidth: 420, justifyContent: "center" }}>
        {[...CUP_FILLER_DEFAULTS, ...extras].map(l => (
          <button key={l} onClick={() => toggle(l)} style={{ padding: "9px 16px", borderRadius: 20, border: `2px solid ${selected.includes(l) ? color : "var(--border)"}`, background: selected.includes(l) ? color + "18" : "var(--card)", color: selected.includes(l) ? color : "var(--mid)", fontFamily: "Jost, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.12s" }}>{l}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 28, width: "100%", maxWidth: 420 }}>
        <input className="form-input" value={custom} onChange={e => setCustom(e.target.value)} placeholder="Add your own…" onKeyDown={e => e.key === "Enter" && addCustom()} style={{ flex: 1, fontSize: 13 }} />
        <button onClick={addCustom} style={{ padding: "0 16px", background: color, color: "white", border: "none", borderRadius: 10, fontFamily: "Jost, sans-serif", fontWeight: 700, cursor: "pointer", fontSize: 18 }}>+</button>
      </div>
      <button onClick={() => canContinue && onDone(allSelected)} style={{ width: "100%", maxWidth: 420, padding: 15, background: canContinue ? color : "var(--border)", color: canContinue ? "white" : "var(--soft)", border: "none", borderRadius: 14, fontFamily: "Jost, sans-serif", fontSize: 15, fontWeight: 700, cursor: canContinue ? "pointer" : "not-allowed", transition: "all 0.15s" }}>
        {canContinue ? `Let's go — ${allSelected.length} filler${allSelected.length > 1 ? "s" : ""} added` : "Pick at least one to continue"}
      </button>
    </div>
  );
}

function RootedCommunity({ user, rfColor }) {
  const [posts, setPosts] = useState([]);
  const [hearts, setHearts] = useState({});
  const [filter, setFilter] = useState("all");
  const [type, setType] = useState("win");
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const data = await getCommunityPosts(['rooted']);
      const filtered = data.filter(p => p.type === 'win' || p.type === 'gratitude');
      setPosts(filtered);
      if (filtered.length > 0) {
        const h = await getCommunityHearts(filtered.map(p => p.id));
        const map = {};
        h.forEach(hr => { if (!map[hr.post_id]) map[hr.post_id] = []; map[hr.post_id].push(hr.user_id); });
        setHearts(map);
      }
      setLoading(false);
    };
    load();
  }, []);

  const handlePost = async () => {
    if (!content.trim()) return;
    setPosting(true);
    const post = { id: `cp_${user.id}_${Date.now()}`, userId: user.id, userName: user.name, program: 'rooted', type, content: content.trim() };
    const ok = await saveCommunityPost(post);
    if (ok) { setPosts(prev => [{ ...post, created_at: new Date().toISOString() }, ...prev]); setContent(""); }
    setPosting(false);
  };

  const handleHeart = async (postId) => {
    const result = await toggleCommunityHeart(postId, user.id);
    setHearts(prev => {
      const cur = prev[postId] || [];
      return { ...prev, [postId]: result === 'added' ? [...cur, user.id] : cur.filter(id => id !== user.id) };
    });
  };

  const visible = posts.filter(p => filter === "all" || p.type === filter);

  return (
    <div>
      {/* New Post */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Share with the community</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {[["win","Win"],["gratitude","Gratitude"]].map(([v,l]) => (
            <button key={v} onClick={() => setType(v)} style={{ flex: 1, padding: "9px", borderRadius: 6, border: `1.5px solid ${type === v ? rfColor : "var(--border)"}`, fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 700, cursor: "pointer", background: type === v ? rfColor + "18" : "transparent", color: type === v ? rfColor : "var(--soft)" }}>{l}</button>
          ))}
        </div>
        <textarea className="form-input form-textarea" value={content} onChange={e => setContent(e.target.value)} placeholder={type === "win" ? "Share your win, big or small..." : "What are you grateful for today?"} style={{ minHeight: 80, marginBottom: 10 }} />
        <button onClick={handlePost} disabled={posting || !content.trim()} style={{ background: rfColor, color: "white", border: "none", borderRadius: 10, padding: "11px 20px", fontFamily: "Jost, sans-serif", fontWeight: 700, cursor: "pointer" }}>
          {posting ? "Posting..." : "Post"}
        </button>
      </div>

      {/* Filter */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {[["all","All"],["win","Wins"],["gratitude","Gratitudes"]].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{ padding: "7px 14px", borderRadius: 20, border: "1px solid var(--border)", background: filter === v ? rfColor : "white", color: filter === v ? "white" : "var(--soft)", fontFamily: "Jost, sans-serif", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{l}</button>
        ))}
      </div>

      {loading && <div className="empty-state">Loading...</div>}
      {!loading && visible.length === 0 && <div className="empty-state"><Leaf size={32} color={rfColor} style={{ marginBottom: 8 }} /><div>Be the first to share!</div></div>}
      {visible.map(p => {
        const postHearts = hearts[p.id] || [];
        const iHearted = postHearts.includes(user.id);
        return (
          <div key={p.id} className="card" style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--deep)" }}>{p.user_name}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: p.type === "win" ? "#FFF3CD" : rfColor + "20", color: p.type === "win" ? "#B8860B" : rfColor, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{p.type}</span>
                <span style={{ fontSize: 11, color: "var(--soft)" }}>{fmt(p.created_at?.slice(0,10))}</span>
              </div>
            </div>
            <p style={{ fontSize: 14, color: "var(--mid)", lineHeight: 1.6, marginBottom: 10 }}>{p.content}</p>
            <button onClick={() => handleHeart(p.id)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: iHearted ? "#E8647A" : "var(--soft)", fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 600 }}>
              <Heart size={16} weight={iHearted ? "fill" : "regular"} /> {postHearts.length > 0 && postHearts.length}
            </button>
          </div>
        );
      })}
    </div>
  );
}

const RF_NUTRITION_GROUPS = [
  { name: "PROTEIN", unit: "Palm", Icon: Fire,   color: "#E8647A", items: ["Chicken breast","Lean beef","Bison","Buffalo","Pork tenderloin","Lean turkey","Fish & shellfish","Venison","Eggs","Nonfat Greek yogurt","1% Cottage cheese","Protein powder","RX or minimal ingredient protein bars"] },
  { name: "FAT",     unit: "Thumb", Icon: Drop,   color: "#F6A623", items: ["Fatty fish","Peanut butter","Other nut butters","Nuts & seeds","Olives","Coconut oil","Olive oil","Dark chocolate (70%+)","Coffee creamer","Grassfed butter","Any cooking oil","Salad dressing","Cheese or vegan cheese","Avocado"] },
  { name: "CARBS",   unit: "Cupped Hand", Icon: Sun, color: "#48BB78", items: ["Peas","Plantains","Sweet & white potatoes","Whole wheat pasta","Winter squash","Black beans","Lentils","Oatmeal","Quinoa","Brown & white rice","Fruit","High-fiber bread/English muffins/tortillas (5g+)"] },
  { name: "VEGGIES", unit: "Fist", Icon: Plant,  color: "#9CAF88", items: ["Asparagus","Leafy greens","Beets","Broccoli","Carrots","Cauliflower","Celery","Cucumber","Eggplant","Green beans","Mushrooms","Onions","Peppers","Radishes","Salad greens","Tomatoes","Summer squash"] },
];

function RootedNutrition({ user, rfColor }) {
  const [sub, setSub] = useState("plan");
  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
        {[["plan","My Plan"],["basics","Basics"],["diary","My Diary"]].map(([v,l]) => (
          <button key={v} onClick={() => setSub(v)} style={{ flex: 1, padding: "10px 6px", border: "none", borderRadius: 10, fontFamily: "Jost, sans-serif", fontSize: 12, fontWeight: 700, cursor: "pointer", background: sub === v ? rfColor : "var(--bg)", color: sub === v ? "white" : "var(--soft)" }}>{l}</button>
        ))}
      </div>
      {sub === "plan" && (
        <div>
          {/* Where to Start */}
          <div className="card" style={{ marginBottom: 12, borderLeft: `3px solid ${rfColor}` }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Where to Start</div>
            <p style={{ fontSize: 14, color: "var(--mid)", lineHeight: 1.7, marginBottom: 16 }}>
              You don't have to overhaul everything at once. Start slow, build the habit, and let it become easy before you add the next thing. Here's a simple four-week approach:
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              {[
                { week: "Week 1", focus: "Vegetables", detail: "Add a fist-sized portion of veggies to at least 2 meals a day. That's it. Don't change anything else.", Icon: Plant },
                { week: "Week 2", focus: "Protein", detail: "Add a palm-sized protein source to every meal. Eggs, chicken, Greek yogurt, cottage cheese — whatever works for you.", Icon: Fire },
                { week: "Week 3", focus: "Carbs", detail: "Add a cupped handful of quality carbs — oats, rice, potatoes, fruit. If you're moving your body, you need fuel.", Icon: Sun },
                { week: "Week 4", focus: "Fats", detail: "Add a thumb of healthy fat — avocado, olive oil, nuts, nut butter. This is what keeps you full and your hormones happy.", Icon: Drop },
              ].map(({ week, focus, detail, Icon: ItemIcon }) => (
                <div key={week} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 14px", background: "var(--bg)", borderRadius: 10 }}>
                  <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, background: rfColor + "20", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ItemIcon size={18} weight="duotone" color={rfColor} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: rfColor, fontWeight: 700, marginBottom: 2 }}>{week}</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "var(--deep)", marginBottom: 3 }}>Focus on {focus}</div>
                    <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.6 }}>{detail}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: rfColor + "12", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: rfColor, marginBottom: 4 }}>If four weeks still feels like a lot —</div>
              <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.6 }}>
                Pick one or two things this month and do those really well. Protein and veggies is a great place to start. So is protein and carbs. Consistency with two things beats perfect with everything.
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 12, background: rfColor + "12", borderLeft: `3px solid ${rfColor}` }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Per Meal Formula</div>
            <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.8 }}>
              1 Protein + 1 Fat + 1 Carb + 1 Veggie · Eat to 80% full<br/>
              At least 3 servings of protein daily (up to 5)<br/>
              Use 0-calorie seasonings & no added sugars<br/>
              <strong>Water:</strong> 80oz minimum daily<br/>
              <strong>Snacks:</strong> 1-2/day, minimum 2 food groups each (e.g. apple + peanut butter, pear + cottage cheese)<br/>
              <em>e.g. Greek yogurt + berries · apple + nut butter · cucumbers + cottage cheese</em>
            </div>
          </div>
          {RF_NUTRITION_GROUPS.map(g => (
            <details key={g.name} style={{ marginBottom: 8, background: "var(--card)", borderRadius: 12, border: `1px solid var(--border)`, borderLeft: `3px solid ${g.color}`, overflow: "hidden" }}>
              <summary style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", cursor: "pointer", listStyle: "none", userSelect: "none" }}>
                <g.Icon size={20} weight="duotone" color={g.color} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: g.color, letterSpacing: 0.5 }}>{g.name}</div>
                  <div style={{ fontSize: 11, color: "var(--soft)" }}>1 {g.unit} per meal · tap to see foods</div>
                </div>
                <span style={{ fontSize: 12, color: "var(--soft)" }}>▾</span>
              </summary>
              <div style={{ padding: "0 16px 14px", display: "flex", flexWrap: "wrap", gap: 5 }}>
                {g.items.map(item => <span key={item} style={{ padding: "3px 9px", background: g.color + "15", borderRadius: 6, fontSize: 11, color: g.color, fontWeight: 600 }}>{item}</span>)}
              </div>
            </details>
          ))}
        </div>
      )}
      {sub === "basics" && (
        <div className="card">
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Nutrition Basics — Your Starting Point</div>
          {[
            { title: null, body: "Let's keep this simple. You don't need to count calories, track macros, or follow a complicated meal plan to start feeling better. What you need is a foundation — and that's exactly what this is.\n\nThere are four food groups that your body needs at every meal: protein, fat, carbs, and vegetables. Each one plays a role, and leaving any of them out is like trying to run a car with a missing tire. You might move, but not well." },
            { title: "Protein", body: "Builds and repairs your muscles, keeps you full, and supports your metabolism. Think chicken, eggs, Greek yogurt, fish, lean beef." },
            { title: "Fat", body: "Fuels your brain, balances your hormones, and helps your body absorb nutrients. Think avocado, olive oil, nuts, nut butters, dark chocolate." },
            { title: "Carbs", body: "Your body's preferred energy source — especially when you're moving your body daily. Think oats, rice, potatoes, fruit, quinoa. Carbs are not the enemy. They're fuel." },
            { title: "Vegetables", body: "Your body's support system — fiber, micronutrients, and the things that keep everything running behind the scenes. Aim for color and variety." },
            { title: "How Much Should You Eat?", body: "Use your hand — it's always with you and it's already proportioned to your body.\n\n• Protein → one palm\n• Fat → one thumb\n• Carbs → one cupped hand\n• Veggies → one fist\n\nBuild that plate 2-3 times a day, eat slowly, and stop when you're about 80% full." },
            { title: "Water", body: "Your daily minimum is 80oz." },
            { title: "Snacks", body: "Aim for 1-2 snacks per day. Every snack should have at least 2 food groups — think apple + peanut butter, pear + cottage cheese, Greek yogurt + berries." },
            { title: "Supplements (Optional)", body: "Protein or greens powder, multivitamin, fish oils, amino acids or creatine. These fill gaps — they don't replace real food." },
            { title: "The Simple Formula", body: "Every meal: 1 protein + 1 fat + 1 carb + 1 veggie. Eat to 80% full. Drink your water. Repeat." },
          ].map((s, i) => (
            <div key={i} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: i < 9 ? "1px solid var(--border)" : "none" }}>
              {s.title && <div style={{ fontWeight: 700, fontSize: 15, color: rfColor, marginBottom: 6 }}>{s.title}</div>}
              <div style={{ fontSize: 14, color: "var(--mid)", lineHeight: 1.7, whiteSpace: "pre-line" }}>{s.body}</div>
            </div>
          ))}
        </div>
      )}
      {sub === "diary" && <RootedNutritionDiary user={user} rfColor={rfColor} />}
    </div>
  );
}

const DIARY_MEALS = [
  { key: "breakfast", label: "Breakfast", Icon: Sun },
  { key: "snack1",    label: "Snack",     Icon: Acorn },
  { key: "lunch",     label: "Lunch",     Icon: Leaf },
  { key: "dinner",    label: "Dinner",    Icon: Moon },
];

function RootedNutritionDiary({ user, rfColor }) {
  const todayStr = today();
  const [log, setLog] = useState({});
  const [water, setWater] = useState(0);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getJournalEntry(user.id, `nd_${todayStr}`).then(e => {
      if (e) { setLog(e.meals || {}); setWater(e.water || 0); }
    });
  }, [user.id, todayStr]);

  const handleSave = async () => {
    await saveJournalEntry(user.id, `nd_${todayStr}`, { meals: log, water });
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 12, borderLeft: `4px solid ${rfColor}` }}>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Today's Food Diary</div>
        <div style={{ fontSize: 12, color: "var(--soft)" }}>Optional — use this if you'd like to track what you ate today.</div>
      </div>

      {/* Water */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 14, marginBottom: 10 }}><Drop size={16} color={rfColor} /> Water</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <input type="range" min={0} max={120} step={8} value={water} onChange={e => setWater(Number(e.target.value))} style={{ flex: 1, accentColor: rfColor }} />
          <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, color: rfColor, minWidth: 60 }}>{water}oz</span>
        </div>
        <div style={{ fontSize: 11, color: water >= 80 ? rfColor : "var(--soft)", marginTop: 4, fontWeight: 600 }}>{water >= 80 ? "✓ Goal reached!" : `${80 - water}oz to go`}</div>
      </div>

      {/* Meals */}
      {DIARY_MEALS.map(m => (
        <div key={m.key} className="card" style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 14, marginBottom: 8 }}><m.Icon size={15} weight="duotone" color={rfColor} />{m.label}</div>
          <textarea className="form-input form-textarea" value={log[m.key] || ""} onChange={e => setLog(prev => ({ ...prev, [m.key]: e.target.value }))} placeholder="What did you eat? (protein · fat · carb · veggie)" style={{ minHeight: 72, fontSize: 13 }} />
        </div>
      ))}

      <button className="btn-primary" onClick={handleSave} style={{ background: rfColor }}>{saved ? "✓ Saved!" : "Save Diary"}</button>
    </div>
  );
}

function RootedResources({ user, rfColor }) {
  const [links, setLinks] = useState([]);
  useEffect(() => { getProgramResources('rooted', 'link').then(setLinks); }, []);
  const meditationApps = [
    { name: "Insight Timer", desc: "Free, thousands of guided meditations. Great for variety.", url: "https://insighttimer.com" },
    { name: "Calm", desc: "Beautiful and beginner-friendly. Sleep stories, breathing, daily meditations.", url: "https://calm.com" },
    { name: "Headspace", desc: "Teaches you how to meditate, not just takes you through it.", url: "https://headspace.com" },
  ];
  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, marginBottom: 4 }}><Wind size={20} color={rfColor} /> Meditation Apps</div>
        <p style={{ fontSize: 13, color: rfColor, fontWeight: 600, marginBottom: 14 }}>Start with 3 minutes. That's it. Build from there.</p>
        {meditationApps.map(app => (
          <a key={app.name} href={app.url} target="_blank" rel="noreferrer" style={{ display: "block", padding: "12px 0", borderBottom: "1px solid var(--border)", textDecoration: "none" }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: rfColor }}>{app.name} →</div>
            <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2 }}>{app.desc}</div>
          </a>
        ))}
      </div>
      {links.length > 0 && (
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, marginBottom: 14 }}><Link size={18} color={rfColor} /> Additional Resources</div>
          {links.map(l => (
            <a key={l.id} href={l.url} target="_blank" rel="noreferrer" style={{ display: "block", padding: "10px 0", borderBottom: "1px solid var(--border)", textDecoration: "none" }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: rfColor }}>{l.title} →</div>
              {l.content && <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2 }}>{l.content}</div>}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Expansion Nutrition ──────────────────────────────────────────────────────
const EXP_NUTRITION_PATHS = {
  fat_loss:    { label: "Fat Loss",                   desc: "Reduce body fat while preserving lean muscle. Lower carbs, higher protein and veggies keep you full and fueled.", targets: { light: { P:3, V:3, C:1, F:3 }, heavy: { P:4, V:6, C:2, F:4 } } },
  body_recomp: { label: "Body Recomposition",         desc: "Build muscle and lose fat at the same time. Moderate carbs support your training while keeping you in a slight deficit.", targets: { light: { P:4, V:3, C:1, F:3 }, heavy: { P:5, V:6, C:3, F:4 } } },
  muscle_gain: { label: "Muscle Gain",                desc: "Build strength and add lean mass. Higher protein and carbs fuel heavy training and support muscle repair and growth.", targets: { light: { P:5, V:4, C:3, F:3 }, heavy: { P:6, V:6, C:5, F:5 } } },
  maintenance: { label: "Maintenance & General Health", desc: "Feel your best and build lasting healthy habits. Balanced portions support energy, mood, and long-term wellbeing.", targets: { light: { P:3, V:3, C:1, F:3 }, heavy: { P:4, V:5, C:3, F:4 } } },
};

const EXP_FOOD_GROUPS = [
  { key: "P", label: "Protein",  unit: "Palm",         Icon: Fire,  color: "#E8647A", desc: "Chicken, fish, eggs, beef, Greek yogurt, cottage cheese, tofu, legumes" },
  { key: "V", label: "Veggies",  unit: "Fist",         Icon: Plant, color: "#48BB78", desc: "Leafy greens, broccoli, peppers, cucumber, zucchini, asparagus, tomatoes" },
  { key: "C", label: "Carbs",    unit: "Cupped Hand",  Icon: Sun,   color: "#F6A623", desc: "Rice, oats, sweet potato, quinoa, fruit, bread, pasta, beans" },
  { key: "F", label: "Fats",     unit: "Thumb",        Icon: Drop,  color: "#9B8AA6", desc: "Avocado, olive oil, nuts, nut butter, seeds, cheese, full-fat dairy" },
];

function ExpansionNutrition({ user, expColor, nutritionPath, customPlan }) {
  const [subTab, setSubTab] = useState("plan");
  const [trainingDay, setTrainingDay] = useState("light");
  const [counts, setCounts] = useState({ P: 0, V: 0, C: 0, F: 0 });
  const [foodNotes, setFoodNotes] = useState({ P: "", V: "", C: "", F: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const todayStr = today();
  const dateKey = `exp_nd_${todayStr}`;

  // A coach-written plan (Ascension) overrides the shared preset. Merged field
  // by field so a partially filled custom plan still renders sensibly.
  const basePlan = EXP_NUTRITION_PATHS[nutritionPath] || EXP_NUTRITION_PATHS.maintenance;
  const pathData = customPlan && customPlan.targets
    ? {
        label: customPlan.label || basePlan.label,
        desc: customPlan.desc || basePlan.desc,
        targets: {
          light: { ...basePlan.targets.light, ...(customPlan.targets.light || {}) },
          heavy: { ...basePlan.targets.heavy, ...(customPlan.targets.heavy || {}) },
        },
      }
    : basePlan;
  const targets = pathData.targets[trainingDay];

  useEffect(() => {
    getJournalEntry(user.id, dateKey).then(entry => {
      if (entry) {
        setTrainingDay(entry.training_day || "light");
        setCounts({ P: entry.protein || 0, V: entry.veggies || 0, C: entry.carbs || 0, F: entry.fats || 0 });
        setFoodNotes({ P: entry.note_protein || "", V: entry.note_veggies || "", C: entry.note_carbs || "", F: entry.note_fats || "" });
      }
    });
  }, [user.id, dateKey]);

  const adjust = (key, delta) => {
    setCounts(prev => ({ ...prev, [key]: Math.max(0, prev[key] + delta) }));
  };

  const handleSave = async () => {
    setSaving(true);
    await saveJournalEntry(user.id, dateKey, {
      training_day: trainingDay,
      protein: counts.P, veggies: counts.V, carbs: counts.C, fats: counts.F,
      note_protein: foodNotes.P, note_veggies: foodNotes.V, note_carbs: foodNotes.C, note_fats: foodNotes.F,
    });
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const SUB_TABS = [
    { id: "plan",  label: "My Plan" },
    { id: "log",   label: "Daily Log" },
    { id: "basics",label: "Basics" },
  ];

  return (
    <div>
      {/* Sub-tab nav */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, background: "var(--card)", borderRadius: 12, padding: 4, border: "1px solid var(--border)" }}>
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{ flex: 1, padding: "8px 0", borderRadius: 9, border: "none", background: subTab === t.id ? expColor : "transparent", color: subTab === t.id ? "white" : "var(--soft)", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", transition: "all 0.15s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* MY PLAN */}
      {subTab === "plan" && (
        <div>
          <div style={{ background: `linear-gradient(135deg, ${expColor}15, ${expColor}05)`, border: `1px solid ${expColor}25`, borderRadius: 16, padding: "20px 20px", marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: expColor, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Your Goal</div>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 700, color: "var(--deep)", marginBottom: 8 }}>{pathData.label}</div>
            <div style={{ fontSize: 13, color: "var(--soft)", lineHeight: 1.7 }}>{pathData.desc}</div>
          </div>

          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, color: "var(--deep)", marginBottom: 14 }}>Daily Targets</div>

          {/* Light / Heavy toggle */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[["light","Light Training Day"],["heavy","Heavy Training Day"]].map(([key, label]) => (
              <button key={key} onClick={() => setTrainingDay(key)} style={{ flex: 1, padding: "9px 0", borderRadius: 10, border: `2px solid ${trainingDay === key ? expColor : "var(--border)"}`, background: trainingDay === key ? `${expColor}12` : "transparent", color: trainingDay === key ? expColor : "var(--soft)", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {EXP_FOOD_GROUPS.map(({ key, label, unit, Icon: G, color }) => (
              <div key={key} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <G size={22} weight="duotone" color={color} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "var(--deep)" }}>{label}</div>
                  <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2 }}>1 {unit} = 1 serving</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 28, fontWeight: 700, color }}>{pathData.targets[trainingDay][key]}</div>
                  <div style={{ fontSize: 11, color: "var(--soft)" }}>servings/day</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 20, background: `${expColor}08`, border: `1px solid ${expColor}20`, borderRadius: 12, padding: "14px 16px", fontSize: 13, color: "var(--soft)", lineHeight: 1.7 }}>
            <strong style={{ color: expColor }}>Light vs Heavy days:</strong> On light training days aim for the lower targets. On heavy lifting or high-intensity days, fuel up with the higher targets. Both ranges support your goal — listen to your body.
          </div>
        </div>
      )}

      {/* DAILY LOG */}
      {subTab === "log" && (
        <div>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 700, color: "var(--deep)", marginBottom: 4 }}>Today's Log</div>
          <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 18 }}>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>

          {/* Training day toggle */}
          <div style={{ display: "flex", gap: 8, marginBottom: 22 }}>
            {[["light","Light Training Day"],["heavy","Heavy Training Day"]].map(([key, label]) => (
              <button key={key} onClick={() => setTrainingDay(key)} style={{ flex: 1, padding: "9px 0", borderRadius: 10, border: `2px solid ${trainingDay === key ? expColor : "var(--border)"}`, background: trainingDay === key ? `${expColor}12` : "transparent", color: trainingDay === key ? expColor : "var(--soft)", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {EXP_FOOD_GROUPS.map(({ key, label, unit, Icon: G, color }) => {
              const target = targets[key];
              const current = counts[key];
              const pct = Math.min(100, Math.round((current / target) * 100));
              const over = current > target;
              return (
                <div key={key} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: "16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <G size={20} weight="duotone" color={color} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "var(--deep)" }}>{label}</div>
                      <div style={{ fontSize: 12, color: "var(--soft)" }}>1 {unit} per serving · target: {target}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <button onClick={() => adjust(key, -1)} style={{ width: 32, height: 32, borderRadius: "50%", border: "1.5px solid var(--border)", background: "var(--bg)", color: "var(--soft)", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Jost, sans-serif" }}>−</button>
                      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, fontWeight: 700, color: over ? "#E8647A" : color, minWidth: 24, textAlign: "center" }}>{current}</div>
                      <button onClick={() => adjust(key, 1)} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: color, color: "white", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Jost, sans-serif" }}>+</button>
                    </div>
                  </div>
                  <div style={{ background: "var(--bg)", borderRadius: 99, height: 8, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: over ? "#E8647A" : color, borderRadius: 99, transition: "width 0.3s" }} />
                  </div>
                  <div style={{ fontSize: 11, color: over ? "#E8647A" : "var(--soft)", marginTop: 5, textAlign: "right" }}>
                    {over ? `${current - target} over target` : current >= target ? "Target reached!" : `${target - current} more to go`}
                  </div>
                  <input
                    className="form-input"
                    value={foodNotes[key]}
                    onChange={e => setFoodNotes(n => ({ ...n, [key]: e.target.value }))}
                    placeholder={`What ${label.toLowerCase()} did you have today?`}
                    style={{ marginTop: 10, fontSize: 13, padding: "8px 12px" }}
                  />
                </div>
              );
            })}
          </div>

          <button onClick={handleSave} disabled={saving} style={{ width: "100%", marginTop: 20, padding: "14px", background: saved ? "#48BB78" : expColor, color: "white", border: "none", borderRadius: 12, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer", transition: "background 0.2s" }}>
            {saving ? "Saving…" : saved ? "Saved!" : "Save Today's Log"}
          </button>
        </div>
      )}

      {/* BASICS */}
      {subTab === "basics" && (
        <div>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 700, color: "var(--deep)", marginBottom: 6 }}>Hand Portion Guide</div>
          <div style={{ fontSize: 13, color: "var(--soft)", lineHeight: 1.7, marginBottom: 20 }}>Your hand is always with you — use it to measure portions without counting calories or weighing food. It automatically scales to your body size.</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {EXP_FOOD_GROUPS.map(({ key, label, unit, Icon: G, color, desc }) => (
              <div key={key} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: "18px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <G size={24} weight="duotone" color={color} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "var(--deep)" }}>{label}</div>
                    <div style={{ fontSize: 13, color, fontWeight: 600 }}>1 {unit} = 1 serving</div>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "var(--soft)", lineHeight: 1.7 }}>{desc}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 20, background: `${expColor}08`, border: `1px solid ${expColor}20`, borderRadius: 12, padding: "16px", fontSize: 13, color: "var(--soft)", lineHeight: 1.7 }}>
            <strong style={{ color: expColor }}>Remember:</strong> These are guidelines, not rules. Some days you'll hit every target, some days you won't — and both are okay. Consistency over time is what matters most.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Expansion Workouts ───────────────────────────────────────────────────────
// ─── Expansion Resources ──────────────────────────────────────────────────────
function ExpansionResources({ user, expColor }) {
  const [subTab, setSubTab] = useState("links");
  const [links, setLinks] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getProgramResources('expansion', 'link'),
      getProgramResources('expansion', 'recipe'),
      getProgramResources('expansion', 'mindset'),
    ]).then(([l, r, c]) => {
      setLinks(l); setRecipes(r); setChallenges(c);
      setLoading(false);
    });
  }, []);

  const SUB_TABS = [
    { id: "links",      label: "Links",               Icon: Link },
    { id: "recipes",    label: "Recipes",             Icon: Coffee },
    { id: "challenges", label: "Mindset Challenges",  Icon: Sparkle },
  ];

  const empty = (icon, text) => (
    <div className="empty-state" style={{ paddingTop: 40 }}>
      {icon}
      <div style={{ fontSize: 13, color: "var(--soft)", marginTop: 12 }}>{text}</div>
    </div>
  );

  return (
    <div>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, fontWeight: 700, color: expColor, marginBottom: 4 }}>Resources</div>
      <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 20 }}>Everything you need to support your journey.</div>

      {/* Sub-tab nav */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, background: "var(--card)", borderRadius: 12, padding: 4, border: "1px solid var(--border)" }}>
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{ flex: 1, padding: "8px 4px", borderRadius: 9, border: "none", background: subTab === t.id ? expColor : "transparent", color: subTab === t.id ? "white" : "var(--soft)", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
            <t.Icon size={13} weight={subTab === t.id ? "fill" : "regular"} />
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: "var(--soft)", textAlign: "center", padding: 40 }}>Loading…</div>
      ) : (
        <>
          {/* LINKS */}
          {subTab === "links" && (
            <div>
              {links.length === 0
                ? empty(<Link size={36} weight="duotone" color={expColor} />, "No links added yet — check back soon.")
                : links.map(item => (
                  <a key={item.id} href={item.url} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 14, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: "16px", marginBottom: 12, textDecoration: "none" }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: `${expColor}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Link size={20} weight="duotone" color={expColor} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "var(--deep)", marginBottom: 2 }}>{item.title}</div>
                      {item.content && <div style={{ fontSize: 12, color: "var(--soft)", lineHeight: 1.5 }}>{item.content}</div>}
                      <div style={{ fontSize: 11, color: expColor, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.url}</div>
                    </div>
                    <span style={{ color: "var(--soft)", fontSize: 18, flexShrink: 0 }}>›</span>
                  </a>
                ))
              }
            </div>
          )}

          {/* RECIPES */}
          {subTab === "recipes" && (
            <div>
              {recipes.length === 0
                ? empty(<Coffee size={36} weight="duotone" color={expColor} />, "Recipes are on their way — delicious things take time.")
                : recipes.map(item => (
                  <div key={item.id} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: "16px", marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, fontFamily: "Cormorant Garamond, serif", color: "var(--deep)", marginBottom: 6 }}>{item.title}</div>
                    {item.content && <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{item.content}</div>}
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 10, color: expColor, fontSize: 12, fontFamily: "Jost, sans-serif", fontWeight: 700, textDecoration: "none" }}>
                        <Link size={12} /> View full recipe
                      </a>
                    )}
                  </div>
                ))
              }
            </div>
          )}

          {/* MINDSET CHALLENGES */}
          {subTab === "challenges" && (
            <div>
              {challenges.length === 0
                ? empty(<Sparkle size={36} weight="duotone" color={expColor} />, "Mindset challenges coming soon — your brain needs a workout too.")
                : challenges.map((item, i) => (
                  <div key={item.id} style={{ background: "var(--card)", border: `2px solid ${expColor}20`, borderRadius: 14, padding: "18px 16px", marginBottom: 12, position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 12, right: 14, fontFamily: "Cormorant Garamond, serif", fontSize: 36, fontWeight: 700, color: `${expColor}12`, lineHeight: 1 }}>{String(i + 1).padStart(2, "0")}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: expColor, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Challenge {i + 1}</div>
                    <div style={{ fontWeight: 700, fontSize: 16, fontFamily: "Cormorant Garamond, serif", color: "var(--deep)", marginBottom: 8 }}>{item.title}</div>
                    {item.content && <div style={{ fontSize: 13, color: "var(--mid)", lineHeight: 1.7 }}>{item.content}</div>}
                  </div>
                ))
              }
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ExpansionWorkouts({ user, expColor, workoutTrack, onWorkoutComplete }) {
  const [subTab, setSubTab] = useState("this_week");
  const [workouts, setWorkouts] = useState([]);
  const [logs, setLogs] = useState({});
  const [library, setLibrary] = useState([]);
  const [prs, setPRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [openWorkout, setOpenWorkout] = useState(null);
  const [openMovement, setOpenMovement] = useState(null);
  const [workoutNotes, setWorkoutNotes] = useState({});
  const [savingLog, setSavingLog] = useState({});

  // PR state
  const [newPRMovement, setNewPRMovement] = useState("");
  const [newPRValue, setNewPRValue] = useState("");
  const [newPRUnit, setNewPRUnit] = useState("lbs");
  const [prSaving, setPRSaving] = useState(false);

  const trackLabel = workoutTrack === "legs_booty" ? "Legs & Booty" : "Full Body";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [startDateStr, allLogs, lib, userPRs] = await Promise.all([
        getProgramStartDate(),
        getUserWorkoutLogs(user.id),
        getMovementLibrary(),
        getExpansionPRs(user.id),
      ]);

      // Compute current week
      let week = 1;
      if (startDateStr) {
        const start = new Date(startDateStr);
        const now = new Date();
        const diff = Math.floor((now - start) / (7 * 24 * 60 * 60 * 1000));
        week = Math.max(1, diff + 1);
      }
      setCurrentWeek(week);

      const wkts = await getExpansionWorkouts(workoutTrack, week);
      setWorkouts(wkts);

      const logMap = {};
      const notesMap = {};
      allLogs.forEach(l => {
        logMap[l.workout_id] = l.completed;
        notesMap[l.workout_id] = l.notes || "";
      });
      setLogs(logMap);
      setWorkoutNotes(notesMap);
      setLibrary(lib);
      setPRs(userPRs);
      setLoading(false);
    };
    load();
  }, [user.id, workoutTrack]);

  const handleToggleComplete = async (workout) => {
    const newVal = !logs[workout.id];
    setLogs(prev => ({ ...prev, [workout.id]: newVal }));
    setSavingLog(prev => ({ ...prev, [workout.id]: true }));
    await saveWorkoutLog(user.id, workout.id, { completed: newVal, notes: workoutNotes[workout.id] || "" });
    setSavingLog(prev => ({ ...prev, [workout.id]: false }));
    if (newVal) onWorkoutComplete();
  };

  const handleSaveNotes = async (workoutId) => {
    setSavingLog(prev => ({ ...prev, [workoutId]: true }));
    await saveWorkoutLog(user.id, workoutId, { completed: !!logs[workoutId], notes: workoutNotes[workoutId] || "" });
    setSavingLog(prev => ({ ...prev, [workoutId]: false }));
  };

  const handleAddPR = async () => {
    if (!newPRMovement.trim() || !newPRValue) return;
    setPRSaving(true);
    const pr = { id: `pr_${user.id}_${Date.now()}`, userId: user.id, movementName: newPRMovement.trim(), value: parseFloat(newPRValue), unit: newPRUnit };
    await saveExpansionPR(pr);
    setPRs(prev => [{ ...pr, movement_name: pr.movementName, logged_at: new Date().toISOString() }, ...prev]);
    setNewPRMovement(""); setNewPRValue("");
    setPRSaving(false);
  };

  const SUB_TABS = [
    { id: "this_week", label: "This Week" },
    { id: "library",   label: "Movements" },
    { id: "prs",       label: "Strength Log" },
  ];

  const formatDate = (ts) => new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  // Group PRs by movement
  const prsByMovement = {};
  prs.forEach(pr => {
    if (!prsByMovement[pr.movement_name]) prsByMovement[pr.movement_name] = [];
    prsByMovement[pr.movement_name].push(pr);
  });

  return (
    <div>
      {/* Movement detail modal */}
      {openMovement && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(20,15,30,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => setOpenMovement(null)}>
          <div style={{ background: "var(--bg)", borderRadius: 18, maxWidth: 420, width: "100%", padding: "28px 24px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 700, color: expColor, marginBottom: 12 }}>{openMovement.name}</div>
            <div style={{ fontSize: 14, color: "var(--text)", lineHeight: 1.75, marginBottom: 16 }}>{openMovement.description}</div>
            {openMovement.video_url && (
              <a href={openMovement.video_url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: expColor, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
                <Link size={14} /> Watch Video
              </a>
            )}
            <button onClick={() => setOpenMovement(null)} style={{ display: "block", width: "100%", marginTop: 20, padding: "12px", background: expColor, color: "white", border: "none", borderRadius: 10, fontFamily: "Jost, sans-serif", fontWeight: 700, cursor: "pointer" }}>Close</button>
          </div>
        </div>
      )}

      {/* Sub-tab nav */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, background: "var(--card)", borderRadius: 12, padding: 4, border: "1px solid var(--border)" }}>
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{ flex: 1, padding: "8px 0", borderRadius: 9, border: "none", background: subTab === t.id ? expColor : "transparent", color: subTab === t.id ? "white" : "var(--soft)", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", transition: "all 0.15s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* THIS WEEK */}
      {subTab === "this_week" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 700, color: "var(--deep)" }}>Week {currentWeek}</div>
            <div style={{ fontSize: 12, color: expColor, fontWeight: 700 }}>{trackLabel}</div>
          </div>
          <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 20 }}>New workouts drop every Sunday evening.</div>

          {loading ? (
            <div className="empty-state" style={{ paddingTop: 40 }}><div style={{ color: "var(--soft)" }}>Loading…</div></div>
          ) : workouts.length === 0 ? (
            <div className="empty-state" style={{ paddingTop: 40 }}>
              <Barbell size={40} weight="duotone" color={expColor} />
              <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, color: expColor, marginTop: 12, marginBottom: 8 }}>Rest up, bestie.</div>
              <div style={{ fontSize: 13, color: "var(--soft)", maxWidth: 260, textAlign: "center" }}>Your Week {currentWeek} workouts haven't dropped yet — check back Sunday evening. Good things are coming. 💪</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {workouts.map(workout => {
                const completed = !!logs[workout.id];
                const exercises = Array.isArray(workout.exercises) ? workout.exercises : [];
                const isOpen = openWorkout === workout.id;
                return (
                  <div key={workout.id} style={{ background: "var(--card)", border: `2px solid ${completed ? expColor + "60" : "var(--border)"}`, borderRadius: 14, overflow: "hidden" }}>
                    {/* Header */}
                    <div style={{ padding: "16px 16px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }} onClick={() => setOpenWorkout(isOpen ? null : workout.id)}>
                      <div style={{ width: 42, height: 42, borderRadius: 10, background: completed ? expColor + "20" : "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1.5px solid ${completed ? expColor : "var(--border)"}` }}>
                        {completed ? <Check size={22} color={expColor} weight="bold" /> : <Barbell size={22} weight="duotone" color="var(--soft)" />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: expColor, textTransform: "uppercase", letterSpacing: "0.07em" }}>Day {workout.day_number}</div>
                        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, color: "var(--deep)" }}>{workout.title}</div>
                        <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2 }}>{exercises.length} movements</div>
                      </div>
                      <div style={{ color: "var(--soft)", fontSize: 18 }}>{isOpen ? "▲" : "▼"}</div>
                    </div>

                    {/* Expanded */}
                    {isOpen && (
                      <div style={{ borderTop: "1px solid var(--border)", padding: "16px" }}>
                        {/* Exercises */}
                        <div style={{ marginBottom: 16 }}>
                          {exercises.map((ex, i) => {
                            const movement = library.find(m => m.name.toLowerCase() === (ex.movement || "").toLowerCase());
                            return (
                              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: i < exercises.length - 1 ? "1px solid var(--border)" : "none" }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 700, fontSize: 14, color: "var(--deep)" }}>{ex.movement}</div>
                                  <div style={{ fontSize: 13, color: "var(--soft)", marginTop: 2 }}>{ex.sets} sets × {ex.reps}</div>
                                  {ex.notes && <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2, fontStyle: "italic" }}>{ex.notes}</div>}
                                </div>
                                {movement && (
                                  <button onClick={() => setOpenMovement(movement)} style={{ background: `${expColor}15`, border: `1px solid ${expColor}30`, borderRadius: 8, padding: "5px 10px", color: expColor, fontSize: 12, fontFamily: "Jost, sans-serif", fontWeight: 700, cursor: "pointer" }}>
                                    How to
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Notes */}
                        <textarea
                          className="form-input"
                          value={workoutNotes[workout.id] || ""}
                          onChange={e => setWorkoutNotes(n => ({ ...n, [workout.id]: e.target.value }))}
                          placeholder="Add notes, how it felt, weight used…"
                          rows={2}
                          style={{ resize: "none", marginBottom: 12, fontSize: 13 }}
                        />

                        {/* Complete button */}
                        <div style={{ display: "flex", gap: 10 }}>
                          <button onClick={() => handleSaveNotes(workout.id)} style={{ flex: 1, padding: "10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, fontFamily: "Jost, sans-serif", fontWeight: 600, fontSize: 13, cursor: "pointer", color: "var(--soft)" }}>
                            Save Notes
                          </button>
                          <button onClick={() => handleToggleComplete(workout)} style={{ flex: 2, padding: "10px", background: completed ? "var(--bg)" : expColor, border: completed ? `2px solid ${expColor}` : "none", color: completed ? expColor : "white", borderRadius: 10, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                            {savingLog[workout.id] ? "Saving…" : completed ? "✓ Completed" : "Mark Complete"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* MOVEMENT LIBRARY */}
      {subTab === "library" && (
        <div>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 700, color: "var(--deep)", marginBottom: 6 }}>Movement Library</div>
          <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 20 }}>Tap any movement to see how to perform it.</div>
          {library.length === 0 ? (
            <div className="empty-state" style={{ paddingTop: 40 }}>
              <Book size={36} weight="duotone" color={expColor} />
              <div style={{ color: "var(--soft)", fontSize: 14, marginTop: 12 }}>Your coach hasn't added movements yet.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {library.map(m => (
                <div key={m.id} onClick={() => setOpenMovement(m)} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: `${expColor}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Barbell size={18} weight="duotone" color={expColor} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{m.name}</div>
                    {m.track && m.track !== "both" && <div style={{ fontSize: 11, color: expColor, fontWeight: 600, marginTop: 2 }}>{m.track === "legs_booty" ? "Legs & Booty" : "Full Body"}</div>}
                  </div>
                  {m.video_url && <Link size={14} color="var(--soft)" />}
                  <span style={{ color: "var(--soft)", fontSize: 16 }}>›</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MY PRs */}
      {subTab === "prs" && (
        <div>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 700, color: "var(--deep)", marginBottom: 6 }}>Strength Log</div>
          <div style={{ fontSize: 13, color: "var(--soft)", marginBottom: 20 }}>Log your best lifts and track your progress over time.</div>

          {/* Add PR */}
          <div style={{ background: "var(--card)", border: `1px solid ${expColor}25`, borderRadius: 14, padding: "16px", marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: expColor, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>Log a PR</div>
            <input className="form-input" value={newPRMovement} onChange={e => setNewPRMovement(e.target.value)} placeholder="Movement (e.g. Hip Thrust, Back Squat)" style={{ marginBottom: 10 }} />
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <input className="form-input" type="number" value={newPRValue} onChange={e => setNewPRValue(e.target.value)} placeholder="Weight / reps" style={{ flex: 1 }} />
              <select className="form-input" value={newPRUnit} onChange={e => setNewPRUnit(e.target.value)} style={{ width: 80 }}>
                <option value="lbs">lbs</option>
                <option value="kg">kg</option>
                <option value="reps">reps</option>
                <option value="mins">mins</option>
              </select>
            </div>
            <button onClick={handleAddPR} disabled={prSaving || !newPRMovement.trim() || !newPRValue} style={{ width: "100%", padding: "11px", background: expColor, color: "white", border: "none", borderRadius: 10, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer", opacity: (!newPRMovement.trim() || !newPRValue) ? 0.5 : 1 }}>
              {prSaving ? "Saving…" : "Log PR"}
            </button>
          </div>

          {/* PR list grouped by movement */}
          {Object.keys(prsByMovement).length === 0 ? (
            <div className="empty-state" style={{ paddingTop: 20 }}>
              <Star size={36} weight="duotone" color={expColor} />
              <div style={{ color: "var(--soft)", fontSize: 14, marginTop: 12 }}>No PRs logged yet — add your first one above!</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {Object.entries(prsByMovement).map(([movement, entries]) => {
                const best = entries.reduce((a, b) => (b.value > a.value ? b : a));
                return (
                  <div key={movement} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
                    <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{movement}</div>
                        <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2 }}>{entries.length} {entries.length === 1 ? "entry" : "entries"}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 700, color: expColor }}>{best.value} <span style={{ fontSize: 14 }}>{best.unit}</span></div>
                        <div style={{ fontSize: 11, color: "var(--soft)" }}>best</div>
                      </div>
                    </div>
                    <div style={{ borderTop: "1px solid var(--border)", padding: "8px 16px" }}>
                      {entries.map(e => (
                        <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                          <span style={{ color: "var(--soft)" }}>{formatDate(e.logged_at)}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span style={{ fontWeight: 700 }}>{e.value} {e.unit}</span>
                            <button onClick={async () => { await deleteExpansionPR(e.id); setPRs(p => p.filter(x => x.id !== e.id)); }} style={{ background: "none", border: "none", color: "var(--soft)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── THE EXPANSION ─────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function ExpansionApp({ user, onLogout }) {
  const EXP_COLOR = "#5B9BD5";
  const [tab, setTab] = useState("home");
  const [prefs, setPrefs] = useState(null);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [showPathModal, setShowPathModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsNutrition, setSettingsNutrition] = useState("");
  const [settingsWorkout, setSettingsWorkout] = useState("");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [selNutritionPath, setSelNutritionPath] = useState("");
  const [selWorkoutTrack, setSelWorkoutTrack] = useState("");
  const [pathSaving, setPathSaving] = useState(false);

  // Intro form
  const [introCity, setIntroCity] = useState("");
  const [introWhy, setIntroWhy] = useState("");
  const [introGoal, setIntroGoal] = useState("");
  const [introFunFact, setIntroFunFact] = useState("");
  const [introSaving, setIntroSaving] = useState(false);

  // Feed
  const [posts, setPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [hearts, setHearts] = useState({});
  const [heartCounts, setHeartCounts] = useState({});
  const [openComments, setOpenComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPostType, setNewPostType] = useState("status");
  const [newPostContent, setNewPostContent] = useState("");
  const [newPostImage, setNewPostImage] = useState(null);
  const [newPostImagePreview, setNewPostImagePreview] = useState(null);
  const [postSaving, setPostSaving] = useState(false);

  const lsKey = (k) => `exp_${user.id}_${k}`;
  const lsGet = (k) => localStorage.getItem(lsKey(k));
  const lsSet = (k, v) => localStorage.setItem(lsKey(k), v);

  useEffect(() => {
    const init = async () => {
      const [p, posts] = await Promise.all([
        getUserPrefs(user.id),
        getCommunityPosts(['expansion', 'ascension']),
      ]);
      setPrefs(p || {});
      const hasIntroPost = posts.some(post => post.user_id === user.id && post.type === 'intro');
      const introDone = p?.intro_post_done || lsGet('intro_done') === '1' || hasIntroPost;
      const pathDone  = p?.path_selected   || lsGet('path_done')  === '1';
      if (hasIntroPost) lsSet('intro_done', '1');
      if (!introDone) {
        setShowIntroModal(true);
      } else if (!pathDone) {
        setSelNutritionPath(p?.nutrition_path || lsGet('nutrition_path') || "");
        setSelWorkoutTrack(p?.workout_track   || lsGet('workout_track')  || "");
        setShowPathModal(true);
      }
      setPosts(posts);
      if (posts.length) {
        const ids = posts.map(p => p.id);
        const allHearts = await getCommunityHearts(ids);
        const counts = {};
        const myHearts = {};
        allHearts.forEach(h => {
          counts[h.post_id] = (counts[h.post_id] || 0) + 1;
          if (h.user_id === user.id) myHearts[h.post_id] = true;
        });
        setHeartCounts(counts);
        setHearts(myHearts);
      }
      setFeedLoading(false);
    };
    init();
  }, [user.id]);

  const loadFeed = async () => {
    setFeedLoading(true);
    const data = await getCommunityPosts(['expansion', 'ascension']);
    setPosts(data);
    if (data.length) {
      const ids = data.map(p => p.id);
      const allHearts = await getCommunityHearts(ids);
      const counts = {};
      const myHearts = {};
      allHearts.forEach(h => {
        counts[h.post_id] = (counts[h.post_id] || 0) + 1;
        if (h.user_id === user.id) myHearts[h.post_id] = true;
      });
      setHeartCounts(counts);
      setHearts(myHearts);
    }
    setFeedLoading(false);
  };

  const handleIntroSubmit = async () => {
    if (!introCity.trim() || !introWhy.trim() || !introGoal.trim() || !introFunFact.trim()) return;
    setIntroSaving(true);
    lsSet('intro_done', '1');
    setShowIntroModal(false);
    setShowPathModal(true);
    const content = `Hi everyone! I'm ${user.name} from ${introCity.trim()}.\n\nI joined The Expansion because ${introWhy.trim()}.\n\nMy goal is ${introGoal.trim()}.\n\nFun fact about me: ${introFunFact.trim()}\n\nSo excited to be here!`;
    await saveCommunityPost({
      id: `post_${user.id}_intro_${Date.now()}`,
      userId: user.id,
      userName: user.name,
      program: 'expansion',
      type: 'intro',
      content,
    });
    const updated = { ...(prefs || {}), intro_post_done: true };
    await saveUserPrefs(user.id, updated);
    setPrefs(updated);
    setIntroSaving(false);
    loadFeed();
  };

  const handleOpenSettings = () => {
    setSettingsNutrition(prefs?.nutrition_path || "");
    setSettingsWorkout(prefs?.workout_track || "");
    setShowSettings(true);
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    const updated = { ...(prefs || {}), nutrition_path: settingsNutrition, workout_track: settingsWorkout };
    await saveUserPrefs(user.id, updated);
    setPrefs(updated);
    setSettingsSaving(false);
    setSettingsSaved(true);
    setTimeout(() => { setSettingsSaved(false); setShowSettings(false); }, 1200);
  };

  const handlePathSubmit = async () => {
    if (!selNutritionPath || !selWorkoutTrack) return;
    setPathSaving(true);
    lsSet('path_done', '1');
    lsSet('nutrition_path', selNutritionPath);
    lsSet('workout_track', selWorkoutTrack);
    const updated = { ...(prefs || {}), nutrition_path: selNutritionPath, workout_track: selWorkoutTrack, path_selected: true };
    await saveUserPrefs(user.id, updated);
    setPrefs(updated);
    setShowPathModal(false);
    setPathSaving(false);
  };

  const handleHeart = async (postId) => {
    const result = await toggleCommunityHeart(postId, user.id);
    setHearts(h => ({ ...h, [postId]: result === 'added' }));
    setHeartCounts(c => ({ ...c, [postId]: (c[postId] || 0) + (result === 'added' ? 1 : -1) }));
  };

  const loadComments = async (postId) => {
    const data = await getCommunityComments(postId);
    setComments(c => ({ ...c, [postId]: data }));
  };

  const toggleComments = (postId) => {
    setOpenComments(o => {
      const next = { ...o, [postId]: !o[postId] };
      if (next[postId] && !comments[postId]) loadComments(postId);
      return next;
    });
  };

  const handleComment = async (postId) => {
    const text = (newComment[postId] || "").trim();
    if (!text) return;
    await saveCommunityComment({
      id: `comment_${user.id}_${Date.now()}`,
      postId,
      userId: user.id,
      userName: user.name,
      content: text,
    });
    setNewComment(c => ({ ...c, [postId]: "" }));
    loadComments(postId);
  };

  const handleNewPost = async () => {
    if (!newPostContent.trim() && !newPostImage) return;
    setPostSaving(true);
    let imageUrl = null;
    if (newPostImage) imageUrl = await uploadCommunityImage(newPostImage, user.id);
    await saveCommunityPost({
      id: `post_${user.id}_${Date.now()}`,
      userId: user.id,
      userName: user.name,
      program: 'expansion',
      type: newPostType,
      content: newPostContent.trim(),
      imageUrl,
    });
    setNewPostContent("");
    setNewPostImage(null);
    setNewPostImagePreview(null);
    setShowNewPost(false);
    setPostSaving(false);
    loadFeed();
  };

  const EXP_QUOTES = [
    "You are not starting over — you are starting stronger.",
    "Progress is not always visible, but it is always happening.",
    "Your body hears everything your mind says. Be kind.",
    "Small steps every day add up to big changes.",
    "You don't have to be perfect to be powerful.",
    "Consistency beats intensity every single time.",
    "Believe in the version of you that is still becoming.",
    "Rest is not giving up. Rest is how you come back stronger.",
    "You are allowed to take up space. You are allowed to thrive.",
    "The work you do today is a gift to your future self.",
    "Strength isn't just built in the gym — it's built in the quiet moments too.",
    "Trust the process, even when you can't see the progress.",
    "You've survived every hard day so far. That's 100%.",
    "Nourish your body. Honor your mind. Show up for your soul.",
    "The only competition is who you were yesterday.",
    "Your commitment to yourself is the most important one you'll ever make.",
    "Every meal, every movement, every moment of rest — it all counts.",
    "You are worthy of the energy you give to everyone else.",
    "Discipline is just self-love in action.",
    "Growth is uncomfortable. That means it's working.",
    "You are exactly where you need to be, doing exactly what you need to do.",
    "Your habits are becoming your identity. Make them worth it.",
    "The version of you that shows up today matters.",
    "Some days progress looks like a workout. Some days it looks like rest. Both are wins.",
    "You were made for more — and you already know it.",
    "Showing up is half the battle. You're already winning.",
    "Let your why be bigger than your excuses.",
    "Good things take time. You are a good thing in progress.",
    "Be patient with yourself. You are growing.",
    "Every single rep, every single choice — it's all adding up.",
  ];
  const dailyQuote = EXP_QUOTES[Math.floor(Date.now() / 86400000) % EXP_QUOTES.length];

  const EXP_TABS = [
    { id: "home",      label: "Home",      Icon: House },
    { id: "workouts",  label: "Workouts",  Icon: Barbell },
    { id: "nutrition", label: "Nutrition", Icon: Acorn },
    { id: "resources", label: "Resources", Icon: Book },
  ];

  const POST_TYPES = [
    { key: "share",     label: "Share" },
    { key: "win",       label: "Win" },
    { key: "gratitude", label: "Gratitude" },
    { key: "workout",   label: "Workout Log" },
    { key: "question",  label: "Question" },
  ];

  const formatTime = (ts) => {
    const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const TYPE_LABEL = { share: "Share", win: "Win", gratitude: "Gratitude", workout: "Workout", intro: "Introduction", question: "Question" };
  const TYPE_COLOR = { win: "#48BB78", gratitude: "#9B8AA6", intro: "#F6A623", workout: "#5B9BD5", share: "#999", question: "#E8647A" };

  const allFilled = introCity.trim() && introWhy.trim() && introGoal.trim() && introFunFact.trim();

  if (!prefs) return <div style={{ minHeight: "100vh", background: "var(--bg)" }}><style>{styles}</style><AppSkeleton color={EXP_COLOR} /></div>;

  if (!prefs.cup_fillers_setup) return <CupFillerSetup color={EXP_COLOR} onDone={async (selected) => {
    for (const label of selected) {
      const f = { id: `cf_${user.id}_${Date.now()}_${Math.random().toString(36).slice(2)}`, userId: user.id, label, sortOrder: 0 };
      await saveCupFiller(f);
    }
    await saveUserPrefs(user.id, { cup_fillers_setup: true });
    setPrefs(p => ({ ...p, cup_fillers_setup: true }));
  }} />;

  return (
    <div className="app-shell">
      <style>{styles}</style>

      {/* ── INTRO GATE MODAL ── */}
      {showIntroModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(20,15,30,0.72)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px" }}>
          <div style={{ background: "var(--bg)", borderRadius: 20, maxWidth: 480, width: "100%", padding: "32px 28px", boxShadow: "0 24px 80px rgba(0,0,0,0.3)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <Sparkle size={44} weight="duotone" color={EXP_COLOR} />
              <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, fontWeight: 700, color: EXP_COLOR, marginTop: 10, marginBottom: 8 }}>Welcome to The Expansion!</div>
              <div style={{ fontSize: 13, color: "var(--soft)", lineHeight: 1.7 }}>Before you dive in, introduce yourself to the community. Your intro will be your first post — your people want to meet you!</div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 5 }}>Your city</label>
                <input className="form-input" value={introCity} onChange={e => setIntroCity(e.target.value)} placeholder="e.g. Austin, TX" />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 5 }}>Why did you join The Expansion?</label>
                <textarea className="form-input" value={introWhy} onChange={e => setIntroWhy(e.target.value)} placeholder="I joined because..." rows={2} style={{ resize: "none" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 5 }}>One goal you have</label>
                <input className="form-input" value={introGoal} onChange={e => setIntroGoal(e.target.value)} placeholder="My goal is to..." />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 5 }}>A fun fact about you</label>
                <input className="form-input" value={introFunFact} onChange={e => setIntroFunFact(e.target.value)} placeholder="Something fun or surprising!" />
              </div>
            </div>

            {/* Live preview */}
            <div style={{ background: `${EXP_COLOR}10`, border: `1px solid ${EXP_COLOR}25`, borderRadius: 12, padding: "14px 16px", marginTop: 20, fontSize: 13, color: "var(--text)", lineHeight: 1.75 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: EXP_COLOR, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Preview</div>
              Hi everyone! I'm <strong>{user.name}</strong> from <em>{introCity || "…"}</em>.<br />
              I joined The Expansion because <em>{introWhy || "…"}</em>.<br />
              My goal is <em>{introGoal || "…"}</em>.<br />
              Fun fact about me: <em>{introFunFact || "…"}</em>
            </div>

            <button
              onClick={handleIntroSubmit}
              disabled={introSaving || !allFilled}
              style={{ width: "100%", marginTop: 20, padding: "14px", background: EXP_COLOR, color: "white", border: "none", borderRadius: 12, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 15, cursor: allFilled ? "pointer" : "not-allowed", opacity: allFilled ? 1 : 0.45, transition: "opacity 0.2s" }}
            >
              {introSaving ? "Posting…" : "Post My Introduction"}
            </button>
            <div style={{ textAlign: "center", fontSize: 11, color: "var(--soft)", marginTop: 10 }}>Fill in all four fields to continue</div>
          </div>
        </div>
      )}

      {/* ── PATH SELECTION MODAL ── */}
      {showPathModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(20,15,30,0.72)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px" }}>
          <div style={{ background: "var(--bg)", borderRadius: 20, maxWidth: 500, width: "100%", padding: "32px 28px", boxShadow: "0 24px 80px rgba(0,0,0,0.3)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <Flower size={44} weight="duotone" color={EXP_COLOR} />
              <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, fontWeight: 700, color: EXP_COLOR, marginTop: 10, marginBottom: 8 }}>Choose Your Path</div>
              <div style={{ fontSize: 13, color: "var(--soft)", lineHeight: 1.7 }}>Select your nutrition goal and workout focus. You can always change these in Settings.</div>
            </div>

            {/* Nutrition path */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <Acorn size={14} color={EXP_COLOR} /> Nutrition Goal
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { key: "fat_loss",    label: "Fat Loss",                  desc: "Reduce body fat while preserving lean muscle",        Icon: Fire },
                  { key: "body_recomp", label: "Body Recomposition",        desc: "Build muscle and lose fat at the same time",          Icon: Scales },
                  { key: "muscle_gain", label: "Muscle Gain",               desc: "Build strength and add lean mass",                    Icon: Barbell },
                  { key: "maintenance", label: "Maintenance & General Health", desc: "Feel your best and build lasting healthy habits",   Icon: Leaf },
                ].map(({ key, label, desc, Icon: PathIcon }) => (
                  <div key={key} onClick={() => setSelNutritionPath(key)} style={{ border: `2px solid ${selNutritionPath === key ? EXP_COLOR : "var(--border)"}`, borderRadius: 14, padding: "14px 16px", cursor: "pointer", background: selNutritionPath === key ? `${EXP_COLOR}0e` : "var(--card)", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: selNutritionPath === key ? `${EXP_COLOR}20` : "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <PathIcon size={22} weight="duotone" color={selNutritionPath === key ? EXP_COLOR : "var(--soft)"} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: selNutritionPath === key ? EXP_COLOR : "var(--text)" }}>{label}</div>
                      <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2 }}>{desc}</div>
                    </div>
                    {selNutritionPath === key && <Check size={20} color={EXP_COLOR} style={{ marginLeft: "auto", flexShrink: 0 }} />}
                  </div>
                ))}
              </div>
            </div>

            {/* Workout track */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <Barbell size={14} color={EXP_COLOR} /> Workout Track
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { key: "full_body",  label: "Full Body",      desc: "3x/week total-body resistance training — balanced strength and tone",  Icon: Lightning },
                  { key: "legs_booty", label: "Legs & Booty",   desc: "Focused lower body and glute development with targeted programming",   Icon: Flower },
                ].map(({ key, label, desc, Icon: TrackIcon }) => (
                  <div key={key} onClick={() => setSelWorkoutTrack(key)} style={{ border: `2px solid ${selWorkoutTrack === key ? EXP_COLOR : "var(--border)"}`, borderRadius: 14, padding: "14px 16px", cursor: "pointer", background: selWorkoutTrack === key ? `${EXP_COLOR}0e` : "var(--card)", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: selWorkoutTrack === key ? `${EXP_COLOR}20` : "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <TrackIcon size={22} weight="duotone" color={selWorkoutTrack === key ? EXP_COLOR : "var(--soft)"} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: selWorkoutTrack === key ? EXP_COLOR : "var(--text)" }}>{label}</div>
                      <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2 }}>{desc}</div>
                    </div>
                    {selWorkoutTrack === key && <Check size={20} color={EXP_COLOR} style={{ marginLeft: "auto", flexShrink: 0 }} />}
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={handlePathSubmit}
              disabled={pathSaving || !selNutritionPath || !selWorkoutTrack}
              style={{ width: "100%", padding: "14px", background: EXP_COLOR, color: "white", border: "none", borderRadius: 12, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 15, cursor: (selNutritionPath && selWorkoutTrack) ? "pointer" : "not-allowed", opacity: (selNutritionPath && selWorkoutTrack) ? 1 : 0.45, transition: "opacity 0.2s" }}
            >
              {pathSaving ? "Saving…" : "Let's Go"}
            </button>
            <div style={{ textAlign: "center", fontSize: 11, color: "var(--soft)", marginTop: 10 }}>Choose one from each section to continue</div>
          </div>
        </div>
      )}

      <div className="top-bar" style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 8 }}>
        <div onClick={handleOpenSettings} style={{ cursor: "pointer" }}>
          <div className="top-bar-title" style={{ color: EXP_COLOR }}>The Expansion</div>
          <div className="top-bar-sub" style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {user.name} <Gear size={11} color="var(--soft)" />
          </div>
        </div>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: 30, color: "var(--deep)", textAlign: "center", lineHeight: 1.3, maxWidth: 360 }}>"{dailyQuote}"</div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn-ghost" onClick={onLogout} style={{ fontSize: 11 }}>Out</button>
        </div>
      </div>

      {/* ── SETTINGS MODAL ── */}
      {showSettings && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(20,15,30,0.55)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={e => { if (e.target === e.currentTarget) setShowSettings(false); }}>
          <div style={{ background: "var(--bg)", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 520, padding: "28px 24px 40px", boxShadow: "0 -12px 60px rgba(0,0,0,0.2)", maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 700, color: EXP_COLOR }}>Settings</div>
              <button onClick={() => setShowSettings(false)} style={{ background: "none", border: "none", fontSize: 22, color: "var(--soft)", cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Name</div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{user.name}</div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <Acorn size={13} color={EXP_COLOR} /> Nutrition Goal
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { key: "fat_loss",    label: "Fat Loss",                    Icon: Fire },
                  { key: "body_recomp", label: "Body Recomposition",          Icon: Scales },
                  { key: "muscle_gain", label: "Muscle Gain",                 Icon: Barbell },
                  { key: "maintenance", label: "Maintenance & General Health", Icon: Leaf },
                ].map(({ key, label, Icon: I }) => (
                  <div key={key} onClick={() => setSettingsNutrition(key)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, border: `2px solid ${settingsNutrition === key ? EXP_COLOR : "var(--border)"}`, background: settingsNutrition === key ? `${EXP_COLOR}0e` : "var(--card)", cursor: "pointer", transition: "all 0.15s" }}>
                    <I size={18} weight="duotone" color={settingsNutrition === key ? EXP_COLOR : "var(--soft)"} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: settingsNutrition === key ? EXP_COLOR : "var(--text)" }}>{label}</span>
                    {settingsNutrition === key && <Check size={16} color={EXP_COLOR} style={{ marginLeft: "auto" }} />}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <Barbell size={13} color={EXP_COLOR} /> Workout Track
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { key: "full_body",  label: "Full Body",    Icon: Lightning },
                  { key: "legs_booty", label: "Legs & Booty", Icon: Flower },
                ].map(({ key, label, Icon: I }) => (
                  <div key={key} onClick={() => setSettingsWorkout(key)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, border: `2px solid ${settingsWorkout === key ? EXP_COLOR : "var(--border)"}`, background: settingsWorkout === key ? `${EXP_COLOR}0e` : "var(--card)", cursor: "pointer", transition: "all 0.15s" }}>
                    <I size={18} weight="duotone" color={settingsWorkout === key ? EXP_COLOR : "var(--soft)"} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: settingsWorkout === key ? EXP_COLOR : "var(--text)" }}>{label}</span>
                    {settingsWorkout === key && <Check size={16} color={EXP_COLOR} style={{ marginLeft: "auto" }} />}
                  </div>
                ))}
              </div>
            </div>

            <button onClick={handleSaveSettings} disabled={settingsSaving || !settingsNutrition || !settingsWorkout} style={{ width: "100%", padding: "13px", background: settingsSaved ? "#48BB78" : EXP_COLOR, color: "white", border: "none", borderRadius: 12, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer", transition: "background 0.2s", marginBottom: 12 }}>
              {settingsSaved ? "Saved!" : settingsSaving ? "Saving…" : "Save Changes"}
            </button>
            <button onClick={onLogout} style={{ width: "100%", padding: "13px", background: "transparent", color: "var(--soft)", border: "1px solid var(--border)", borderRadius: 12, fontFamily: "Jost, sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer", marginBottom: 12 }}>
              Sign Out
            </button>
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 20, marginTop: 4 }}>
              <button onClick={() => {
                if (window.confirm("Are you sure you want to cancel your membership? This cannot be undone. Please reach out to your coach if you have any questions.")) {
                  alert("Your cancellation request has been sent. Your coach will be in touch shortly.");
                }
              }} style={{ width: "100%", padding: "13px", background: "transparent", color: "#E8647A", border: "1px solid #E8647A40", borderRadius: 12, fontFamily: "Jost, sans-serif", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                Cancel Membership
              </button>
              <div style={{ fontSize: 11, color: "var(--soft)", textAlign: "center", marginTop: 8 }}>Need help? Reach out before cancelling — we'd love to work it out.</div>
            </div>
          </div>
        </div>
      )}
      <div className="bottom-nav" style={{ borderBottom: `2px solid ${EXP_COLOR}20` }}>
        {EXP_TABS.map(t => (
          <div key={t.id} className={`nav-item ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)} style={{ color: tab === t.id ? EXP_COLOR : undefined }}>
            <t.Icon size={20} weight={tab === t.id ? "fill" : "regular"} />
            {t.label}
          </div>
        ))}
      </div>
      <div className="page-content">

        {/* ── HOME ── */}
        {tab === "home" && <RootedHome user={user} rfColor={EXP_COLOR} />}

        {tab === "workouts" && (
          <ExpansionWorkouts
            user={user}
            expColor={EXP_COLOR}
            workoutTrack={prefs?.workout_track || lsGet('workout_track') || "full_body"}
            onWorkoutComplete={async () => {
              const todayStr = today();
              const existing = await getJournalEntry(user.id, todayStr);
              const checks = { ...(existing?.checks || {}), movement: true };
              await saveJournalEntry(user.id, todayStr, { ...(existing || {}), checks });
            }}
          />
        )}

        {tab === "nutrition" && <ExpansionNutrition user={user} expColor={EXP_COLOR} nutritionPath={prefs?.nutrition_path || lsGet('nutrition_path') || "maintenance"} />}

        {tab === "resources" && <ExpansionResources user={user} expColor={EXP_COLOR} />}

      </div>
    </div>
  );
}

// ─── Expansion Coach Panel ────────────────────────────────────────────────────
function ExpansionCoachPanel({ members }) {
  const [expTab, setExpTab] = useState("members");
  const [memberData, setMemberData] = useState({}); // { [userId]: { entries: [], cupFillers: [] } }
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    if (!members.length) { setDataLoading(false); return; }
    const ids = members.map(m => m.id);
    Promise.all([
      getAllJournalEntriesForUsers(ids),
      getAllCupFillersForUsers(ids),
    ]).then(([entries, cupFillers]) => {
      const map = {};
      ids.forEach(id => {
        map[id] = {
          entries: entries.filter(e => e.user_id === id),
          cupFillers: cupFillers.filter(f => f.user_id === id),
        };
      });
      setMemberData(map);
      setDataLoading(false);
    });
  }, [members.length]);

  const getConsistency = (member) => {
    const data = memberData[member.id] || { entries: [], cupFillers: [] };
    const joined = member.created_at ? new Date(member.created_at) : new Date();
    const daysSinceJoin = Math.max(1, Math.floor((Date.now() - joined.getTime()) / 86400000));
    const checkInPct = Math.min(100, Math.round((data.entries.filter(e => e.checks).length / daysSinceJoin) * 100));
    const cfTotal = data.cupFillers.length;
    const cfChecked = data.cupFillers.filter(f => f.is_checked).length;
    const cupPct = cfTotal ? Math.round((cfChecked / cfTotal) * 100) : 0;
    return { checkInPct, cupPct, totalEntries: data.entries.length, cfTotal };
  };

  const PctBar = ({ pct, color }) => (
    <div style={{ background: "#f0f0f0", borderRadius: 4, height: 5, overflow: "hidden", marginTop: 3 }}>
      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.4s" }} />
    </div>
  );

  if (selectedMember) {
    const data = memberData[selectedMember.id] || { entries: [], cupFillers: [] };
    const { checkInPct, cupPct } = getConsistency(selectedMember);
    return (
      <div>
        <button onClick={() => setSelectedMember(null)} style={{ background: "none", border: "none", color: "#5B9BD5", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 20 }}>← Back to Members</button>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#5B9BD520", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, color: "#5B9BD5" }}>{selectedMember.name[0]}</div>
          <div>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, fontWeight: 800, color: "#1a1f2e" }}>{selectedMember.name}</div>
            <div style={{ fontSize: 12, color: "#aaa" }}>@{selectedMember.username} · Joined {selectedMember.created_at?.slice(0,10)}</div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
          {[
            { label: "Check-in Consistency", pct: checkInPct, color: "#5B9BD5", note: `${data.entries.filter(e => e.checks).length} check-ins total` },
            { label: "Cup Filler Progress", pct: cupPct, color: "#9B8AA6", note: `${data.cupFillers.filter(f => f.is_checked).length} / ${data.cupFillers.length} filled today` },
          ].map(stat => (
            <div key={stat.label} style={{ background: "white", borderRadius: 14, padding: 18, border: "1px solid #eee" }}>
              <div style={{ fontSize: 11, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{stat.label}</div>
              <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 32, fontWeight: 800, color: stat.color }}>{stat.pct}%</div>
              <PctBar pct={stat.pct} color={stat.color} />
              <div style={{ fontSize: 11, color: "#bbb", marginTop: 6 }}>{stat.note}</div>
            </div>
          ))}
        </div>
        <div style={{ background: "white", borderRadius: 14, border: "1px solid #eee", padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#5B9BD5", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.05em" }}>Recent Check-ins</div>
          {data.entries.filter(e => e.checks).slice(0, 10).map(e => (
            <div key={e.id} style={{ padding: "10px 0", borderBottom: "1px solid #f5f5f5" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{e.date}</span>
                {e.mood && <span style={{ fontSize: 11, color: "#aaa" }}>{e.mood} · {e.energy}</span>}
              </div>
              {e.notes && <div style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>{e.notes}</div>}
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                {Object.entries(e.checks || {}).filter(([,v]) => v).map(([k]) => (
                  <span key={k} style={{ fontSize: 10, background: "#5B9BD520", color: "#5B9BD5", padding: "2px 7px", borderRadius: 20, fontWeight: 700 }}>✓ {k}</span>
                ))}
              </div>
            </div>
          ))}
          {!data.entries.filter(e => e.checks).length && <div style={{ color: "#ccc", fontSize: 13, textAlign: "center", padding: 20 }}>No check-ins logged yet.</div>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="blm-section-title" style={{ color: "#5B9BD5" }}>The Expansion</div>
      <div className="blm-section-sub">Member consistency and workout programming.</div>

      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {[["members","Members"],["workouts","Workouts"]].map(([id, label]) => (
          <button key={id} onClick={() => setExpTab(id)} style={{ padding: "8px 18px", borderRadius: 20, border: "none", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", background: expTab === id ? "#5B9BD5" : "white", color: expTab === id ? "white" : "#888", boxShadow: expTab === id ? "0 2px 8px #5B9BD530" : "0 1px 4px rgba(0,0,0,0.06)" }}>{label}</button>
        ))}
      </div>

      {expTab === "members" && (
        dataLoading ? <div style={{ color: "#aaa", padding: 20 }}>Loading member data…</div> :
        members.length === 0 ? (
          <div style={{ background: "white", borderRadius: 14, padding: 32, textAlign: "center", color: "#ccc", fontSize: 14 }}>No Expansion members yet.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
            {members.map(m => {
              const { checkInPct, cupPct, totalEntries, cfTotal } = getConsistency(m);
              return (
                <div key={m.id} onClick={() => setSelectedMember(m)} style={{ background: "white", borderRadius: 14, padding: 18, border: "1px solid #eee", borderTop: "3px solid #5B9BD5", cursor: "pointer", transition: "box-shadow 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 20px #5B9BD525"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = ""}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#5B9BD520", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: "#5B9BD5", flexShrink: 0 }}>{m.name[0]}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1f2e" }}>{m.name}</div>
                      <div style={{ fontSize: 11, color: "#bbb" }}>@{m.username}</div>
                    </div>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 2 }}>
                      <span style={{ color: "#888" }}>Check-ins</span>
                      <span style={{ fontWeight: 700, color: checkInPct >= 70 ? "#48BB78" : checkInPct >= 40 ? "#E8A020" : "#E8647A" }}>{checkInPct}%</span>
                    </div>
                    <PctBar pct={checkInPct} color={checkInPct >= 70 ? "#48BB78" : checkInPct >= 40 ? "#E8A020" : "#E8647A"} />
                  </div>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 2 }}>
                      <span style={{ color: "#888" }}>Cup Fillers</span>
                      <span style={{ fontWeight: 700, color: "#9B8AA6" }}>{cfTotal ? `${cupPct}%` : "Not set up"}</span>
                    </div>
                    {cfTotal > 0 && <PctBar pct={cupPct} color="#9B8AA6" />}
                  </div>
                  <div style={{ fontSize: 10, color: "#ccc", marginTop: 10 }}>{totalEntries} check-ins total · Joined {m.created_at?.slice(0,10)}</div>
                </div>
              );
            })}
          </div>
        )
      )}

      {expTab === "workouts" && <CoachExpansionWorkouts />}
    </div>
  );
}

// Weekly program builder for one client. Shared by the Belmondo dashboard's
// Ascension client page and the legacy Personal Coaching view.
function PTProgramEditor({ client, color = "#5B9BD5", collapsible = false }) {
  const emptySlots = () => Array(7).fill(null).map(() => ({ label: "", content: "", notes: "", video_ids: [] }));
  const [open, setOpen] = useState(!collapsible);
  const [programs, setPrograms] = useState([]);
  const [videoLibrary, setVideoLibrary] = useState([]);
  const [editProgramId, setEditProgramId] = useState(null);
  const [progWeek, setProgWeek] = useState("");
  const [progSlots, setProgSlots] = useState(emptySlots);
  const [progSaved, setProgSaved] = useState(false);
  const [showVideoPicker, setShowVideoPicker] = useState(null);
  const [progAtSlot, setProgAtSlot] = useState(null);
  const [progAtQuery, setProgAtQuery] = useState("");

  useEffect(() => {
    Promise.all([getPTPrograms(client.id), getVideoLibrary()]).then(([p, v]) => {
      setPrograms(p || []); setVideoLibrary(v || []);
    });
  }, [client.id]);

  const resetForm = () => { setEditProgramId(null); setProgWeek(""); setProgSlots(emptySlots()); };

  const postProgram = async () => {
    if (!progWeek) return;
    setProgSaved("saving");
    const filledSlots = progSlots.filter(s => s.content.trim() || s.label.trim());
    if (editProgramId) {
      const ok = await updatePTProgram(editProgramId, { week: progWeek, slots: filledSlots });
      if (ok) { setPrograms(prev => prev.map(p => p.id === editProgramId ? { ...p, week: progWeek, slots: filledSlots } : p)); setEditProgramId(null); }
      setProgSaved(ok ? "saved" : "error");
    } else {
      const prog = { id: `prog_${Date.now()}`, clientId: client.id, week: progWeek, slots: filledSlots, postedDate: today() };
      const ok = await savePTProgram(prog);
      if (ok) setPrograms(prev => [{ ...prog, client_id: client.id, posted_date: prog.postedDate }, ...prev]);
      setProgSaved(ok ? "saved" : "error");
    }
    setTimeout(() => { setProgSaved(false); if (!editProgramId) { setProgWeek(""); setProgSlots(emptySlots()); } }, 2000);
  };

  const startEditProgram = (prog) => {
    setOpen(true);
    setEditProgramId(prog.id);
    setProgWeek(prog.week);
    setProgSlots(Array(7).fill(null).map((_, i) => prog.slots[i] || { label: "", content: "", notes: "", video_ids: [] }));
  };

  const updateProgSlot = (i, field, val) => setProgSlots(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  const updateProgSlotContent = (i, val, cursor) => {
    const before = val.slice(0, cursor);
    const atIdx = before.lastIndexOf('@');
    if (atIdx !== -1) {
      const query = before.slice(atIdx + 1);
      if (!query.includes(' ') && !query.includes('\n')) { setProgAtSlot(i); setProgAtQuery(query); }
      else setProgAtSlot(null);
    } else setProgAtSlot(null);
    setProgSlots(prev => prev.map((s, idx) => idx === i ? { ...s, content: val } : s));
  };

  const pickProgAtVideo = (i, video) => {
    setProgSlots(prev => prev.map((s, idx) => {
      if (idx !== i) return s;
      const searchStr = '@' + progAtQuery;
      const atIdx = s.content.lastIndexOf(searchStr);
      const newContent = atIdx !== -1 ? s.content.slice(0, atIdx) + progAtQuery + s.content.slice(atIdx + searchStr.length) : s.content;
      const ids = s.video_ids || [];
      return { ...s, content: newContent, video_ids: ids.includes(video.id) ? ids : [...ids, video.id] };
    }));
    setProgAtSlot(null); setProgAtQuery("");
  };

  const toggleProgVideo = (slotIdx, videoId) => setProgSlots(prev => prev.map((s, idx) => {
    if (idx !== slotIdx) return s;
    const ids = s.video_ids || [];
    return { ...s, video_ids: ids.includes(videoId) ? ids.filter(x => x !== videoId) : [...ids, videoId] };
  }));

  const matches = videoLibrary.filter(v => !progAtQuery
    || v.title.toLowerCase().includes(progAtQuery.toLowerCase())
    || (v.exercise || '').toLowerCase().includes(progAtQuery.toLowerCase()));

  const body = (
    <div>
      <div className="card">
        <div className="form-group"><label>Week Label</label><input className="form-input" placeholder="e.g. Week 1 — June 16–22" value={progWeek} onChange={e => setProgWeek(e.target.value)} /></div>
        {progSlots.map((slot, i) => (
          <div key={i} className="slot-form">
            <div className="slot-form-label" style={{ color }}>Workout {i + 1}</div>
            <div className="form-group" style={{ marginBottom: 8 }}><label>Name</label><input className="form-input" placeholder="e.g. Upper Body Push" value={slot.label} onChange={e => updateProgSlot(i, "label", e.target.value)} /></div>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label>Workout Details</label>
              <textarea className="form-input form-textarea" placeholder="Exercises, sets, reps... (type @ to attach a video)" value={slot.content}
                onChange={e => updateProgSlotContent(i, e.target.value, e.target.selectionStart)}
                onBlur={() => setTimeout(() => setProgAtSlot(null), 200)} />
              {progAtSlot === i && (
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", maxHeight: 200, overflowY: "auto", marginTop: 4 }}>
                  {matches.length === 0
                    ? <div style={{ padding: "10px 14px", fontSize: 13, color: "var(--soft)" }}>No videos match "{progAtQuery}"</div>
                    : matches.map(v => (
                      <div key={v.id} onMouseDown={e => { e.preventDefault(); pickProgAtVideo(i, v); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid var(--border)" }}>
                        <span>▶</span>
                        <div><div style={{ fontWeight: 700, fontSize: 13 }}>{v.title}</div><div style={{ fontSize: 11, color: "var(--soft)" }}>{v.exercise}</div></div>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div className="form-group" style={{ marginBottom: 8 }}><label>Coach Notes</label><input className="form-input" placeholder="Tips, cues..." value={slot.notes} onChange={e => updateProgSlot(i, "notes", e.target.value)} /></div>
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--soft)", fontWeight: 700, marginBottom: 6 }}>Attached Videos</div>
              {(slot.video_ids || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
                  {(slot.video_ids || []).map(vid => { const v = videoLibrary.find(vl => vl.id === vid); return v ? <span key={vid} style={{ padding: "4px 10px", background: "#FF000015", borderRadius: 20, fontSize: 11, color: "#CC0000", fontWeight: 600 }}>▶ {v.title} <span style={{ cursor: "pointer", marginLeft: 4 }} onClick={() => toggleProgVideo(i, vid)}>×</span></span> : null; })}
                </div>
              )}
              <button className="btn-ghost" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => setShowVideoPicker(showVideoPicker === i ? null : i)}>{showVideoPicker === i ? "Done" : "+ Video"}</button>
              {showVideoPicker === i && (
                <div style={{ marginTop: 8, background: "var(--bg)", borderRadius: 10, padding: 10, maxHeight: 160, overflowY: "auto" }}>
                  {videoLibrary.length === 0 ? <div style={{ fontSize: 12, color: "var(--soft)" }}>No videos in library yet</div>
                    : videoLibrary.map(v => <div key={v.id} onClick={() => toggleProgVideo(i, v.id)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 6px", borderRadius: 7, cursor: "pointer", background: (slot.video_ids || []).includes(v.id) ? color + "18" : "transparent" }}><span>{(slot.video_ids || []).includes(v.id) ? "✓" : "○"}</span><span style={{ fontSize: 12, fontWeight: 600 }}>{v.title}</span><span style={{ fontSize: 11, color: "var(--soft)" }}>{v.exercise}</span></div>)}
                </div>
              )}
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-primary mt-8" style={{ flex: 1, background: color }} onClick={postProgram}>{progSaved === "saving" ? "Saving..." : progSaved === "saved" ? "✓ Saved!" : editProgramId ? "Save Changes" : "Post Program"}</button>
          {editProgramId && <button className="btn-ghost mt-8" onClick={resetForm}>Cancel</button>}
        </div>
      </div>

      {programs.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Past Programs</div>
          {programs.map(prog => (
            <div key={prog.id} className="week-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><div className="week-title">{prog.week}</div><div className="week-posted">Posted {fmt(prog.posted_date)}</div></div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => startEditProgram(prog)}>✏️ Edit</button>
                  <button className="btn-ghost" style={{ fontSize: 12, color: "#E8647A" }} onClick={async () => { if (window.confirm(`Delete "${prog.week}"? Their workout history stays saved.`)) { await deletePTProgram(prog.id); setPrograms(prev => prev.filter(p => p.id !== prog.id)); } }}>🗑 Delete</button>
                </div>
              </div>
              {(prog.slots || []).map((slot, i) => {
                const slotVids = (slot.video_ids || []).map(vid => videoLibrary.find(v => v.id === vid)).filter(Boolean);
                return (
                  <div key={i} className="workout-slot">
                    <div className="slot-label" style={{ color }}>{slot.label || `Workout ${i + 1}`}</div>
                    <div className="slot-content">{slot.content}</div>
                    {slotVids.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>{slotVids.map(v => <a key={v.id} href={v.youtube_url} target="_blank" rel="noreferrer" style={{ padding: "3px 9px", background: color + "18", borderRadius: 20, fontSize: 11, color, fontWeight: 600, textDecoration: "none" }}>▶ {v.title}</a>)}</div>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (!collapsible) return body;

  return (
    <div style={{ marginBottom: 20 }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: "100%", background: "none", border: `1.5px solid ${color}30`, borderRadius: 12, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 14, color, marginBottom: open ? 12 : 0 }}>
        <span>Build Program for {client.name}{programs.length ? ` (${programs.length} week${programs.length !== 1 ? "s" : ""})` : ""}</span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open && body}
    </div>
  );
}

// ─── Ascension Coach Panel ────────────────────────────────────────────────────
function AscensionClientDetail({ client, onBack, coachUser }) {
  const ASC_COLOR = "#9B8AA6";
  const [note, setNote] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);
  const [nutritionPath, setNutritionPath] = useState("maintenance");
  const [workoutTrack, setWorkoutTrack] = useState("full_body");
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const blankPlan = (base) => ({
    label: base.label,
    desc: base.desc,
    targets: { light: { ...base.targets.light }, heavy: { ...base.targets.heavy } },
  });
  const [plan, setPlan] = useState(() => blankPlan(EXP_NUTRITION_PATHS.maintenance));
  const [training, setTraining] = useState({ label: "", notes: "" });

  useEffect(() => {
    getUserPrefs(client.id).then(p => {
      setNote(p?.coach_note || "");
      const path = p?.nutrition_path || "maintenance";
      setNutritionPath(path);
      setWorkoutTrack(p?.workout_track || "full_body");
      const base = EXP_NUTRITION_PATHS[path] || EXP_NUTRITION_PATHS.maintenance;
      setPlan(p?.custom_nutrition?.targets ? {
        label: p.custom_nutrition.label || base.label,
        desc: p.custom_nutrition.desc || base.desc,
        targets: {
          light: { ...base.targets.light, ...(p.custom_nutrition.targets.light || {}) },
          heavy: { ...base.targets.heavy, ...(p.custom_nutrition.targets.heavy || {}) },
        },
      } : blankPlan(base));
      setTraining({ label: p?.custom_training?.label || "", notes: p?.custom_training?.notes || "" });
      setLoading(false);
    });
  }, [client.id]);

  // Loading a preset replaces the editable fields; the coach then tweaks freely.
  const loadPreset = (key) => {
    setNutritionPath(key);
    setPlan(blankPlan(EXP_NUTRITION_PATHS[key]));
  };

  const setTarget = (day, macro, value) => {
    const n = Math.max(0, Math.min(99, parseInt(value, 10) || 0));
    setPlan(p => ({ ...p, targets: { ...p.targets, [day]: { ...p.targets[day], [macro]: n } } }));
  };

  const saveNote = async () => {
    setNoteSaved("saving");
    await saveUserPrefs(client.id, { coach_note: note });
    setNoteSaved("saved");
    setTimeout(() => setNoteSaved(false), 2000);
  };

  const savePrefs = async () => {
    setPrefsSaved("saving");
    await saveUserPrefs(client.id, {
      nutrition_path: nutritionPath,
      workout_track: workoutTrack,
      custom_nutrition: plan,
      custom_training: training,
    });
    setPrefsSaved("saved");
    setTimeout(() => setPrefsSaved(false), 2000);
  };

  const NUTRITION_OPTIONS = Object.entries(EXP_NUTRITION_PATHS).map(([key, val]) => ({ key, label: val.label, desc: val.desc }));
  const WORKOUT_OPTIONS = [
    { key: "full_body", label: "Full Body" },
    { key: "legs_booty", label: "Legs & Booty" },
  ];

  return (
    <div>
      <button onClick={onBack} style={{ background: "none", border: "none", color: ASC_COLOR, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 20 }}>← Back to Clients</button>

      {/* Note to client */}
      <div className="card" style={{ borderLeft: `3px solid ${ASC_COLOR}`, marginBottom: 20 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: ASC_COLOR, fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
          <Feather size={12} color={ASC_COLOR} weight="fill" /> Note to {client.name}
        </div>
        <div style={{ fontSize: 11, color: "var(--soft)", marginBottom: 12 }}>Shows on their home screen — personal, just for them.</div>
        {loading ? <div style={{ fontSize: 13, color: "var(--soft)" }}>Loading…</div> : <>
          <textarea className="form-input form-textarea" style={{ minHeight: 90 }} placeholder={`Write a note to ${client.name}…`} value={note} onChange={e => setNote(e.target.value)} />
          <button onClick={saveNote} style={{ marginTop: 10, padding: "10px 20px", background: noteSaved === "saved" ? "#48BB78" : ASC_COLOR, color: "white", border: "none", borderRadius: 10, fontFamily: "'Jost', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            {noteSaved === "saving" ? "Saving…" : noteSaved === "saved" ? "Saved!" : "Save Note"}
          </button>
        </>}
      </div>

      {/* Nutrition & Workout Track */}
      <div className="card" style={{ borderLeft: `3px solid ${ASC_COLOR}`, marginBottom: 20 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: ASC_COLOR, fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
          <Leaf size={12} color={ASC_COLOR} weight="fill" /> Nutrition & Training Plan
        </div>
        {loading ? <div style={{ fontSize: 13, color: "var(--soft)" }}>Loading…</div> : <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Start from a basic layout</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
              {NUTRITION_OPTIONS.map(o => (
                <button key={o.key} onClick={() => loadPreset(o.key)} title={o.desc} style={{ padding: "6px 12px", borderRadius: 20, border: `1.5px solid ${nutritionPath === o.key ? ASC_COLOR : "var(--border)"}`, background: nutritionPath === o.key ? ASC_COLOR + "12" : "transparent", color: nutritionPath === o.key ? ASC_COLOR : "var(--mid)", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                  {o.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "var(--soft)", marginBottom: 14, lineHeight: 1.5 }}>
              Loading a layout fills the fields below — then change anything you like. What you save here is what {client.name} sees.
            </div>

            <div className="form-group">
              <label>Plan name</label>
              <input className="form-input" value={plan.label} onChange={e => setPlan(p => ({ ...p, label: e.target.value }))} placeholder="e.g. Carina's Fat Loss Plan" />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea className="form-input form-textarea" style={{ minHeight: 80 }} value={plan.desc} onChange={e => setPlan(p => ({ ...p, desc: e.target.value }))} placeholder="Explain the approach in your own words…" />
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, margin: "18px 0 8px" }}>Daily Portions</div>
            <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <div />
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textAlign: "center" }}>Light day</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textAlign: "center" }}>Heavy day</div>
            </div>
            {EXP_FOOD_GROUPS.map(g => (
              <div key={g.key} style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <g.Icon size={14} color={g.color} weight="fill" />
                  <span style={{ fontSize: 12, fontFamily: "Jost, sans-serif", fontWeight: 700, color: "var(--deep)" }}>{g.label}</span>
                  <span style={{ fontSize: 10, color: "var(--soft)" }}>({g.unit})</span>
                </div>
                {["light", "heavy"].map(day => (
                  <input
                    key={day}
                    type="number"
                    min="0"
                    max="99"
                    className="form-input"
                    value={plan.targets[day][g.key]}
                    onChange={e => setTarget(day, g.key, e.target.value)}
                    style={{ textAlign: "center", padding: "7px 4px", fontSize: 13 }}
                  />
                ))}
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 16, borderTop: "1px solid var(--border)", paddingTop: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Training Plan</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {WORKOUT_OPTIONS.map(o => (
                <button key={o.key} onClick={() => setWorkoutTrack(o.key)} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: `1.5px solid ${workoutTrack === o.key ? ASC_COLOR : "var(--border)"}`, background: workoutTrack === o.key ? ASC_COLOR + "10" : "transparent", color: workoutTrack === o.key ? ASC_COLOR : "var(--mid)", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                  {o.label}
                </button>
              ))}
            </div>
            <div className="form-group">
              <label>Plan name (optional)</label>
              <input className="form-input" value={training.label} onChange={e => setTraining(t => ({ ...t, label: e.target.value }))} placeholder={`e.g. ${client.name}'s Strength Block`} />
            </div>
            <div className="form-group">
              <label>Notes for {client.name} (optional)</label>
              <textarea className="form-input form-textarea" style={{ minHeight: 90 }} value={training.notes} onChange={e => setTraining(t => ({ ...t, notes: e.target.value }))} placeholder="Frequency, focus, substitutions, anything to work around…" />
            </div>
            <div style={{ fontSize: 11, color: "var(--soft)", lineHeight: 1.5 }}>
              Shows at the top of their Workouts tab. Week-by-week workouts are still posted under My Program.
            </div>
          </div>
          <button onClick={savePrefs} style={{ width: "100%", padding: "11px 0", background: prefsSaved === "saved" ? "#48BB78" : ASC_COLOR, color: "white", border: "none", borderRadius: 10, fontFamily: "'Jost', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            {prefsSaved === "saving" ? "Saving…" : prefsSaved === "saved" ? "✓ Saved — client will see this on next load" : "Save Plan"}
          </button>
        </>}
      </div>

      <PTProgramEditor client={client} color={ASC_COLOR} collapsible />

      <CoachMemberDetail member={client} onBack={onBack} coachUser={coachUser} />
    </div>
  );
}

function AscensionCoachPanel({ members, coachUser }) {
  const [ascTab, setAscTab] = useState("clients");
  const [selectedClient, setSelectedClient] = useState(null);
  const [allCheckins, setAllCheckins] = useState([]);
  const [allWorkouts, setAllWorkouts] = useState([]);
  const [allWins, setAllWins] = useState([]);
  const [dotMessages, setDotMessages] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const lsKey = (t) => `gt_ptcoach_last_seen_${t}`;

  useEffect(() => {
    Promise.all([
      getCheckins(null, 'pt'),
      getWorkouts(null, 'pt'),
      getWins('pt'),
    ]).then(([ci, w, wn]) => {
      setAllCheckins(ci);
      setAllWorkouts(w);
      setAllWins(wn.filter(x => x.type === "win" || x.type === "gratitude"));
      if (members.length) {
        const lastMsg = localStorage.getItem(lsKey('messages'));
        Promise.all(members.map(c => getMessages(`pt_dm_${[c.id, "coach"].sort().join("_")}`))).then(arrays => {
          const all = arrays.flat().filter(m => m.sender_id !== "coach");
          setDotMessages(lastMsg ? all.some(m => m.created_at > lastMsg) : all.length > 0);
        });
      }
      setDataLoading(false);
    });
  }, [members.length]);

  const getMIA = (clientId) => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 3);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    const last = allCheckins.filter(c => c.user_id === clientId).sort((a,b) => b.date.localeCompare(a.date))[0];
    return !last || last.date < cutoffStr;
  };

  const recentActivity = [
    ...allCheckins.map(c => ({ ...c, _type: "check-in", _date: c.date, _clientId: c.user_id })),
    ...allWorkouts.map(w => ({ ...w, _type: "workout", _date: w.date, _clientId: w.user_id })),
    ...allWins.map(w => ({ ...w, _type: w.type, _date: w.date, _clientId: w.user_id })),
  ].filter(a => members.some(c => c.id === a._clientId))
    .sort((a,b) => b._date.localeCompare(a._date))
    .slice(0, 20);

  const TABS = [
    { id: "clients", label: "Clients" },
    { id: "activity", label: "Activity" },
    { id: "messages", label: "Messages", dot: dotMessages },
  ];

  if (selectedClient) {
    return <AscensionClientDetail client={selectedClient} onBack={() => setSelectedClient(null)} coachUser={coachUser} />;
  }

  return (
    <div>
      <div className="blm-section-title" style={{ color: "#9B8AA6" }}>Ascension</div>
      <div className="blm-section-sub">Your personal coaching clients — all their data in one place.</div>

      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => { if (t.id === "messages") { localStorage.setItem(lsKey('messages'), new Date().toISOString()); setDotMessages(false); } setAscTab(t.id); }}
            style={{ position: "relative", padding: "8px 18px", borderRadius: 20, border: "none", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", background: ascTab === t.id ? "#9B8AA6" : "white", color: ascTab === t.id ? "white" : "#888", boxShadow: ascTab === t.id ? "0 2px 8px #9B8AA630" : "0 1px 4px rgba(0,0,0,0.06)" }}>
            {t.label}
            {t.dot && <span style={{ position: "absolute", top: 5, right: 5, width: 7, height: 7, background: "#E8647A", borderRadius: "50%" }} />}
          </button>
        ))}
      </div>

      {ascTab === "clients" && (
        <div>
          {dataLoading ? <div style={{ color: "#aaa", padding: 20 }}>Loading…</div> : members.length === 0 ? (
            <div style={{ background: "white", borderRadius: 14, padding: 40, textAlign: "center", border: "1px solid #eee" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
              <div style={{ fontWeight: 700, color: "#1a1f2e", marginBottom: 8 }}>No Ascension clients yet</div>
              <div style={{ fontSize: 13, color: "#aaa" }}>Clients with the <code>pt_client</code> role will appear here once they sign up.</div>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
                {[
                  ["Total Clients", members.length, "#9B8AA6"],
                  ["Checked In Today", allCheckins.filter(c => c.date === today() && members.some(m => m.id === c.user_id)).length, "#48BB78"],
                  ["MIA 3+ Days", members.filter(m => getMIA(m.id)).length, "#E8647A"],
                ].map(([label, val, color]) => (
                  <div key={label} style={{ background: "white", borderRadius: 14, padding: "16px 12px", textAlign: "center", border: "1px solid #eee", borderTop: `3px solid ${color}` }}>
                    <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 30, fontWeight: 800, color }}>{val}</div>
                    <div style={{ fontSize: 10, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>
              {members.map(m => {
                const mia = getMIA(m.id);
                const lastCI = allCheckins.filter(c => c.user_id === m.id).sort((a,b) => b.date.localeCompare(a.date))[0];
                return (
                  <div key={m.id} onClick={() => setSelectedClient(m)} style={{ background: "white", borderRadius: 14, padding: 16, border: "1px solid #eee", borderLeft: `4px solid ${mia ? "#E8647A" : "#9B8AA6"}`, marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 14, transition: "box-shadow 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px #9B8AA625"}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = ""}>
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: mia ? "#E8647A" : "#9B8AA6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "white", flexShrink: 0 }}>{m.name[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                        <span style={{ fontWeight: 800, fontSize: 15, color: "#1a1f2e" }}>{m.name}</span>
                        {mia && <span style={{ fontSize: 10, fontWeight: 700, color: "#E8647A", background: "#FFE8EC", padding: "2px 8px", borderRadius: 20 }}>⚠️ MIA 3+ days</span>}
                      </div>
                      <div style={{ fontSize: 11, color: "#bbb", marginBottom: 4 }}>@{m.username}</div>
                      <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#888" }}>
                        <span>✅ {allCheckins.filter(c => c.user_id === m.id).length} check-ins</span>
                        <span>💪 {allWorkouts.filter(w => w.user_id === m.id).length} workouts</span>
                        {lastCI && <span>Last: {fmt(lastCI.date)}</span>}
                      </div>
                    </div>
                    <span style={{ color: "#9B8AA6", fontSize: 18 }}>→</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {ascTab === "activity" && (
        <div>
          {recentActivity.length === 0
            ? <div style={{ background: "white", borderRadius: 14, padding: 40, textAlign: "center", color: "#ccc", border: "1px solid #eee", fontSize: 14 }}>No activity yet — it'll show here once clients start logging.</div>
            : recentActivity.map((item, i) => {
              const client = members.find(c => c.id === item._clientId);
              if (!client) return null;
              const typeLabel = item._type === "check-in" ? "✅ Check-in" : item._type === "workout" ? "💪 Workout" : item._type === "win" ? "🏆 Win" : "🙏 Gratitude";
              const typeColor = item._type === "check-in" ? "#48BB78" : item._type === "workout" ? "#9B8AA6" : "#E8A020";
              return (
                <div key={`${item._type}_${item.id || i}`} onClick={() => setSelectedClient(client)} style={{ background: "white", borderRadius: 12, padding: "12px 16px", marginBottom: 8, border: "1px solid #eee", borderLeft: `3px solid ${typeColor}`, cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: item.notes || item.text ? 6 : 0 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#9B8AA620", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "#9B8AA6", flexShrink: 0 }}>{client.name[0]}</div>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#1a1f2e" }}>{client.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: typeColor }}>{typeLabel}</span>
                    <span style={{ marginLeft: "auto", fontSize: 11, color: "#bbb" }}>{fmt(item._date)}</span>
                  </div>
                  {(item.notes || item.text) && <div style={{ fontSize: 12, color: "#888", fontStyle: "italic", paddingLeft: 38 }}>{(item.notes || item.text || "").slice(0, 100)}</div>}
                </div>
              );
            })}
        </div>
      )}

      {ascTab === "messages" && members.length > 0 && (
        <PTCoachMessaging user={coachUser} clients={members} />
      )}
      {ascTab === "messages" && members.length === 0 && (
        <div style={{ background: "white", borderRadius: 14, padding: 40, textAlign: "center", color: "#ccc", border: "1px solid #eee" }}>No clients to message yet.</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── BELMONDO COACH DASHBOARD ─────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function BelmondoCoachDashboard({ user, onLogout }) {
  const [tab, setTab] = useState("overview");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailSearch, setEmailSearch] = useState("");
  const [previewAs, setPreviewAs] = useState(null); // "rooted" | "expansion" | "ascension"

  // hooks must come before any conditional returns
  useEffect(() => {
    getBelmondoMembers().then(m => { setMembers(m); setLoading(false); });
  }, []);

  if (previewAs === "rooted") {
    const previewUser = { ...user, role: "rooted", id: user.id, name: user.name };
    return (
      <div>
        <style>{styles}</style>
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, background: "#9CAF88", color: "white", padding: "8px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "Jost, sans-serif", fontSize: 13, fontWeight: 700 }}>
          <span>👁 Preview: Rooted Foundation</span>
          <button onClick={() => setPreviewAs(null)} style={{ background: "rgba(255,255,255,0.25)", border: "none", color: "white", borderRadius: 8, padding: "4px 12px", cursor: "pointer", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12 }}>Exit Preview</button>
        </div>
        <div style={{ paddingTop: 36 }}>
          <RootedApp user={previewUser} onLogout={() => setPreviewAs(null)} isPreview={true} />
        </div>
      </div>
    );
  }
  if (previewAs === "expansion") {
    const previewUser = { ...user, role: "expansion", id: user.id, name: user.name };
    return (
      <div>
        <style>{styles}</style>
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, background: "#5B9BD5", color: "white", padding: "8px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "Jost, sans-serif", fontSize: 13, fontWeight: 700 }}>
          <span>👁 Preview: The Expansion</span>
          <button onClick={() => setPreviewAs(null)} style={{ background: "rgba(255,255,255,0.25)", border: "none", color: "white", borderRadius: 8, padding: "4px 12px", cursor: "pointer", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12 }}>Exit Preview</button>
        </div>
        <div style={{ paddingTop: 36 }}>
          <ExpansionApp user={previewUser} onLogout={() => setPreviewAs(null)} />
        </div>
      </div>
    );
  }
  if (previewAs === "ascension") {
    const previewUser = { ...user, role: "pt_client", id: user.id, name: user.name };
    return (
      <div>
        <style>{styles}</style>
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, background: "#9B8AA6", color: "white", padding: "8px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "Jost, sans-serif", fontSize: 13, fontWeight: 700 }}>
          <span>👁 Preview: Ascension</span>
          <button onClick={() => setPreviewAs(null)} style={{ background: "rgba(255,255,255,0.25)", border: "none", color: "white", borderRadius: 8, padding: "4px 12px", cursor: "pointer", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12 }}>Exit Preview</button>
        </div>
        <div style={{ paddingTop: 36 }}>
          <PTClientApp user={previewUser} onLogout={() => setPreviewAs(null)} />
        </div>
      </div>
    );
  }

  const downloadCSV = () => {
    const rows = [["Name","Username","Email","Program","Joined"]];
    members.forEach(m => rows.push([m.name, m.username, m.email || "", m.role === "rooted" ? "Rooted Foundation" : "The Expansion", m.created_at?.slice(0,10) || ""]));
    const csv = rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "belmondo-members.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const rootedMembers = members.filter(m => m.role === "rooted");
  const expansionMembers = members.filter(m => m.role === "expansion");
  const ascensionMembers = members.filter(m => m.role === "pt_client");

  const SIDEBAR_SECTIONS = [
    { id: "overview",   label: "Overview",           icon: "◈", color: "#9B8AA6" },
    { id: "ascension",  label: "Ascension",          icon: "✦", color: "#9B8AA6" },
    { id: "expansion",  label: "The Expansion",      icon: "✨", color: "#5B9BD5" },
    { id: "rooted",     label: "Rooted Foundation",  icon: "🌱", color: "#9CAF88" },
    { id: "bootcamp",   label: "Boot Camp",          icon: "⚡", color: "#7B8FB8" },
    { id: "admin",      label: "Admin",              icon: "⚙", color: "#888" },
  ];

  const sidebarStyle = `
    .blm-shell { display: flex; min-height: 100vh; background: #F4F2F8; font-family: 'Jost', sans-serif; }
    .blm-sidebar { width: 220px; min-height: 100vh; background: linear-gradient(180deg, #1a1f2e 0%, #1e2228 100%); display: flex; flex-direction: column; flex-shrink: 0; position: fixed; top: 0; left: 0; bottom: 0; z-index: 100; }
    .blm-sidebar-logo { padding: 28px 20px 20px; border-bottom: 1px solid rgba(255,255,255,0.06); }
    .blm-sidebar-logo-text { font-family: 'Cormorant Garamond', serif; font-size: 18px; font-weight: 800; background: linear-gradient(135deg, #9CAF88, #5B9BD5, #9B8AA6); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; line-height: 1.2; }
    .blm-sidebar-sub { font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,0.3); margin-top: 4px; }
    .blm-sidebar-nav { flex: 1; padding: 16px 12px; display: flex; flex-direction: column; gap: 2px; }
    .blm-nav-item { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 10px; cursor: pointer; transition: all 0.15s; color: rgba(255,255,255,0.45); font-size: 13px; font-weight: 600; border: none; background: none; width: 100%; text-align: left; }
    .blm-nav-item:hover { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.75); }
    .blm-nav-item.active { background: rgba(255,255,255,0.08); color: white; }
    .blm-nav-icon { font-size: 16px; width: 20px; text-align: center; flex-shrink: 0; }
    .blm-sidebar-footer { padding: 16px 12px 24px; border-top: 1px solid rgba(255,255,255,0.06); display: flex; flex-direction: column; gap: 8px; }
    .blm-preview-btn { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.5); font-size: 11px; font-weight: 700; cursor: pointer; font-family: 'Jost', sans-serif; transition: all 0.12s; }
    .blm-preview-btn:hover { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.8); }
    .blm-main { margin-left: 220px; flex: 1; min-height: 100vh; }
    .blm-topbar { background: white; border-bottom: 1px solid #eee; padding: 16px 32px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 50; }
    .blm-content { padding: 32px; }
    .blm-stat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; margin-bottom: 32px; }
    .blm-stat-card { background: white; border-radius: 16px; padding: 20px; border: 1px solid #eee; }
    .blm-stat-num { font-family: 'Cormorant Garamond', serif; font-size: 36px; font-weight: 800; line-height: 1; margin-bottom: 4px; }
    .blm-stat-label { font-size: 11px; color: #999; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
    .blm-section-title { font-family: 'Cormorant Garamond', serif; font-size: 24px; font-weight: 700; margin-bottom: 6px; }
    .blm-section-sub { font-size: 13px; color: #999; margin-bottom: 24px; }
    .blm-member-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: white; border-radius: 12px; margin-bottom: 8px; border: 1px solid #eee; }
    @media (max-width: 768px) { .blm-sidebar { width: 60px; } .blm-sidebar-logo, .blm-nav-label, .blm-sidebar-sub, .blm-preview-btn span { display: none; } .blm-main { margin-left: 60px; } .blm-content { padding: 16px; } .blm-topbar { padding: 12px 16px; } }
  `;

  return (
    <div className="blm-shell">
      <style>{styles}</style>
      <style>{sidebarStyle}</style>

      {/* Sidebar */}
      <div className="blm-sidebar">
        <div className="blm-sidebar-logo">
          <div className="blm-sidebar-logo-text">Belmondo Lifestyle</div>
          <div className="blm-sidebar-sub">Coach Dashboard</div>
        </div>
        <nav className="blm-sidebar-nav">
          {SIDEBAR_SECTIONS.map(s => (
            <button key={s.id} className={`blm-nav-item ${tab === s.id ? "active" : ""}`} onClick={() => setTab(s.id)} style={tab === s.id ? { borderLeft: `3px solid ${s.color}`, paddingLeft: 9 } : {}}>
              <span className="blm-nav-icon" style={{ color: tab === s.id ? s.color : undefined }}>{s.icon}</span>
              <span className="blm-nav-label">{s.label}</span>
            </button>
          ))}
        </nav>
        <div className="blm-sidebar-footer">
          <button className="blm-preview-btn" onClick={() => setPreviewAs("rooted")}><span style={{ color: "#9CAF88" }}>🌱</span><span>Preview Rooted</span></button>
          <button className="blm-preview-btn" onClick={() => setPreviewAs("expansion")}><span style={{ color: "#5B9BD5" }}>✨</span><span>Preview Expansion</span></button>
          <button className="blm-preview-btn" onClick={() => setPreviewAs("ascension")}><span style={{ color: "#9B8AA6" }}>✦</span><span>Preview Ascension</span></button>
          <button className="blm-preview-btn" onClick={onLogout}><span>→</span><span>Sign Out</span></button>
        </div>
      </div>

      {/* Main content */}
      <div className="blm-main">
        <div className="blm-topbar">
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, color: "#1a1f2e" }}>
            {SIDEBAR_SECTIONS.find(s => s.id === tab)?.label}
          </div>
          <div style={{ fontSize: 13, color: "#999" }}>Welcome back, {user.name}</div>
        </div>
        <div className="blm-content">

          {/* ── OVERVIEW ── */}
          {tab === "overview" && (
            <div>
              <div className="blm-section-title">App Overview</div>
              <div className="blm-section-sub">A snapshot of your entire community right now.</div>
              <div className="blm-stat-grid">
                {[
                  { label: "Total Members", value: members.length, color: "#9B8AA6" },
                  { label: "Rooted Foundation", value: rootedMembers.length, color: "#9CAF88" },
                  { label: "The Expansion", value: expansionMembers.length, color: "#5B9BD5" },
                  { label: "Ascension Clients", value: ascensionMembers.length, color: "#9B8AA6" },
                ].map(s => (
                  <div key={s.label} className="blm-stat-card" style={{ borderTop: `4px solid ${s.color}` }}>
                    <div className="blm-stat-num" style={{ color: s.color }}>{loading ? "—" : s.value}</div>
                    <div className="blm-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                {[
                  { title: "Rooted Foundation", color: "#9CAF88", members: rootedMembers },
                  { title: "The Expansion", color: "#5B9BD5", members: expansionMembers },
                  { title: "Ascension", color: "#9B8AA6", members: ascensionMembers },
                ].map(({ title, color, members: list }) => (
                  <div key={title} style={{ background: "white", borderRadius: 16, padding: 20, border: "1px solid #eee" }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{title}</div>
                    {list.slice(0, 5).map(m => (
                      <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid #f5f5f5" }}>
                        <div style={{ width: 30, height: 30, borderRadius: "50%", background: color + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color, flexShrink: 0 }}>{m.name[0]}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</div>
                          <div style={{ fontSize: 11, color: "#aaa" }}>{m.created_at?.slice(0,10)}</div>
                        </div>
                      </div>
                    ))}
                    {list.length > 5 && <div style={{ fontSize: 11, color: "#aaa", marginTop: 8, textAlign: "center" }}>+{list.length - 5} more</div>}
                    {list.length === 0 && <div style={{ fontSize: 12, color: "#ccc", textAlign: "center", padding: "12px 0" }}>No members yet</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ASCENSION ── */}
          {tab === "ascension" && (
            <div>
              <AscensionCoachPanel members={ascensionMembers} coachUser={user} />
              <div style={{ marginTop: 24 }}>
                <BelmondoResourcesManager defaultProgram="expansion" />
              </div>
            </div>
          )}

          {/* ── EXPANSION ── */}
          {tab === "expansion" && (
            <div>
              <ExpansionCoachPanel members={expansionMembers} />
              <div style={{ marginTop: 24 }}>
                <BelmondoResourcesManager defaultProgram="expansion" />
              </div>
            </div>
          )}


          {/* ── ROOTED ── */}
          {tab === "rooted" && (
            <div>
              <div className="blm-section-title" style={{ color: "#9CAF88" }}>Rooted Foundation</div>
              <div className="blm-section-sub">Your entry-level community.</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 28 }}>
                <div style={{ background: "white", borderRadius: 14, padding: 20, border: "1px solid #eee", borderTop: "3px solid #9CAF88", textAlign: "center" }}>
                  <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 32, fontWeight: 800, color: "#9CAF88" }}>{rootedMembers.length}</div>
                  <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>Members</div>
                </div>
              </div>
              <div style={{ background: "white", borderRadius: 16, padding: 24, border: "1px solid #eee" }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#9CAF88", marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 11 }}>Member List</div>
                {rootedMembers.map(m => (
                  <div key={m.id} className="blm-member-row">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#9CAF8820", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#9CAF88" }}>{m.name[0]}</div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{m.name}</div>
                        <div style={{ fontSize: 12, color: "#aaa" }}>@{m.username}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: "#ccc" }}>{m.created_at?.slice(0,10)}</div>
                  </div>
                ))}
                {rootedMembers.length === 0 && <div style={{ color: "#ccc", fontSize: 13, textAlign: "center", padding: 20 }}>No members yet.</div>}
              </div>
              <div style={{ marginTop: 24 }}>
                <BelmondoResourcesManager defaultProgram="rooted" />
              </div>
            </div>
          )}

          {/* ── BOOT CAMP ── */}
          {tab === "bootcamp" && (
            <div>
              <div className="blm-section-title">Boot Camp</div>
              <div className="blm-section-sub">Manage your Get Thicc Boot Camp program.</div>
              <BelmondoBootCampManager user={user} />
            </div>
          )}

          {/* ── ADMIN ── */}
          {tab === "admin" && (
            <div>
              <div className="blm-section-title">Admin</div>
              <div className="blm-section-sub">Settings, invite codes and member management.</div>
              <div style={{ marginBottom: 24 }}>
                <PushNotificationSender members={members} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <BelmondoSettings />
                <div>
                  <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Email List</div>
                  <input placeholder="Search members..." value={emailSearch} onChange={e => setEmailSearch(e.target.value)} style={{ width: "100%", padding: "9px 14px", borderRadius: 10, border: "1px solid #ddd", fontFamily: "Jost, sans-serif", fontSize: 13, marginBottom: 12, boxSizing: "border-box" }} />
                  {members.filter(m => m.email && (!emailSearch || m.name.toLowerCase().includes(emailSearch.toLowerCase()) || m.email.toLowerCase().includes(emailSearch.toLowerCase()))).map(m => (
                    <div key={m.id} className="blm-member-row">
                      <div><div style={{ fontWeight: 600, fontSize: 14 }}>{m.name}</div><div style={{ fontSize: 12, color: "#aaa" }}>{m.email}</div></div>
                      <div style={{ fontSize: 11, padding: "3px 8px", borderRadius: 20, background: m.role === "rooted" ? "#9CAF8820" : m.role === "expansion" ? "#5B9BD520" : "#9B8AA620", color: m.role === "rooted" ? "#9CAF88" : m.role === "expansion" ? "#5B9BD5" : "#9B8AA6", fontWeight: 700 }}>{m.role === "rooted" ? "Rooted" : m.role === "expansion" ? "Expansion" : "Ascension"}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function BelmondoBootCampManager({ user }) {
  const [bcTab, setBcTab] = useState("today");
  const BC_GREEN = "#9CAF88";
  const BC_BLUE = "#5B9BD5";
  const BC_PURPLE = "#9B8AA6";
  const BC_TABS = [
    ["today","Today"],["members","Members"],["messages","Messages"],
    ["wins","Wins"],["workouts","Workouts"],["videos","Videos"],
    ["qa","Q&A"],["nutrition","Nutrition"],["announce","Announcements"],
    ["attendance","Attendance"],["camp","Settings"],["summary","End of Camp"],
  ];
  return (
    <div className="bc-shell">
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 24 }}>
        {BC_TABS.map(([id, label]) => (
          <button key={id} onClick={() => setBcTab(id)} style={{ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${bcTab === id ? BC_GREEN : "#ddd"}`, background: bcTab === id ? BC_GREEN + "22" : "white", color: bcTab === id ? BC_GREEN : "#888", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>{label}</button>
        ))}
      </div>
      <div style={{ background: "white", borderRadius: 16, padding: 24, border: "1px solid #eee" }}>
        {bcTab === "today" && <CoachToday setSelectedMember={() => {}} />}
        {bcTab === "members" && <CoachMembers setSelectedMember={() => {}} />}
        {bcTab === "messages" && <BCMessaging user={user} isCoach={true} />}
        {bcTab === "wins" && <CoachWins user={user} />}
        {bcTab === "workouts" && <CoachPostWorkouts />}
        {bcTab === "videos" && <CoachVideoLibrary />}
        {bcTab === "qa" && <CoachQA user={user} />}
        {bcTab === "nutrition" && <CoachNutritionSettings />}
        {bcTab === "announce" && <CoachAnnouncements />}
        {bcTab === "attendance" && <CoachAttendance />}
        {bcTab === "camp" && <CoachCampSettings />}
        {bcTab === "summary" && <CoachEndOfCampSummary />}
      </div>
    </div>
  );
}

function BelmondoResourcesManager({ defaultProgram = "rooted" }) {
  const program = defaultProgram;
  const [type, setType] = useState("link");
  const [resources, setResources] = useState([]);
  const [title, setTitle] = useState(""); const [url, setUrl] = useState(""); const [content, setContent] = useState(""); const [saving, setSaving] = useState(false);

  useEffect(() => { getProgramResources(program, type).then(setResources); }, [program, type]);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const res = { id: `res_${Date.now()}`, program, type, title: title.trim(), url: url.trim() || null, content: content.trim() || null, sortOrder: resources.length };
    const ok = await saveProgramResource(res);
    if (ok) { setResources(prev => [...prev, { ...res, created_at: new Date().toISOString() }]); setTitle(""); setUrl(""); setContent(""); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await deleteProgramResource(id);
    setResources(prev => prev.filter(r => r.id !== id));
  };

  const accentColor = program === "rooted" ? "#9CAF88" : "#5B9BD5";

  const TYPE_OPTIONS = program === "rooted"
    ? [{ id: "link", label: "Link", Icon: Link }]
    : [
        { id: "link",     label: "Link",     Icon: Link },
        { id: "mindset",  label: "Mindset",  Icon: Sparkle },
        { id: "recipe",   label: "Recipe",   Icon: Coffee },
        { id: "core",     label: "Core",     Icon: Barbell },
        { id: "mobility", label: "Mobility", Icon: Wind },
      ];

  return (
    <div>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Resources Manager</div>
      {TYPE_OPTIONS.length > 1 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {TYPE_OPTIONS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setType(id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 20, border: "1px solid var(--border)", fontFamily: "Jost, sans-serif", fontSize: 12, fontWeight: 700, cursor: "pointer", background: type === id ? accentColor : "white", color: type === id ? "white" : "var(--soft)" }}>
              <Icon size={13} weight={type === id ? "fill" : "regular"} />
              {label}
            </button>
          ))}
        </div>
      )}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 12 }}>Add New {type}</div>
        <div className="form-group"><label>Title</label><input className="form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title or name" /></div>
        <div className="form-group"><label>URL (optional)</label><input className="form-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." /></div>
        <div className="form-group"><label>Description (optional)</label><textarea className="form-input form-textarea" value={content} onChange={e => setContent(e.target.value)} placeholder="Short description..." /></div>
        <button onClick={handleSave} disabled={saving} style={{ background: accentColor, color: "white", border: "none", borderRadius: 10, padding: "10px 20px", fontFamily: "Jost, sans-serif", fontWeight: 700, cursor: "pointer" }}>{saving ? "Saving..." : "Add Resource"}</button>
      </div>
      {resources.length === 0 ? <div className="empty-state">No {type}s added yet</div> : resources.map(r => (
        <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 14px", background: "white", borderRadius: 10, marginBottom: 8, border: "1px solid var(--border)" }}>
          <div><div style={{ fontWeight: 700, fontSize: 14 }}>{r.title}</div>{r.url && <div style={{ fontSize: 12, color: "#9B8AA6" }}>{r.url}</div>}{r.content && <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 2 }}>{r.content}</div>}</div>
          <button onClick={() => handleDelete(r.id)} style={{ background: "none", border: "none", color: "#E8647A", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Delete</button>
        </div>
      ))}
    </div>
  );
}

function PushNotificationSender({ members }) {
  const [mode, setMode] = useState("now"); // "now" | "schedule"
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [sendAt, setSendAt] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);

  const AUDIENCES = [
    { id: "all",       label: "Everyone" },
    { id: "rooted",    label: "Rooted Foundation" },
    { id: "expansion", label: "The Expansion" },
    { id: "pt_client", label: "Ascension" },
    { id: "member",    label: "Boot Camp" },
  ];

  const loadScheduled = async () => {
    setLoadingScheduled(true);
    const data = await getScheduledNotifications();
    setScheduled(data);
    setLoadingScheduled(false);
  };

  useEffect(() => { loadScheduled(); }, []);

  const handleSendNow = async () => {
    if (!title.trim() || !body.trim()) return;
    setSending(true);
    setResult(null);
    const userIds = audience === "all" ? null : members.filter(m => m.role === audience).map(m => m.id);
    const res = await sendPushNotification({ title: title.trim(), body: body.trim(), userIds });
    setSending(false);
    setResult(res ? `Sent to ${res.sent} of ${res.total} devices` : "Error sending — try again");
    if (res) { setTitle(""); setBody(""); }
    setTimeout(() => setResult(null), 4000);
  };

  const handleSchedule = async () => {
    if (!title.trim() || !body.trim() || !sendAt) return;
    setSending(true);
    setResult(null);
    const data = await scheduleNotification({ title: title.trim(), body: body.trim(), audience, sendAt: new Date(sendAt).toISOString() });
    setSending(false);
    if (data) {
      setResult("Scheduled!");
      setTitle(""); setBody(""); setSendAt(""); setAudience("all");
      loadScheduled();
    } else {
      setResult("Error scheduling — try again");
    }
    setTimeout(() => setResult(null), 4000);
  };

  const handleDelete = async (id) => {
    await deleteScheduledNotification(id);
    setScheduled(prev => prev.filter(n => n.id !== id));
  };

  const audienceLabel = (a) => AUDIENCES.find(x => x.id === a)?.label || a;

  const formatSendAt = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " at " +
      d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  // min datetime-local value = now (local)
  const nowLocal = () => {
    const d = new Date();
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  };

  const canSend = title.trim() && body.trim();
  const canSchedule = canSend && sendAt;

  return (
    <div className="card" style={{ borderLeft: "3px solid #9B8AA6" }}>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Push Notifications</div>
      <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 16 }}>Pops up on your clients' phones even when they're not in the app.</div>

      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, background: "#f0f0f0", borderRadius: 10, padding: 3, width: "fit-content" }}>
        {[{ id: "now", label: "Send Now" }, { id: "schedule", label: "Schedule" }].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)} style={{ padding: "7px 20px", borderRadius: 8, border: "none", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", background: mode === m.id ? "#9B8AA6" : "transparent", color: mode === m.id ? "white" : "#888", transition: "all 0.15s" }}>{m.label}</button>
        ))}
      </div>

      {/* Audience */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--soft)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Send to</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {AUDIENCES.map(a => (
            <button key={a.id} onClick={() => setAudience(a.id)} style={{ padding: "6px 14px", borderRadius: 20, border: "none", fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", background: audience === a.id ? "#9B8AA6" : "#f0f0f0", color: audience === a.id ? "white" : "#888" }}>{a.label}</button>
          ))}
        </div>
      </div>

      {/* Title + Message */}
      <div className="form-group">
        <label>Title</label>
        <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Don't forget to check in today!" />
      </div>
      <div className="form-group">
        <label>Message</label>
        <textarea className="form-input form-textarea" value={body} onChange={e => setBody(e.target.value)} placeholder="e.g. Your streak is waiting — let's keep it going 💪" style={{ minHeight: 70 }} />
      </div>

      {/* Schedule date/time picker */}
      {mode === "schedule" && (
        <div className="form-group">
          <label>Send at (your local time)</label>
          <input type="datetime-local" className="form-input" value={sendAt} min={nowLocal()} onChange={e => setSendAt(e.target.value)} />
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {mode === "now" ? (
          <button onClick={handleSendNow} disabled={sending || !canSend} style={{ padding: "10px 24px", background: "#9B8AA6", color: "white", border: "none", borderRadius: 10, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", opacity: !canSend ? 0.5 : 1 }}>
            {sending ? "Sending…" : "Send Now"}
          </button>
        ) : (
          <button onClick={handleSchedule} disabled={sending || !canSchedule} style={{ padding: "10px 24px", background: "#9B8AA6", color: "white", border: "none", borderRadius: 10, fontFamily: "Jost, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", opacity: !canSchedule ? 0.5 : 1 }}>
            {sending ? "Scheduling…" : "Schedule Notification"}
          </button>
        )}
        {result && <div style={{ fontSize: 13, color: result.startsWith("Error") ? "#E53E3E" : "#48BB78", fontWeight: 700 }}>{result}</div>}
      </div>

      {/* Upcoming scheduled list */}
      {scheduled.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: "var(--soft)", textTransform: "uppercase", letterSpacing: 1 }}>Upcoming Scheduled</div>
          {scheduled.map(n => (
            <div key={n.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 14px", background: "var(--bg-alt, #f8f8f8)", borderRadius: 10, marginBottom: 8, gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{n.title}</div>
                <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.body}</div>
                <div style={{ fontSize: 11, color: "#9B8AA6", fontWeight: 700 }}>{audienceLabel(n.audience)} · {formatSendAt(n.send_at)}</div>
              </div>
              <button onClick={() => handleDelete(n.id)} style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "2px 4px", flexShrink: 0 }} title="Cancel">✕</button>
            </div>
          ))}
        </div>
      )}
      {loadingScheduled && <div style={{ fontSize: 12, color: "var(--soft)", marginTop: 12 }}>Loading scheduled…</div>}
    </div>
  );
}

function BelmondoSettings() {
  const [codes, setCodes] = useState({ expansion_invite_code: "", rooted_invite_code: "" });
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    Promise.all([getSetting('expansion_invite_code')]).then(([exp]) => {
      setCodes({ expansion_invite_code: exp || "" });
    });
  }, []);
  const handleSave = async () => {
    await saveSetting('expansion_invite_code', codes.expansion_invite_code);
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  return (
    <div>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, fontWeight: 700, marginBottom: 20 }}>Program Settings</div>
      <div className="card">
        <div style={{ fontWeight: 700, marginBottom: 4 }}>The Expansion — Invite Code</div>
        <div style={{ fontSize: 12, color: "var(--soft)", marginBottom: 10 }}>Share this code with members who are joining The Expansion.</div>
        <input className="form-input" value={codes.expansion_invite_code} onChange={e => setCodes(c => ({ ...c, expansion_invite_code: e.target.value }))} placeholder="e.g. EXPANSION2025" />
        <div style={{ fontWeight: 700, marginTop: 16, marginBottom: 4 }}>The Rooted Foundation</div>
        <div style={{ fontSize: 12, color: "var(--soft)" }}>Free program — no invite code required.</div>
        <button onClick={handleSave} style={{ marginTop: 16, background: "#9B8AA6", color: "white", border: "none", borderRadius: 10, padding: "10px 20px", fontFamily: "Jost, sans-serif", fontWeight: 700, cursor: "pointer" }}>{saved ? "✓ Saved!" : "Save Settings"}</button>
      </div>
    </div>
  );
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gt_current_user')) || null; } catch { return null; }
  });
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);

  const handleLogin = (user) => {
    localStorage.setItem('gt_current_user', JSON.stringify(user));
    setCurrentUser(user);
    if (!user.email_prompted && (user.role === 'member' || user.role === 'pt_client')) {
      setShowEmailPrompt(true);
    }
    if (user.role !== 'belmondo_coach' && user.role !== 'coach') {
      setTimeout(() => registerPushSubscription(user.id), 3000);
    }
  };

  // Also register if already logged in from a previous session
  useEffect(() => {
    if (currentUser && currentUser.role !== 'belmondo_coach' && currentUser.role !== 'coach') {
      setTimeout(() => registerPushSubscription(currentUser.id), 3000);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('gt_current_user');
    setCurrentUser(null);
    setShowEmailPrompt(false);
  };

  const handleEmailDone = (email) => {
    const updated = { ...currentUser, email, email_prompted: true };
    localStorage.setItem('gt_current_user', JSON.stringify(updated));
    setCurrentUser(updated);
    setShowEmailPrompt(false);
  };

  if (!currentUser) return <><style>{styles}</style><AuthScreen onLogin={handleLogin} /></>;
  if (showEmailPrompt) return <EmailPrompt user={currentUser} onDone={handleEmailDone} />;
  // new tier routing handled below after component definitions
  if (currentUser.role === "pt_client") return <PTClientApp user={currentUser} onLogout={handleLogout} />;
  if (currentUser.role === "coach") return <CoachApp user={currentUser} onLogout={handleLogout} />;
  if (currentUser.role === "rooted") return <RootedApp user={currentUser} onLogout={handleLogout} />;
  if (currentUser.role === "expansion") return <ExpansionApp user={currentUser} onLogout={handleLogout} />;
  if (currentUser.role === "belmondo_coach") return <BelmondoCoachDashboard user={currentUser} onLogout={handleLogout} />;
  return <MemberApp user={currentUser} onLogout={handleLogout} />;
}
